import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import MaintenanceBanner from '../components/MaintenanceBanner';
import StudioBrandingOnboarding from '../components/StudioBrandingOnboarding';
import ExportBrandingModal from '../components/ExportBrandingModal';
import OutreachLookbookModal from '../components/OutreachLookbookModal'; // eslint-disable-line no-unused-vars
import ColorAnalysisCard, { useColorMatcher } from '../components/ColorAnalysisCard';
import WeddingPartyQuestionnaire from '../components/WeddingPartyQuestionnaire';
import WeddingSetupModal from '../components/WeddingSetupModal';
import AddCustomSwatchModal from '../components/AddCustomSwatchModal';
import CustomFabricQuestionnaire from '../components/CustomFabricQuestionnaire';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { readClientPhoto, readClientLooks, writeClientPhoto, writeClientLooks, clearSelectionMarkers } from '../lib/clientCache';
import { CLIENT_CONTEXTS, OCCUPATION_CONTEXTS, EVENT_CONTEXTS, contextLabel, isValidContext } from '../lib/clientContexts';
import { ContextTilePicker, iconForContext } from '../components/ContextTilePicker';

// ────────────────────────────────────────────────────────────────────
// Image URL helper — turns a storage path (e.g. "tailorflow/looks/..jpg")
// into an authenticated <img src> URL hitting /api/images/{path}. This
// is the foundation of the Feb-2026 glitchiness fix: instead of every
// gallery response embedding base64 image bytes inside JSON (6-10MB
// per client switch), the gallery returns paths and the browser fetches
// each image natively in parallel with HTTP/2 multiplexing + native
// caching (Cache-Control: 1 year immutable headers on the backend).
//
// Result: client switch payload drops from ~8MB → ~30KB. The images
// stream in over the next ~500ms via parallel HTTP/2 requests, hitting
// the browser cache on every subsequent switch back.
//
// Falls through transparently: if `imageOrPath` is already a data: URI
// or http: URL, we return it as-is (in-flight Gemini renders return
// base64 directly, and we don't want to break that hot path).
const imageUrl = (imageOrPath) => {
  if (!imageOrPath) return null;
  if (typeof imageOrPath !== 'string') return null;
  // Already a data: URL (in-flight Gemini result) or a remote URL
  if (imageOrPath.startsWith('data:') || imageOrPath.startsWith('http')) {
    return imageOrPath;
  }
  // Iter 121 — dropped the `?token=<jwt>` query param that used to be
  // appended here. The token was invalidating the browser cache on
  // every session (the URL identity changed with each new JWT), so
  // every login re-fetched every look image from origin. Now the
  // image URL is stable — `<img src="/api/images/{path}">` — and the
  // `access_token` cookie (bumped to 8h in the same iteration) handles
  // auth automatically. Result: images that were previously cached
  // hit the browser cache instantly on repeat page loads.
  return `${BACKEND_URL}/api/images/${imageOrPath}`;
};

// Given a gallery item from /api/gallery, pick the best available image
// source (thumbnail preferred for grid views, falls back to full path,
// falls back to the legacy inline base64 if the server still sent it).
const galleryItemImage = (it, { thumb = true } = {}) => {
  if (!it) return null;
  const path = (thumb && it.image_path_thumb) || it.image_path;
  if (path) {
    const url = imageUrl(path);
    return it.category?.startsWith('inspiration_')
      ? `${url}?center=inspiration-v1` : url;
  }
  if (it.generated_image) return it.generated_image; // legacy fallback
  return null;
};

// Heuristic: decide whether `value` is a renderable image URL (data:, http(s):,
// or relative /api/...) vs a bare base64 string that needs a data: prefix.
// Necessary because BACKEND_URL is an empty string on preview/prod (same-origin
// pattern in config.js) so imageUrl() returns "/api/images/..." with no
// protocol — `startsWith('http')` alone wouldn't catch those.
// IMPORTANT: must NOT match base64-JPEG strings (which start with "/9j/") —
// hence we anchor on "/api/" specifically rather than any leading slash.
const isRenderableUrl = (v) =>
  typeof v === 'string' && (v.startsWith('data:') || v.startsWith('http') || v.startsWith('/api/'));

// Render-safe src builder. If `v` already looks like a URL, return it as-is.
// Otherwise wrap in a data:image/jpeg base64 prefix. Empty/null → empty src.
const srcFor = (v) => {
  if (!v || typeof v !== 'string') return '';
  if (isRenderableUrl(v)) return v;
  return `data:image/jpeg;base64,${v}`;
};
import CustomizeModal, {
  SUIT_COLORS, SHIRT_COLORS, PANT_COLORS, SHOE_OPTIONS, SHOE_COLORS,
  SUIT_PATTERNS, SHIRT_PATTERNS, LAPEL_OPTIONS, BUTTON_OPTIONS, BUTTON_STYLE_OPTIONS,
  TROUSER_STYLES, TIE_OPTIONS, TIE_COLORS,
} from '../components/CustomizeModal';
import SuitDesignerModal from '../components/SuitDesignerModal';
import FitPicker from '../components/FitPicker';
import CasualShirtsQuestionnaire from '../components/CasualShirtsQuestionnaire';
import SweaterQuestionnaire from '../components/SweaterQuestionnaire';
import DressShirtsQuestionnaire from '../components/DressShirtsQuestionnaire';
import { useStore } from '../contexts/StoreContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { 
  Sparkles, Loader2, X, Shirt, Coffee,
  Palette, User, Users, ThumbsUp, Download,
  FileText, Shuffle, Footprints, ArrowLeft, RefreshCw, Check, Send, ZoomIn, Heart, Layers, Tag, Lock, Plus, Upload, ChevronDown, ChevronLeft, ChevronRight, Trash2, Maximize2, Mail,
  // Iter 229 — occupation / event tile icons (intake step 2)
  Scale, TrendingUp, Cpu, Briefcase, Camera, Stethoscope, Mic, Home, Landmark,
  PartyPopper, Gem, Wine, Sun, Plane, Search,
} from 'lucide-react';

import { BACKEND_URL } from '../config';
import { error as logError, warn as logWarn } from '../lib/logger';
import { createLimiter, withRetry } from '../lib/limiter';

// Gemini rate-limits when too many parallel image generations fire at once.
// Parallel Gemini call limiter — set to 14 (Feb 2026) for snappy new-client
// onboarding. A fresh client photo upload kicks off 11 simultaneous look gens
// (5 Essentials + 6 Style Inspirations). At limit=14 they ALL run in a single
// parallel wave (~12-18s wall-clock) instead of the previous 2-wave staircase
// at limit=8 (~25-30s). Nano Banana via Emergent Universal Key handles 12-16
// concurrent comfortably; backend already has exp-backoff retry (2s/5s/10s)
// + multi-key rotation so the rare 429 self-heals without UI disruption.
// Dial back to 10-12 if 429s trend up in prod.
// Concurrency limiter for parallel Gemini calls. Cut from 14 → 3 (Feb 2026)
// because production was 429-storming Google's Vertex AI quotas when the UI
// fan-out fired all looks at once. With 14 in flight a single accordion +
// Essential 5 + Wedding Party + Inspirations could burst 20+ simultaneous
// calls — Google throttled the entire project, the backend retried each
// call up to 12× per key, and a single image render could take 3+ minutes
// to fail. Three in flight keeps Gemini happy and resolves a 5-look
// subcategory in 30-60s instead of timing out forever.
const genLimit = createLimiter(5);

// ==================== DATA (shared constants imported from CustomizeModal) ====================

// Industry-standard 4-way fit options used by:
//   - Client CRM page (PUT /api/clients) — picks default fit
//   - NewClientIntakeModal — capture fit when adding a walk-in
//   - Generation prompts — fit_label drives the actual silhouette
// Legacy 'semi_slim' value maps to 'modern' on the backend.
// FIT_OPTIONS — user-facing picker (Feb 2026 simplification).
// Only three tiles are shown in the UI now: Classic (the current default
// contemporary silhouette), Savile Row (the roomy British drape cut, new),
// and Slim (close-cut). Legacy stored values `relaxed` and `semi_slim`
// still render correctly on the backend for pre-existing clients — we
// just don't offer them as new picks. `label`s live at module scope so
// the CRM row, the intake modal, the sidebar Fit panel and the fit-preview
// endpoint all read from ONE list.
const FIT_OPTIONS = [
  { value: 'modern',     label: 'Classic',    blurb: 'Contemporary tailoring — the default look' },
  { value: 'savile_row', label: 'Savile Row', blurb: 'Roomy British drape — softly-shouldered, generous chest, fuller leg' },
  { value: 'slim',       label: 'Slim',       blurb: 'Close-cut body, tapered leg' },
];

// Pattern sub-categories — generate 5 suits in each style
// Full-Suit style subcategories — REPLACED Feb 2026: stripes / checks /
// patterned (random combo generators) traded out for two curated HOUSE
// SIGNATURE pools — Tom Ford and Brunello Cucinelli — so the gallery sells
// recognizable aspirational looks instead of generic pattern variations.
const STYLE_SUBCATEGORIES = [
  { value: 'tom_ford',           label: 'Tom Ford',           curated: true, house: 'tom_ford',           desc: '5 strong-shouldered Tom Ford signature suits' },
  { value: 'brunello_cucinelli', label: 'Brunello Cucinelli', curated: true, house: 'brunello_cucinelli', desc: '5 soft-tailored Cucinelli signature looks' },
  { value: 'pinstripes',         label: 'Pinstripes',         curated: true, desc: '5 chalk-stripe & pinstripe suits' },
  { value: 'windowpanes',        label: 'Windowpanes',        curated: true, desc: '5 windowpane-checked suits' },
  { value: 'formal',             label: 'Formal',             curated: true, desc: '5 gala & black-tie dinner jackets' },
  { value: 'patterned',          label: 'Patterned',          curated: true, desc: '5 sharkskin · herringbone · birdseye textures' },
  // Feb 2026 — Template Looks. Populated ONLY by looks seeded from a
  // /admin/templates apply — never auto-generated. Renders whatever
  // `full_suits_templates` looks the client has, no shuffle, no fill.
  { value: 'templates',          label: 'Template Looks',     curated: false, is_template: true, desc: 'Suits seeded from your saved templates' },
];

// ── WOMEN'S SUBCATEGORIES (Feb 2026) ────────────────────────────────────
// Auto-swapped for female clients. Each subcategory's combos carry an
// explicit women-silhouette `fabric_note` so Gemini renders a feminine
// hourglass — princess seams, bust darts, nipped waist, women's lapel
// scale — never a man's suit on a woman. The `gender: 'female'` field is
// added to the look payload so the backend gender-block prefix kicks in.
const WOMENS_STYLE_SUBCATEGORIES = [
  { value: 'power_suit_women',   label: 'Tailored Pant-Suit', curated: true, desc: '5 women\'s tailored pant-suits — sharp blazer + trouser' },
  { value: 'skirt_suit_women',   label: 'Skirt Suit',         curated: true, desc: '5 pencil / A-line skirt suits with matching jacket' },
  { value: 'sheath_dress_women', label: 'Sheath Dress + Jacket', curated: true, desc: '5 knee-length sheath dresses with tailored jacket' },
  { value: 'le_smoking_women',   label: 'Le Smoking Tuxedo',  curated: true, desc: '5 Saint-Laurent-style women\'s tuxedos & gowns' },
];

// Curated Cigar Night pool — smoking jackets with black tux trousers. 10 variations; the
// subcategory auto-gen and the "Generate New Looks" button each pick a fresh sample of 5,
// excluding combos already on screen so the user always sees new colours.
const CIGAR_NIGHT_COMBOS = [
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#722F37', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Burgundy Velvet Smoking Jacket', fabric_note: 'BURGUNDY velvet SMOKING JACKET with black silk-satin shawl lapels and matching frogged closures, worn OVER solid BLACK wool tuxedo trousers with satin side-stripe, crisp white French-cuff dress shirt underneath, black silk bow tie, cigar-lounge ambience with mahogany wood panelling and warm amber lighting', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#1F3A2E', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Forest Green Velvet Smoking Jacket', fabric_note: 'FOREST GREEN velvet SMOKING JACKET with black silk shawl collar and self-tie silk belt, over solid BLACK wool tuxedo trousers with satin side stripe, white pleated-front dress shirt, black silk bow tie, cigar-lounge setting', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#0A1128', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Midnight Navy Velvet Smoking Jacket', fabric_note: 'MIDNIGHT NAVY velvet SMOKING JACKET with black silk peak lapels, one-button closure, over solid BLACK wool tux trousers with satin stripe, white French-cuff shirt, onyx studs, black silk bow tie, private-club cigar-room ambience', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Black Brocade Smoking Jacket', fabric_note: 'BLACK silk-BROCADE smoking jacket with tonal damask pattern, black silk shawl collar, worn OVER solid BLACK tuxedo trousers with satin stripe, white pleated dress shirt, black silk bow tie, cigar-lounge mood with deep emerald leather armchairs and low lamp-light', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#3B2F2F', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Chocolate Velvet Smoking Jacket', fabric_note: 'CHOCOLATE BROWN velvet smoking jacket with black silk satin shawl lapels and self-tie silk belt, over solid BLACK wool tuxedo trousers with satin side-stripe, white French-cuff shirt, black silk bow tie, gentleman\u2019s cigar-room ambience', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#4A2545', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Aubergine Velvet Smoking Jacket', fabric_note: 'DEEP AUBERGINE velvet smoking jacket with jet-black silk peak lapels and black velvet covered buttons, worn OVER solid BLACK wool tuxedo trousers with satin stripe, white French-cuff shirt, black silk bow tie, speakeasy cigar-parlour lighting', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#2B2B2B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Charcoal Paisley Smoking Jacket', fabric_note: 'CHARCOAL silk PAISLEY smoking jacket with tonal self-pattern, black silk shawl lapels, worn OVER solid BLACK wool tux trousers with satin stripe, white pleated shirt, black silk bow tie, dim cigar-lounge atmosphere', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#8B1E2E', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Oxblood Velvet Smoking Jacket', fabric_note: 'OXBLOOD RED velvet smoking jacket with black silk shawl lapels, one-button covered closure, over solid BLACK wool tuxedo trousers with satin side-stripe, crisp white French-cuff dress shirt, black silk bow tie, cigar-lounge with antique leather chesterfield and warm sconce light', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#1A3A52', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Teal Velvet Smoking Jacket', fabric_note: 'DEEP TEAL velvet smoking jacket with black silk peak lapels and pickstitched edges, worn OVER solid BLACK wool tuxedo trousers with satin stripe, white pleated shirt, black silk bow tie, private-room cigar ambience', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Smoking Jacket', garment_id: 'tuxedo', suit: '#C5A572', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'cigar_night', label: 'Champagne Brocade Smoking Jacket', fabric_note: 'CHAMPAGNE GOLD silk-BROCADE smoking jacket with tonal jacquard pattern, black silk shawl lapels, worn OVER solid BLACK wool tuxedo trousers with satin stripe, white French-cuff pleated shirt, black silk bow tie, elegant cigar-lounge with moody gold uplighting', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
];

// Curated Gala Night pool — velvet / ceremonial dinner jackets. 10 variations.
const GALA_NIGHT_COMBOS = [
  { garment: 'Velvet Tuxedo', garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Black Velvet Peak-Lapel Tuxedo', fabric_note: 'BLACK velvet dinner jacket with satin peak lapels and covered buttons, worn with solid BLACK tuxedo trousers (satin side-stripe), white pleated-front formal shirt, black silk bow tie, black patent Oxford shoes. Grand gala ballroom, chandelier light', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Velvet Dinner Jacket', garment_id: 'tuxedo', suit: '#1F3A2E', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Emerald Velvet Dinner Jacket', fabric_note: 'EMERALD GREEN velvet dinner jacket with black silk shawl lapels, worn with solid BLACK tuxedo trousers (satin stripe), crisp white pleated-front dress shirt, black silk bow tie, black patent leather Oxfords. Marble-floor gala ballroom with soft chandelier glow', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Velvet Dinner Jacket', garment_id: 'tuxedo', suit: '#722F37', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Burgundy Velvet Dinner Jacket', fabric_note: 'DEEP BURGUNDY velvet dinner jacket with black silk peak lapels, worn with solid BLACK tuxedo trousers (satin stripe), white French-cuff pleated-front dress shirt, onyx studs, black silk bow tie, black patent Oxford shoes. Opulent gala setting', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Ivory Dinner Jacket', garment_id: 'tuxedo', suit: '#F5F1E8', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Ivory Double-Breasted Dinner Jacket', fabric_note: 'IVORY off-white DOUBLE-BREASTED dinner jacket with self-fabric peak lapels, 6x1 buttoning, worn with solid BLACK tux trousers (satin side-stripe), white pleated shirt, black silk bow tie, black patent Oxfords. Riviera gala evening with soft dusk light', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Brocade Dinner Jacket', garment_id: 'tuxedo', suit: '#0A1128', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Midnight Blue Brocade Dinner Jacket', fabric_note: 'MIDNIGHT BLUE silk-BROCADE dinner jacket with tonal jacquard pattern and black silk shawl lapels, worn with solid BLACK tuxedo trousers (satin stripe), crisp white pleated dress shirt, black silk bow tie, black patent Oxfords. Red-carpet ceremony ambience, backlit with soft gold uplighting', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Velvet Dinner Jacket', garment_id: 'tuxedo', suit: '#4A2545', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Plum Velvet Shawl-Lapel Dinner Jacket', fabric_note: 'DEEP PLUM velvet dinner jacket with jet-black silk shawl lapels, worn with solid BLACK tuxedo trousers (satin stripe), white French-cuff pleated dress shirt, black silk bow tie, black patent Oxfords. Museum-gala ballroom with dramatic side lighting', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Velvet Dinner Jacket', garment_id: 'tuxedo', suit: '#5A3A1F', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Cognac Velvet Dinner Jacket', fabric_note: 'WARM COGNAC velvet dinner jacket with black silk peak lapels and black satin covered buttons, worn with solid BLACK tux trousers (satin side-stripe), white pleated-front shirt, black silk bow tie, black patent Oxfords. Bronze-lit ceremonial ballroom', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Brocade Dinner Jacket', garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Black-on-Black Floral Brocade Tuxedo', fabric_note: 'BLACK silk FLORAL BROCADE tuxedo jacket with tonal black self-pattern and black silk peak lapels, worn with solid BLACK tuxedo trousers (satin stripe), crisp white pleated-front shirt, black silk bow tie, black patent Oxfords. Designer red-carpet look', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Velvet Dinner Jacket', garment_id: 'tuxedo', suit: '#2B3E50', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Sapphire Velvet Dinner Jacket', fabric_note: 'RICH SAPPHIRE BLUE velvet dinner jacket with black silk shawl lapels, worn with solid BLACK tuxedo trousers (satin stripe), white pleated dress shirt, black silk bow tie, black patent Oxfords. Grand opera-gala entrance with crystal chandeliers', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Velvet Dinner Jacket', garment_id: 'tuxedo', suit: '#8B6F47', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'gala_night', label: 'Champagne Velvet Dinner Jacket', fabric_note: 'CHAMPAGNE-GOLD velvet dinner jacket with jet-black silk peak lapels and covered buttons, worn with solid BLACK tuxedo trousers (satin stripe), white pleated-front formal shirt, black silk bow tie, black patent Oxfords. Gilded ballroom gala with ornate woodwork and warm uplighting', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
];

// ── Tom Ford signature suits ────────────────────────────────────────────────
// House DNA baked into every fabric_note: strong padded shoulders, suppressed
// waist, longer jacket length, WIDE PEAK LAPELS, cinematic side-light, plush
// luxury-hotel-lobby backdrop (marble / dark wood / brass / mood lighting).
// Each `house` field flags the card for the wordmark overlay in the UI.
const TOM_FORD_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#0B0B0B', shirt: 'White',     shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Onyx Power Suit',         fabric_note: 'Tom Ford SIGNATURE silhouette — STRONG STRUCTURED padded SHOULDERS with a clean roped sleeve-head, SUPPRESSED nipped WAIST, LONGER JACKET LENGTH that covers the seat, WIDE peak lapels in matching cloth, dual side vents. Solid jet-black super-150s worsted wool, hand-padded peak lapels, working surgeon cuffs, dark horn buttons. Crisp white spread-collar dress shirt with hand-rolled placket, jet-black silk grenadine tie, black silk pocket square folded flat. Background: LUXURY HOTEL LOBBY — polished black marble columns, brass sconces, deep mahogany walls, CINEMATIC LOW-KEY SIDE LIGHTING with deep shadow, plush carpet, ambient warm tungsten glow.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White',     shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Midnight Navy',           fabric_note: 'Tom Ford SIGNATURE silhouette — strong structured padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels, dual side vents. Midnight navy super-150s wool, sharp 3D drape, hand-padded peak lapels. White spread-collar shirt, midnight-navy silk grenadine tie, ivory silk pocket square. Background: 5-STAR HOTEL LOBBY at night — black marble floor reflecting brass chandeliers, walnut panelling, CINEMATIC SIDE LIGHTING from a single warm bronze sconce, deep velvet armchairs in the bokeh.', lapel_override: 'peak' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#36454F', shirt: 'White',  shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Charcoal Double-Breasted', fabric_note: 'Tom Ford double-breasted SIGNATURE — 6x2 buttoning, strong padded shoulders with roped sleeve-head, deeply suppressed waist, longer jacket length, WIDE peak lapels in matching cloth, dual side vents. Charcoal super-150s worsted wool. White spread-collar shirt, deep burgundy silk grenadine tie, white linen pocket square TV-fold. Background: LUXURY HOTEL LOBBY — black marble pillars, vintage Persian rug, warm brass accents, CINEMATIC SIDE LIGHTING, deep shadow on the offside.', lapel_override: 'peak' },
  { garment: 'Tuxedo / Black Tie', garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Classic Black Tuxedo', fabric_note: "Tom Ford SIGNATURE tuxedo — strong padded shoulders, deeply suppressed waist, longer jacket length covering the seat, WIDE black SATIN PEAK LAPELS, one-button satin-covered closure, dual jetted hip pockets, no side vents. Black super-150s wool with matching satin facings, black silk satin trouser stripe. White pleated-front formal shirt, jet-black silk SATIN BOW TIE, onyx & mother-of-pearl studs, black silk pocket square. Black patent leather opera pumps. Background: LUXURY HOTEL BALLROOM ANTEROOM — black marble, brass detailing, CINEMATIC LOW-KEY SIDE LIGHTING, single column of warm light raking across the lapel.", lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#36454F', shirt: 'White',     shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Charcoal Power Suit',     fabric_note: 'Tom Ford SIGNATURE silhouette — strong padded shoulders, suppressed waist, longer jacket length covering the seat, WIDE peak lapels in matching cloth, dual side vents. Deep charcoal super-150s worsted wool with a smooth, refined drape — a CLASSIC TWO-PIECE SUIT (NEVER velvet, NEVER a dinner jacket). White spread-collar dress shirt, charcoal-and-silver silk grenadine tie, white linen pocket square TV-fold. Background: 5-STAR HOTEL ATRIUM at twilight — Carrara marble columns, brass chandeliers casting a cinematic glow, walnut panelling, CINEMATIC SIDE LIGHTING with a deep shadow on the offside, leather Chesterfield in the bokeh, ambient warm tungsten ambience.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1F3A2E', shirt: 'White',     shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Forest Green',            fabric_note: 'Tom Ford SIGNATURE — strong padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels in matching cloth. Deep forest-green wool flannel with hidden depth. White spread-collar shirt, charcoal silk grenadine tie, hunter-green silk pocket square. Background: LUXURY HOTEL LIBRARY-BAR — emerald velvet wall panels, vintage brass globe lamps, CINEMATIC SIDE LIGHTING, mahogany bookshelves in soft bokeh.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#3B2F2F', shirt: 'Cream',     shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Chocolate Brown',          fabric_note: 'Tom Ford SIGNATURE silhouette — strong structured shoulders, suppressed waist, longer jacket length, WIDE peak lapels in matching cloth. Rich chocolate-brown super-150s wool. Cream spread-collar dress shirt, tonal chocolate silk grenadine tie, white silk pocket square. Background: LUXURY HOTEL ATRIUM — coffered ceiling, bronze art-deco columns, marble floor, CINEMATIC WARM SIDE LIGHTING, ambient bronze glow.', lapel_override: 'peak' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#1C2841', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Navy Chalk-Stripe DB', fabric_note: 'Tom Ford SIGNATURE double-breasted — 6x2, strong padded shoulders, deeply suppressed waist, longer jacket length, WIDE peak lapels in matching navy cloth, dual side vents. Midnight navy super-150s with subtle chalk pinstripe (4mm spacing). White spread-collar shirt, navy & silver micro-stripe silk grenadine tie, white linen pocket square. Background: LUXURY HOTEL LOBBY — Carrara marble columns, brass elevators, CINEMATIC SIDE LIGHTING from a tall window.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#2B2B2B', shirt: 'White',     shoe: 'oxford',  shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Storm Grey',              fabric_note: 'Tom Ford SIGNATURE — strong padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels. Storm-grey super-150s wool with cool steel undertone. White spread-collar shirt, silver-grey silk grenadine tie, silver-grey silk pocket square. Background: MODERN LUXURY HOTEL — polished concrete with brass inlay, smoked-glass partitions, CINEMATIC COOL SIDE LIGHTING with a warm rim light.', lapel_override: 'peak' },
  { garment: 'Tuxedo / Black Tie', garment_id: 'tuxedo', suit: '#0A1128', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'tom_ford', house: 'tom_ford', label: 'Tom Ford · Midnight Tuxedo', fabric_note: 'Tom Ford SIGNATURE tuxedo — strong padded shoulders, suppressed waist, longer jacket length, WIDE black SATIN PEAK LAPELS, one-button satin-covered closure, no side vents. Midnight-blue super-150s with BLACK SATIN facings (the colour Tom Ford famously wears at night — looks blacker than black under tungsten). White pleated formal shirt, BLACK SATIN bow tie, onyx studs. Background: LUXURY HOTEL BAR — black marble bar-top, antique mirror, brass spirit-rail, CINEMATIC TUNGSTEN LIGHTING with crystal-tumbler bokeh.', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
];

// ── Brunello Cucinelli signature looks ──────────────────────────────────────
// House DNA: soft un-padded "Neapolitan" shoulder, open collar (NO TIE),
// warm earth-tone palette (oatmeal / cashmere / camel / olive / cream),
// Amalfi / Tuscany / Solomeo background, natural warm sunlight.
const BRUNELLO_CUCINELLI_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#C19A6B', shirt: 'White',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Camel Cashmere Suit',    fabric_note: 'Brunello Cucinelli SIGNATURE silhouette — SOFT NEAPOLITAN UN-PADDED shoulder (no roped sleeve-head), natural waist, regular jacket length, slim notch lapels, single side vent, patch hip pockets. CAMEL-COLOURED CASHMERE-WOOL blend with a soft hand and matte finish. Crisp white linen-cotton SPREAD-COLLAR shirt worn OPEN at the collar with NO TIE (top two buttons casually undone). Suede chocolate loafers. Background: AMALFI COAST TERRACE — sun-bleached terracotta tiles, white-washed stone walls, cypress trees, lemon-grove view to a turquoise sea, NATURAL WARM GOLDEN-HOUR SUNLIGHT, soft shadow.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#F5F1E8', shirt: 'White',  shoe: 'loafer', shoeColor: 'Tan',    pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Oatmeal Linen',          fabric_note: 'Brunello Cucinelli SIGNATURE — soft Neapolitan unpadded shoulder, natural waist, slim notch lapels, patch pockets, single vent. OATMEAL-CREAM PURE LINEN with deliberate natural creases and a relaxed lived-in drape. White linen spread-collar shirt OPEN at the collar with NO TIE. Tan suede loafers, no socks. Background: TUSCAN FARMHOUSE COURTYARD — pale-cream limestone walls, ancient olive tree casting dappled shade, terracotta urns, NATURAL WARM AFTERNOON SUNLIGHT.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#556B2F', shirt: 'Cream',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Olive Wool-Linen',       fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded Neapolitan shoulder, natural easy waist, slim notch lapels in matching cloth, patch hip pockets. OLIVE-GREEN WOOL-LINEN blend with a soft matte finish. Cream linen spread-collar shirt OPEN at the collar, NO TIE. Brown suede penny loafers. Background: AMALFI COAST CLIFFTOP VILLA — sun-bleached pastel-yellow stucco wall, climbing bougainvillea, lemon trees, sea-view bokeh, NATURAL WARM SUNLIGHT.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#D2B48C', shirt: 'White',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Sand Cashmere',          fabric_note: "Brunello Cucinelli SIGNATURE — soft unpadded Neapolitan shoulder, natural waist, slim notch lapels. WARM SAND-BEIGE cashmere-wool with soft matte hand. White Sea-Island cotton spread-collar shirt OPEN at the collar with NO TIE. Brown suede loafers. Background: SOLOMEO HILLTOP VILLAGE (Cucinelli's home in Umbria) — honey-coloured medieval stone arches, narrow cobbled lane, olive trees on the hillside, NATURAL GOLDEN AFTERNOON LIGHT.", lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#A47551', shirt: 'White',  shoe: 'loafer', shoeColor: 'Cognac', pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Tobacco Brown',          fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded shoulder, natural waist, slim notch lapels, patch pockets, single vent. TOBACCO-BROWN cashmere-wool with a soft matte finish. White spread-collar linen-cotton shirt OPEN at the collar, NO TIE. Cognac suede tassel loafers. Background: TUSCAN VINEYARD ROW — rust-red vines, sandstone retaining wall, cypress avenue in the background, NATURAL WARM SUNLIGHT at low angle.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#B0A18C', shirt: 'White',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Greige Cashmere',        fabric_note: 'Brunello Cucinelli SIGNATURE — soft Neapolitan unpadded shoulder, natural waist, slim notch lapels. GREIGE (warm grey-beige) cashmere with a powder matte finish. White linen spread-collar shirt OPEN at the collar, NO TIE. Brown suede loafers. Background: AMALFI STAIRCASE LANDING — pale lime-washed wall, white wisteria climbing, terracotta planter, sea bokeh, NATURAL WARM MIDDAY LIGHT.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#8B7355', shirt: 'White',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Tan Cashmere',           fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded shoulder, natural waist, slim notch lapels, patch pockets. WARM TAN cashmere-wool with soft matte finish. White cotton-linen spread-collar shirt worn OPEN at the collar, NO TIE. Brown suede loafers. Background: POSITANO CLIFF VILLA — sun-bleached pastel-pink stucco, vine-covered pergola, sea view, NATURAL GOLDEN-HOUR LIGHT.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#7A6B5D', shirt: 'White',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Walnut Cashmere',        fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded shoulder, natural waist, slim notch lapels, patch pockets, single vent. WALNUT-BROWN cashmere with a matte powder finish. White linen-cotton spread-collar shirt OPEN at the collar, NO TIE. Brown suede penny loafers. Background: UMBRIAN COUNTRYSIDE — rolling hills, ancient olive grove, honey-coloured stone farmhouse, NATURAL WARM AFTERNOON LIGHT.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#E8DCC4', shirt: 'White',  shoe: 'loafer', shoeColor: 'Tan',    pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Cream Linen',            fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded shoulder, natural waist, slim notch lapels, patch pockets, single vent. CREAM PURE-LINEN with deliberate natural creases. White linen spread-collar shirt OPEN at the collar, NO TIE. Tan suede loafers, no socks. Background: AMALFI COASTAL WALK — sun-bleached white-washed stone wall, climbing pink bougainvillea, lemon trees, distant turquoise sea, NATURAL WARM MORNING LIGHT.', lapel_override: 'notch', accessory: 'none' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#5F5444', shirt: 'White',  shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', patternCategory: 'brunello_cucinelli', house: 'brunello_cucinelli', label: 'Cucinelli · Dark Olive Cashmere',    fabric_note: 'Brunello Cucinelli SIGNATURE — soft Neapolitan unpadded shoulder, natural easy waist, slim notch lapels in matching cloth, patch hip pockets. DARK MUTED OLIVE cashmere with a soft matte finish. White linen-cotton spread-collar shirt OPEN at the collar with NO TIE. Brown suede tassel loafers. Background: TUSCAN COUNTRY ROAD — gravel path lined with ancient cypress trees, terracotta-tiled villa in the background, NATURAL WARM GOLDEN-HOUR LIGHT.', lapel_override: 'notch', accessory: 'none' },
];

// ── Pinstripes ──────────────────────────────────────────────────────────────
const PINSTRIPE_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#1C2841', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Navy Chalk-Stripe',         fabric_note: 'Wall-Street navy super-130s wool with crisp white CHALK pinstripe (3-4mm spacing). White spread-collar shirt, silver-grey silk grenadine tie, white linen pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#1C2841', shirt: 'Light Blue', shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Navy Pinstripe',            fabric_note: 'Midnight-navy worsted with subtle WHITE pinstripe (2mm spacing). Light-blue spread-collar shirt, burgundy silk grenadine tie, white linen pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#36454F', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Charcoal Chalk-Stripe',     fabric_note: 'Charcoal-grey worsted with crisp WHITE CHALK pinstripe (4mm spacing). White spread-collar shirt, deep-purple silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#2B2B2B', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Slate Pinstripe',           fabric_note: 'Slate-grey super-130s with narrow IVORY pinstripe (2mm spacing). White spread-collar shirt, navy silk grenadine tie, white pocket square. Black calf oxfords.' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#1C2841', shirt: 'White',    shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Navy Pinstripe DB',         fabric_note: 'Six-button double-breasted in midnight-navy super-130s with subtle WHITE pinstripe (3mm spacing). PEAK lapels in matching cloth, dual side vents. White spread-collar shirt, silver silk grenadine tie, white linen pocket square. Black calf oxfords.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#3B2F2F', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Brown Chalk-Stripe',        fabric_note: 'Chocolate-brown worsted with subtle CREAM CHALK pinstripe (3mm spacing). White spread-collar shirt, tan-and-brown silk knit tie, ivory pocket square. Cognac calf oxfords.' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#36454F', shirt: 'White',    shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Charcoal Chalk DB',         fabric_note: 'Six-button double-breasted in charcoal worsted with broad WHITE CHALK pinstripe (5mm spacing). PEAK lapels in matching cloth, dual side vents. White spread-collar shirt, purple silk grenadine tie, white silk pocket square. Black calf oxfords.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#0A1128', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Midnight Pinstripe',        fabric_note: 'Deep midnight-blue super-150s with subtle WHITE pinstripe (2mm spacing). White spread-collar shirt, navy silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#1F3A2E', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'pinstripes', label: 'Forest Chalk-Stripe',       fabric_note: 'Deep forest-green wool flannel with subtle CREAM CHALK pinstripe (3mm spacing). White spread-collar shirt, hunter-green silk grenadine tie, ivory pocket square. Dark-brown calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#1C2841', shirt: 'White',      shoe: 'oxford', shoeColor: 'Black', pattern: 'rope_stripe', patternCategory: 'pinstripes', label: 'Navy Rope-Stripe',        fabric_note: 'Navy worsted with classic ROPE-STRIPE — alternating thin/thick white rope-pattern stripes (3mm spacing). White spread-collar shirt, gold silk knit tie, white silk pocket square. Black calf oxfords.' },
];

// ── Windowpanes ─────────────────────────────────────────────────────────────
const WINDOWPANE_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Navy Windowpane',           fabric_note: 'Midnight-navy super-130s worsted with subtle TAN WINDOWPANE CHECK (large 5cm x 5cm pane, single-line). White spread-collar shirt, burgundy silk grenadine tie, white linen pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#36454F', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Charcoal Windowpane',       fabric_note: 'Charcoal worsted with subtle CREAM WINDOWPANE check (5cm x 5cm pane). White spread-collar shirt, deep-purple silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#7A6B5D', shirt: 'White',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Walnut Windowpane',         fabric_note: 'Warm walnut-brown wool flannel with TAN WINDOWPANE check (large 5cm x 5cm). White spread-collar shirt, navy-and-tan striped silk knit tie, ivory pocket square. Brown suede penny loafers.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#8B7355', shirt: 'White',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Tan Windowpane',            fabric_note: 'Warm tan wool flannel with subtle BROWN WINDOWPANE check (4cm x 5cm). White spread-collar shirt, brown silk knit tie, ivory linen pocket square. Brown calf loafers.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#556B2F', shirt: 'White',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Olive Windowpane',          fabric_note: 'Muted olive-green wool flannel with TAN WINDOWPANE check (5cm x 5cm). White spread-collar shirt, brown silk knit tie, ivory pocket square. Brown suede loafers.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#808080', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Mid-Grey Windowpane',       fabric_note: 'Mid-grey worsted with subtle BURGUNDY WINDOWPANE accent (5cm x 5cm pane, single-line). White spread-collar shirt, burgundy silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#3B2F2F', shirt: 'Light Blue', shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Chocolate Windowpane',      fabric_note: 'Chocolate-brown wool flannel with CREAM WINDOWPANE check (5cm x 5cm). Light-blue spread-collar shirt, blue-and-brown silk knit tie, ivory pocket square. Cognac calf double-monk straps.' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#36454F', shirt: 'White',   shoe: 'oxford',     shoeColor: 'Black',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Charcoal Windowpane DB',    fabric_note: 'Six-button DB in charcoal worsted with subtle BLUE WINDOWPANE accent (4cm x 5cm). PEAK lapels in matching cloth, dual side vents. White spread-collar shirt, navy silk grenadine tie, white linen pocket square. Black calf oxfords.', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1F3A2E', shirt: 'Cream',      shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Forest Windowpane',         fabric_note: 'Deep forest-green wool flannel with TAN WINDOWPANE check (5cm x 5cm). Cream Sea-Island shirt, brown silk knit tie, ivory linen pocket square. Cognac calf double-monk straps.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#5F5444', shirt: 'White',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'windowpane', patternCategory: 'windowpanes', label: 'Olive-Brown Windowpane',    fabric_note: 'Muted olive-brown wool flannel with CREAM WINDOWPANE check (5cm x 5cm). White spread-collar shirt, brown silk knit tie, white linen pocket square. Brown suede penny loafers.' },
];

// ── Formal · Gala / Black Tie ───────────────────────────────────────────────
const FORMAL_COMBOS = [
  { garment: 'Tuxedo / Black Tie',   garment_id: 'tuxedo',        suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'formal', label: 'Classic Black Tuxedo · Shawl', fabric_note: 'Classic black tuxedo in super-150s wool with BLACK SILK SATIN shawl lapels and matching satin trouser stripe. White pleated-front formal shirt, BLACK SATIN bow tie hand-tied, onyx & mother-of-pearl studs, white silk pocket square TV-fold. Black patent leather opera pumps.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Tuxedo / Black Tie',   garment_id: 'tuxedo',        suit: '#0A1128', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'formal', label: 'Midnight Tuxedo · Peak',     fabric_note: 'Midnight-blue super-150s tuxedo (reads blacker than black under tungsten) with BLACK SATIN peak lapels and matching satin trouser stripe. White pleated formal shirt, BLACK SATIN bow tie, onyx studs, white silk pocket square. Black patent oxfords.', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#722F37', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'velvet', patternCategory: 'formal', label: 'Burgundy Velvet Dinner Jacket', fabric_note: 'Deep burgundy SILK-VELVET dinner jacket worn over solid BLACK formal tuxedo trousers with satin stripe. Hand-padded SHAWL LAPELS in matching velvet, one-button satin-covered closure. White pleated-front formal shirt, BLACK SATIN bow tie, onyx studs, white silk pocket square. Black patent oxfords.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#1F3A2E', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'velvet', patternCategory: 'formal', label: 'Forest Velvet Dinner Jacket',   fabric_note: 'Deep forest-green SILK-VELVET dinner jacket worn over solid BLACK formal tuxedo trousers with satin stripe. Hand-padded SHAWL LAPELS in matching velvet, one-button satin-covered closure. White pleated formal shirt, BLACK SATIN bow tie, white silk pocket square. Black patent oxfords.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#0A1A4F', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'velvet', patternCategory: 'formal', label: 'Navy Velvet Smoking Jacket',    fabric_note: 'Deep navy SILK-VELVET smoking jacket worn over solid BLACK formal tuxedo trousers with satin stripe. Hand-padded SHAWL LAPELS in matching velvet with quilted satin trim, one-button closure, frogged braid detail. White pleated formal shirt, BLACK SATIN bow tie, white silk pocket square. Black patent opera pumps.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Tuxedo / Black Tie',   garment_id: 'tuxedo',        suit: '#F8F4EE', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'formal', label: 'Ivory Dinner Jacket · Tropical', fabric_note: 'Ivory cream tropical-wool dinner jacket worn over solid BLACK formal tuxedo trousers with satin stripe. BLACK SILK SATIN SHAWL lapels and covered buttons, one-button closure. White pleated formal shirt, BLACK SATIN bow tie, BLACK silk cummerbund, white silk pocket square. Black patent oxfords.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Tuxedo / Black Tie',   garment_id: 'tuxedo',        suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'formal', label: 'Black Tuxedo · Peak Lapel',  fabric_note: 'Classic black super-150s tuxedo with BLACK SILK SATIN PEAK lapels and matching satin trouser stripe. White pleated-front formal shirt, BLACK SATIN bow tie hand-tied, onyx & mother-of-pearl studs, white silk pocket square. Black patent leather oxfords.', lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'formal', label: 'Black Tuxedo · Double-Breasted', fabric_note: 'Six-button double-breasted black super-150s tuxedo with BLACK SATIN PEAK lapels and matching satin trouser stripe. White pleated formal shirt, BLACK SATIN bow tie, onyx studs, white silk pocket square. No cummerbund (DB jackets stay closed). Black patent oxfords.', lapel_override: 'peak', accessory: 'black silk bow tie' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#36454F', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'velvet', patternCategory: 'formal', label: 'Charcoal Velvet Smoking',    fabric_note: 'Deep charcoal SILK-VELVET smoking jacket worn over solid BLACK formal tuxedo trousers with satin stripe. Hand-padded SHAWL LAPELS in matching velvet with quilted satin trim, one-button closure, frogged braid detail. White pleated formal shirt, BLACK SATIN bow tie, white silk pocket square. Black patent oxfords.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  suit: '#722F37', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid',  patternCategory: 'formal', label: 'Burgundy Wool Dinner Jacket',  fabric_note: 'Deep burgundy super-150s WOOL dinner jacket (not velvet) worn over solid BLACK formal tuxedo trousers with satin stripe. Hand-padded SHAWL LAPELS faced in BLACK SATIN, one-button satin-covered closure. White pleated formal shirt, BLACK SATIN bow tie, white silk pocket square. Black patent oxfords.', lapel_override: 'shawl', buttons_override: '1', accessory: 'black silk bow tie' },
];

// ── Patterned · Sharkskin · Herringbone · Birdseye · Donegal · Tweed ────────
const PATTERNED_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#36454F', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'sharkskin',   patternCategory: 'patterned', label: 'Charcoal Sharkskin',        fabric_note: 'Charcoal super-130s SHARKSKIN — fine two-tone weave with subtle silver iridescence. White spread-collar shirt, deep-burgundy silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'sharkskin',   patternCategory: 'patterned', label: 'Navy Sharkskin',            fabric_note: 'Midnight-navy super-130s SHARKSKIN — fine two-tone weave with subtle blue iridescence. White spread-collar shirt, silver-grey silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#7A6B5D', shirt: 'White',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'herringbone', patternCategory: 'patterned', label: 'Walnut Herringbone',        fabric_note: 'Warm walnut-brown HERRINGBONE wool flannel — large 1cm chevron pattern. White spread-collar shirt, navy-and-tan striped silk knit tie, ivory pocket square. Brown suede penny loafers.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#2B2B2B', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'herringbone', patternCategory: 'patterned', label: 'Charcoal Herringbone',      fabric_note: 'Charcoal HERRINGBONE wool flannel — fine 5mm chevron pattern. White spread-collar shirt, silver-grey silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'birdseye',    patternCategory: 'patterned', label: 'Navy Birdseye',             fabric_note: 'Navy super-130s BIRDSEYE worsted — micro-dot pattern visible only close up, reads textured from distance. White spread-collar shirt, burgundy silk grenadine tie, white linen pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#36454F', shirt: 'Light Blue', shoe: 'oxford',     shoeColor: 'Black',  pattern: 'birdseye',    patternCategory: 'patterned', label: 'Charcoal Birdseye',         fabric_note: 'Charcoal super-130s BIRDSEYE worsted — micro-dot weave. Light-blue spread-collar shirt, navy silk grenadine tie, white silk pocket square. Black calf oxfords.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#5F5444', shirt: 'White',      shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'donegal',     patternCategory: 'patterned', label: 'Olive Donegal Tweed',       fabric_note: 'Muted olive DONEGAL TWEED — wool flecked with rust, cream and navy nepps. Patch hip pockets, leather button. White spread-collar shirt, hunter-green silk knit tie, white linen pocket square. Cognac calf double-monks.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#7A6B5D', shirt: 'Cream',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'donegal',     patternCategory: 'patterned', label: 'Walnut Donegal Tweed',      fabric_note: 'Warm walnut DONEGAL TWEED — wool flecked with cream, blue and rust nepps. Patch hip pockets, horn buttons. Cream Sea-Island shirt, brown silk knit tie, ivory pocket square. Brown suede penny loafers.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#808080', shirt: 'White',      shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'herringbone', patternCategory: 'patterned', label: 'Mid-Grey Herringbone',      fabric_note: 'Mid-grey HERRINGBONE wool flannel — fine 5mm chevron. White spread-collar shirt, deep-burgundy silk grenadine tie, white silk pocket square. Cognac calf double-monk straps.' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#3B2F2F', shirt: 'Cream',      shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'glen_plaid',  patternCategory: 'patterned', label: 'Brown Glen-Plaid',          fabric_note: 'Chocolate-brown GLEN-PLAID (Prince of Wales check) wool flannel with subtle blue windowpane overlay. Cream Sea-Island shirt, navy-and-cream silk knit tie, ivory pocket square. Cognac calf double-monk straps.' },
];




// ── Women's Tailored Pant-Suit ──────────────────────────────────────────
// Saint-Laurent / Stella McCartney / Max Mara silhouette. Each combo
// hard-codes a women-only silhouette into the fabric_note so Gemini
// renders princess seams + bust dart + nipped waist + women's lapel.
const POWER_SUIT_WOMEN_COMBOS = [
  { garment: "Women's Tailored Pant-Suit", garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'power_suit_women', label: 'Navy Power Pant-Suit', fabric_note: "WOMEN'S TAILORED PANT-SUIT — feminine silhouette with PRINCESS SEAMS through the bust, BUST DART for shape, NIPPED WAIST (4-6 cm narrower than bust/hip), women's NARROW NOTCH LAPEL cut HIGH on the chest, single-breasted one-button closure that sits at the natural waist. Midnight navy super-130s worsted wool. High-rise straight-leg tailored TROUSER cut to the woman's hip-to-waist ratio, hem skimming the top of the shoe. Crisp white silk blouse with a feminine V or open collar (NO men's tie). BLACK pointed-toe leather PUMPS with a 6-8 cm heel. Background: editorial Manhattan office lobby, polished marble floor, late-afternoon side light. Confident feminine pose — one hand in pocket, shoulder slightly forward. NEVER a man's-cut suit.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Tailored Pant-Suit", garment_id: 'classic_suit', suit: '#0B0B0B', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'power_suit_women', label: 'Onyx Le-Smoking Pant-Suit', fabric_note: "WOMEN'S LE SMOKING TUXEDO-INSPIRED PANT-SUIT — Saint-Laurent silhouette. Slim feminine satin-faced PEAK LAPEL (narrower than men's), single-button satin-covered closure, NIPPED WAIST, PRINCESS SEAMS, BUST DART. Jet-black super-150s wool with BLACK SILK SATIN lapels and matching satin SIDE-STRIPE on the tapered cigarette TROUSER. Ivory silk blouse with a soft V-neckline (NO men's tie, optional slim black silk ribbon). BLACK pointed-toe patent PUMPS. Background: dimly-lit private club bar, brass details, low-key tungsten side-light, deep shadow. Editorial confidence. NEVER a man's tuxedo.", lapel_override: 'peak', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Tailored Pant-Suit", garment_id: 'classic_suit', suit: '#36454F', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'pinstripe', patternCategory: 'power_suit_women', label: 'Charcoal Pinstripe Pant-Suit', fabric_note: "WOMEN'S TAILORED PINSTRIPE PANT-SUIT — feminine silhouette with PRINCESS SEAMS, BUST DART, deeply NIPPED WAIST, women's NARROW PEAK LAPEL cut high. Charcoal super-130s wool with subtle white CHALK pinstripe (2-3 mm spacing — natural scale, NOT cartoon stripes). Single-breasted two-button (top button fastened). High-rise wide-leg tailored TROUSER. White silk blouse with a pussy-bow neckline OR a V-neck cami. NUDE leather pointed-toe PUMPS with a slim heel. Background: editorial Wall-Street boardroom with floor-to-ceiling windows and city skyline bokeh, soft side-light. Powerful, polished, feminine.", lapel_override: 'peak', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Tailored Pant-Suit", garment_id: 'classic_suit', suit: '#C19A6B', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', patternCategory: 'power_suit_women', label: 'Camel Cashmere Pant-Suit', fabric_note: "WOMEN'S SOFT-TAILORED CAMEL PANT-SUIT — Max-Mara-house silhouette. Soft natural feminine shoulder with NO padding, PRINCESS SEAMS, light BUST DART, natural easy waist suppression. Slim NOTCH LAPEL in matching cashmere-wool. Single-breasted one-button. Camel CASHMERE-WOOL fabric with a powder matte hand. High-rise straight tailored TROUSER, hem just kissing the shoe. Ivory silk shell top underneath (NO tie). NUDE suede pointed-toe PUMPS. Background: Milan town-house terrace, soft pastel stucco, climbing wisteria, natural golden-hour light. Effortless feminine elegance.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Tailored Pant-Suit", garment_id: 'classic_suit', suit: '#722F37', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'power_suit_women', label: 'Burgundy Statement Pant-Suit', fabric_note: "WOMEN'S TAILORED BURGUNDY PANT-SUIT — bold confident silhouette with PRINCESS SEAMS, BUST DART, NIPPED WAIST creating a clear hourglass, women's slim PEAK LAPEL cut high. Deep burgundy super-150s wool with a subtle sheen. Single-breasted one-button satin-covered closure. High-rise tapered tailored TROUSER. Ivory silk blouse with a deep V-neckline. BLACK pointed-toe patent leather PUMPS. Background: editorial gallery opening, white-walled exhibition space, dramatic single-key warm side-light, deep shadow on the offside. Confident feminine pose — hand on hip, gaze direct.", lapel_override: 'peak', buttons_override: '1', accessory: 'none' },
];

// ── Women's Skirt Suit ──────────────────────────────────────────────────
const SKIRT_SUIT_WOMEN_COMBOS = [
  { garment: "Women's Skirt Suit", garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', patternCategory: 'skirt_suit_women', label: 'Navy Pencil Skirt Suit', fabric_note: "WOMEN'S SKIRT SUIT — feminine silhouette. Tailored blazer with PRINCESS SEAMS, BUST DART, NIPPED WAIST (4-6 cm narrower than bust/hip), women's NARROW NOTCH LAPEL cut HIGH on the chest, single-breasted one-button closure ending at high-hip. Midnight navy super-130s worsted wool. PENCIL SKIRT in matching navy wool — hits AT the knee, contoured hip yoke, back kick-vent, fitted-but-not-tight (1-2 cm ease). Crisp white silk blouse with a feminine V-neckline (NO men's tie). Sheer nude hosiery. NUDE pointed-toe leather PUMPS with a 6-8 cm heel. Background: editorial Manhattan office lobby, polished marble floor, soft side-light. Confident feminine pose. NEVER a man's-cut suit.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Skirt Suit", garment_id: 'classic_suit', suit: '#0B0B0B', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'skirt_suit_women', label: 'Onyx A-Line Skirt Suit', fabric_note: "WOMEN'S SKIRT SUIT — feminine silhouette. Tailored blazer with PRINCESS SEAMS, BUST DART, nipped waist, women's slim PEAK LAPEL cut high, single-breasted one-button covered closure. Jet-black super-150s wool. A-LINE SKIRT (gentle flare from the waist, fitted hip) hitting just AT the knee, in matching black wool. Ivory silk blouse with a soft V-neck OR boat neckline. Sheer black hosiery. BLACK pointed-toe patent PUMPS with a slim heel. Background: minimal-luxe gallery, white walls, dramatic single warm side-light, deep negative-space shadow. Editorial pose.", lapel_override: 'peak', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Skirt Suit", garment_id: 'classic_suit', suit: '#722F37', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'skirt_suit_women', label: 'Burgundy Pencil Skirt Suit', fabric_note: "WOMEN'S SKIRT SUIT — feminine silhouette. Tailored blazer with PRINCESS SEAMS, BUST DART, deeply nipped waist creating a clear hourglass, women's NARROW NOTCH LAPEL cut HIGH on the chest, single-breasted one-button closure. Deep burgundy super-150s wool with a subtle sheen. PENCIL SKIRT in matching burgundy — knee-length, contoured hip yoke, back kick-vent. Ivory silk camisole shell. Sheer nude hosiery. BLACK pointed-toe patent leather PUMPS with a 7 cm heel. Background: art-deco hotel lounge, brass detailing, warm tungsten side-light. Editorial confident pose.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Skirt Suit", garment_id: 'classic_suit', suit: '#C19A6B', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', patternCategory: 'skirt_suit_women', label: 'Camel A-Line Skirt Suit', fabric_note: "WOMEN'S SOFT-TAILORED CAMEL SKIRT SUIT — Max-Mara silhouette. Blazer with soft feminine shoulder, PRINCESS SEAMS, light BUST DART, natural easy waist, slim NOTCH LAPEL in matching cashmere-wool, single-breasted one-button. Camel CASHMERE-WOOL fabric with a powder matte hand. A-LINE SKIRT in matching camel — knee-length, gentle flare from the waist. Cream silk shell top. Sheer nude hosiery. NUDE suede pointed-toe PUMPS. Background: Milan town-house terrace at golden hour, pastel stucco walls, climbing wisteria, natural warm light. Effortless feminine elegance.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Skirt Suit", garment_id: 'classic_suit', suit: '#808080', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'windowpane', patternCategory: 'skirt_suit_women', label: 'Grey Windowpane Skirt Suit', fabric_note: "WOMEN'S SKIRT SUIT — feminine silhouette. Tailored blazer with PRINCESS SEAMS, BUST DART, nipped waist, women's NOTCH LAPEL cut high, single-breasted two-button (top button fastened). Mid-grey worsted wool with a subtle BURGUNDY WINDOWPANE accent (3 cm x 4 cm pane, single thin line — natural tailored scale, NOT cartoon checks). Matching PENCIL SKIRT, knee-length, kick-vent. Crisp white silk blouse with a tied bow at the neck. Sheer nude hosiery. NUDE pointed-toe leather PUMPS. Background: editorial London boardroom, walnut panelling, brass library lamps, soft side-light. Powerful feminine pose.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
];

// ── Women's Sheath Dress + Jacket ───────────────────────────────────────
const SHEATH_DRESS_WOMEN_COMBOS = [
  { garment: "Women's Sheath Dress + Jacket", garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', patternCategory: 'sheath_dress_women', label: 'Navy Sheath Dress · Tailored Jacket', fabric_note: "WOMEN'S SHEATH DRESS WITH MATCHING TAILORED JACKET — feminine silhouette. SHEATH DRESS: knee-length, sleeveless, boat-neckline, PRINCESS SEAMS shaping over the bust and through the waist, BUST DARTS, nipped waist, fitted skim through the hip (1-2 cm ease), back zip. TAILORED JACKET layered open: matches the dress fabric, PRINCESS SEAMS, BUST DART, nipped waist, women's slim NOTCH LAPEL cut HIGH, single-breasted one-button at natural waist, three-quarter sleeves. Navy super-130s worsted wool. Delicate pearl stud earrings. NUDE pointed-toe leather PUMPS with a 6 cm heel. Background: editorial museum gala anteroom, marble columns, soft side-light. Confident feminine pose.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Sheath Dress + Jacket", garment_id: 'classic_suit', suit: '#0B0B0B', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'sheath_dress_women', label: 'Black Sheath · Cropped Bolero', fabric_note: "WOMEN'S BLACK SHEATH DRESS WITH MATCHING CROPPED BOLERO JACKET — feminine silhouette. SHEATH DRESS: knee-length, scoop-neckline, PRINCESS SEAMS, BUST DARTS, nipped waist, fitted skim through the hip. CROPPED BOLERO JACKET in matching black wool — ends at the natural waist, ROUND COLLARLESS NECKLINE (no lapel), single covered button. Jet-black super-150s wool. Pearl drop earrings, delicate gold pendant. BLACK pointed-toe patent PUMPS. Background: art-gallery cocktail event, white walls with single dramatic spotlit canvas in the bokeh, low-key warm side-light. Editorial elegant pose.", lapel_override: 'none', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Sheath Dress + Jacket", garment_id: 'classic_suit', suit: '#722F37', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'sheath_dress_women', label: 'Burgundy Sheath + Open Jacket', fabric_note: "WOMEN'S BURGUNDY SHEATH DRESS WITH MATCHING TAILORED JACKET layered OPEN. SHEATH: knee-length, V-neckline, PRINCESS SEAMS, BUST DARTS, deeply nipped waist creating a clear hourglass, fitted skim through the hip. JACKET: matching burgundy super-150s wool, PRINCESS SEAMS, BUST DART, nipped waist, women's slim NOTCH LAPEL cut HIGH, single-breasted one-button (worn OPEN). Three-quarter or full-length sleeves. Delicate gold hoop earrings. BLACK pointed-toe patent PUMPS with a 7 cm heel. Background: editorial cocktail lounge, brass and amber lighting, warm side-light. Sophisticated feminine pose.", lapel_override: 'notch', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Sheath Dress + Jacket", garment_id: 'classic_suit', suit: '#36454F', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', patternCategory: 'sheath_dress_women', label: 'Charcoal Sheath · Belted Jacket', fabric_note: "WOMEN'S CHARCOAL SHEATH DRESS WITH MATCHING BELTED TAILORED JACKET. SHEATH: knee-length, classic round neckline, PRINCESS SEAMS, BUST DARTS, nipped waist, fitted skim through the hip. JACKET: matching charcoal worsted wool, PRINCESS SEAMS, BUST DART, women's NARROW NOTCH LAPEL cut HIGH, single-breasted with a SELF-TIE FABRIC BELT cinching the natural waist (NOT buttons). Three-quarter sleeves. Pearl stud earrings. NUDE pointed-toe leather PUMPS. Background: editorial Manhattan office building lobby, marble floor, brass detailing, soft side-light. Confident polished pose.", lapel_override: 'notch', buttons_override: 'belt', accessory: 'none' },
  { garment: "Women's Sheath Dress + Jacket", garment_id: 'classic_suit', suit: '#5F4B7F', shirt: 'White', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', patternCategory: 'sheath_dress_women', label: 'Plum Sheath · Cocoon Coat', fabric_note: "WOMEN'S PLUM SHEATH DRESS WITH MATCHING COCOON JACKET. SHEATH: knee-length, boat-neckline, PRINCESS SEAMS, BUST DARTS, nipped waist, fitted skim through the hip. COCOON JACKET: matching plum wool, COLLARLESS round neck, slightly rounded shoulder, soft cocoon body that grazes the dress at hip — NO lapel, NO buttons (open kimono drape), three-quarter bell sleeves. Plum colour throughout, refined matte wool. Delicate gold pendant. NUDE pointed-toe leather PUMPS. Background: editorial perfume-counter / luxury department-store atrium, polished cream marble, brass display lighting, soft glow. Elegant editorial pose.", lapel_override: 'none', buttons_override: '0', accessory: 'none' },
];

// ── Women's Le Smoking / Black-Tie ──────────────────────────────────────
const LE_SMOKING_WOMEN_COMBOS = [
  { garment: "Women's Le Smoking Tuxedo", garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'le_smoking_women', label: 'Saint-Laurent Le Smoking', fabric_note: "WOMEN'S LE SMOKING TUXEDO — Saint-Laurent 1966 silhouette. Tailored tuxedo blazer with PRINCESS SEAMS, BUST DART, deeply nipped waist creating a clean hourglass, women's slim satin-faced PEAK LAPEL cut narrower and higher than men's, single-button satin-covered closure. Jet-black super-150s wool with BLACK SILK SATIN lapel facings. Tapered CIGARETTE TROUSER in matching wool with a BLACK SATIN SIDE-STRIPE down the leg. Crisp WHITE FORMAL SHIRT with a feminine point or hidden-placket — option 1 a thin black silk BOW-TIE (intentional androgynous accent), option 2 unbuttoned open collar with a pearl pendant. BLACK pointed-toe patent leather PUMPS with a slim 8 cm heel. Background: late-night Parisian private club, smoked mirror, brass detail, low-key cinematic side-light. Editorial androgynous feminine pose. NEVER a man's tuxedo.", lapel_override: 'peak', buttons_override: '1', accessory: 'black silk bow tie' },
  { garment: "Women's Tuxedo Dress", garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'le_smoking_women', label: 'Mini Tuxedo Dress', fabric_note: "WOMEN'S TUXEDO DRESS — feminine cocktail silhouette. PRINCESS SEAMS, BUST DARTS, nipped waist, fitted skim through the hip. Hits AT or just above the knee. Long-sleeved with women's slim satin-faced SHAWL LAPEL ending in a single satin-covered button at the waist, fabric folds open at the chest to reveal a fitted bodice. Jet-black super-150s wool with BLACK SILK SATIN lapel facings. Ivory silk camisole peeking at the V. Sheer black hosiery. BLACK pointed-toe patent PUMPS. Background: opening-night theatre lobby, red velvet curtains in the bokeh, brass chandelier, warm side-light. Editorial confident pose.", lapel_override: 'shawl', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Black-Tie Gown", garment_id: 'tuxedo', suit: '#0B0B0B', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'le_smoking_women', label: 'Floor-Length Column Gown', fabric_note: "WOMEN'S FLOOR-LENGTH FORMAL GOWN — elegant column silhouette. PRINCESS SEAMS, BUST DARTS, fitted bodice with a boat or sweetheart neckline (option 1) or a soft V-neck (option 2), nipped waist, columnar skirt sweeping to the floor with a subtle train. Jet-black silk crêpe with a refined matte finish. Delicate diamond stud earrings, slim gold tennis bracelet. BLACK satin pointed-toe PUMPS hidden beneath the hem. Background: opera-house grand staircase, polished marble, gilded balustrade, warm chandelier light. Editorial red-carpet pose. NEVER a man's tuxedo silhouette.", lapel_override: 'none', buttons_override: '0', accessory: 'none' },
  { garment: "Women's Le Smoking Tuxedo", garment_id: 'tuxedo', suit: '#F8F4EE', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'solid', patternCategory: 'le_smoking_women', label: 'Ivory Le Smoking', fabric_note: "WOMEN'S IVORY LE SMOKING TUXEDO. Tailored ivory tropical-wool blazer with PRINCESS SEAMS, BUST DART, nipped waist, women's slim BLACK SILK SATIN-FACED PEAK LAPEL cut narrower and higher than men's, single satin-covered button closure. Matching ivory tapered CIGARETTE TROUSER with a BLACK SATIN SIDE-STRIPE. Crisp WHITE silk blouse with an open V-collar (no tie) or a thin black silk bow-tie. BLACK pointed-toe patent PUMPS. Background: Riviera gala evening, sun-bleached limestone, crystal chandelier behind, warm dusk-tungsten side-light. Editorial poised pose.", lapel_override: 'peak', buttons_override: '1', accessory: 'none' },
  { garment: "Women's Velvet Tuxedo Jacket", garment_id: 'tuxedo', suit: '#722F37', shirt: 'White', shoe: 'pump', shoeColor: 'Black', pattern: 'velvet', patternCategory: 'le_smoking_women', label: 'Burgundy Velvet Tuxedo + Cigarette Trouser', fabric_note: "WOMEN'S BURGUNDY VELVET TUXEDO JACKET + cigarette trouser. JACKET: silk-velvet, PRINCESS SEAMS, BUST DART, nipped waist, women's slim BLACK SILK SATIN-FACED SHAWL LAPEL, single satin-covered button. Burgundy velvet body, BLACK SATIN shawl. CIGARETTE TROUSER in solid black super-150s wool with a black satin side-stripe. White silk blouse with an open V-neckline (no tie). BLACK pointed-toe patent PUMPS. Background: speakeasy cocktail bar, brass spirit rail, antique mirror, warm tungsten side-light. Editorial editorial pose.", lapel_override: 'shawl', buttons_override: '1', accessory: 'none' },
];

// ── Women's BC Wrap Dress ───────────────────────────────────────────────
const WRAP_DRESS_WOMEN_COMBOS = [
  { garment: "Women's Wrap Dress", garment_id: 'single_blazer', suit: '#1C2841', shirt: 'White', pant: '#1C2841', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Navy Jersey Wrap Dress', fabric_note: "WOMEN'S WRAP DRESS — Diane-von-Furstenberg silhouette. SOFT JERSEY OR SILK CREPE in solid navy, faux-wrap closure tied at the natural waist with a SELF-TIE FABRIC BELT creating a clear hourglass, V-neckline (modest depth), three-quarter sleeves, A-line skirt finishing at the knee with a gentle flowing sweep. Feminine BUST shaping built into the wrap fold. Delicate gold pendant. NUDE pointed-toe leather PUMPS. Background: editorial cafe terrace, sun-dappled bistro chairs, late-morning warm side-light. Effortless feminine pose." },
  { garment: "Women's Wrap Dress", garment_id: 'single_blazer', suit: '#722F37', shirt: 'White', pant: '#722F37', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Burgundy Silk Wrap Dress', fabric_note: "WOMEN'S WRAP DRESS in deep burgundy SILK CREPE. Faux-wrap closure with a SELF-TIE silk belt at the natural waist, V-neckline, three-quarter bell sleeves, A-line skirt sweeping to the knee. Feminine bust shaping in the wrap fold. Delicate pearl drop earrings. NUDE suede pointed-toe PUMPS. Background: editorial gallery foyer, white walls, single dramatic warm side-light. Polished feminine pose." },
  { garment: "Women's Wrap Dress", garment_id: 'single_blazer', suit: '#36454F', shirt: 'White', pant: '#36454F', shoe: 'pump', shoeColor: 'Nude', pattern: 'floral', label: 'Charcoal Floral Wrap Dress', fabric_note: "WOMEN'S WRAP DRESS in charcoal SILK CREPE with a subtle TONAL DITSY FLORAL pattern (tiny 5-8 mm motifs scattered evenly, NOT cartoon-sized blooms). Faux-wrap closure with a self-tie silk belt at the natural waist, V-neckline, long fluttery sleeves, A-line skirt to mid-calf. Pearl stud earrings. NUDE leather pointed-toe PUMPS. Background: editorial brunch terrace, soft natural daylight, ivy on the wall. Relaxed feminine pose." },
  { garment: "Women's Wrap Dress", garment_id: 'single_blazer', suit: '#1F3A2E', shirt: 'White', pant: '#1F3A2E', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Forest Green Wrap Dress', fabric_note: "WOMEN'S WRAP DRESS in deep forest-green SILK CREPE. Faux-wrap closure with self-tie belt at the natural waist, soft V-neckline, three-quarter sleeves, A-line skirt sweeping to the knee. Feminine bust shaping in the wrap fold. Delicate gold hoops. NUDE pointed-toe leather PUMPS. Background: editorial city park late-afternoon walk, dappled sunlight through trees. Editorial relaxed pose." },
  { garment: "Women's Wrap Dress", garment_id: 'single_blazer', suit: '#5F4B7F', shirt: 'White', pant: '#5F4B7F', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Plum Knit Wrap Dress', fabric_note: "WOMEN'S WRAP DRESS in plum FINE-KNIT MERINO. Faux-wrap closure with a self-tie knit belt at the natural waist, modest V-neckline, long sleeves, A-line skirt to the knee. Feminine bust shaping built into the wrap fold. Delicate gold pendant. NUDE suede pointed-toe PUMPS. Background: editorial cosy library / private members' club, walnut shelves, warm tungsten lamp light. Polished feminine pose." },
];

// ── Women's BC Midi Skirt + Blouse ──────────────────────────────────────
const MIDI_BLOUSE_WOMEN_COMBOS = [
  { garment: "Women's Midi Skirt + Blouse", garment_id: 'single_blazer', suit: '#F5F1E8', shirt: 'White', pant: '#1C2841', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Ivory Blouse + Navy Midi Skirt', fabric_note: "WOMEN'S OUTFIT: ivory silk BLOUSE + navy MIDI SKIRT. BLOUSE: silk-crepe with a soft pussy-bow neckline tied at the throat OR open V-collar, three-quarter sleeves, feminine bust shaping (DARTS), tucked into the skirt waistband. MIDI SKIRT: navy wool, A-line cut from the natural waist flaring gently, hits at mid-calf, has clear feminine sweep. Slim leather BELT at the natural waist. NUDE pointed-toe leather PUMPS. Background: editorial Mediterranean coastline cafe, sun-bleached pastel walls, soft side-light. Polished feminine pose." },
  { garment: "Women's Midi Skirt + Blouse", garment_id: 'single_blazer', suit: '#0B0B0B', shirt: 'White', pant: '#D2B48C', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Black Blouse + Camel Midi Skirt', fabric_note: "WOMEN'S OUTFIT: black silk BLOUSE + camel MIDI SKIRT. BLOUSE: black silk-crepe with a boat-neckline, three-quarter sleeves, fitted through the bust with darts, tucked into the skirt waistband. MIDI SKIRT: camel-coloured wool, slim PENCIL-CUT to the natural waist down to mid-calf, back kick-vent. Slim black leather BELT at the natural waist. NUDE pointed-toe leather PUMPS. Background: editorial London town-house front steps, painted door, warm afternoon side-light. Polished feminine pose." },
  { garment: "Women's Midi Skirt + Blouse", garment_id: 'single_blazer', suit: '#F5F1E8', shirt: 'White', pant: '#722F37', shoe: 'pump', shoeColor: 'Nude', pattern: 'pleated', label: 'Cream Blouse + Burgundy Pleated Midi', fabric_note: "WOMEN'S OUTFIT: cream silk BLOUSE + burgundy PLEATED MIDI SKIRT. BLOUSE: cream silk-crepe with a soft V-neckline and long fluttery sleeves, gathered at the waist into the skirt. PLEATED MIDI SKIRT: deep burgundy with fine knife-pleats from the natural waist to mid-calf — flowing kinetic movement, NOT chunky pleats. NUDE suede pointed-toe PUMPS. Background: editorial autumn city park, golden-hour side-light, fallen leaves bokeh. Editorial graceful pose." },
  { garment: "Women's Midi Skirt + Blouse", garment_id: 'single_blazer', suit: '#1C2841', shirt: 'Light Blue', pant: '#F5F1E8', shoe: 'pump', shoeColor: 'Nude', pattern: 'stripe', label: 'Striped Shirt + Cream Midi Skirt', fabric_note: "WOMEN'S OUTFIT: blue-and-white VERTICAL-STRIPED COTTON SHIRT (slim feminine fit, BUST DARTS, semi-spread collar, three-quarter rolled-up sleeves) tucked into a CREAM A-LINE MIDI SKIRT in light wool-cotton, mid-calf length with a slim self-belt at the natural waist. NUDE pointed-toe leather PUMPS. Background: editorial Mediterranean garden terrace, terracotta tiles, ivy-covered stone wall, natural midday sun. Editorial relaxed feminine pose." },
  { garment: "Women's Midi Skirt + Blouse", garment_id: 'single_blazer', suit: '#1F3A2E', shirt: 'White', pant: '#F5F1E8', shoe: 'pump', shoeColor: 'Nude', pattern: 'solid', label: 'Forest Green Silk Blouse + Cream Midi', fabric_note: "WOMEN'S OUTFIT: forest-green silk BLOUSE + cream MIDI SKIRT. BLOUSE: silk-charmeuse with a soft cowl neckline, long sleeves, gathered at the natural waist into the skirt. MIDI SKIRT: cream wool-blend, slim A-line cut from the natural waist to mid-calf. Slim gold mesh BELT at the waist. NUDE suede pointed-toe PUMPS. Background: editorial gallery courtyard, polished travertine, soft dappled light. Editorial polished pose." },
];



const SUIT_COLORS_POOL = [
  { value: '#1C2841', name: 'Navy' }, { value: '#36454F', name: 'Charcoal' },
  { value: '#0B0B0B', name: 'Black' }, { value: '#808080', name: 'Grey' },
  { value: '#3B2F2F', name: 'Brown' }, { value: '#722F37', name: 'Burgundy' },
  { value: '#556B2F', name: 'Olive' }, { value: '#D2B48C', name: 'Tan' },
  { value: '#4A4A7A', name: 'Slate Blue' }, { value: '#8B7355', name: 'Camel' },
];

function generateSubcategoryCombos(subcat, existingLooks = []) {
  // All four full-suit subcategories are now CURATED — Cigar Night, Gala
  // Night, Tom Ford, Brunello Cucinelli — each ships with a hand-tuned pool
  // of 10 combos. We randomly sample 5, prioritising combos NOT already on
  // screen so each "Generate New Looks" click reliably produces fresh
  // colours / aesthetics.
  const POOLS = {
    cigar_night: CIGAR_NIGHT_COMBOS,
    gala_night: GALA_NIGHT_COMBOS,
    tom_ford: TOM_FORD_COMBOS,
    brunello_cucinelli: BRUNELLO_CUCINELLI_COMBOS,
    pinstripes: PINSTRIPE_COMBOS,
    windowpanes: WINDOWPANE_COMBOS,
    formal: FORMAL_COMBOS,
    patterned: PATTERNED_COMBOS,
    // Women's subcategories (Feb 2026) — only ever surface when the
    // selected client's gender is female (the UI filters subcategories
    // by gender). Each pool carries a women-silhouette fabric_note and
    // the generator below stamps `gender: 'female'` on the look payload.
    power_suit_women: POWER_SUIT_WOMEN_COMBOS,
    skirt_suit_women: SKIRT_SUIT_WOMEN_COMBOS,
    sheath_dress_women: SHEATH_DRESS_WOMEN_COMBOS,
    le_smoking_women: LE_SMOKING_WOMEN_COMBOS,
  };
  const pool = POOLS[subcat.value];
  if (pool) {
    const existingCat = `full_suits_${subcat.value}`;
    const existingColors = new Set(
      existingLooks.filter(l => l.category === existingCat).map(l => (l.color || '').toLowerCase())
    );
    const fresh = pool.filter(c => !existingColors.has((c.suit || '').toLowerCase()));
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    const ordered = fresh.length >= 5 ? shuffle(fresh) : [...shuffle(fresh), ...shuffle(pool.filter(c => !fresh.includes(c)))];
    return ordered.slice(0, 5);
  }
  // Legacy fallback — random colour+pattern sampler (no longer reachable from
  // the UI since all subcategories above are curated, but kept for safety in
  // case a hand-coded test or future addition wants random sampling again).
  const existingCat = `full_suits_${subcat.value}`;
  const existingKeys = new Set(
    existingLooks
      .filter(l => l.category === existingCat)
      .map(l => `${(l.color || '').toLowerCase()}|${(l.suit_pattern || '').toLowerCase()}`)
  );
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const used = new Set();
  const result = [];
  let attempts = 0;
  while (result.length < 5 && attempts < 40 && Array.isArray(subcat.patterns)) {
    attempts += 1;
    const color = pick(SUIT_COLORS_POOL);
    const pattern = pick(subcat.patterns);
    const key = `${color.value.toLowerCase()}|${pattern.toLowerCase()}`;
    if (used.has(key) || existingKeys.has(key)) continue;
    used.add(key);
    result.push({
      garment: 'Single-Breasted Suit', garment_id: 'classic_suit',
      suit: color.value, shirt: pick(SHIRT_COLORS).name,
      shoe: pick(SHOE_OPTIONS).id, shoeColor: pick(SHOE_COLORS).name,
      pattern, patternCategory: subcat.value, label: `${color.name} ${subcat.label}`,
    });
  }
  return result;
}

// The Essentials — curated foundation. Tuxedo removed (Feb 2026) per
// user request — Black Tie now lives under the new "Formal" subcategory
// where Gala / Wedding / Charity-ball looks belong. Feb 2026 (user spec
// update) — the 5 Essentials are now the SAME canonical foundation for
// every client, in this exact order: Solid Navy, Charcoal, Medium Grey,
// Dark Brown Textured, Tan. Wardrobe context still steers the Style
// Inspirations grid below, but every client walks out with the same
// 5-suit workhorse base.
const SUIT_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'solid',       patternCategory: 'solid',     label: 'Solid Navy (Worsted Wool)',           fabric_note: 'worsted wool' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#36454F', shirt: 'Light Blue', shoe: 'derby',      shoeColor: 'Black',  pattern: 'solid',       patternCategory: 'solid',     label: 'Charcoal Grey (Worsted Wool)',        fabric_note: 'worsted wool' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#7E8388', shirt: 'White',      shoe: 'oxford',     shoeColor: 'Black',  pattern: 'solid',       patternCategory: 'solid',     label: 'Medium Grey (High-Twist Wool)',       fabric_note: 'high-twist wool, fresco weave' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#4A3728', shirt: 'White',      shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'herringbone', patternCategory: 'patterned', label: 'Dark Brown Textured (Herringbone)',   fabric_note: 'rich dark chocolate-brown wool with a subtle herringbone chevron texture — the weave visible up close but reading as a solid dark brown at conversational distance' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#D2B48C', shirt: 'White',      shoe: 'loafer',     shoeColor: 'Brown',  pattern: 'solid',       patternCategory: 'solid',     label: 'Tan (Linen/Cotton)',                  fabric_note: 'linen cotton blend' },
];
// Extra suit variations for "Generate New Looks" — broader colour + pattern palette
const EXTRA_SUIT_COMBOS = [
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#3B2F2F', shirt: 'Light Blue', shoe: 'loafer', shoeColor: 'Cognac', pattern: 'solid', patternCategory: 'solid', label: 'Chocolate Brown Solid', fabric_note: 'fresco wool' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1F3A2E', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'solid', label: 'Forest Green Solid', fabric_note: 'worsted wool' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#722F37', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', patternCategory: 'solid', label: 'Burgundy Solid', fabric_note: 'wool flannel' },
  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', suit: '#36454F', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'pinstripe', patternCategory: 'patterned', label: 'Charcoal Pinstripe DB', fabric_note: 'chalk-stripe wool', lapel_override: 'peak' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#CBD5E1', shirt: 'White', shoe: 'loafer', shoeColor: 'Brown', pattern: 'solid', patternCategory: 'solid', label: 'Light Grey Solid', fabric_note: 'high-twist wool' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#2E4A3B', shirt: 'Cream', shoe: 'derby', shoeColor: 'Cognac', pattern: 'check', patternCategory: 'patterned', label: 'Olive Windowpane', fabric_note: 'windowpane wool' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'chalk_stripe', patternCategory: 'patterned', label: 'Navy Chalk-Stripe', fabric_note: 'worsted wool with chalk stripe' },
  { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#F5F5DC', shirt: 'White', shoe: 'loafer', shoeColor: 'Tan', pattern: 'solid', patternCategory: 'solid', label: 'Ivory Summer', fabric_note: 'linen' },
];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const BC_GARMENTS = [
  { garment: 'Sports Coat', garment_id: 'single_blazer' },
];
// Business Casual scenario categories
const BC_SCENARIOS = [
  {
    value: 'smart_friday', label: 'Smart Friday', desc: 'Polished but relaxed office looks',
    combos: [
      { garment: 'Windowpane Sports Coat', garment_id: 'single_blazer', suit: '#1C2841', shirt: 'Light Blue', pant: '#D2B48C', shoe: 'loafer', shoeColor: 'Brown', pattern: 'windowpane', label: 'Navy Windowpane + Tan Chinos', fabric_note: 'textured windowpane wool-linen blend sports coat, jacket worn OPEN and UNBUTTONED, casual drape' },
      { garment: 'Glen Plaid Sports Coat', garment_id: 'single_blazer', suit: '#36454F', shirt: 'White', pant: '#1C2841', shoe: 'derby', shoeColor: 'Cognac', pattern: 'glen_plaid', label: 'Charcoal Glen Plaid + Dark Jeans', fabric_note: 'glen plaid wool-linen sports coat worn OPEN and UNBUTTONED, paired with dark indigo JEANS (denim), NOT chinos, NOT dress trousers' },
      { garment: 'Houndstooth Sports Coat', garment_id: 'single_blazer', suit: '#808080', shirt: 'White', pant: '#36454F', shoe: 'loafer', shoeColor: 'Brown', pattern: 'houndstooth', label: 'Grey Houndstooth + Charcoal Chinos', fabric_note: 'grey houndstooth wool-linen sports coat worn OPEN and UNBUTTONED over a crisp white oxford collared shirt, paired with charcoal chinos, NO tie' },
      { garment: 'Herringbone Sports Coat with Plain T-Shirt', garment_id: 'single_blazer', suit: '#1C2841', shirt: 'White', pant: '#808080', shoe: 'chelsea_boot', shoeColor: 'Black', pattern: 'herringbone', label: 'Navy Herringbone + White Tee', fabric_note: 'herringbone wool-linen sports coat worn OPEN and UNBUTTONED over a plain white crew-neck t-shirt, NO dress shirt' },
      { garment: 'Wool-Linen Sports Coat', garment_id: 'single_blazer', suit: '#556B2F', shirt: 'Cream', pant: '#1C2841', shoe: 'loafer', shoeColor: 'Brown', pattern: 'houndstooth', label: 'Olive Houndstooth + Dark Jeans', fabric_note: 'houndstooth wool-linen blend sports coat worn OPEN and UNBUTTONED, paired with dark indigo JEANS (denim), NOT chinos' },
    ],
  },
  {
    value: 'weekend_brunch', label: 'Weekend Brunch', desc: 'Effortless weekend sophistication',
    combos: [
      { garment: 'Wool-Silk Forest Windowpane Sports Coat', garment_id: 'single_blazer', suit: '#1F4E2F', shirt: 'Cream', pant: '#F5F5DC', shoe: 'loafer', shoeColor: 'Brown', pattern: 'windowpane', label: 'Forest Windowpane + Cream', fabric_note: 'rich forest-green WOOL-SILK sports coat with a refined cream windowpane overcheck (Italian luxury weave with visible slubby texture and subtle slubs), worn OPEN and UNBUTTONED over a cream linen spread-collar shirt with NO TIE, paired with cream cotton chinos, brown suede loafers. Editorial Italian sprezzatura.' },
      { garment: 'Linen Sports Coat with Plain T-Shirt', garment_id: 'single_blazer', suit: '#556B2F', shirt: 'Cream', pant: '#D2B48C', shoe: 'loafer', shoeColor: 'Brown', pattern: 'houndstooth', label: 'Olive Houndstooth + Cream Tee', fabric_note: 'linen-wool houndstooth sports coat worn OPEN and UNBUTTONED over a plain cream crew-neck t-shirt, NO dress shirt' },
      { garment: 'Wool-Linen Sports Coat', garment_id: 'single_blazer', suit: '#C19A6B', shirt: 'White', pant: '#1C2841', shoe: 'loafer', shoeColor: 'Cognac', pattern: 'solid', label: 'Camel Sports Coat + Navy Chinos', fabric_note: 'camel-tan wool-linen blend sports coat worn OPEN and UNBUTTONED over a crisp white oxford collared shirt, paired with navy chinos, casual weekend fit' },
      { garment: 'Wool-Linen Sports Coat', garment_id: 'single_blazer', suit: '#808080', shirt: 'Light Blue', pant: '#D2B48C', shoe: 'derby', shoeColor: 'Brown', pattern: 'windowpane', label: 'Grey Windowpane + Tan Chinos', fabric_note: 'grey windowpane wool-linen blend sports coat worn OPEN and UNBUTTONED, relaxed casual fit' },
      { garment: 'Wool-Silk Sky Herringbone Sports Coat', garment_id: 'single_blazer', suit: '#7BA7C9', shirt: 'White', pant: '#A59A7C', shoe: 'loafer', shoeColor: 'Cognac', pattern: 'herringbone', label: 'Sky Herringbone + Stone', fabric_note: 'sky-blue and ivory WOOL-SILK herringbone sports coat with visible textured weave (Italian luxury herringbone — bright, summer-weight, breathable), worn OPEN and UNBUTTONED over a crisp white oxford spread-collar shirt NO TIE, paired with stone cotton chinos, cognac suede loafers.' },
    ],
  },
  {
    value: 'date_night', label: 'Date Night', desc: 'Sharp and confident evening looks',
    combos: [
      { garment: 'Textured Sports Coat', garment_id: 'single_blazer', suit: '#0B0B0B', shirt: 'Black', pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', pattern: 'herringbone', label: 'All-Black Herringbone', fabric_note: 'black herringbone textured wool-linen sports coat worn OPEN and UNBUTTONED, paired with black chinos (BLACK fabric, NOT burgundy, NOT red)' },
      { garment: 'Sports Coat with Plain T-Shirt', garment_id: 'single_blazer', suit: '#722F37', shirt: 'Black', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', label: 'Burgundy Blazer + Black Tee', fabric_note: 'slim wool-linen sports coat worn OPEN and UNBUTTONED over a plain black crew-neck t-shirt, paired with black chinos (BLACK fabric)' },
      { garment: 'Windowpane Sports Coat', garment_id: 'single_blazer', suit: '#1C2841', shirt: 'White', pant: '#36454F', shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'windowpane', label: 'Navy Windowpane + Crisp White', fabric_note: 'navy windowpane wool-linen sports coat worn OPEN and UNBUTTONED over a crisp white spread-collar dress shirt, NO tie, paired with charcoal grey trousers' },
      { garment: 'Wool-Linen Sports Coat', garment_id: 'single_blazer', suit: '#36454F', shirt: 'Light Pink', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', pattern: 'glen_plaid', label: 'Charcoal Check + Pink Shirt', fabric_note: 'glen check wool-linen sports coat worn OPEN and UNBUTTONED, paired with black chinos (BLACK fabric, NOT burgundy)' },
      { garment: 'Wool-Linen Sports Coat with T-Shirt', garment_id: 'single_blazer', suit: '#1C2841', shirt: 'White', pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', pattern: 'herringbone', label: 'Navy Herringbone + White Tee', fabric_note: 'navy herringbone wool-linen sports coat worn OPEN and UNBUTTONED over a plain white t-shirt, paired with black JEANS (denim)' },
    ],
  },
  {
    value: 'outdoor_social', label: 'Outdoor Social', desc: 'Garden parties, races & outdoor events',
    combos: [
      { garment: 'Wool-Silk Burgundy Plaid Sports Coat', garment_id: 'single_blazer', suit: '#722F37', shirt: 'White', pant: '#F5F1E8', shoe: 'loafer', shoeColor: 'Brown', pattern: 'bold_plaid', label: 'Burgundy Plaid + Cream', fabric_note: 'rich burgundy WOOL-SILK sports coat with a refined blue & tan plaid overcheck (sophisticated country plaid, NOT loud), worn OPEN and UNBUTTONED over a crisp white spread-collar shirt with NO TIE, paired with cream cotton chinos, brown suede loafers. Fun, confident, garden-party energy.' },
      { garment: 'Windowpane Sports Coat', garment_id: 'single_blazer', suit: '#D2B48C', shirt: 'Light Blue', pant: '#F5F5DC', shoe: 'loafer', shoeColor: 'Cognac', pattern: 'windowpane', label: 'Tan Windowpane + Cream Chinos', fabric_note: 'linen-wool windowpane sports coat worn OPEN and UNBUTTONED' },
      { garment: 'Textured Sports Coat', garment_id: 'single_blazer', suit: '#1C2841', shirt: 'Light Pink', pant: '#D2B48C', shoe: 'loafer', shoeColor: 'Brown', pattern: 'herringbone', label: 'Navy Herringbone + Tan Chinos', fabric_note: 'slim navy herringbone wool-linen sports coat worn OPEN and UNBUTTONED over a light pink collared shirt, NO tie' },
      { garment: 'Houndstooth Sports Coat with Plain T-Shirt', garment_id: 'single_blazer', suit: '#808080', shirt: 'White', pant: '#556B2F', shoe: 'derby', shoeColor: 'Brown', pattern: 'houndstooth', label: 'Grey Houndstooth + White Tee', fabric_note: 'houndstooth wool-linen blend sports coat worn OPEN and UNBUTTONED over a plain white crew-neck t-shirt' },
      { garment: 'Wool-Silk Terracotta Sports Coat', garment_id: 'single_blazer', suit: '#B26A4A', shirt: 'Cream', pant: '#A59A7C', shoe: 'loafer', shoeColor: 'Brown', pattern: 'solid', label: 'Terracotta + Stone Chinos', fabric_note: 'warm terracotta / rust-orange WOOL-SILK sports coat (saturated and confident, summer-weight Italian luxury weave with visible texture), worn OPEN and UNBUTTONED over a cream linen spread-collar shirt with NO TIE, paired with stone cotton chinos, brown suede loafers. Editorial Italian sprezzatura.' },
    ],
  },
  // ── House signature scenarios ─────────────────────────────────────────────
  // Tom Ford + Brunello Cucinelli — same recognisable house DNA as the
  // Full-Suits subcategories but adapted for Business Casual (sports coats,
  // safari jackets, open-collar shirts, no ties).
  {
    value: 'tom_ford', label: 'Tom Ford', desc: '5 strong-shouldered Tom Ford sports coats', house: 'tom_ford',
    combos: [
      { garment: 'Sports Coat', garment_id: 'single_blazer', suit: '#0B0B0B', shirt: 'Black',    pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', pattern: 'solid',     label: 'TF · All-Black Power Sport', house: 'tom_ford', fabric_note: 'Tom Ford SIGNATURE silhouette — STRONG STRUCTURED padded SHOULDERS, SUPPRESSED nipped WAIST, LONGER JACKET LENGTH, WIDE peak lapels, dual side vents. Solid jet-black wool-cashmere sports coat worn OVER a black silk t-shirt, paired with solid BLACK wool trousers (BLACK fabric, NOT burgundy), black Chelsea boots. Jacket worn OPEN. Background: LUXURY HOTEL BAR — black marble, brass detailing, CINEMATIC LOW-KEY SIDE LIGHTING.', lapel_override: 'peak' },
      { garment: 'Sports Coat', garment_id: 'single_blazer', suit: '#36454F', shirt: 'White',    pant: '#0B0B0B', shoe: 'oxford',       shoeColor: 'Black', pattern: 'solid',     label: 'TF · Charcoal Hotel-Lobby',  house: 'tom_ford', fabric_note: 'Tom Ford SIGNATURE — strong padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels in matching cloth, dual side vents. Charcoal super-150s wool sports coat worn OPEN over a crisp white spread-collar dress shirt with NO TIE (top button undone), paired with solid BLACK wool trousers. Background: LUXURY HOTEL LOBBY — black marble columns, brass sconces, CINEMATIC SIDE LIGHTING.', lapel_override: 'peak' },
      { garment: 'Sports Coat', garment_id: 'single_blazer', suit: '#1F3A2E', shirt: 'White',    pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', pattern: 'solid',     label: 'TF · Forest Velvet Jacket',  house: 'tom_ford', fabric_note: 'Tom Ford SIGNATURE silk-velvet sports coat — strong padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels in matching velvet, dual side vents. Deep forest-green silk velvet worn OPEN over a crisp white spread-collar shirt NO TIE, with solid BLACK wool trousers, black Chelsea boots. Background: LUXURY HOTEL CIGAR LOUNGE — green leather chesterfields, brass library lamps, CINEMATIC RIM LIGHT.', lapel_override: 'peak' },
      { garment: 'Sports Coat', garment_id: 'single_blazer', suit: '#722F37', shirt: 'White',    pant: '#0B0B0B', shoe: 'oxford',       shoeColor: 'Black', pattern: 'solid',     label: 'TF · Burgundy Velvet',       house: 'tom_ford', fabric_note: 'Tom Ford SIGNATURE — strong padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels in matching cloth. Deep burgundy silk-velvet dinner jacket fabric worn OPEN over a crisp white spread-collar shirt NO TIE, with solid BLACK trousers, black Oxfords. Background: LUXURY HOTEL BAR — antique mirror, brass spirit-rail, CINEMATIC TUNGSTEN LIGHTING.', lapel_override: 'peak' },
      { garment: 'Sports Coat', garment_id: 'single_blazer', suit: '#3B2F2F', shirt: 'Cream',    pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', pattern: 'solid',     label: 'TF · Chocolate Hotel-Suite', house: 'tom_ford', fabric_note: 'Tom Ford SIGNATURE — strong padded shoulders, suppressed waist, longer jacket length, WIDE peak lapels. Rich chocolate-brown wool sports coat worn OPEN over a cream silk spread-collar shirt NO TIE, with solid BLACK wool trousers, black Chelsea boots. Background: LUXURY HOTEL ATRIUM — coffered ceiling, bronze art-deco columns, CINEMATIC WARM SIDE LIGHTING.', lapel_override: 'peak' },
    ],
  },
  {
    value: 'brunello_cucinelli', label: 'Brunello Cucinelli', desc: '5 soft-tailored Cucinelli sports coats', house: 'brunello_cucinelli',
    combos: [
      { garment: 'Sports Coat',   garment_id: 'single_blazer',  suit: '#C19A6B', shirt: 'White', pant: '#F5F1E8', shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', label: 'BC · Camel Cashmere Sport',     house: 'brunello_cucinelli', fabric_note: 'Brunello Cucinelli SIGNATURE silhouette — SOFT NEAPOLITAN UN-PADDED shoulder, natural waist, slim notch lapels, patch hip pockets, single vent. Camel cashmere-wool sports coat worn OPEN over a crisp white linen spread-collar shirt with NO TIE (top buttons casually undone), paired with oatmeal-cream linen trousers, brown suede loafers no socks. Background: AMALFI COAST TERRACE — terracotta tiles, white-washed walls, lemon trees, NATURAL WARM GOLDEN-HOUR SUNLIGHT.', lapel_override: 'notch', accessory: 'none' },
      { garment: 'Sports Coat',   garment_id: 'single_blazer',  suit: '#556B2F', shirt: 'White', pant: '#D2B48C', shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', label: 'BC · Olive Linen-Wool Sport',   house: 'brunello_cucinelli', fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded Neapolitan shoulder, natural waist, slim notch lapels, patch pockets, single vent. Olive wool-linen sports coat worn OPEN over a white linen spread-collar shirt NO TIE (top buttons open), paired with tan linen chinos, brown suede loafers no socks. Background: TUSCAN FARMHOUSE COURTYARD — limestone walls, ancient olive tree, terracotta urns, NATURAL WARM AFTERNOON SUNLIGHT.', lapel_override: 'notch', accessory: 'none' },
      { garment: 'Sports Coat',   garment_id: 'single_blazer',  suit: '#D9CDB3', shirt: 'White', pant: '#A59A7C', shoe: 'loafer', shoeColor: 'Brown',  pattern: 'windowpane', label: 'BC · Cream Windowpane · Solomeo',    house: 'brunello_cucinelli', fabric_note: 'Brunello Cucinelli SIGNATURE silhouette — soft Neapolitan UN-PADDED shoulder, natural waist, slim notch lapels, patch pockets, single vent. Cream WOOL-SILK sports coat with a refined taupe windowpane overcheck (subtle rectangular pane overlay, Italian luxury weave with visible slubby texture). Worn OPEN over a crisp white linen spread-collar shirt with NO TIE (top buttons casually undone), paired with stone linen chinos, brown suede loafers no socks. Background: SOLOMEO HILLTOP VILLAGE — honey-coloured medieval stone arches, cobbled lane, olive trees, NATURAL GOLDEN LIGHT.', lapel_override: 'notch', accessory: 'none' },
      { garment: 'Sports Coat',   garment_id: 'single_blazer',  suit: '#8B7355', shirt: 'White', pant: '#F5F1E8', shoe: 'loafer', shoeColor: 'Brown',  pattern: 'solid', label: 'BC · Tan Cashmere Sport',       house: 'brunello_cucinelli', fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded shoulder, natural waist, slim notch lapels, patch pockets. Warm tan cashmere sports coat worn OPEN over a white Sea-Island cotton spread-collar shirt NO TIE, with oatmeal linen trousers, brown suede penny loafers no socks. Background: POSITANO CLIFF VILLA — pastel-pink stucco, vine-covered pergola, sea view, NATURAL GOLDEN-HOUR LIGHT.', lapel_override: 'notch', accessory: 'none' },
      { garment: 'Sports Coat',   garment_id: 'single_blazer',  suit: '#E8DCC4', shirt: 'White', pant: '#D2B48C', shoe: 'loafer', shoeColor: 'Tan',    pattern: 'solid', label: 'BC · Cream Linen Sport',        house: 'brunello_cucinelli', fabric_note: 'Brunello Cucinelli SIGNATURE — soft unpadded shoulder, natural waist, slim notch lapels, patch pockets, single vent. Cream pure-linen sports coat with deliberate natural creases worn OPEN over a white linen spread-collar shirt NO TIE (top buttons open), with tan linen chinos, tan suede loafers no socks. Background: AMALFI COASTAL WALK — white-washed stone wall, climbing pink bougainvillea, distant turquoise sea, NATURAL WARM MORNING LIGHT.', lapel_override: 'notch', accessory: 'none' },
    ],
  },
  // Knitwear subcategory removed (Feb 2026 product decision) — sweaters,
  // cardigans, turtlenecks no longer surfaced. Tailoring focus only.
  // Feb 2026 — Template Looks lives at the bottom of the scenarios. It
  // never auto-fires combos; it just shows any looks a tailor seeded
  // onto this client from /admin/templates.
  { value: 'templates', label: 'Template Looks', desc: 'Business casual seeded from your saved templates', is_template: true, combos: [] },
];

// ── Women's BC scenarios (Feb 2026) — auto-swapped for female clients ──
// Same shape as BC_SCENARIOS so the existing renderer iterates them
// without changes. Each combo's fabric_note hard-codes a feminine
// silhouette (wrap fold, midi A-line, bust shaping) so Gemini never
// reverts to a men's sports coat.
const WOMENS_BC_SCENARIOS = [
  { value: 'wrap_dress_women',  label: 'Wrap Dress',          desc: 'Soft jersey & silk wrap dresses', combos: WRAP_DRESS_WOMEN_COMBOS },
  { value: 'midi_blouse_women', label: 'Midi Skirt + Blouse', desc: 'Tailored blouse & midi skirt outfits', combos: MIDI_BLOUSE_WOMEN_COMBOS },
  { value: 'templates',         label: 'Template Looks',      desc: 'Outfits seeded from your saved templates', is_template: true, combos: [] },
];

const ALL_PATTERNS_FLAT = ['solid', 'pinstripe', 'herringbone', 'houndstooth', 'windowpane', 'glen_plaid', 'bold_plaid', 'floral', 'novelty'];

// ==================== OUTERWEAR SUBCATEGORIES (one tab per coat type) ====================
const OUTERWEAR_SUBCATEGORIES = [
  {
    value: 'overcoat', label: 'Overcoats', desc: 'Single & double-breasted wool overcoats',
    combos: [
      { garment: 'Single-Breasted Overcoat', garment_id: 'overcoat', suit: '#C19A6B', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'oxford', shoeColor: 'Brown', lapel: 'notch', buttons: '3', fabric_note: 'knee-length camel wool overcoat, notch lapels, 3-button single-breasted, worn OPEN over a charcoal suit' },
      { garment: 'Double-Breasted Overcoat', garment_id: 'overcoat', suit: '#1C2841', pattern: 'herringbone', shirt: 'White', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '6', fabric_note: 'knee-length navy herringbone wool overcoat, double-breasted 6x2 with peak lapels, worn OPEN over a dark suit' },
      { garment: 'Chesterfield Overcoat', garment_id: 'overcoat', suit: '#36454F', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'oxford', shoeColor: 'Black', lapel: 'notch', buttons: '3', fabric_note: 'classic Chesterfield overcoat in charcoal wool with BLACK velvet collar, knee-length, fly-front, worn OPEN over a matching suit' },
      { garment: 'Polo Coat', garment_id: 'overcoat', suit: '#8B6F4E', pattern: 'solid', shirt: 'White', pant: '#C19A6B', shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '6', fabric_note: 'classic camel POLO coat (double-breasted, half-belt at back, patch pockets, turn-back cuffs) in warm toffee wool, knee-length, worn OPEN' },
      { garment: 'Guards Overcoat', garment_id: 'overcoat', suit: '#0B0B0B', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '6', fabric_note: 'Guards-style knee-length black wool overcoat, double-breasted with peak lapels, structured shoulder, half-belt at the back, worn OPEN' },
    ],
  },
  {
    value: 'car_coat', label: 'Car Coats', desc: 'Hip-to-mid-thigh length, minimal, modern',
    combos: [
      { garment: 'Charcoal Car Coat', garment_id: 'car_coat', suit: '#36454F', pattern: 'solid', shirt: 'Light Blue', pant: '#1C2841', shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'notch', buttons: '4', fabric_note: 'hip-to-mid-thigh length charcoal wool CAR COAT, minimal notch collar, slash pockets, worn OPEN over a sweater and navy chinos' },
      { garment: 'Navy Car Coat', garment_id: 'car_coat', suit: '#1C2841', pattern: 'solid', shirt: 'White', pant: '#C19A6B', shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'notch', buttons: '3', fabric_note: 'hip-length navy wool car coat, notch collar, welted pockets, worn OPEN over a crew-neck sweater and tan chinos' },
      { garment: 'Camel Car Coat', garment_id: 'car_coat', suit: '#C19A6B', pattern: 'herringbone', shirt: 'Cream', pant: '#36454F', shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '3', fabric_note: 'mid-thigh length camel herringbone wool car coat, minimalist notch collar, worn OPEN over a cream turtleneck' },
      { garment: 'Olive Field Car Coat', garment_id: 'car_coat', suit: '#4A5A2F', pattern: 'solid', shirt: 'Cream', pant: '#3B2F2F', shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'notch', buttons: '4', fabric_note: 'hip-length OLIVE waxed-cotton car coat with utility patch pockets, subtle field-jacket influence, worn OPEN over a cream cable-knit sweater' },
      { garment: 'Stone Car Coat', garment_id: 'car_coat', suit: '#A59A7C', pattern: 'solid', shirt: 'White', pant: '#1C2841', shoe: 'derby', shoeColor: 'Brown', lapel: 'notch', buttons: '4', fabric_note: 'stone-grey wool car coat, hip-length, notch collar, worn OPEN over a crisp white shirt and navy chinos' },
    ],
  },
  {
    value: 'trench_coat', label: 'Trench Coats', desc: 'Belted, storm-flap, iconic silhouette',
    combos: [
      { garment: 'Stone Trench Coat', garment_id: 'trench_coat', suit: '#A59A7C', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'derby', shoeColor: 'Brown', lapel: 'notch', buttons: '10', fabric_note: 'classic stone-beige cotton gabardine TRENCH COAT, knee-length, double-breasted with storm flap, epaulettes, and belted waist (belt tied at the back), worn OPEN' },
      { garment: 'Black Trench Coat', garment_id: 'trench_coat', suit: '#0B0B0B', pattern: 'solid', shirt: 'Black', pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'notch', buttons: '10', fabric_note: 'black cotton-poly gabardine TRENCH COAT, knee-length, double-breasted 10-button, storm flap, D-rings, belted, worn OPEN over all-black base' },
      { garment: 'Navy Trench Coat', garment_id: 'trench_coat', suit: '#1C2841', pattern: 'solid', shirt: 'White', pant: '#1C2841', shoe: 'oxford', shoeColor: 'Black', lapel: 'notch', buttons: '10', fabric_note: 'navy cotton gabardine TRENCH COAT, knee-length, double-breasted with storm flap and belted waist, worn OPEN over a navy suit' },
      { garment: 'Olive Trench Coat', garment_id: 'trench_coat', suit: '#4A5A2F', pattern: 'solid', shirt: 'White', pant: '#D2B48C', shoe: 'derby', shoeColor: 'Brown', lapel: 'notch', buttons: '10', fabric_note: 'olive cotton gabardine TRENCH COAT, knee-length, classic trench construction (storm flap, epaulettes, belt), worn OPEN over a white shirt and tan chinos' },
      { garment: 'Taupe Short Trench', garment_id: 'trench_coat', suit: '#8B7D6B', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '8', fabric_note: 'taupe cotton gabardine trench coat, HIP-LENGTH (short trench variant), double-breasted with storm flap and belted waist, worn OPEN' },
    ],
  },
  {
    value: 'mandarin_coat', label: 'Mandarin Coats', desc: 'Standing collar, architectural, modern formal',
    combos: [
      { garment: 'Midnight Mandarin Coat', garment_id: 'mandarin_coat', suit: '#0A1128', pattern: 'solid', shirt: 'Black', pant: '#0B0B0B', shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'mandarin', buttons: '5', fabric_note: 'mid-thigh length midnight navy wool MANDARIN-COLLAR coat (Nehru-style standing collar, NO lapels, NO notch, NO peak), single-breasted with 5 concealed placket buttons, worn OVER a black turtleneck' },
      { garment: 'Charcoal Mandarin Coat', garment_id: 'mandarin_coat', suit: '#36454F', pattern: 'solid', shirt: 'White', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', lapel: 'mandarin', buttons: '5', fabric_note: 'knee-length charcoal wool mandarin-collar coat (standing collar, NO lapels), single-breasted, clean placket, worn OVER a crisp white shirt and black trousers' },
      { garment: 'Ivory Mandarin Coat', garment_id: 'mandarin_coat', suit: '#EDE5C9', pattern: 'solid', shirt: 'White', pant: '#1C2841', shoe: 'loafer', shoeColor: 'Brown', lapel: 'mandarin', buttons: '5', fabric_note: 'mid-thigh length ivory wool-silk mandarin-collar coat (standing Nehru collar, NO lapels), single-breasted with concealed buttons, worn OVER a white shirt and navy trousers' },
      { garment: 'Forest Mandarin Coat', garment_id: 'mandarin_coat', suit: '#2F4F4F', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'mandarin', buttons: '5', fabric_note: 'mid-thigh length deep forest green wool mandarin-collar coat (standing collar, NO lapels), single-breasted, clean silhouette, worn OVER a white shirt and charcoal trousers' },
      { garment: 'Tobacco Mandarin Coat', garment_id: 'mandarin_coat', suit: '#5C4033', pattern: 'solid', shirt: 'Cream', pant: '#3B2F2F', shoe: 'derby', shoeColor: 'Brown', lapel: 'mandarin', buttons: '5', fabric_note: 'mid-thigh length tobacco-brown wool mandarin-collar coat (Nehru standing collar), single-breasted, worn OVER a cream shirt' },
    ],
  },
  {
    value: 'peacoat', label: 'Peacoats', desc: 'Hip-length, naval-derived, heavy wool',
    combos: [
      { garment: 'Navy Peacoat', garment_id: 'peacoat', suit: '#1C2841', pattern: 'solid', shirt: 'White', pant: '#1C2841', shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'peak', buttons: '6', fabric_note: 'hip-length navy melton wool PEACOAT, double-breasted with 6 large anchor-style buttons, broad peak lapels, worn OPEN over a white shirt' },
      { garment: 'Black Peacoat', garment_id: 'peacoat', suit: '#0B0B0B', pattern: 'solid', shirt: 'White', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '6', fabric_note: 'hip-length black melton wool peacoat, double-breasted 6-button with peak lapels, worn OPEN over a white shirt and black trousers' },
      { garment: 'Walnut Brown Peacoat', garment_id: 'peacoat', suit: '#5C4033', pattern: 'herringbone', shirt: 'Cream', pant: '#3B2F2F', shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'peak', buttons: '6', fabric_note: 'hip-length walnut-brown herringbone wool peacoat, double-breasted with 6 oversized buttons, broad peak lapels, worn OPEN over a cream turtleneck' },
      { garment: 'Charcoal Peacoat', garment_id: 'peacoat', suit: '#36454F', pattern: 'solid', shirt: 'Light Blue', pant: '#36454F', shoe: 'derby', shoeColor: 'Black', lapel: 'peak', buttons: '6', fabric_note: 'hip-length charcoal melton wool peacoat, double-breasted with peak lapels, worn OPEN over a light-blue shirt' },
      { garment: 'Forest Peacoat', garment_id: 'peacoat', suit: '#2F4F4F', pattern: 'solid', shirt: 'Cream', pant: '#3B2F2F', shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'peak', buttons: '6', fabric_note: 'hip-length deep forest green melton wool peacoat, double-breasted, worn OPEN over a cream crew-neck sweater' },
    ],
  },
];

// ==================== WEDDING ====================
const WEDDING_GROOM_STYLES = [
  'Classic Tuxedo',
  'Three-Piece Suit',
  'Single-Breasted Suit',
  'Double-Breasted Suit',
  'Morning Suit',
  'Shawl-Lapel Tuxedo',
  'Peak-Lapel Tuxedo',
  'Velvet Dinner Jacket with Black Trousers',
  'White Dinner Jacket with Black Trousers',
  'Ivory Linen Suit',
  'Mandarin-Collar Nehru Suit',
  'Kurta Sherwani',
];
const WEDDING_GROOMSMEN_STYLES = [
  'Matching Single-Breasted Suits',
  'Matching Three-Piece Suits',
  'Matching Tuxedos',
  'Matching Double-Breasted Suits',
  'Matching Shawl-Lapel Tuxedos',
  'Matching Peak-Lapel Tuxedos',
  'Matching Morning Coats',
  'Matching Linen Suits',
  'Matching Mandarin-Collar Suits',
  'Matching Blazers + Chinos',
];
const WEDDING_BRIDESMAIDS_STYLES = [
  'Floor-Length A-Line Dresses',
  'Floor-Length Sheath Dresses',
  'Floor-Length Mermaid Dresses',
  'Floor-Length Empire Waist Dresses',
  'Floor-Length Off-the-Shoulder Dresses',
  'Floor-Length One-Shoulder Dresses',
  'Floor-Length V-Neck Dresses',
  'Floor-Length Halter-Neck Dresses',
  'Floor-Length Strapless Dresses',
  'Tea-Length Dresses',
  'Midi Cocktail Dresses',
  'Two-Piece Sets (Crop Top + Skirt)',
  'Saree',
  'Lehenga Choli',
];

const WEDDING_THEMES = [
  {
    id: 'timeless', title: 'The Classic Affair', desc: 'Timeless & elegant — white, cream, greenery, formal black-and-white',
    gradient: 'from-[#2C2C2C] to-[#1A1A1E]',
    groom: { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#0B0B0B', shirt: 'White', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', label: 'Classic Black Suit', fabric_note: 'fine worsted wool, pristine and timeless', accessory: 'white pocket square and silver cufflinks' },
    groomsmen: { suit: '#36454F', shirt: 'White', label: 'Charcoal Groomsmen' },
    bridesmaids: '#556B2F',
  },
  {
    id: 'romantic', title: 'Garden Romance', desc: 'Soft & romantic — blush, soft blue, champagne, muted lavender',
    gradient: 'from-[#3D2B3D] to-[#1A1A1E]',
    groom: { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#1C2841', shirt: 'Light Pink', shoe: 'loafer', shoeColor: 'Cognac', pattern: 'solid', label: 'Navy with Blush', fabric_note: 'light navy wool, soft romantic feel', accessory: 'blush pink tie and rose gold cufflinks' },
    groomsmen: { suit: '#B0C4DE', shirt: 'White', label: 'Dusty Blue Groomsmen' },
    bridesmaids: '#FFB6C1',
  },
  {
    id: 'vibrant', title: 'Sunset Celebration', desc: 'Bold & vibrant — magenta, teal, citrus tones for high-energy weddings',
    gradient: 'from-[#3D1C2B] to-[#1A1A1E]',
    groom: { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#008080', shirt: 'White', shoe: 'monk_strap', shoeColor: 'Cognac', pattern: 'solid', label: 'Teal Statement', fabric_note: 'rich teal wool-mohair blend', accessory: 'coral pocket square' },
    groomsmen: { suit: '#1C2841', shirt: 'White', label: 'Navy Groomsmen' },
    bridesmaids: '#FF1493',
  },
  {
    id: 'moody', title: 'Midnight Luxe', desc: 'Moody & deep — emerald, sapphire, burgundy, plum for autumn/winter',
    gradient: 'from-[#1A2A1A] to-[#1A1A1E]',
    groom: { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#722F37', shirt: 'Cream', shoe: 'oxford', shoeColor: 'Black', pattern: 'solid', label: 'Burgundy Elegance', fabric_note: 'deep burgundy velvet-touch wool', accessory: 'emerald green pocket square and gold cufflinks' },
    groomsmen: { suit: '#006400', shirt: 'Cream', label: 'Emerald Groomsmen' },
    bridesmaids: '#4B0082',
  },
  {
    id: 'boho', title: 'Rustic Reverie', desc: 'Earth & boho — rust, terracotta, sage green, warm beige for rustic chic',
    gradient: 'from-[#3D2B1C] to-[#1A1A1E]',
    groom: { garment: 'Single-Breasted Suit', garment_id: 'classic_suit', suit: '#D2B48C', shirt: 'Cream', shoe: 'chelsea_boot', shoeColor: 'Brown', pattern: 'herringbone', label: 'Tan Herringbone', fabric_note: 'linen-wool herringbone, relaxed rustic', accessory: 'sage green bow tie and leather boutonniere wrap' },
    groomsmen: { suit: '#808080', shirt: 'Cream', label: 'Grey Groomsmen' },
    bridesmaids: '#CC5500',
  },
];

const WEDDING_SEASONAL = [
  { id: 'spring', label: 'Spring', desc: 'Pastel pinks, sage green, lavender, sky blue', groom_suit: '#B0C4DE', groom_shirt: 'White', groomsmen_suit: '#808080', bridesmaids: '#E6E6FA', accent: '#98FB98' },
  { id: 'summer', label: 'Summer', desc: 'Coral, turquoise, sunny yellow, bright white', groom_suit: '#F5F5DC', groom_shirt: 'White', groomsmen_suit: '#B0C4DE', bridesmaids: '#FF7F50', accent: '#40E0D0' },
  { id: 'fall', label: 'Fall', desc: 'Burgundy, burnt orange, mustard, forest green', groom_suit: '#722F37', groom_shirt: 'Cream', groomsmen_suit: '#556B2F', bridesmaids: '#CC5500', accent: '#DAA520' },
  { id: 'winter', label: 'Winter', desc: 'Emerald, sapphire, ruby, black, silver/gold', groom_suit: '#0B0B0B', groom_shirt: 'White', groomsmen_suit: '#1C2841', bridesmaids: '#006400', accent: '#C0C0C0' },
];

// (removed dead helpers: getPatternCategory + generateRandomCombos — referenced removed constants)

// ==================== STYLE INSPIRATIONS ====================

// Feb 2026 (user spec) — expanded Style Inspirations to lean into
// pattern + colour variety. The 6 hero cards now mix TWO classic solid
// anchors (light-grey Monaco + tuxedo) with FOUR pattern / colour
// statement pieces (navy pinstripe, grey glen plaid, bottle-green
// solid, oxblood windowpane). Wardrobe context (context_profiles.py)
// still swaps in occupation-specific tiles when a context is set —
// this array is only the fallback for "no context yet".
const STYLE_INSPIRATIONS = [
  {
    id: 'power_ceo', title: 'The Power CEO',
    desc: 'Command the boardroom. Navy pinstripe authority.',
    garment_id: 'classic_suit', garment: 'Classic Two-Piece Suit', category: 'full_suits',
    suit: '#1C2841', pattern: 'pinstripe', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'notch', buttons: '2',
    accessory: 'burgundy silk grenadine tie, perfectly knotted with a half-Windsor, four-in-hand dimple',
    gradient: 'from-[#1C2841] to-[#0A0A0C]',
  },
  {
    id: 'highland_glen_plaid', title: 'Highland Glen Plaid',
    desc: 'Grey Glen plaid three-piece. Old-money confidence.',
    garment_id: 'classic_suit', garment: 'Glen Plaid Three-Piece Suit', category: 'full_suits',
    suit: '#6E6E72', pattern: 'glen_plaid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Cognac', lapel: 'notch', buttons: '2',
    accessory: 'burnt-orange silk knit tie, four-in-hand, subtle dimple; ivory linen pocket square TV-fold',
    fabric_note: 'grey Glen plaid worsted wool suit with a subtle sky-blue overcheck — the plaid motif reads clearly across the jacket and matching trousers at conversational distance. Traditional bespoke silhouette, notch lapels, single-breasted two-button.',
    gradient: 'from-[#6E6E72] to-[#2E2E32]',
  },
  {
    id: 'envoy_forest', title: 'The Envoy',
    desc: 'Bottle-green solid suit. Quiet-luxury statement.',
    garment_id: 'classic_suit', garment: 'Classic Two-Piece Suit', category: 'full_suits',
    suit: '#1F3A2E', pattern: 'solid', shirt: 'Cream', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Cognac', lapel: 'notch', buttons: '2',
    accessory: 'oxblood silk grenadine tie, half-Windsor, deep dimple; cognac leather belt matched to shoes',
    fabric_note: 'deep bottle-green (forest-green) super-150s wool suit — matching jacket and trousers cut from the EXACT same cloth, single-breasted with notch lapels, worn over a cream cotton spread-collar shirt. Editorial quiet-luxury palette — the green must read RICH and DEEP (never olive, never mint, never grass).',
    gradient: 'from-[#1F3A2E] to-[#0A1A12]',
  },
  {
    id: 'black_tie', title: 'Black Tie Affair',
    desc: 'The definitive evening statement.',
    garment_id: 'tuxedo', garment: 'Tuxedo / Black Tie', category: 'full_suits',
    suit: '#0B0B0B', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '1',
    accessory: 'black silk satin self-tie bow tie, butterfly shape, perfectly centred at the collar',
    gradient: 'from-[#0B0B0B] to-[#27272A]',
  },
  {
    id: 'monaco', title: 'Weekend in Monaco',
    desc: 'Light grey two-piece suit, no tie. Riviera ease.',
    garment_id: 'classic_suit', garment: 'Classic Two-Piece Suit', category: 'full_suits',
    suit: '#B0B0B2', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '2',
    accessory: 'none',
    fabric_note: 'classic two-piece LIGHT GREY (pale silver-grey) super-150s wool suit — matching jacket and trousers cut from the EXACT same cloth (full suit, NOT separates), single-breasted with notch lapels, suit worn over a crisp white spread-collar shirt with NO TIE (top button casually undone), brown suede loafers. Editorial Riviera aesthetic — relaxed elegance, summer-weight wool, sun-warmed.',
    gradient: 'from-[#B0B0B2] to-[#6E6E70]',
  },
  // Feb 2026 (user spec — Sep 2026 update) — Summer in Tuscany is
  // now a permanent Style Inspiration anchor for every relaxed /
  // creative / warm-weather context. Previously lived only in
  // BC_INSPIRATIONS; user asked to keep the tan-linen blazer in
  // the main style grid "forever" and dropped the maroon
  // (oxblood) windowpane variant that was here.
  {
    id: 'tuscany', title: 'Summer in Tuscany',
    desc: 'Effortless Mediterranean elegance — no tie.',
    garment_id: 'single_blazer', garment: 'Single-Breasted Blazer', category: 'business_casual',
    suit: '#D2B48C', pattern: 'solid', shirt: 'Cream', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '2',
    pant: '#F5F5DC',
    fabric_note: 'tan (camel) linen-wool blend single-breasted blazer worn OPEN and UNBUTTONED over a cream cotton spread-collar shirt with the top button casually undone (NO TIE), paired with cream cotton chino trousers and brown suede loafers. Editorial Mediterranean/Tuscan aesthetic — relaxed, sun-warmed, effortless.',
    gradient: 'from-[#D2B48C] to-[#8B7355]',
  },
];

// Light shuffle for the inspirations row — same 6 entries every session, but
// randomised order so a tailor flipping between clients sees a fresh starting
// card each time. Deterministic per-client via the selected client id (keeps
// the order stable for the duration of a fitting).
function shuffleInspirations(arr, seed = '') {
  const a = [...arr];
  // Simple deterministic shuffle (Mulberry32 from a hashed seed)
  let h = 0; for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  let s = (h >>> 0) || Math.floor(Math.random() * 0xffffffff);
  const rnd = () => { s |= 0; s = (s + 0x6D2B79F5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Business Casual inspirations — auto-generate and replace cards on the BC tab
const BC_INSPIRATIONS = [
  {
    id: 'bc_friday_polish', title: 'Friday Polish',
    desc: 'Unbuttoned sports coat + chinos. Relaxed authority.',
    garment_id: 'single_blazer', garment: 'Single-Breasted Blazer', category: 'business_casual',
    suit: '#1C2841', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '2',
    pant: '#D2B48C',
    gradient: 'from-[#1C2841] to-[#0A0A0C]',
  },
  {
    id: 'bc_creative_director', title: 'Creative Director',
    desc: 'Charcoal blazer over a crew tee with dark denim.',
    garment_id: 'single_blazer', garment: 'Sports Coat with T-Shirt', category: 'business_casual',
    suit: '#36454F', pattern: 'herringbone', shirt: 'White', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'notch', buttons: '2',
    pant: '#1A1A1E',
    fabric_note: 'herringbone wool-linen blazer worn OPEN and UNBUTTONED over a plain white crew-neck T-SHIRT, paired with dark indigo JEANS (denim, NOT chinos)',
    gradient: 'from-[#36454F] to-[#1A1A1E]',
  },
  {
    id: 'bc_riviera', title: 'Riviera Weekend',
    desc: 'Wool-silk herringbone sports coat + cream chinos. Effortless Riviera energy.',
    garment_id: 'single_blazer', garment: 'Wool-Silk Sky Herringbone Sports Coat', category: 'business_casual',
    suit: '#7BA7C9', pattern: 'herringbone', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Cognac', lapel: 'notch', buttons: '2',
    pant: '#F5F5DC',
    fabric_note: 'sky-blue and ivory WOOL-SILK herringbone sports coat with visible textured weave (Italian luxury herringbone — summer-weight, breathable, slightly slubby), worn OPEN and UNBUTTONED over a crisp white open-collar linen shirt with NO TIE, paired with cream cotton chinos, cognac suede loafers no socks.',
    gradient: 'from-[#7BA7C9] to-[#3C627C]',
  },
  {
    id: 'bc_date_night', title: 'Date Night',
    desc: 'All-black sports coat + dark denim. Quiet confidence.',
    garment_id: 'single_blazer', garment: 'Black Sports Coat with Tee', category: 'business_casual',
    suit: '#0B0B0B', pattern: 'solid', shirt: 'Black', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'peak', buttons: '2',
    pant: '#1A1A1E',
    fabric_note: 'black wool-linen sports coat worn OPEN and UNBUTTONED over a plain black crew-neck t-shirt, paired with black denim',
    gradient: 'from-[#0B0B0B] to-[#27272A]',
  },
  {
    id: 'bc_brunch', title: 'Sunday Brunch',
    desc: 'Navy linen sports coat + cream chinos. Sun-drenched ease.',
    garment_id: 'single_blazer', garment: 'Navy Linen Sports Coat', category: 'business_casual',
    suit: '#1C2A44', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Cognac', lapel: 'notch', buttons: '2',
    pant: '#EFE4CE',
    fabric_note: 'unstructured NAVY LINEN sports coat (soft-shouldered, natural drape, patch pockets, worn OPEN and UNBUTTONED) over a crisp white cotton spread-collar shirt with the top button casually undone (NO TIE), paired with cream cotton chino trousers, cognac suede loafers with no socks. Editorial Mediterranean brunch aesthetic — relaxed, sun-warmed, considered. PATCH POCKETS only, TWO-BUTTON stance — never three-button.',
    gradient: 'from-[#1C2A44] to-[#0D1424]',
  },
  {
    id: 'bc_gallery', title: 'Gallery Opening',
    desc: 'Windowpane blazer + tan chinos. Considered ease.',
    garment_id: 'single_blazer', garment: 'Windowpane Sports Coat', category: 'business_casual',
    suit: '#808080', pattern: 'windowpane', shirt: 'White', shirtPattern: 'solid',
    shoe: 'derby', shoeColor: 'Brown', lapel: 'notch', buttons: '2',
    pant: '#D2B48C',
    fabric_note: 'grey windowpane wool-linen sports coat worn OPEN and UNBUTTONED',
    gradient: 'from-[#808080] to-[#4A4A4A]',
  },
];

// Shirt inspirations — standalone shirts (business, polo, casual, fun)
// Rendered WITHOUT a suit/jacket so each is a clean shirt portrait.
// All shirts TUCKED IN. Polos are short-sleeve plain pique. No untucked looks.
const SHIRT_INSPIRATIONS = [
  {
    id: 'sh_white_dress',
    title: 'Classic White Dress Shirt',
    desc: 'Crisp Egyptian-cotton spread-collar dress shirt — the foundation of every wardrobe.',
    garment_id: 'dress_shirt', garment: 'Business Dress Shirt', category: 'shirts',
    suit: '#F5F5F5', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    fabric_note: 'crisp white Egyptian cotton long-sleeve dress shirt with semi-spread collar, French cuffs, mother-of-pearl buttons, TUCKED INTO charcoal dress trousers with brown leather belt — no jacket. Standalone shirt portrait on a clean neutral light-grey backdrop. Never untucked, never short-sleeve.',
    gradient: 'from-[#FFFFFF] to-[#D4D4D8]',
  },
  {
    id: 'sh_blue_stripe',
    title: 'Banker Stripe',
    desc: 'White & royal-blue banker stripe — sharp business pattern with quiet authority.',
    garment_id: 'dress_shirt', garment: 'Business Pattern Shirt', category: 'shirts',
    suit: '#1B3F6F', pattern: 'pinstripe', shirt: 'Blue Pinstripe', shirtPattern: 'pinstripe',
    fabric_note: 'white background long-sleeve dress shirt with crisp royal-blue BANKER PINSTRIPES (4mm spacing), semi-spread collar, single-button barrel cuffs, TUCKED INTO charcoal dress trousers with leather belt — no jacket. Standalone shirt portrait. Never untucked, never short-sleeve.',
    gradient: 'from-[#FFFFFF] to-[#1B3F6F]',
  },
  {
    id: 'sh_short_polo_navy',
    title: 'Plain Navy Polo',
    desc: 'Short-sleeve plain navy cotton-pique polo — clean two-button placket, tucked.',
    garment_id: 'polo_shirt', garment: 'Short-Sleeve Polo', category: 'shirts',
    suit: '#1C2841', pattern: 'solid', shirt: 'Navy', shirtPattern: 'solid',
    fabric_note: 'plain navy cotton-pique SHORT-SLEEVE polo — clean two-button placket (NO contrasting tipping, NO contrast piping, NO logo, NO graphic detail), plain knit collar in the SAME navy as the body, ribbed short sleeves ending at mid-bicep, slim fit, TUCKED INTO mid-grey cotton chinos with a slim brown leather belt — no jacket. NEVER long-sleeve, NEVER untucked.',
    gradient: 'from-[#1C2841] to-[#0A0E1E]',
  },
  {
    id: 'sh_blue_oxford',
    title: 'Light Blue Oxford',
    desc: 'Long-sleeve light-blue cotton Oxford with spread collar — classic, tucked, refined.',
    garment_id: 'dress_shirt', garment: 'Oxford Dress Shirt', category: 'shirts',
    suit: '#A9C7E6', pattern: 'solid', shirt: 'Light Blue', shirtPattern: 'solid',
    fabric_note: 'long-sleeve light-blue cotton OXFORD dress shirt with a clean semi-spread collar, single-button barrel cuffs at the wrists, TUCKED INTO charcoal dress trousers with a brown leather belt and Oxford shoes — no jacket. Long sleeves to the wrists. Never untucked, never short-sleeve.',
    gradient: 'from-[#A9C7E6] to-[#5A8BB8]',
  },
  {
    id: 'sh_slight_stripe',
    title: 'Tonal Twill Stripe',
    desc: 'Long-sleeve white shirt with a subtle tonal-grey twill stripe — refined, tucked, business-ready.',
    garment_id: 'dress_shirt', garment: 'Striped Dress Shirt', category: 'shirts',
    suit: '#E8E8EA', pattern: 'pinstripe', shirt: 'Tonal Stripe', shirtPattern: 'pinstripe',
    fabric_note: 'white Egyptian cotton long-sleeve dress shirt with a refined TONAL light-grey TWILL STRIPE (subtle, sophisticated, NOT loud, fine and visible on close inspection), semi-spread collar, single-button barrel cuffs, TUCKED INTO charcoal dress trousers with brown leather belt — no jacket. Long sleeves to the wrists. Never untucked, never short-sleeve.',
    gradient: 'from-[#F8F8FA] to-[#9090A0]',
  },
  {
    id: 'sh_micro_check',
    title: 'Micro Tattersall',
    desc: 'Long-sleeve white shirt with refined navy & cognac micro-tattersall — slight pattern, tucked.',
    garment_id: 'dress_shirt', garment: 'Patterned Dress Shirt', category: 'shirts',
    suit: '#F8F4EE', pattern: 'check', shirt: 'Micro Tattersall', shirtPattern: 'check',
    fabric_note: 'white cotton long-sleeve dress shirt with a refined MICRO TATTERSALL pattern (very fine intersecting navy and cognac lines forming small uniform squares, NOT a loud check — sophisticated and editorial), semi-spread collar, single-button barrel cuffs, TUCKED INTO navy dress trousers with brown leather belt — no jacket. Long sleeves to the wrists. Never untucked, never short-sleeve.',
    gradient: 'from-[#FAF5EA] to-[#9F8B65]',
  },
];

// ==================== SHIRT SUBCATEGORIES (one accordion per shirt type) ====================
// Mirrors OUTERWEAR_SUBCATEGORIES — lazy-loads 5 colour variations per shirt style.
// Polos & Golf shirts MUST be SHORT-SLEEVE. Dress shirts & patterned shirts are LONG-SLEEVE.
// EVERY shirt MUST be TUCKED IN. Absolutely no untucked looks.
const SHIRT_SUBCATEGORIES = [
  {
    value: 'essential_dress', label: 'Essential Dress Shirts', desc: 'The ten essential dress shirts every wardrobe needs — colours + patterns',
    combos: [
      { garment: 'White Dress Shirt', garment_id: 'dress_shirt', suit: '#FFFFFF', pattern: 'solid', shirt: 'White', pant: '#36454F', shoe: 'oxford', shoeColor: 'Black', label: 'Crisp White', fabric_note: 'crisp white Egyptian-cotton POPLIN LONG-SLEEVE dress shirt with semi-spread collar and single-button barrel cuffs, TUCKED INTO mid-grey dress trousers with leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Light Blue Dress Shirt', garment_id: 'dress_shirt', suit: '#BCD4E6', pattern: 'solid', shirt: 'Light Blue', pant: '#1C2841', shoe: 'oxford', shoeColor: 'Black', label: 'Light Blue End-on-End', fabric_note: 'light-blue END-ON-END cotton LONG-SLEEVE dress shirt (subtle heather cast from alternating white + blue warp threads) with semi-spread collar and single-button barrel cuffs, TUCKED INTO navy dress trousers with leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Pale Pink Dress Shirt', garment_id: 'dress_shirt', suit: '#F5D7D9', pattern: 'solid', shirt: 'Pale Pink', pant: '#36454F', shoe: 'derby', shoeColor: 'Dark Brown', label: 'Pale Pink Poplin', fabric_note: 'refined pale-pink cotton POPLIN LONG-SLEEVE dress shirt (soft, editorial, NEVER hot pink) with semi-spread collar and single-button barrel cuffs, TUCKED INTO charcoal dress trousers with dark-brown leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Cream Dress Shirt', garment_id: 'dress_shirt', suit: '#F4ECD8', pattern: 'solid', shirt: 'Cream', pant: '#3B2F2F', shoe: 'loafer', shoeColor: 'Cognac', label: 'Cream Twill', fabric_note: 'rich cream / ecru cotton-TWILL LONG-SLEEVE dress shirt (subtle diagonal weave, faint sheen) with semi-spread collar and barrel cuffs, TUCKED INTO chocolate-brown dress trousers with cognac leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Light Grey Dress Shirt', garment_id: 'dress_shirt', suit: '#D6D6D8', pattern: 'solid', shirt: 'Light Grey', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', label: 'Pale Grey Poplin', fabric_note: 'pale-grey cotton POPLIN LONG-SLEEVE dress shirt with semi-spread collar and barrel cuffs, TUCKED INTO black dress trousers with black leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Banker Pinstripe Shirt', garment_id: 'dress_shirt', suit: '#1B3F6F', pattern: 'pinstripe', shirt: 'Blue Pinstripe', pant: '#36454F', shoe: 'oxford', shoeColor: 'Black', label: 'Royal Banker Pinstripe', fabric_note: 'white background LONG-SLEEVE dress shirt with crisp royal-blue BANKER PINSTRIPES (4mm spacing, clean parallel lines), semi-spread collar, single-button barrel cuffs, TUCKED INTO charcoal dress trousers with black leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'University Stripe Oxford', garment_id: 'dress_shirt', suit: '#A9C7E6', pattern: 'pinstripe', shirt: 'University Stripe', pant: '#36454F', shoe: 'loafer', shoeColor: 'Cognac', label: 'University Stripe Oxford', fabric_note: 'white and pale-blue UNIVERSITY STRIPE (narrow 3mm stripes) OXFORD cotton LONG-SLEEVE shirt (visibly textured basketweave) with button-down collar and single-button barrel cuffs, TUCKED INTO charcoal dress trousers with cognac leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Micro Gingham Shirt', garment_id: 'dress_shirt', suit: '#D4E8F5', pattern: 'check', shirt: 'Micro Gingham', pant: '#36454F', shoe: 'derby', shoeColor: 'Dark Brown', label: 'Blue Micro Gingham', fabric_note: 'crisp white and light-blue MICRO GINGHAM (tiny 2-3 mm even checks in a clean square grid — think Brooks Brothers / Charvet) cotton LONG-SLEEVE dress shirt with semi-spread collar and single-button barrel cuffs, TUCKED INTO charcoal dress trousers with dark-brown leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Tattersall Check Shirt', garment_id: 'dress_shirt', suit: '#F8F4EE', pattern: 'check', shirt: 'Tattersall', pant: '#1C2841', shoe: 'derby', shoeColor: 'Dark Brown', label: 'Navy & Cognac Tattersall', fabric_note: 'white cotton LONG-SLEEVE dress shirt with a refined TATTERSALL check pattern (fine intersecting NAVY and COGNAC lines forming small uniform squares roughly 8-10 mm across, sophisticated + editorial), semi-spread collar, single-button barrel cuffs, TUCKED INTO navy dress trousers with dark-brown leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Windowpane Check Shirt', garment_id: 'dress_shirt', suit: '#EDEDEF', pattern: 'check', shirt: 'Windowpane', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', label: 'Blue Windowpane', fabric_note: 'white cotton LONG-SLEEVE dress shirt with a widely-spaced BLUE WINDOWPANE CHECK (large 3-4 cm open squares outlined in thin french-blue lines, refined editorial pattern), semi-spread collar, single-button barrel cuffs, TUCKED INTO black dress trousers with black leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
    ],
  },
  {
    value: 'stripe_pattern', label: 'Stripe & Slight Pattern Shirts', desc: 'Refined business stripes & micro-patterns · long-sleeve · tucked',
    combos: [
      { garment: 'Banker Pinstripe Shirt', garment_id: 'dress_shirt', suit: '#1B3F6F', pattern: 'pinstripe', shirt: 'Blue Pinstripe', pant: '#36454F', shoe: 'oxford', shoeColor: 'Black', label: 'Royal Banker Pinstripe', fabric_note: 'white background LONG-SLEEVE dress shirt with crisp royal-blue BANKER PINSTRIPES (4mm spacing, clean parallel lines), semi-spread collar, single-button barrel cuffs, TUCKED INTO charcoal dress trousers with leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Bengal Stripe Shirt', garment_id: 'dress_shirt', suit: '#D4E8F5', pattern: 'pinstripe', shirt: 'Bengal Stripe', pant: '#1C2841', shoe: 'oxford', shoeColor: 'Black', label: 'Light Blue Bengal Stripe', fabric_note: 'white and light-blue BENGAL STRIPE (6–8mm wide alternating stripes) cotton LONG-SLEEVE dress shirt with semi-spread collar, single-button barrel cuffs, TUCKED INTO navy dress trousers with leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'University Stripe Shirt', garment_id: 'dress_shirt', suit: '#A9C7E6', pattern: 'pinstripe', shirt: 'University Stripe', pant: '#36454F', shoe: 'loafer', shoeColor: 'Cognac', label: 'University Stripe', fabric_note: 'white and pale-blue UNIVERSITY STRIPE (narrow 3mm stripes) Oxford cotton LONG-SLEEVE shirt with button-down collar, single-button barrel cuffs, TUCKED INTO charcoal dress trousers with brown leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Tonal Twill Stripe Shirt', garment_id: 'dress_shirt', suit: '#E8E8EA', pattern: 'pinstripe', shirt: 'Tonal Stripe', pant: '#0B0B0B', shoe: 'oxford', shoeColor: 'Black', label: 'Tonal Twill Stripe', fabric_note: 'white Egyptian cotton LONG-SLEEVE dress shirt with a refined TONAL light-grey TWILL STRIPE (subtle, sophisticated, NOT loud), semi-spread collar, single-button barrel cuffs, TUCKED INTO black dress trousers with leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
      { garment: 'Micro Tattersall Shirt', garment_id: 'dress_shirt', suit: '#F8F4EE', pattern: 'check', shirt: 'Micro Tattersall', pant: '#1C2841', shoe: 'derby', shoeColor: 'Brown', label: 'Micro Tattersall', fabric_note: 'white cotton LONG-SLEEVE dress shirt with a refined MICRO TATTERSALL pattern (very fine intersecting navy and cognac lines forming small uniform squares, sophisticated and editorial), semi-spread collar, single-button barrel cuffs, TUCKED INTO navy dress trousers with brown leather belt, NO jacket. Long sleeves only — never short, never untucked.' },
    ],
  },
  // Iter 129h — casual shirts is a PICKER subcategory (no auto-generated
  // preset combos). Clicking the header opens CasualShirtsQuestionnaire
  // so the tailor chooses style + shirt / pant / shoe combo, then a
  // single render is fired.
  {
    value: 'casual_shirts', label: 'Casual Shirts', desc: 'Polos · tees · smart-casual knitwear — you pick the style',
    picker: true,
    combos: [],
  },
  // Feb 2026 — Sweaters picker (sits directly under Casual Shirts as
  // requested). Opens SweaterQuestionnaire; on submit a single knitwear
  // render fires under category `shirts_sweaters` which the backend
  // routes to its dedicated `sweaters` base_category prompt.
  {
    value: 'sweaters', label: 'Sweaters', desc: 'Crewneck · V-neck · cable · turtleneck · cardigan — you pick the knit',
    picker: true,
    combos: [],
  },
  // Feb 2026 iter 170 — Dress Shirt Builder picker. Opens
  // DressShirtsQuestionnaire (collar × weave × colour × pattern +
  // trouser + shoes). On submit ONE editorial dress-shirt render fires
  // under category `shirts_dress_shirts` which the backend routes to
  // the existing `shirts` base_category prompt.
  {
    value: 'dress_shirts', label: 'Dress Shirt Builder', desc: 'Collar · fabric weave · colour · pattern — you pick the shirt',
    picker: true,
    combos: [],
  },
  // Feb 2026 — Template Looks. Populated exclusively by template applies
  // (category = shirts_templates). Never auto-generates.
  {
    value: 'templates', label: 'Template Looks', desc: 'Shirts seeded from your saved templates',
    is_template: true,
    combos: [],
  },
];

// Outerwear inspirations — coats for every occasion
const OUTERWEAR_INSPIRATIONS = [
  {
    id: 'ow_camel_overcoat', title: 'Camel Overcoat',
    desc: 'The definitive camel wool overcoat — knee-length, notch lapel.',
    garment_id: 'overcoat', garment: 'Single-Breasted Overcoat', category: 'outerwear',
    suit: '#C19A6B', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Brown', lapel: 'notch', buttons: '3',
    fabric_note: 'mid-thigh length camel wool overcoat, notch lapels, worn OPEN over a charcoal suit',
    gradient: 'from-[#C19A6B] to-[#6B4E2A]',
  },
  {
    id: 'ow_navy_topcoat', title: 'Navy Topcoat',
    desc: 'Classic navy topcoat, sharp for city winters.',
    garment_id: 'overcoat', garment: 'Double-Breasted Topcoat', category: 'outerwear',
    suit: '#1C2841', pattern: 'herringbone', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '6',
    fabric_note: 'knee-length navy herringbone wool topcoat, double-breasted with 6x2 peaked lapels, worn over a dark grey suit',
    gradient: 'from-[#1C2841] to-[#0A0E1E]',
  },
  {
    id: 'ow_charcoal_car', title: 'Charcoal Car Coat',
    desc: 'Mid-length car coat — modern, minimal.',
    garment_id: 'car_coat', garment: 'Car Coat', category: 'outerwear',
    suit: '#36454F', pattern: 'solid', shirt: 'Light Blue', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'notch', buttons: '4',
    fabric_note: 'hip-to-mid-thigh length charcoal wool car coat, minimal notch collar, slash pockets, worn OPEN over a sweater and chinos',
    gradient: 'from-[#36454F] to-[#1A1A1E]',
  },
  {
    id: 'ow_stone_trench', title: 'Stone Trench',
    desc: 'The iconic stone trench — double-breasted, belted.',
    garment_id: 'trench_coat', garment: 'Trench Coat', category: 'outerwear',
    suit: '#A59A7C', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'derby', shoeColor: 'Brown', lapel: 'notch', buttons: '10',
    fabric_note: 'classic stone-beige cotton gabardine trench coat, knee-length, double-breasted with storm flap, epaulettes and belted waist, worn OPEN with belt tied at the back',
    gradient: 'from-[#A59A7C] to-[#645C45]',
  },
  {
    id: 'ow_midnight_mandarin', title: 'Midnight Mandarin',
    desc: 'Mandarin collar overcoat — architectural, modern.',
    garment_id: 'mandarin_coat', garment: 'Mandarin-Collar Coat', category: 'outerwear',
    suit: '#0A1128', pattern: 'solid', shirt: 'Black', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Black', lapel: 'mandarin', buttons: '5',
    fabric_note: 'mid-thigh length midnight navy wool mandarin-collar coat (Nehru-style standing collar, NO lapels, NO notch, NO peak), single-breasted with 5 concealed placket buttons, worn OVER a black turtleneck',
    gradient: 'from-[#0A1128] to-[#050A14]',
  },
  {
    id: 'ow_brown_peacoat', title: 'Walnut Peacoat',
    desc: 'Tobacco-brown peacoat — tailored and tactile.',
    garment_id: 'peacoat', garment: 'Peacoat', category: 'outerwear',
    suit: '#5C4033', pattern: 'herringbone', shirt: 'Cream', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'peak', buttons: '6',
    fabric_note: 'hip-length walnut-brown herringbone wool peacoat, double-breasted with 6 oversized buttons, broad peak lapels, worn OPEN over a cream turtleneck',
    gradient: 'from-[#5C4033] to-[#3A2820]',
  },
];

// Wedding-specific style inspirations (swap in when Wedding tab is active)
const WEDDING_INSPIRATIONS = [
  {
    id: 'wed_classic', title: 'Classic Black Tuxedo',
    desc: 'Timeless formality for the traditional groom.',
    garment_id: 'tuxedo', garment: 'Tuxedo / Black Tie', category: 'wedding',
    suit: '#0B0B0B', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '1',
    accessory: 'black bow tie',
    gradient: 'from-[#0B0B0B] to-[#27272A]',
  },
  {
    id: 'wed_navy', title: 'Navy Romance',
    desc: 'Sophisticated navy with soft detailing.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#1C2841', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Cognac', lapel: 'peak', buttons: '2',
    accessory: 'blush pink tie and rose gold cufflinks',
    gradient: 'from-[#1C2841] to-[#0A0A0C]',
  },
  {
    id: 'wed_burgundy', title: 'Burgundy Elegance',
    desc: 'Deep burgundy with emerald accents.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#722F37', pattern: 'solid', shirt: 'Cream', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '2',
    accessory: 'emerald green pocket square and gold cufflinks',
    gradient: 'from-[#722F37] to-[#3D1C2B]',
  },
  {
    id: 'wed_ivory', title: 'Ivory Modern Groom',
    desc: 'Fresh, modern ivory for destination weddings.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#F5F5DC', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Brown', lapel: 'notch', buttons: '1',
    accessory: 'sage green silk tie',
    gradient: 'from-[#F5F5DC] to-[#D8CFB8]',
  },
  {
    id: 'wed_white_tux', title: 'White Tuxedo · Black Tie',
    desc: 'The summer-formal statement — crisp white dinner jacket, black tuxedo trousers.',
    garment_id: 'tuxedo', garment: 'White Dinner Jacket', category: 'wedding',
    suit: '#FAFAFA', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'shawl', buttons: '1',
    accessory: 'black silk satin self-tie bow tie, perfectly centred at the wing-collar, black tuxedo trousers with satin side stripe',
    fabric_note: 'pristine ivory-white wool-mohair dinner jacket with grosgrain shawl lapels, single-button closure, paired with solid BLACK tuxedo trousers featuring a black silk satin side stripe, crisp white pleated-front formal shirt with wing collar, black silk satin self-tie bow tie, black patent-leather Oxfords. Summer-evening garden venue, soft string-light glow',
    gradient: 'from-[#F8F8F8] to-[#1A1A1E]',
  },
  {
    id: 'wed_tan', title: 'Tan Rustic Chic',
    desc: 'Earthy tan for outdoor & rustic weddings.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#D2B48C', pattern: 'herringbone', shirt: 'Cream', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'notch', buttons: '2',
    accessory: 'sage green bow tie and leather boutonniere wrap',
    gradient: 'from-[#D2B48C] to-[#8B7355]',
  },
];

// Extra wedding variations for "Generate New Wedding Looks" — broader creative palette
const EXTRA_WEDDING_INSPIRATIONS = [
  {
    id: 'wed_x_dove', title: 'Dove Grey Ceremony',
    desc: 'Soft dove grey — fresh and modern.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#B8B8B8', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Cognac', lapel: 'peak', buttons: '2',
    accessory: 'dusty blue silk tie and white pocket square',
    gradient: 'from-[#B8B8B8] to-[#6B6B6B]',
  },
  {
    id: 'wed_x_midnight', title: 'Midnight Blue Velvet',
    desc: 'Velvet jacket with black trousers, pure luxe.',
    garment_id: 'tuxedo', garment: 'Tuxedo / Black Tie', category: 'wedding',
    suit: '#0E1A3C', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'shawl', buttons: '1',
    accessory: 'black silk bow tie and onyx studs', fabric_note: 'midnight blue velvet jacket with solid black wool trousers',
    gradient: 'from-[#0E1A3C] to-[#050A1E]',
  },
  {
    id: 'wed_x_olive', title: 'Olive Garden',
    desc: 'Earthy olive for garden & boho weddings.',
    garment_id: 'classic_suit', garment: 'Three-Piece Suit', category: 'wedding',
    suit: '#556B2F', pattern: 'herringbone', shirt: 'Cream', shirtPattern: 'solid',
    shoe: 'chelsea_boot', shoeColor: 'Brown', lapel: 'notch', buttons: '2',
    accessory: 'burnt orange tie and dried floral boutonniere',
    gradient: 'from-[#556B2F] to-[#2F3D1A]',
  },
  {
    id: 'wed_x_plum', title: 'Plum Evening',
    desc: 'Deep plum for autumn romance.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#4B0082', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '2',
    accessory: 'blush pink pocket square and silver cufflinks',
    gradient: 'from-[#4B0082] to-[#2A0F4E]',
  },
  {
    id: 'wed_x_sand', title: 'Sand Beach',
    desc: 'Linen-blend sand for coastal ceremonies.',
    garment_id: 'classic_suit', garment: 'Single-Breasted Suit', category: 'wedding',
    suit: '#EDE5C9', pattern: 'solid', shirt: 'White', shirtPattern: 'solid',
    shoe: 'loafer', shoeColor: 'Tan', lapel: 'notch', buttons: '2',
    accessory: 'coral silk tie and natural raffia boutonniere', fabric_note: 'lightweight linen cotton blend',
    gradient: 'from-[#EDE5C9] to-[#BFB68A]',
  },
  {
    id: 'wed_x_charcoal_pin', title: 'Charcoal Pinstripe Groom',
    desc: 'Pinstripe drama with a twist.',
    garment_id: 'classic_suit', garment: 'Three-Piece Suit', category: 'wedding',
    suit: '#36454F', pattern: 'pinstripe', shirt: 'White', shirtPattern: 'solid',
    shoe: 'oxford', shoeColor: 'Black', lapel: 'peak', buttons: '2',
    accessory: 'burgundy tie and gold tie bar',
    gradient: 'from-[#36454F] to-[#1A1A1E]',
  },
];

// ==================== SMALL COMPONENTS ====================

const ColourPicker = React.memo(function ColourPicker({ colors, value, onChange, testId }) {
  const dark = ['#0B0B0B','#1C2841','#3B2F2F','#722F37','#36454F','#556B2F','#6F4E37'];
  return (
    <div className="flex flex-wrap gap-2">
      {colors.map(c => (
        <button key={c.value} onClick={() => onChange(c.value)} title={c.name}
          className={`w-7 h-7 border-2 transition-all relative ${value === c.value ? 'border-[#F5F5F5] scale-110' : 'border-[#27272A] hover:border-[#52525B]'}`}
          style={{ backgroundColor: c.value }} data-testid={`${testId}-${c.name.toLowerCase()}`}>
          {value === c.value && <Check className="w-3 h-3 absolute inset-0 m-auto" style={{ color: dark.includes(c.value) ? '#fff' : '#000' }} />}
        </button>
      ))}
      <input type="color" value={value || '#808080'} onChange={(e) => onChange(e.target.value)}
        className="w-7 h-7 cursor-pointer border-2 border-[#27272A] bg-transparent" title="Custom" />
    </div>
  );
});

const OptionPills = React.memo(function OptionPills({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-xs border transition-all ${value === o.value ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
});

const InspirationCard = React.memo(function InspirationCard({ inspiration, onGenerate, disabled }) {
  return (
    <button onClick={() => onGenerate(inspiration)} disabled={disabled}
      className="group relative overflow-hidden border border-[#27272A] hover:border-[hsl(var(--accent))] transition-all text-left disabled:opacity-50"
      data-testid={`inspiration-${inspiration.id}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${inspiration.gradient} opacity-60`}></div>
      <div className="relative p-5 flex flex-col justify-between min-h-[160px]">
        <div>
          <h4 className="text-base font-light text-[#F5F5F5] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {inspiration.title}
          </h4>
          <p className="text-[11px] text-[#A1A1AA] leading-relaxed">{inspiration.desc}</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-[#F5F5F5]/60 group-hover:text-[#F5F5F5] transition-colors mt-3">
          <Sparkles className="w-3 h-3" /> Generate this look
        </div>
      </div>
    </button>
  );
});

// House wordmark badge — Tom Ford / Brunello Cucinelli signature tag.
// Hoisted out of LookCard so React's reconciler treats it as a stable
// component (no remount on every parent render). Pure CSS overlay; does
// not burn into the underlying generated image.
const HouseWordmark = React.memo(function HouseWordmark({ house }) {
  if (house === 'tom_ford') {
    return (
      <div
        className="absolute top-2 left-2 px-2 py-0.5 bg-black/75 backdrop-blur-sm pointer-events-none"
        data-testid="house-wordmark-tom-ford"
        style={{ letterSpacing: '0.18em' }}
      >
        <span
          className="text-[10px] uppercase font-semibold"
          style={{ color: '#E8C77A', fontFamily: '"Trajan Pro", "Cinzel", serif' }}
        >
          TOM FORD
        </span>
      </div>
    );
  }
  if (house === 'brunello_cucinelli') {
    return (
      <div
        className="absolute top-2 left-2 px-2 py-0.5 bg-[#F5F1E8]/90 backdrop-blur-sm pointer-events-none"
        data-testid="house-wordmark-brunello-cucinelli"
        style={{ letterSpacing: '0.10em' }}
      >
        <span
          className="text-[9px] uppercase tracking-[0.12em] font-normal"
          style={{ color: '#3B2F2F', fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}
        >
          Brunello Cucinelli
        </span>
      </div>
    );
  }
  return null;
});

// Map a look's category to its house slug for HouseWordmark.
const _houseFromCategory = (cat) => {
  const c = (cat || '').toLowerCase();
  if (c.endsWith('_tom_ford') || c === 'tom_ford') return 'tom_ford';
  if (c.endsWith('_brunello_cucinelli') || c === 'brunello_cucinelli') return 'brunello_cucinelli';
  return null;
};

const LookCard = React.memo(function LookCard({ look, onClick, isGenerating, onZoom, onDelete, onRetry, priority = false }) {
  // House wordmark from the look's category — Tom Ford / Brunello Cucinelli
  // subcategories render a styled wordmark on the top-left of the card.
  const houseFromCategory = _houseFromCategory(look?.category);
  // ── Variant carousel state ────────────────────────────────────────
  // After a pant recolour (or any quick-edit) the card preserves each
  // prior render in `look.variants` so the client can scroll back
  // through ("show me the navy ones again"). Index 0 = the latest
  // (canonical) image; 1..n = older snapshots, newest-first.
  //
  // No `useEffect([look?.id], setVariantIdx(0))` here on purpose — the
  // LookCard is keyed by `look.id` at every callsite, so React fully
  // unmounts + remounts on a new edit and `useState(0)` reinitialises
  // naturally. The previous reset effect was a no-op that occasionally
  // raced with arrow clicks.
  const [variantIdx, setVariantIdx] = React.useState(0);
  const variantCount = 1 + (Array.isArray(look?.variants) ? look.variants.length : 0);
  // Iter 129g — allow the parent to force the carousel to a specific
  // slot via `look._focusVariantIdx` + `look._focusToken`. Used by
  // Customize Liner so the card automatically jumps to the "client
  // holding the jacket open" render as soon as the pair lands.
  React.useEffect(() => {
    if (typeof look?._focusVariantIdx === 'number') {
      setVariantIdx(Math.min(look._focusVariantIdx, variantCount - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look?._focusToken]);
  const activeSrc = variantIdx === 0
    ? (look?.image_thumb || look?.image)
    : (look?.variants?.[variantIdx - 1]?.image);
  // Iter 170.8 — image auto-retry + manual reload for the broken-image
  // "?" placeholder the user reported ("Sometimes it loads like this
  // can you fix"). The look doc in Mongo is fine (label + heart +
  // trash all render); the object-storage URL is transiently
  // unreachable. `imgAttempt` bumps a cache-busting `?r=N` on the src
  // so the browser re-fetches instead of serving a poisoned 404. Auto
  // retries up to 3× with 500 / 1000 / 2000 ms backoff before showing
  // the manual reload overlay.
  const [imgAttempt, setImgAttempt] = React.useState(0);
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => {
    // Reset retry state when the source changes (variant flip / new look).
    setImgAttempt(0);
    setImgFailed(false);
  }, [activeSrc]);
  const _srcWithBuster = React.useMemo(() => {
    const base = srcFor(activeSrc);
    if (!base || imgAttempt === 0) return base;
    // Data URLs can't take a query string.
    if (base.startsWith('data:')) return base;
    return base + (base.includes('?') ? '&' : '?') + 'r=' + imgAttempt;
  }, [activeSrc, imgAttempt]);
  const _onImgError = React.useCallback(() => {
    if (imgAttempt < 3) {
      const delay = 500 * Math.pow(2, imgAttempt); // 500 / 1000 / 2000 ms
      setTimeout(() => setImgAttempt(a => a + 1), delay);
    } else {
      setImgFailed(true);
    }
  }, [imgAttempt]);
  const _manualReload = React.useCallback((e) => {
    e?.stopPropagation();
    setImgFailed(false);
    setImgAttempt(a => a + 1);
  }, []);
  // `restoring` = look came from sessionStorage cache on remount. It has a
  // label but no image yet — show the same skeleton UX as a generating card
  // so the user sees "your looks are coming back" instead of empty slots.
  if (isGenerating || look?.restoring) {
    return (
      <div className="relative aspect-[3/4] border border-[#27272A] overflow-hidden bg-[#0A0A0C]" data-testid="lookcard-skeleton">
        {/* Layered skeleton: faux jacket silhouette block + pulsing wash +
            shimmer sweep. Reads as "something is materialising here" far
            better than a lone spinner on a flat background. */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#121215] via-[#1A1A1E] to-[#0F0F12] animate-pulse" />
        {/* Faux figure silhouette (head + shoulders + torso) so the
            skeleton hints at the rendered subject's shape. */}
        <div className="absolute inset-0 flex flex-col items-center justify-end opacity-30">
          <div className="w-1/4 aspect-square rounded-full bg-[#27272A] mb-2" />
          <div className="w-2/3 h-1/2 bg-[#27272A] rounded-t-3xl" />
        </div>
        {/* Shimmer sweep — left-to-right diagonal highlight. */}
        <div className="absolute inset-0 lookcard-shimmer pointer-events-none" />
        {/* Centred caption + spinner so the user still knows this is in flight. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3">
          <Loader2 className="w-5 h-5 text-[hsl(var(--accent))] spinner" />
          <span className="text-[10px] text-[#A1A1AA] mt-2 uppercase tracking-widest">
            {look?.label && look.label !== 'Generating...' ? look.label : 'Rendering…'}
          </span>
          <span className="text-[9px] text-[#52525B] mt-1">tailoring the swatch</span>
        </div>
      </div>
    );
  }
  if (look?.failed) {
    return (
      <button
        type="button"
        onClick={() => onRetry?.(look)}
        data-testid={`look-retry-${look.id}`}
        className="aspect-[3/4] border border-[#3a2a2a] bg-[#1a0f0f] flex flex-col items-center justify-center text-center p-3 hover:border-[hsl(var(--accent))] transition-colors group"
        title="Tap to retry this look"
      >
        <RefreshCw className="w-6 h-6 text-[hsl(var(--accent))] mb-2 group-hover:rotate-45 transition-transform" />
        <span className="text-[11px] text-[#F5F5F5] font-light line-clamp-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{look.label}</span>
        <span className="text-[9px] text-[#ef4444] mt-2 uppercase tracking-wider">Tap to retry</span>
      </button>
    );
  }
  if (!look?.image) return null;
  return (
    <div className="group cursor-pointer tf-cv-card" onClick={() => onClick(look)} data-testid={`look-${look.id}`}>
      <div className="aspect-[3/4] bg-[#1A1A1E] border border-[#27272A] overflow-hidden relative hover:border-[hsl(var(--accent))] transition-colors">
        {/* look.image is either a URL (data:, http, or /api/images/...) or a
            bare base64 string. srcFor() normalises both into a renderable src.
            For grid rendering we prefer the lower-res `image_thumb` when present
            (fetches ~10x faster, ~1/8 the bytes), falling back to full-res for
            looks that don't have a thumb yet. Zoom/Customize modal still uses
            the full-res `look.image`. `decoding="async"` lets the browser
            decode JPEGs off the main thread, `fetchpriority="high"` is
            honoured by Chromium for above-the-fold cards. */}
        <img
          src={_srcWithBuster}
          alt={look.label}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onError={_onImgError}
          // Oct 2026 (user: "Running into framing issues with this client.
          // Why? Don't change the gemini prompts at all.") — swapped from
          // `object-cover` to `object-contain object-center` to match the
          // Style Inspirations tile styling (which never had this
          // problem). For Gemini renders that happen to come out exactly
          // 3:4 (the common case) there's zero visual change — the image
          // still fills the tile 1:1. For renders that come back at a
          // non-3:4 aspect (what was happening to this specific client),
          // `object-cover` was center-cropping the figure's head and
          // feet to force fill; `object-contain` keeps the full figure
          // visible with thin letterbox bars (`#1A1A1E` tile bg blends
          // into the surrounding dark theme). No prompt change required.
          className="w-full h-full object-contain object-center transition-transform duration-300 group-hover:scale-105"
        />
        {/* Iter 170.8 — auto-retry exhausted → surface a manual reload
            overlay INSTEAD of the browser's default broken-image "?"
            icon. Overlay reads as "Image temporarily unavailable · Tap
            to reload". `stopPropagation` prevents the card click from
            firing when the tailor taps Reload. */}
        {imgFailed && (
          <button
            type="button"
            onClick={_manualReload}
            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0A0A0C] text-[#F5F5F5] text-xs uppercase tracking-widest hover:bg-[#1A1A1E] transition-colors"
            data-testid={`look-image-reload-${look.id}`}
            title="Reload image"
          >
            <RefreshCw className="w-6 h-6 mb-2 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Image unavailable</span>
            <span className="text-[hsl(var(--accent))] mt-1">Tap to reload</span>
          </button>
        )}
        {/* ── Variant carousel arrows + indicator ───────────────────
            Shown only when the look has prior renders cached on it. The
            previous render is preserved by `handleRegenerate` so a
            consultant can flip back and forth in front of the client
            ("the charcoal pants vs the stone pants"). Index 0 is the
            latest render. */}
        {variantCount > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVariantIdx(i => (i - 1 + variantCount) % variantCount); }}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 w-8 h-8 z-10 flex items-center justify-center bg-black/70 hover:bg-black/95 text-white border border-white/15 hover:border-[hsl(var(--accent))] transition-colors"
              aria-label="Previous render"
              data-testid={`look-carousel-prev-${look.id}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVariantIdx(i => (i + 1) % variantCount); }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 z-10 flex items-center justify-center bg-black/70 hover:bg-black/95 text-white border border-white/15 hover:border-[hsl(var(--accent))] transition-colors"
              aria-label="Next render"
              data-testid={`look-carousel-next-${look.id}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div
              className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 text-[9px] uppercase tracking-widest bg-black/70 text-white px-2 py-0.5 border border-white/15 pointer-events-none"
              data-testid={`look-carousel-indicator-${look.id}`}
            >
              {variantIdx === 0 ? 'Latest' : `Prev ${variantIdx}`} · {variantIdx + 1} / {variantCount}
            </div>
          </>
        )}
        {/* Iter 80 — HouseWordmark removed entirely per user feedback
            ("remove the big Tom Ford tag that covers the face on the
            cards"). Brand identity now lives in the prompt + the BC
            scenario header card, not as an overlay on the photo. */}
        {/* Iter 80 — bottom label/garment overlay is shown ONLY for
            "inspiration" cards (curated Wedding / colour / inspiration
            categories). All other small cards (Essentials, Business
            Casual scenarios, Custom Fabric, etc.) get a clean, label-free
            editorial layout per user spec. */}
        {(look?.category || '').startsWith('inspiration_') && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <p className="text-xs text-[#F5F5F5] font-medium">{look.label}</p>
            <p className="text-[10px] text-[#A1A1AA]">{look.garment}</p>
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {onZoom && (
            <button onClick={(e) => { e.stopPropagation(); onZoom(look); }}
              className="p-1.5 bg-black/60 text-white/80 hover:text-white hover:bg-black/80 transition-colors"
              title="Zoom"
              data-testid={`look-zoom-${look.id}`}>
              <ZoomIn className="w-4 h-4" />
            </button>
          )}
          {/* Iter 125 — per-look "Email" button removed. All client-card
              email delivery lives on the Clients page now (single
              curated shortlist). LookCard only exposes zoom + delete. */}
          {onDelete && (
            <button onClick={(e) => {
              e.stopPropagation();
              // Iter 170.3 — variant-aware delete. When the card has a
              // variant carousel we delete ONLY the currently-shown
              // slot (previous behaviour nuked all snapshots in one
              // click, which is exactly the "delete individual looks"
              // production ask). Confirm copy explains the scope.
              const _multi = variantCount > 1;
              const _slotLabel = _multi
                ? (variantIdx === 0
                    ? 'the latest render on this card'
                    : `variant ${variantIdx + 1} of ${variantCount}`)
                : `"${look.label || look.garment || 'this look'}"`;
              const _suffix = _multi
                ? ' The other renders on this card stay untouched.'
                : ' This cannot be undone.';
              if (window.confirm(`Delete ${_slotLabel}?${_suffix}`)) {
                onDelete(look, variantIdx);
                // Reset carousel index if we just deleted the current
                // slot at index >= 1 so the card doesn't render a
                // stale variantIdx pointing past the end.
                if (_multi && variantIdx > 0) setVariantIdx(0);
              }
            }}
              className="p-1.5 bg-red-500/80 text-white hover:bg-red-500 transition-colors"
              title={variantCount > 1 ? `Delete render ${variantIdx + 1} of ${variantCount}` : 'Delete look'}
              data-testid={`look-delete-${look.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {/* Horizontal accent bar beneath the card — subtle gradient with a soft
          glow that picks up the store's primary colour (hsl(var(--accent))).
          Replaces the previous left-border tailor-notes description block. */}
      <div
        className="mt-1.5 h-[2px] w-full opacity-80 group-hover:opacity-100 transition-opacity"
        style={{
          backgroundImage:
            'linear-gradient(90deg, transparent 0%, hsl(var(--accent) / 0.15) 12%, hsl(var(--accent)) 50%, hsl(var(--accent) / 0.15) 88%, transparent 100%)',
          boxShadow: '0 0 8px hsl(var(--accent) / 0.45), 0 0 14px hsl(var(--accent) / 0.18)',
        }}
      />
    </div>
  );
});


// ==================== NEW CLIENT INTAKE MODAL ====================
// Captures the minimum-viable client onboarding fields on a walk-in or new
// consult: face photo (required), optional full-body photo, and fit
// preference (4 industry-standard options). On submit it creates the
// client, uploads the photos, and hands control back to the gate so the
// caller can enter the studio with a fully-seeded client.
// Fit options live at module scope (FIT_OPTIONS above) so the CRM page +
// modal use the same list.
function NewClientIntakeModal({ onClose, onCreated, storeSettings }) {
  const [step, setStep] = React.useState(1);   // 1=details, 2=context, 3=photo
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [photoB64, setPhotoB64] = React.useState(null);
  const [photoPreview, setPhotoPreview] = React.useState(null);
  const [bodyPhotoB64, setBodyPhotoB64] = React.useState(null);
  const [bodyPhotoPreview, setBodyPhotoPreview] = React.useState(null);
  const [fit, setFit] = React.useState('modern');
  // Client gender (Feb 2026). Drives the backend's "GENDER CONTEXT"
  // prompt prefix so feminine clients render in women's tailored
  // pantsuits / dresses / women's tuxedos instead of the default
  // men's two-piece.
  const [gender, setGender] = React.useState('male');
  // Iter 95 — explicit body composition. Drives Gemini render
  // proportions much more accurately than a full-body photo guess.
  // Height stored canonically in cm; UI toggles imperial ↔ metric for
  // display. body_type ∈ {slim, athletic, regular, stocky}. The body
  // photo on Step 2, if uploaded, hits /api/clients/analyze-body which
  // prefills these values for the consultant to confirm/edit.
  const [heightCm, setHeightCm] = React.useState(178);   // ~5'10" default
  const [heightUnit, setHeightUnit] = React.useState('imperial'); // 'imperial' | 'metric'
  const [bodyType, setBodyType] = React.useState('regular');
  // AI-processing consent (June 2026) — REQUIRED before the client can
  // be created (blocks Step 1 → Step 2, and both create paths). The
  // backend stores a timestamped, versioned consent record.
  const [aiConsent, setAiConsent] = React.useState(false);
  // Feb 2026 — client "Occupation or upcoming event" (closed enum).
  // Value must match `backend/context_profiles.py::CONTEXTS`. Sent on
  // client create so the Studio's inspiration tiles are curated the
  // moment the client is opened. Required at intake.
  const [context, setContext] = React.useState('');
  const [bodyAnalyzing, setBodyAnalyzing] = React.useState(false);
  const [bodyAISuggested, setBodyAISuggested] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // Preemptive-gen state: once the face photo is uploaded at Step 2, we
  // create the client in the background and fire all 11 Gemini calls
  // (5 Essentials + 6 Style Inspirations) in parallel. By the time the
  // user finishes picking a wardrobe context at Step 3, the looks are
  // already done (or close to it). When `onCreated` fires, the dashboard
  // skips its own auto-gen via the sessionStorage `preempt_gen_<id>` marker.
  const [preemptiveClient, setPreemptiveClient] = React.useState(null); // { id, slug }
  const preemptiveInflightRef = React.useRef(false);

  // Feb 2026 — Optional multi-template picker at step 3 (wardrobe context).
  // Multiple templates can be chosen; after handleSubmit creates the client
  // we fire an apply call for each. The apply endpoint regens each look on
  // the new client's face, so seeded looks are personalised — no more
  // source-client faces.
  const [chosenTemplateIds, setChosenTemplateIds] = React.useState(() => new Set());
  const [availableTemplates, setAvailableTemplates] = React.useState([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const toggleChosenTemplate = React.useCallback((id) => {
    setChosenTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  React.useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;
    setTemplatesLoading(true);
    axios
      .get(`${BACKEND_URL}/api/look-templates?inline_images=true`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setAvailableTemplates(data?.templates || []);
      })
      .catch(() => { /* silent — templates are optional */ })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [step]);

  const accent = storeSettings?.primary_color || '#C2A878';
  const button = storeSettings?.button_color || accent;

  // Background prefetch: as soon as we have a name + face photo, create the
  // client, upload the photo, and start firing Gemini look gens in parallel.
  // Idempotent — guarded by `preemptiveInflightRef` so a rapid re-upload
  // doesn't double-fire. Failures are silent (the regular Step 3 submit path
  // will retry the create) so the user never sees a half-state.
  const fireMissingPreemptive = React.useCallback(async (forName, photo) => {
    if (preemptiveInflightRef.current) return;
    if (!forName.trim() || !photo) return;
    if (!aiConsent) return; // never create a profile without recorded consent
    // If the tailor is seeding this client from templates, skip the
    // preemptive Gemini burn — the templates will supply the looks after
    // handleSubmit finishes creating the client.
    if (chosenTemplateIds.size > 0) return;
    preemptiveInflightRef.current = true;
    try {
      // 1) Create client with the fit picked in Step 1's Fit tiles (defaults
      //    to 'modern' = the UI's "Classic" tile). Feb 2026 — the preemptive
      //    burn now uses THIS fit so the auto-generated Style Inspirations,
      //    Essentials and subcategory looks all silhouette-match what the
      //    tailor saw in the fit preview.
      const createRes = await axios.post(`${BACKEND_URL}/api/clients`, {
        name: forName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        fit_preference: fit,
        gender,
        height_cm: heightCm,
        body_type: bodyType,
        ai_consent: aiConsent,
        context: context || null,
      }, { withCredentials: true });
      const created = createRes.data;
      setPreemptiveClient({ id: created.id, slug: created.slug });
      // 2) Upload face photo (triggers backend color analysis as background task)
      await axios.post(`${BACKEND_URL}/api/clients/${created.id}/photo-b64`, {
        image: photo,
      }, { withCredentials: true });
      // 3) Mark sessionStorage so the dashboard's auto-gen useEffects skip
      //    duplicate calls when the user enters the studio. Includes a
      //    timestamp so stale markers from abandoned intakes age out cleanly.
      try {
        sessionStorage.setItem(`preempt_gen_${created.id}`, JSON.stringify({
          essentials: true,
          style_inspirations: true,
          ts: Date.now(),
        }));
      } catch { /* quota */ }
      // 4) Fire all 11 Gemini gens in parallel via the shared genLimit (14).
      //    These persist to /api/gallery automatically (save_to_gallery=true),
      //    so when the dashboard hydrates via /api/gallery on mount, the
      //    looks are already there — zero perceived wait.
      const fitForGen = fit;  // matches the client's created fit
      // Fetch the context-curated Style Inspirations for this client so the
      // 6 hero tiles reflect their occupation/event. Falls back to the
      // legacy STYLE_INSPIRATIONS constant if the fetch fails or returns
      // empty (matches the pre-feature behaviour).
      let styleTiles = STYLE_INSPIRATIONS;
      try {
        const insp = await axios.get(
          `${BACKEND_URL}/api/inspirations?category=style&client_id=${created.id}`,
          { withCredentials: true, timeout: 5000 },
        );
        if (insp?.data?.tiles?.length) styleTiles = insp.data.tiles;
      } catch { /* fail-soft — use legacy defaults */ }
      // Style Inspirations (6 hero cards — heroes first)
      styleTiles.forEach(insp => {
        genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
          client_image: photo,
          garment_type: insp.garment_id, garment_name: insp.garment,
          category: insp.category, sub_category: `inspiration_${insp.category}`,
          fit: fitForGen, styles: ['flat_front'], color: insp.suit,
          suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
          lapel: insp.lapel, button_count: insp.buttons,
          shoe_type: insp.shoe, shoe_color: insp.shoeColor,
          pant_color: insp.pant ? PANT_COLORS.find(c => c.value === insp.pant)?.name : undefined,
          fabric_note: insp.fabric_note || undefined,
          accessory: insp.accessory || undefined,
          client_id: created.id,
          save_to_gallery: true,
          creative_boost: true,
        }, { withCredentials: true, timeout: 90000 }))).catch(() => { /* will retry on dashboard if needed */ });
      });
      // Essential 5 — fire ~50ms later so Style Inspirations get first queue
      // slots (heroes-first ordering, matching the in-Dashboard behavior).
      setTimeout(() => {
        SUIT_COMBOS.forEach((combo, i) => {
          genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
            client_image: photo,
            garment_type: combo.garment_id, garment_name: combo.garment,
            category: 'full_suits', fit: fitForGen, styles: ['flat_front'],
            color: combo.suit, shirt_color: combo.shirt,
            shirt_pattern: 'solid', suit_pattern: combo.pattern || 'solid',
            lapel: combo.lapel_override || 'notch',
            button_count: combo.buttons_override || '2',
            shoe_type: combo.shoe, shoe_color: combo.shoeColor,
            pant_color: combo.pant ? PANT_COLORS.find(c => c.value === combo.pant)?.name : undefined,
            client_id: created.id,
            save_to_gallery: true,
            fabric_note: combo.fabric_note || undefined,
            accessory: combo.accessory || undefined,
          }, { withCredentials: true, timeout: 90000 }))).catch(() => { /* dashboard retry */ });
        });
      }, 50);
    } catch (err) {
      // Preemptive flow failed — fall back to the normal Step 3 submit path.
      // Reset the inflight ref so a manual retry (e.g. user goes back + re-
      // uploads photo) can try again. The user never sees the failure.
      preemptiveInflightRef.current = false;
      setPreemptiveClient(null);
    }
  }, [email, phone, aiConsent, chosenTemplateIds, fit]);

  const handlePhotoChange = (e, isBody = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Photo must be under 8MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const b64 = result.includes(',') ? result.split(',')[1] : result;
      if (isBody) {
        setBodyPhotoB64(b64);
        setBodyPhotoPreview(result);
        // Iter 95 — Gemini Vision auto-fill. Fire-and-forget; we never
        // block the modal on this. On success we prefill heightCm +
        // bodyType so the consultant has a head start, and flag
        // bodyAISuggested so the UI can show a small "AI suggested"
        // chip next to the controls.
        setBodyAnalyzing(true);
        setBodyAISuggested(false);
        axios.post(`${BACKEND_URL}/api/clients/analyze-body`, { image: b64 }, { withCredentials: true })
          .then(({ data }) => {
            if (data?.height_cm) setHeightCm(data.height_cm);
            if (data?.body_type) setBodyType(data.body_type);
            setBodyAISuggested(true);
          })
          .catch(() => { /* fail-soft — defaults stand */ })
          .finally(() => setBodyAnalyzing(false));
      } else {
        setPhotoB64(b64);
        setPhotoPreview(result);
        // Kick off preemptive client creation + Gemini gens in background
        // the moment we have a face photo. By the time the user clicks
        // through Step 3, the 11 looks are already cooking (or done).
        fireMissingPreemptive(name, b64);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!aiConsent) { toast.error('Client consent to AI processing is required'); return; }
    if (!context) { toast.error('Pick an occupation or event so we can tailor their wardrobe'); return; }
    if (!photoB64) { toast.error('Face photo is required to generate looks'); return; }
    setSubmitting(true);
    try {
      let newClient = preemptiveClient;
      if (newClient) {
        // Preemptive path: client already exists with photo + gens in flight.
        // Patch the fit_preference + body composition if any changed from
        // their defaults at preempt-time (fit was 'modern', height was 178,
        // body_type was 'regular'). We always PATCH on submit so anything
        // the user edited between preempt and submit is persisted.
        await axios.put(`${BACKEND_URL}/api/clients/${newClient.id}`, {
          fit_preference: fit,
          height_cm: heightCm,
          body_type: bodyType,
        }, { withCredentials: true }).catch(() => { /* non-fatal */ });
      } else {
        // Cold path (preemptive failed or photo upload happened so late
        // the user clicked Submit before fireMissingPreemptive returned):
        // do the original create → upload sequence.
        const createRes = await axios.post(`${BACKEND_URL}/api/clients`, {
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          fit_preference: fit,
          gender,
          height_cm: heightCm,
          body_type: bodyType,
          ai_consent: aiConsent,
          context: context || null,
        }, { withCredentials: true });
        newClient = createRes.data;
        await axios.post(`${BACKEND_URL}/api/clients/${newClient.id}/photo-b64`, {
          image: photoB64,
        }, { withCredentials: true });
      }
      // Body photo is always processed at submit time (no preemptive needed —
      // it doesn't gate look generation).
      if (bodyPhotoB64) {
        await axios.post(`${BACKEND_URL}/api/clients/${newClient.id}/body-photo-b64`, {
          image: bodyPhotoB64,
        }, { withCredentials: true });
      }
      // Feb 2026 — if templates were chosen at step 2, prepopulate the
      // newly-created client with every look from EACH selected template.
      // Each look is regenerated on the client's face via /generate-look,
      // so seeded results carry the new client's identity — not the source.
      if (chosenTemplateIds.size > 0) {
        try {
          // Belt-and-braces: also mark the essentials/style_inspirations
          // preempt flags so the dashboard's auto-gen useEffects skip
          // firing when the tailor lands on the client card.
          try {
            sessionStorage.setItem(`preempt_gen_${newClient.id}`, JSON.stringify({
              essentials: true,
              style_inspirations: true,
              ts: Date.now(),
            }));
          } catch { /* quota */ }
          const ids = [...chosenTemplateIds];
          const results = await Promise.allSettled(
            ids.map((tid) =>
              axios.post(
                `${BACKEND_URL}/api/look-templates/${tid}/apply`,
                { client_id: newClient.id },
                { withCredentials: true },
              ),
            ),
          );
          const queued = results
            .filter((r) => r.status === 'fulfilled')
            .reduce((sum, r) => sum + (r.value?.data?.queued || 0), 0);
          const failed = results.filter((r) => r.status === 'rejected').length;
          if (queued) {
            toast.success(
              `Seeding ${queued} looks from ${ids.length} template${ids.length > 1 ? 's' : ''} — they'll appear as they render`,
            );
          }
          if (failed) {
            const first = results.find((r) => r.status === 'rejected');
            const msg = first?.reason?.response?.data?.detail || 'Some template seeds failed';
            toast.error(typeof msg === 'string' ? msg : 'Template seeding failed');
          }
        } catch (e) {
          const msg = e?.response?.data?.detail || 'Template seeding failed';
          toast.error(typeof msg === 'string' ? msg : 'Template seeding failed');
        }
      }
      onCreated({ ...newClient, fit_preference: fit });
    } catch (err) {
      // Sep 2026 — improved error surfacing. Previously the toast fell
      // back to a bland "Could not create client. Try again." whenever
      // `err.response.data.detail` wasn't a plain string, which hid:
      //   • Pydantic validation errors (detail = array of field errors)
      //   • 402 plan-cap messages (surface the plan name + limit)
      //   • Network / preview-pod-restart failures (surface the HTTP
      //     code so the tailor knows it's a transient issue)
      // We now extract a human-readable reason for every path AND log
      // the raw error to console so DevTools shows the full trace.
      // eslint-disable-next-line no-console
      console.error('[create-client] failed', err);
      let msg = 'Could not create client. Try again.';
      const resp = err?.response;
      if (resp) {
        const detail = resp.data?.detail;
        if (typeof detail === 'string' && detail.trim()) {
          msg = detail;
        } else if (Array.isArray(detail) && detail.length) {
          const first = detail[0] || {};
          const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : first.loc;
          const fieldMsg = first.msg || 'invalid value';
          msg = field ? `${String(field).replace(/_/g, ' ')} — ${fieldMsg}` : `Validation error — ${fieldMsg}`;
        } else if (resp.status === 402) {
          msg = 'Plan limit reached — upgrade to add more clients.';
        } else if (resp.status === 401) {
          msg = 'Your session expired — please sign back in.';
        } else if (resp.status >= 500) {
          msg = `Server error (${resp.status}) — try again in a moment.`;
        }
      } else if (err?.code === 'ECONNABORTED') {
        msg = 'Request timed out — check your connection and try again.';
      } else if (err?.message) {
        msg = `${err.message} — try again.`;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <div className="bg-[#121215] border border-[#27272A] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-client-intake-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#27272A]">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: accent }}>Step {step} of 3</p>
            <h2 className="text-2xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {step === 1 ? 'New client'
                : step === 2 ? 'Add their photo'
                : 'Wardrobe context'}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#52525B] hover:text-[#F5F5F5] p-1" data-testid="new-client-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {step === 1 && (
            <>
              <div>
                <Label className="label-luxury mb-2 block">Full name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  className="bg-[#0A0A0C] border-[#27272A] text-[#F5F5F5] rounded-none h-11" autoFocus
                  placeholder="e.g. Marcus Chen" data-testid="new-client-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-luxury mb-2 block">Email <span className="opacity-50">(optional)</span></Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                    className="bg-[#0A0A0C] border-[#27272A] text-[#F5F5F5] rounded-none h-11"
                    placeholder="marcus@..." data-testid="new-client-email" />
                </div>
                <div>
                  <Label className="label-luxury mb-2 block">Phone <span className="opacity-50">(optional)</span></Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                    className="bg-[#0A0A0C] border-[#27272A] text-[#F5F5F5] rounded-none h-11"
                    placeholder="+1 ..." data-testid="new-client-phone" />
                </div>
              </div>
              {/* Iter 229 (Feb 2026) — Wardrobe context (occupation / event)
                  moved to its own dedicated Step 3 with tile pickers. */}

              <div>
                <Label className="label-luxury mb-2 block">Gender</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="new-client-gender">
                  {[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                  ].map((opt) => {
                    const active = gender === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setGender(opt.value)}
                        className="h-11 border text-xs uppercase tracking-widest font-semibold transition-colors"
                        style={{
                          backgroundColor: active ? button : '#0A0A0C',
                          color: active ? '#0A0A0C' : '#F5F5F5',
                          borderColor: active ? button : '#27272A',
                        }}
                        data-testid={`new-client-gender-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Iter 95 — Height slider with imperial ↔ metric toggle.
                  Imperial values render as ft'in" (e.g. 5'10"); metric
                  as cm. Slider always operates on cm internally for a
                  single source of truth on the backend payload. */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="label-luxury">Height</Label>
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest" data-testid="new-client-height-unit-toggle">
                    {['imperial', 'metric'].map(u => {
                      const active = heightUnit === u;
                      return (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setHeightUnit(u)}
                          className="px-2 py-0.5 border transition-colors"
                          style={{
                            backgroundColor: active ? button : 'transparent',
                            color: active ? '#0A0A0C' : '#A1A1AA',
                            borderColor: active ? button : '#27272A',
                          }}
                          data-testid={`new-client-height-unit-${u}`}
                        >
                          {u === 'imperial' ? 'ft / in' : 'cm'}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min={145}
                    max={215}
                    step={1}
                    value={heightCm}
                    onChange={(e) => setHeightCm(parseInt(e.target.value, 10))}
                    className="flex-1 accent-current"
                    style={{ accentColor: button }}
                    data-testid="new-client-height-slider"
                  />
                  <div
                    className="min-w-[64px] text-right text-sm font-medium tabular-nums text-[#F5F5F5]"
                    data-testid="new-client-height-value"
                  >
                    {heightUnit === 'imperial'
                      ? (() => {
                          const totalIn = Math.round(heightCm / 2.54);
                          const ft = Math.floor(totalIn / 12);
                          const inch = totalIn % 12;
                          return `${ft}′${inch}″`;
                        })()
                      : `${heightCm} cm`}
                  </div>
                </div>
              </div>

              {/* Iter 95 — Body type icon cards (4 menswear-classic).
                  Drives the BODY COMPOSITION prompt clause so renders
                  match the client's real proportions instead of an
                  idealised slim default. Cards include simple SVG
                  silhouettes so the consultant doesn't have to read. */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="label-luxury">Body type</Label>
                  {bodyAISuggested && (
                    <span
                      className="text-[9px] uppercase tracking-widest flex items-center gap-1"
                      style={{ color: accent }}
                      data-testid="new-client-body-ai-suggested"
                    >
                      <Sparkles className="w-3 h-3" /> AI suggested
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2" data-testid="new-client-body-type">
                  {[
                    {
                      value: 'slim',
                      label: 'Slim',
                      // narrow shoulders, slender torso
                      path: 'M50 12 a8 8 0 1 1 0 16 a8 8 0 1 1 0 -16 M44 30 L44 56 L42 96 L46 96 L48 60 L52 60 L54 96 L58 96 L56 56 L56 30 Z',
                    },
                    {
                      value: 'athletic',
                      label: 'Athletic',
                      // wide shoulders, tapered waist
                      path: 'M50 10 a9 9 0 1 1 0 18 a9 9 0 1 1 0 -18 M36 30 L40 48 L42 60 L40 96 L46 96 L48 62 L52 62 L54 96 L60 96 L58 60 L60 48 L64 30 Z',
                    },
                    {
                      value: 'regular',
                      label: 'Regular',
                      // average — straight torso
                      path: 'M50 10 a8.5 8.5 0 1 1 0 17 a8.5 8.5 0 1 1 0 -17 M41 29 L41 56 L40 96 L46 96 L48 60 L52 60 L54 96 L60 96 L59 56 L59 29 Z',
                    },
                    {
                      value: 'stocky',
                      label: 'Stocky',
                      // broader shoulders + midsection
                      path: 'M50 9 a9.5 9.5 0 1 1 0 19 a9.5 9.5 0 1 1 0 -19 M36 30 L34 54 L36 96 L46 96 L48 62 L52 62 L54 96 L64 96 L66 54 L64 30 Z',
                    },
                  ].map((opt) => {
                    const active = bodyType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setBodyType(opt.value); setBodyAISuggested(false); }}
                        className="flex flex-col items-center justify-center py-3 border transition-colors group"
                        style={{
                          backgroundColor: active ? button : '#0A0A0C',
                          color: active ? '#0A0A0C' : '#F5F5F5',
                          borderColor: active ? button : '#27272A',
                        }}
                        data-testid={`new-client-body-type-${opt.value}`}
                      >
                        <svg viewBox="0 0 100 100" className="w-10 h-12 mb-1" aria-hidden="true">
                          <path d={opt.path} fill={active ? '#0A0A0C' : '#F5F5F5'} stroke="none" />
                        </svg>
                        <span className="text-[10px] uppercase tracking-widest font-semibold">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#52525B] mt-2 uppercase tracking-wider">
                  Drives the exact proportions of every rendered look — confirm honestly for best fit accuracy
                </p>
              </div>

              {/* AI-processing consent (June 2026) — REQUIRED. GDPR Art. 7 /
                  BIPA-style provable opt-in: timestamp + wording version +
                  capturing account are stored on the client record. */}
              <div className="border border-[#27272A] bg-[#0A0A0C] p-4" data-testid="new-client-ai-consent">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={aiConsent}
                    onClick={() => setAiConsent(v => !v)}
                    className="mt-0.5 w-5 h-5 shrink-0 border flex items-center justify-center transition-colors"
                    style={{ backgroundColor: aiConsent ? button : 'transparent', borderColor: aiConsent ? button : '#3F3F46' }}
                    data-testid="new-client-ai-consent-checkbox"
                  >
                    {aiConsent && <Check className="w-3.5 h-3.5" style={{ color: '#0A0A0C' }} />}
                  </button>
                  <span className="text-xs leading-relaxed text-[#A1A1AA] cursor-pointer" onClick={() => setAiConsent(v => !v)}>
                    My client consents to their photo and details being processed by AI to
                    generate garment visualisations, stored securely, and used only for this
                    fitting service. They may withdraw consent and request deletion at any
                    time.{' '}
                    <a
                      href="/privacy#ai-consent"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: accent }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="new-client-consent-privacy-link"
                    >
                      AI &amp; privacy disclosure
                    </a>
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ─── Step 3 (was Step 2, moved Feb 2026) — Wardrobe context ── */}
          {step === 3 && (
            <>
              <ContextTilePicker
                value={context}
                onChange={setContext}
                accent={accent}
                activeColor={button}
                testidPrefix="new-client-context-tile"
              />
              <p className="text-[10px] uppercase tracking-widest text-[#52525B] leading-relaxed">
                Pick one — tailors the Style Inspirations, Business Casual, Shirts, Outerwear &amp; Wedding tiles for this client.
              </p>

              {/* Feb 2026 — Optional template seeding (multi-select). Any
                  saved templates the tailor has under /admin/templates
                  surface here. Picking one or more skips preemptive Gemini
                  generation and instead regenerates each template's looks
                  on the new client's face after handleSubmit. */}
              <div
                className="pt-4 border-t border-[#27272A]"
                data-testid="new-client-template-picker"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="label-luxury">
                    Or seed from templates <span className="opacity-50">· optional, multi-select</span>
                  </Label>
                  {chosenTemplateIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setChosenTemplateIds(new Set())}
                      className="text-[10px] uppercase tracking-widest text-[#A1A1AA] hover:text-[#C2A878]"
                      data-testid="new-client-template-clear"
                    >
                      Clear ({chosenTemplateIds.size})
                    </button>
                  )}
                </div>
                {templatesLoading ? (
                  <div className="flex items-center gap-2 text-xs text-[#A1A1AA] py-3" data-testid="new-client-template-loading">
                    <Loader2 className="w-3 h-3 spinner" /> Loading your templates…
                  </div>
                ) : availableTemplates.length === 0 ? (
                  <p className="text-[11px] text-[#52525B]" data-testid="new-client-template-empty">
                    No templates yet. Create one from a client's row on the Clients page.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="new-client-template-options">
                    {availableTemplates.map((tpl) => {
                      const active = chosenTemplateIds.has(tpl.id);
                      return (
                        <button
                          type="button"
                          key={tpl.id}
                          onClick={() => toggleChosenTemplate(tpl.id)}
                          className={`text-left border transition-colors overflow-hidden bg-[#0A0A0C] relative ${
                            active ? 'border-[#C2A878]' : 'border-[#27272A] hover:border-[#52525B]'
                          }`}
                          data-testid={`new-client-template-option-${tpl.id}`}
                        >
                          <div className="aspect-[3/4] bg-[#0A0A0C]">
                            {tpl.image_b64 ? (
                              <img
                                src={`data:image/jpeg;base64,${tpl.image_b64}`}
                                alt={tpl.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[10px] text-[#3F3F46]">No preview</div>
                            )}
                          </div>
                          {active && (
                            <div className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-[#C2A878] text-[#0A0A0C]">
                              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4L8.4 15a1 1 0 01-1.4 0L3.3 11.3a1 1 0 011.4-1.4l3 3 7.6-7.6a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                            </div>
                          )}
                          <div className="p-2">
                            <div className="text-[11px] text-[#F5F5F5] truncate" title={tpl.name}>{tpl.name}</div>
                            <div className="text-[9px] uppercase tracking-widest text-[#71717A]">
                              {tpl.looks_count} {tpl.looks_count === 1 ? 'look' : 'looks'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {chosenTemplateIds.size > 0 && (
                  <p className="text-[10px] uppercase tracking-widest text-[#C2A878] mt-3" data-testid="new-client-template-notice">
                    AI generation will be skipped — this client will be seeded from {chosenTemplateIds.size} template{chosenTemplateIds.size > 1 ? 's' : ''} (regenerated on their face).
                  </p>
                )}
              </div>
            </>
          )}

          {/* ─── Step 2 (moved Feb 2026) — Face + optional body photo,
              with Fit tiles sitting DIRECTLY UNDER the photo upload so
              the tailor sees the 3 fit previews (Classic / Savile Row
              / Slim) render on the client's face the instant the photo
              lands. Wardrobe context is now the LAST step. ────────── */}
          {step === 2 && (
            <>
              <div>
                <Label className="label-luxury mb-2 block">Face photo <span style={{ color: accent }}>· required</span></Label>
                <label htmlFor="new-client-face-upload" className="cursor-pointer block">
                  <div className={`aspect-square w-40 border-2 border-dashed flex items-center justify-center transition-colors ${photoPreview ? 'border-transparent' : 'border-[#3F3F46] hover:border-[hsl(var(--accent))]'}`}>
                    {photoPreview ? (
                      <img src={photoPreview} alt="Face" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-7 h-7 mx-auto mb-2" style={{ color: accent }} />
                        <p className="text-xs text-[#A1A1AA]">Tap to upload</p>
                      </div>
                    )}
                  </div>
                  <input id="new-client-face-upload" type="file" accept="image/*" className="hidden"
                    onChange={(e) => handlePhotoChange(e, false)} data-testid="new-client-face-input" />
                </label>
                <p className="text-[10px] text-[#52525B] mt-2 uppercase tracking-wider">Clear headshot — used for accurate face matching in every look</p>
              </div>

              <div>
                <Label className="label-luxury mb-2 block">
                  Full-body photo <span className="opacity-50">· optional · AI prefills body type + height</span>
                  {bodyAnalyzing && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest inline-flex items-center gap-1" style={{ color: accent }} data-testid="new-client-body-analyzing">
                      <Loader2 className="w-3 h-3 spinner" /> Analysing…
                    </span>
                  )}
                </Label>
                <label htmlFor="new-client-body-upload" className="cursor-pointer block">
                  <div className={`aspect-[3/4] w-32 border-2 border-dashed flex items-center justify-center transition-colors ${bodyPhotoPreview ? 'border-transparent' : 'border-[#3F3F46] hover:border-[hsl(var(--accent))]'}`}>
                    {bodyPhotoPreview ? (
                      <img src={bodyPhotoPreview} alt="Body" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center px-2">
                        <Upload className="w-5 h-5 mx-auto mb-1 opacity-60" style={{ color: accent }} />
                        <p className="text-[10px] text-[#A1A1AA]">Optional</p>
                      </div>
                    )}
                  </div>
                  <input id="new-client-body-upload" type="file" accept="image/*" className="hidden"
                    onChange={(e) => handlePhotoChange(e, true)} data-testid="new-client-body-input" />
                </label>
              </div>

              {/* Fit tiles — sit UNDER both photo upload slots so the
                  tailor sees Classic / Savile Row / Slim side-by-side
                  on the client's face the moment they approve the
                  headshot. FitPicker autoRender fires 3 parallel
                  /api/fit-preview calls once `photoB64` is set. */}
              <div className="border border-[#27272A] bg-[#0A0A0C] p-4" data-testid="new-client-fit-block">
                <div className="flex items-baseline justify-between mb-3">
                  <Label className="label-luxury">Fit</Label>
                  <span className="text-[9px] uppercase tracking-widest text-[#52525B]">
                    Reference cut — studio mannequin
                  </span>
                </div>
                {/* Oct 2026 (user request — "don't automatically render
                    previews unless the button is clicked for fits") —
                    `autoRender={false}` so the generic white-mannequin
                    references stay visible instantly (approved earlier)
                    but the Gemini on-client renders only kick off when
                    the tailor taps "Render previews" inside FitPicker. */}
                <FitPicker
                  options={FIT_OPTIONS}
                  value={fit}
                  onChange={setFit}
                  clientImage={photoB64}
                  bodyImage={bodyPhotoB64}
                  accent={accent}
                  testidPrefix="new-client-fit"
                  autoRender={false}
                />
              </div>
            </>
          )}

          {/* Fit step removed (Feb 2026) — all new clients render in the
              house signature cut (Tom Ford-meets-Cucinelli aesthetic baked
              into the backend prompt anchor). Saves the user a click; no
              fit-related second-guessing during intake. */}
        </div>

        <div className="p-5 border-t border-[#27272A] flex items-center justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="text-xs uppercase tracking-wider text-[#A1A1AA] hover:text-[#F5F5F5]"
            data-testid="new-client-back"
          >
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          {step < 3 ? (
            <Button
              onClick={() => {
                if (step === 1) {
                  if (!name.trim()) { toast.error('Name is required'); return; }
                  if (!aiConsent) { toast.error('Client consent to AI processing is required'); return; }
                }
                if (step === 2 && !photoB64) {
                  toast.error('Face photo is required to generate looks');
                  return;
                }
                setStep(step + 1);
              }}
              className="rounded-none px-6 py-2 uppercase tracking-widest text-xs font-semibold"
              style={{ backgroundColor: button, color: '#0A0A0C' }}
              data-testid="new-client-next"
            >
              Continue →
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="rounded-none px-6 py-2 uppercase tracking-widest text-xs font-semibold disabled:opacity-50"
              style={{ backgroundColor: button, color: '#0A0A0C' }}
              data-testid="new-client-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 spinner inline mr-2" /> Creating…</> : 'Create & enter studio'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================== CUSTOMIZE MODAL ====================

// ==================== MAIN COMPONENT ====================

export default function Dashboard() {
  // Restore client photo from session if we've been here this session for the same client.
  // Photo/looks cache reads are now delegated to /lib/clientCache.js which enforces
  // strict per-client scoping — never resolves "the currently selected client" from
  // inside the cache layer, eliminating the cross-client bleed bug class.
  const _initialClientId = (() => {
    try { return localStorage.getItem('selected_client_id') || ''; } catch { return ''; }
  })();
  const _cachedPhoto = readClientPhoto(_initialClientId);
  // Restore the last-known generated looks for this client from sessionStorage
  // so the workspace is populated INSTANTLY on Dashboard remount (e.g. after
  // a quick detour to the Gallery page). Otherwise the user sees an empty
  // workspace for the ~1s it takes /api/gallery to return, which feels like a
  // "reset to walk-in" bug. The API call still runs in the background after
  // mount so any new looks + tailor-notes refresh still land correctly.
  const _cachedLooks = readClientLooks(_initialClientId);

  const [clientImage, setClientImage] = useState(_cachedPhoto?.b64 || null);
  const [clientImagePreview, setClientImagePreview] = useState(_cachedPhoto?.preview || null);
  // Feb 2026 — cross-client photo-bleed fix. The `clientImage` state alone
  // can't tell us WHICH client the bytes belong to, and the persistence
  // effect below runs whenever `selectedClientId` changes. Without this ref
  // the effect would write client A's photo under client B's cache key
  // during the switch handoff (before the state wipe commits). We stamp the
  // owning client id every time we deliberately set `clientImage`, and the
  // persist effect refuses to write to any cache key that doesn't match.
  const clientImageOwnerRef = React.useRef(_initialClientId || null);
  // True while the photo for the currently-selected client is being fetched
  // from the backend (cache miss). UI uses this to render a luxury skeleton in
  // place of a blank slot — no white-frame flash during client switches.
  const [clientImageLoading, setClientImageLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('full_suits');
  const [fit, setFit] = useState('modern');
  const [fabricImage, setFabricImage] = useState(null);
  const [fabricImagePreview, setFabricImagePreview] = useState(null);
  // Library fabric id — when set, the backend resolves the swatch off disk
  // and we skip sending the heavy b64 payload. Pre-filled from the deep-link
  // /dashboard?fabric_id=... when the tailor clicks "Render" on a swatch in
  // the Fabric Library page.
  const [libraryFabricId, setLibraryFabricId] = useState(null);
  const [libraryFabricMeta, setLibraryFabricMeta] = useState(null);
  // Library state wiped on client switch handled below.
  const [libraryFabrics, setLibraryFabrics] = useState([]);  // assigned to this store
  const [libraryLoading, setLibraryLoading] = useState(false);
  // Iter 123 — client-side substring filter over the (already paginated
  // server-side, capped at 500) fabric list. Instant response — no
  // network round-trip until the tenant crosses 500 assigned fabrics
  // (nobody's close today). Debounced with a 120ms delay so typing
  // "cha" doesn't re-render 250 tiles on every keystroke.
  const [fabricSearch, setFabricSearch] = useState('');
  const [fabricSearchQ, setFabricSearchQ] = useState('');
  // Iter 126 — garment-type filter tabs for the fabric library.
  const [libGarmentCat, setLibGarmentCat] = useState('suits');
  // Custom-fabric picker: which Collection button is "popped out" (showing
  // its swatches in a flyout panel). null = none open.
  const [openFabricCollection, setOpenFabricCollection] = useState(null);
  // Custom-fabric picker: quick colour-family filter (null = all colours).
  const [libColourFilter, setLibColourFilter] = useState(null);
  React.useEffect(() => {
    const t = setTimeout(() => setFabricSearchQ(fabricSearch.trim().toLowerCase()), 120);
    return () => clearTimeout(t);
  }, [fabricSearch]);
  const fabricLibraryFiltered = React.useMemo(() => {
    // Iter 126 — garment-type segmentation (Suits / Shirts / Liners).
    // Legacy fabrics with no garment_category default to suits.
    let byGarment = libraryFabrics.filter(f => ((f.garment_category || 'suits') === libGarmentCat));
    if (libColourFilter) {
      byGarment = byGarment.filter(f => (f.color_family || 'neutral').toLowerCase() === libColourFilter);
    }
    if (!fabricSearchQ) return byGarment;
    return byGarment.filter(f => (
      (f.sku || '').toLowerCase().includes(fabricSearchQ) ||
      (f.name || '').toLowerCase().includes(fabricSearchQ) ||
      (f.composition || '').toLowerCase().includes(fabricSearchQ) ||
      (f.color_family || '').toLowerCase().includes(fabricSearchQ) ||
      (f.category || '').toLowerCase().includes(fabricSearchQ)
    ));
  }, [libraryFabrics, fabricSearchQ, libGarmentCat, libColourFilter]);
  const [fabricLibrary, setFabricLibrary] = useState([]);  // [{b64, preview}, ...]
  // Iter 96 — Fabric preview popout. User feedback: dashboard fabric
  // tiles are too small to read pattern/colour details. A small
  // zoom button in the top-right of each tile opens a popout modal
  // with a large preview + a "Render" CTA underneath that fires the
  // existing CustomFabricQuestionnaire flow.
  const [previewFabric, setPreviewFabric] = useState(null);
  // Iter 124 — Suit Designer flow. Non-null fabric object → modal open.
  // Launched from FabricPreviewModal's "Design" button. All state lives
  // in SuitDesignerModal.jsx; we just gate its visibility here.
  const [designerFabric, setDesignerFabric] = useState(null);
  const [fabricCategory, setFabricCategory] = useState('full_suits');
  // Which SPECIFIC silhouette within the category the consultant wants to
  // render ONE look for — generating all 3/5 was too expensive per fabric and
  // too cluttered on mobile. User picks one and hits Generate.
  const [fabricStyle, setFabricStyle] = useState('classic_suit');

  // Client linking
  // Hydrate clients from localStorage IMMEDIATELY (instant dropdown) and
  // refresh from /api/clients in the background. This is a classic SWR
  // pattern — the picker never waits on the network, while the cache stays
  // fresh as clients are added/edited elsewhere in the app.
  const [clients, setClients] = useState(() => {
    try {
      const cached = localStorage.getItem('tf_clients_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch { /* noop */ }
    return [];
  });
  const [selectedClientId, setSelectedClientId] = useState(_initialClientId);
  const [selectedClientSlug, setSelectedClientSlug] = useState('');
  // Feb 2026 — client "Occupation or upcoming event". Set on intake,
  // editable via the chip on the client card header. Drives which 6
  // Style / BC / Shirts / Outerwear / Wedding inspiration tiles surface
  // for this client. `null` = no context set → legacy defaults show.
  const [clientContext, setClientContext] = useState(null);
  // Iter 231 (Feb 2026) — sidebar Wardrobe-Context popout picker toggle.
  const [wardrobeContextPopoverOpen, setWardrobeContextPopoverOpen] = useState(false);
  // Feb 2026 — Change Fit popup for existing clients. Separate from the
  // intake fit picker: opens only when the tailor taps "Change Fit"
  // under the sidebar's Wardrobe Context panel.
  const [fitPopoverOpen, setFitPopoverOpen] = useState(false);
  // Feb 2026 — Background-preloaded fit previews for existing clients.
  // Keyed by clientId; each entry is { slim, modern, savile_row } → b64.
  // The actual "fire on client open" effect lives further down, next to
  // `bodyImage` state (which the request payload needs) — declared here
  // just so the popover render below has the cache in scope.
  const [fitPreviewCache, setFitPreviewCache] = useState({});
  const fitPreviewInflightRef = React.useRef(new Set());
  // Sep 2026 (user report — "Savile Row / Slim renders always come out
  // as Classic") — the workspace-scoped `fit` state above defaults to
  // 'modern' and needs to sync with the currently-selected client's
  // `fit_preference` whenever the tailor switches clients or picks a
  // new fit on the sidebar popover. Without this sync, every
  // /api/generate-look call serialises the stale default and every
  // workspace render comes out as Classic.
  //
  // Sep 2026 (TDZ bugfix) — this effect used to sit right after
  // `useState('modern')` above; that placed the dep-array reference
  // to `selectedClientId` BEFORE the `useState` declaration for it,
  // causing `ReferenceError: Cannot access 'selectedClientId' before
  // initialization` on every mount. Moved down here — below both
  // `selectedClientId` and `clients` — so the closure + deps are in
  // scope.
  React.useEffect(() => {
    if (!selectedClientId) return;
    const client = clients.find((c) => c.id === selectedClientId);
    if (!client) return;
    const raw = client.fit_preference || 'modern';
    // Only accept the 3 valid UI values — anything else (legacy
    // 'semi_slim', 'classic', 'relaxed') collapses to 'modern' which
    // renders as the "Classic" tile.
    const next = (raw === 'slim' || raw === 'savile_row') ? raw : 'modern';
    setFit((prev) => (prev === next ? prev : next));
  }, [selectedClientId, clients]);
  // Iter 232 (Feb 2026) — while the workspace is regenerating tiles for
  // a fresh context, hold the human-readable "retuning for X…" label so
  // the banner above the inspirations grid can shout the change. Cleared
  // to null once every visible inspiration slot in the active category
  // has landed (image ready) or errored.
  const [wardrobeRetuning, setWardrobeRetuning] = useState(null);
  // Curated tiles for the currently-selected client, keyed by category.
  // Populated by /api/inspirations after the client (or its context)
  // changes. Empty {} = fall back to the hardcoded arrays below.
  const [contextTiles, setContextTiles] = useState({});
  // Gate into workspace is ALWAYS enforced until the user has explicitly selected a client.
  // (Previously we also allowed `_cachedPhoto` to unlock the workspace, which meant a cached
  // photo from a prior session could bypass the picker. The user wants an explicit choice.)
  const [showClientGate, setShowClientGate] = useState(!_initialClientId);
  const [showNewClientIntake, setShowNewClientIntake] = useState(false);
  const { user, logout } = useAuth();
  const MASTER_EMAIL = 'robson@tailorflow.tech';
  const isMasterAdmin = (user?.email || '').toLowerCase().trim() === MASTER_EMAIL;
  const isDemoStore = !!user?.is_demo_store;

  // Reusable client list refetch — invoked from the gate's new-client intake
  // modal so freshly-created clients show up instantly without a page reload.
  // Iter 123 — client-side search across the client picker. Fetches
  // still cap at 500; this lets tailors instantly narrow to the client
  // they want without scrolling. Debounced 100ms.
  const [clientSearch, setClientSearch] = useState('');
  const [clientSearchQ, setClientSearchQ] = useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setClientSearchQ(clientSearch.trim().toLowerCase()), 100);
    return () => clearTimeout(t);
  }, [clientSearch]);
  const filteredClients = React.useMemo(() => {
    if (!clientSearchQ) return clients;
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(clientSearchQ) ||
      (c.email || '').toLowerCase().includes(clientSearchQ) ||
      (c.phone || '').toLowerCase().includes(clientSearchQ)
    );
  }, [clients, clientSearchQ]);

  const fetchClients = React.useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients?limit=500`, { withCredentials: true });
      const list = r.data.clients || [];
      setClients(list);
      try { localStorage.setItem('tf_clients_cache', JSON.stringify(list)); } catch { /* noop */ }
      return list;
    } catch (err) {
      logError('Failed to load clients:', err);
      return [];
    }
  }, []);

  // Feb 2026 — resolve the selected client's `context` from the loaded
  // `clients` list. Runs whenever the selection or the list changes, so
  // an editable-chip PATCH that hits /api/clients/{id}/context is
  // reflected the next time the client list is refreshed.
  React.useEffect(() => {
    if (!selectedClientId) { setClientContext(null); return; }
    const c = clients.find(x => x.id === selectedClientId);
    setClientContext(c?.context || null);
  }, [selectedClientId, clients]);

  // Fetch the 6 curated tiles per category for the current client's
  // context. Runs once per (selectedClientId, clientContext) combo.
  // When context is null the effect clears `contextTiles` so the
  // downstream `active*Insps` arrays fall back to the legacy defaults.
  React.useEffect(() => {
    if (!selectedClientId || !isValidContext(clientContext)) {
      setContextTiles({});
      return;
    }
    let cancelled = false;
    const cats = ['style', 'business_casual', 'shirts', 'outerwear', 'wedding'];
    Promise.all(cats.map(cat =>
      axios.get(
        `${BACKEND_URL}/api/inspirations?category=${cat}&client_id=${selectedClientId}`,
        { withCredentials: true, timeout: 8000 },
      ).then(r => [cat, r.data?.tiles || []]).catch(() => [cat, null])
    )).then(rows => {
      if (cancelled) return;
      const out = {};
      for (const [cat, tiles] of rows) if (tiles?.length) out[cat] = tiles;
      setContextTiles(out);
    });
    return () => { cancelled = true; };
  }, [selectedClientId, clientContext]);


  React.useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Fetch the fabrics ASSIGNED to this store. Master admin sees every
  // fabric (filtered server-side by store_id). Refetched whenever the
  // Custom Fabric tab becomes active so newly-assigned swatches surface
  // without a page refresh.
  React.useEffect(() => {
    if (activeCategory !== 'custom_fabric') return;
    let cancelled = false;
    setLibraryLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/fabrics`, { withCredentials: true });
        // Iter 129k — liners live in the same fabric_library collection
        // but must NEVER appear as suit-render fabrics in the Custom
        // Fabric tab. They surface only inside the Customize Liner
        // (Suit Designer) popout via /api/liners/library.
        const nonLiners = (data.fabrics || []).filter(f => (f.garment_category || 'suits') !== 'liners');
        if (!cancelled) setLibraryFabrics(nonLiners);
      } catch (e) {
        // Silent — tab still works for ad-hoc uploads even if the library 404s.
      } finally {
        if (!cancelled) setLibraryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCategory]);


  // Deep-link: /dashboard?fabric_id=...&tab=custom_fabric. Triggered from
  // the Fabric Library page when a tailor clicks "Render with this fabric".
  // Pre-loads the swatch into preview state, flips to the Custom Fabric tab,
  // and opens the CustomFabricQuestionnaire so the tailor confirms
  // silhouette + shoe + shirt before any render fires (Iter 82 — no more
  // auto-fire).
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fid = params.get('fabric_id');
    const tab = params.get('tab');
    if (!fid) return;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/fabrics/${fid}`, { withCredentials: true });
        // Iter 129k — liners are never valid deep-link targets for the
        // Custom Fabric tab. If someone shares a liner URL, redirect
        // them into the fabric library page instead.
        if ((data.garment_category || 'suits') === 'liners') {
          toast.error('Liners are not available as suit fabrics — open the Customize Liner popout instead.');
          const url = new URL(window.location.href);
          url.searchParams.delete('fabric_id');
          url.searchParams.delete('tab');
          window.history.replaceState({}, '', url.toString());
          return;
        }
        setLibraryFabricId(data.id);
        setLibraryFabricMeta(data);
        setFabricImagePreview(`${BACKEND_URL}${data.image_url}`);
        if (tab === 'custom_fabric') setActiveCategory('custom_fabric');
        setCustomFabricQTarget({
          fabric: { ...data, image_url: `${BACKEND_URL}${data.image_url}` },
        });
        setShowCustomFabricQ(true);
        toast.success(`Fabric ${data.sku} loaded — pick a silhouette to render`);
        // Strip the query so a refresh doesn't re-load it.
        const url = new URL(window.location.href);
        url.searchParams.delete('fabric_id');
        url.searchParams.delete('tab');
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        toast.error('Failed to load fabric');
      }
    })();
  }, []);

  // Iter 99 — query param ?addClient=1 opens the NewClientIntakeModal on
  // mount. Used by the ClientsPage "Add Client" button so the tailor lands
  // straight in the height + body-type intake flow instead of the legacy
  // dialog. Strips the param after firing so a refresh doesn't reopen it.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('addClient') === '1') {
      setShowNewClientIntake(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('addClient');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Auto-default client: when the user lands at the dashboard without any
  // client selected, fetch/create the "Robson Abraham" default client, store
  // it as selected, hydrate its cached photo, and skip the client gate.
  // Ensures the workspace is instantly usable — no picker required.
  // For the master admin this client comes back with `looks_locked: true`
  // from the API — we propagate it to state so every auto-gen hook skips.
  const [clientLooksLocked, setClientLooksLocked] = useState(false);
  const [disabledSubcategories, setDisabledSubcategories] = useState([]);

  React.useEffect(() => {
    if (selectedClientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/my/default-client`, { withCredentials: true });
        if (cancelled || !data?.id) return;
        localStorage.setItem('selected_client_id', data.id);
        localStorage.setItem('selected_client_name', data.name || 'Robson Abraham');
        setSelectedClientId(data.id);
        setSelectedClientSlug(data.slug || '');
        setShowClientGate(false);
        if (data.looks_locked) setClientLooksLocked(true);
        if (Array.isArray(data.disabled_subcategories)) {
          setDisabledSubcategories(data.disabled_subcategories);
        }
        // Pull the seeded photo so the Client Photo rail renders immediately
        try {
          const resp = await axios.get(
            `${BACKEND_URL}/api/clients/${data.id}/photo-b64`,
            { withCredentials: true }
          );
          if (!cancelled && resp.data?.image) {
            const b64 = resp.data.image;
            const dataUrl = `data:image/jpeg;base64,${b64}`;
            clientImageOwnerRef.current = data.id;
            setClientImage(b64);
            setClientImagePreview(dataUrl);
            try {
              sessionStorage.setItem(
                `client_photo_${data.id}`,
                JSON.stringify({ b64, preview: dataUrl })
              );
            } catch (_e) { /* quota — non-fatal */ }
          }
        } catch (_e) { /* no photo yet — non-fatal */ }
      } catch (err) {
        // 404 from /my/default-client means: this user has no default client.
        // Don't redirect to /clients — that bypasses the gate the tailor
        // explicitly asked for. Instead surface the gate so they can either
        // pick from existing clients (dropdown) or hit the "+ Add new client"
        // link below it to start the 3-step intake. Gate handles both
        // "has clients but no default" AND "no clients at all" gracefully.
        if (err?.response?.status === 404) {
          if (!cancelled) {
            try {
              localStorage.removeItem('selected_client_id');
              localStorage.removeItem('selected_client_name');
            } catch { /* noop */ }
            setShowClientGate(true);
          }
          return;
        }
        logError('Auto-default client failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the client photo to BOTH sessionStorage (session-scope) AND
  // localStorage (cross-session, last 3 most recent clients only — capped so
  // quota doesn't blow up). Keyed strictly by client id so one client's photo
  // can never bleed into another's workspace.
  //
  // Cross-client bleed guard: this effect fires whenever `selectedClientId`
  // changes too — but at that moment `clientImage` may still hold the
  // PREVIOUS client's bytes (state wipe races the client-id flip). Writing
  // them under the NEW client's key would cache the wrong face against the
  // wrong client. We refuse to persist unless `clientImageOwnerRef` (stamped
  // wherever `setClientImage` is called deliberately) matches the currently
  // selected client. The clear branch is likewise gated so a stale wipe on
  // switch can't blow away a legit cache entry.
  React.useEffect(() => {
    const target = selectedClientId || 'demo';
    const owner = clientImageOwnerRef.current || 'demo';
    if (owner !== target) {
      // The current in-memory photo does not belong to the currently
      // selected client — the switch handoff is still in flight. Do
      // nothing; a later effect will call setClientImage with the right
      // bytes (or null) and re-stamp the owner, at which point we'll
      // persist correctly.
      return;
    }
    if (clientImage && clientImagePreview) {
      writeClientPhoto(target, { b64: clientImage, preview: clientImagePreview });
    } else {
      // No image → remove ONLY the sessionStorage entry (durable localStorage
      // copy stays so a quick refresh still hydrates instantly). The
      // sessionStorage clear here keeps the in-tab cache from re-painting
      // a stale photo after a deliberate user "remove photo".
      try {
        sessionStorage.removeItem(`client_photo_${target}`);
      } catch { /* noop */ }
    }
    // Always clear the legacy global key — it caused cross-client photo bleed.
    try { sessionStorage.removeItem('last_client_photo'); } catch { /* noop */ }
  }, [clientImage, clientImagePreview, selectedClientId]);

  const [generatedLooks, setGeneratedLooks] = useState(_cachedLooks);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingCategory, setGeneratingCategory] = useState(null);
  // Track ALL categories currently generating (not just the most recent) so parallel auto-gen
  // shows skeleton loaders across every tab at once.
  const [generatingCats, setGeneratingCats] = useState(() => new Set());
  const isCatGenerating = useCallback((cat) => generatingCats.has(cat) || generatingCategory === cat, [generatingCats, generatingCategory]);
  // EXPLICIT open-state for subcategory accordions. Replaces the old fragile
  // `classList.toggle('hidden')` pattern which broke once React reconciled
  // the className prop on the next render (silently re-hiding panels the
  // user had just opened — symptom: "Auto-generating…" text never advanced
  // to skeletons because the panel was actually hidden underneath).
  const [openSubcats, setOpenSubcats] = useState(() => new Set());
  const isSubcatOpen = useCallback((cat) => openSubcats.has(cat), [openSubcats]);
  const toggleSubcat = useCallback((cat) => {
    setOpenSubcats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }, []);

  // Iter 100 — Per-client UI state reset. Production bug report:
  // "When switching clients, subcategories bleed into other clients."
  // The accordion-open state (openSubcats), the gold-bordered "active
  // fabric" selection (libraryFabricId / libraryFabricMeta), and the
  // ephemeral fabric-image / custom-fabric questionnaire target are all
  // strictly per-client. They were retained across client switches,
  // which made it look like Client A's expanded subcategory panels
  // (still showing A's renders) were attached to Client B. Reset them
  // whenever selectedClientId actually changes (skip first mount via
  // ref; otherwise we'd nuke the values restored from sessionStorage).
  const prevClientIdRef = React.useRef(null);
  React.useEffect(() => {
    if (prevClientIdRef.current === null) {
      prevClientIdRef.current = selectedClientId;
      return;
    }
    if (prevClientIdRef.current === selectedClientId) return;
    prevClientIdRef.current = selectedClientId;
    setOpenSubcats(new Set());
    setLibraryFabricId(null);
    setLibraryFabricMeta(null);
    setFabricImage(null);
    setFabricImagePreview(null);
    setCustomFabricQTarget(null);
    setShowCustomFabricQ(false);
  }, [selectedClientId]);
  const [generatingIdx, setGeneratingIdx] = useState(-1);
  const generatedLooksRef = React.useRef(generatedLooks);
  generatedLooksRef.current = generatedLooks;

  // Persist the active client's looks to sessionStorage so they reappear
  // instantly on Dashboard remount (e.g. after navigating to /gallery and
  // back). We strip the heavy base64 image data before storing because
  // sessionStorage has a ~5MB quota — the gallery rehydration effect will
  // re-fetch the blob images fresh from /api/gallery right after mount.
  React.useEffect(() => {
    if (!selectedClientId) return;
    // Only cache looks that have actually landed (skip failed/restoring placeholders)
    const stripped = generatedLooks
      .filter(l => l && l.id && !l.failed && !l.restoring && l.image)
      .map(l => {
        // Iter 88 — cache the URL form of the image so the card
        // renders INSTANTLY on next mount (no waiting for the
        // /api/gallery roundtrip). We skip base64 bytes because
        // sessionStorage caps at ~5MB total and a single base64
        // look is ~600KB. URLs are tiny strings; the browser HTTP
        // cache handles the actual image bytes between visits.
        const raw = l.image || l.image_thumb || '';
        const isBase64 = typeof raw === 'string' && raw.startsWith('data:');
        return {
          id: l.id,
          category: l.category,
          label: l.label,
          garment: l.garment,
          garment_id: l.garment_id,
          comboIdx: l.comboIdx,
          patternCategory: l.patternCategory,
          // Only stash URL refs (never base64) — keeps the cache
          // under quota even for 50+ look workspaces.
          image: isBase64 ? undefined : raw,
          image_thumb: (typeof l.image_thumb === 'string' && !l.image_thumb.startsWith('data:')) ? l.image_thumb : undefined,
          // restoring=true ONLY when we couldn't snapshot a real URL
          // — those slots still flip to the skeleton loader. When
          // we DID snapshot a URL the card renders instantly.
          restoring: isBase64 && !l.image_thumb,
        };
      });
    writeClientLooks(selectedClientId, stripped);
    // Iter 101 — CRITICAL race fix. Production bug: "other clients are
    // still bleeding into another clients looks when you switch clients".
    // Root cause: this effect previously had `selectedClientId` in its
    // dependency list. The moment selectedClientId flipped from A → B,
    // React re-fired this effect BEFORE the wipe-and-load effect (which
    // sets generatedLooks to []) had a chance to run. The closure
    // captured (selectedClientId=B, generatedLooks=A's looks) and
    // wrote A's looks under the B key — poisoning B's cache. The wipe
    // effect then read that poisoned cache back, and B's workspace
    // displayed A's looks.
    //
    // Fix: depend on `generatedLooks` ONLY. The effect now fires solely
    // when looks change (initial load, new generation, wipe), and the
    // closure always sees a (looks, client) pair that's actually
    // consistent — never the cross-client transition state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedLooks]);
  const selectedClientIdRef = React.useRef(selectedClientId);
  selectedClientIdRef.current = selectedClientId;

  // Cleanup test mode looks (no client) on unmount/session end
  React.useEffect(() => {
    return () => {
      if (!selectedClientIdRef.current) {
        generatedLooksRef.current.forEach(look => {
          if (look.id) {
            axios.delete(`${BACKEND_URL}/api/gallery/${look.id}`, { withCredentials: true }).catch(() => {});
          }
        });
      }
    };
  }, []);

  const [selectedLook, setSelectedLook] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Custom combo state
  const [comboBlazer, setComboBlazer] = useState('single_blazer');
  const [comboTrouser, setComboTrouser] = useState('dress_trousers');
  const [comboShoe, setComboShoe] = useState('oxford');
  const [comboBlazerColor, setComboBlazerColor] = useState('#1C2841');
  const [comboTrouserColor, setComboTrouserColor] = useState('#36454F');
  const [comboShoeColor, setComboShoeColor] = useState('#0B0B0B');
  const [comboResult, setComboResult] = useState(null);
  const [comboGenerating, setComboGenerating] = useState(false);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  // Stable callback so React.memo on LookCard fires correctly — passing an
  // inline `(l) => setZoomImage(l.image)` re-creates a new function ref every
  // render and busts memoization across the ~100-card grid.
  const handleZoomLook = useCallback((l) => setZoomImage(l.image), []);
  // No-op click for placeholder/skeleton LookCards (still generating).
  const noopClick = useCallback(() => {}, []);
  // Wedding state
  const [weddingTheme, setWeddingTheme] = useState(null);
  const [weddingSeason, setWeddingSeason] = useState('spring');
  const [weddingGroomColor, setWeddingGroomColor] = useState('#0B0B0B');
  const [weddingGroomsmenColor, setWeddingGroomsmenColor] = useState('#36454F');
  const [weddingBridesmaidsColor, setWeddingBridesmaidsColor] = useState('#556B2F');
  // Customizable garments for the wedding party
  const [weddingGroomGarment, setWeddingGroomGarment] = useState('Classic Tuxedo');
  const [weddingGroomsmenGarment, setWeddingGroomsmenGarment] = useState('Matching Single-Breasted Suits');
  const [weddingBridesmaidsGarment, setWeddingBridesmaidsGarment] = useState('Floor-Length A-Line Dresses');
  // Tie/neckwear for the wedding party — required for ALL wedding looks so
  // nobody ever gets a tie-less groom/groomsmen render.
  const [weddingGroomTieStyle, setWeddingGroomTieStyle] = useState('classic_tie');
  const [weddingGroomTieColor, setWeddingGroomTieColor] = useState('#0B0B0B');
  const [weddingGroomsmenTieStyle, setWeddingGroomsmenTieStyle] = useState('classic_tie');
  const [weddingGroomsmenTieColor, setWeddingGroomsmenTieColor] = useState('#36454F');
  const [weddingResults, setWeddingResults] = useState({});
  const [weddingGenerating, setWeddingGenerating] = useState({});
  const [weddingPartyGenerating, setWeddingPartyGenerating] = useState(false);
  const [weddingPartyImage, setWeddingPartyImage] = useState(null);
  // P2 — Wedding party TRIO (3 photos): groom (with face) + groomsmen
  // mannequins + bridesmaids mannequins, plus 3 backend-suggested tie palettes.
  // Replaces the single 21:9 group photo for new generations; legacy
  // `weddingPartyImage` still hydrates from the gallery cache for old looks.
  const [weddingPartyTrio, setWeddingPartyTrio] = useState(null);  // { groom, groomsmen, bridesmaids, tie_palettes }
  const [showWeddingQuestionnaire, setShowWeddingQuestionnaire] = useState(false);
  const [exportingWeddingPdf, setExportingWeddingPdf] = useState(false);
  const [weddingEmailTo, setWeddingEmailTo] = useState('');
  const [sendingWeddingEmail, setSendingWeddingEmail] = useState(false);
  // Wedding setup gateway (Feb 2026) — couple identity captured BEFORE
  // the tailor can access the Wedding tab. Hydrated from the selected
  // client doc and persisted via PATCH /api/clients/{id}.
  const [weddingSetup, setWeddingSetup] = useState({
    groom_name: '', bride_name: '', wedding_date: '', wedding_colours: [],
  });
  const [showWeddingSetup, setShowWeddingSetup] = useState(false);
  const [savingWeddingSetup, setSavingWeddingSetup] = useState(false);
  // Add Custom Swatch modal (Feb 2026) — replaces the legacy upload row.
  const [showAddCustomSwatch, setShowAddCustomSwatch] = useState(false);
  // Iter 82 — Custom Fabric Questionnaire (replaces auto-fire on fabric
  // click). When the tailor picks an assigned fabric, this modal opens
  // and asks for category/silhouette + shoe + shirt before generating.
  const [showCustomFabricQ, setShowCustomFabricQ] = useState(false);
  const [customFabricQTarget, setCustomFabricQTarget] = useState(null); // { fabric }
  // Iter 129h — Casual Shirts picker. Opens when the "Casual Shirts"
  // shirt-subcategory header is clicked (the subcat has picker:true so
  // it does NOT auto-generate).
  const [showCasualShirtsQ, setShowCasualShirtsQ] = useState(false);
  // Feb 2026 — Sweater picker (parallel to Casual Shirts). Opens when
  // the "Sweaters" shirt-subcategory header is clicked.
  const [showSweaterQ, setShowSweaterQ] = useState(false);
  // Feb 2026 iter 170 — Dress Shirt Builder picker (collar / weave /
  // colour / pattern / trouser / shoe). Opens when the "Dress Shirt
  // Builder" shirt-subcategory header is clicked.
  const [showDressShirtsQ, setShowDressShirtsQ] = useState(false);
  // Custom Fabric tab: no per-silhouette threshold, no overage billing.
  // The previous CUSTOM_FABRIC_FREE_THRESHOLD constant + UI badge has
  // been removed per the "no cap on custom fabrics" user spec — tailors
  // generate as many renders as they want without any visible threshold.
  const { storeSettings } = useStore();

  const btnStyle = { backgroundColor: storeSettings.button_color || storeSettings.primary_color || '#C2A878', color: '#0A0A0C' };

  const handleClientImage = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const r = new FileReader();
      r.onload = (ev) => {
        const b64 = ev.target.result.split(',')[1];
        clientImageOwnerRef.current = selectedClientId || 'demo';
        setClientImage(b64);
        setClientImagePreview(ev.target.result);
        // If the photo is being uploaded for a saved client, persist it to the backend so it survives logout / device change
        if (selectedClientId && /^[a-f0-9]{24}$/i.test(selectedClientId)) {
          axios.post(`${BACKEND_URL}/api/clients/${selectedClientId}/photo-b64`, { image: b64 }, { withCredentials: true })
            .then(() => toast.success('Photo saved to client'))
            .catch(() => { /* non-fatal */ });
        }
      };
      r.readAsDataURL(file);
    }
  }, [selectedClientId]);

  // Optional full-body reference photo — separate from the face headshot.
  // When uploaded, the backend keeps it on the client doc and the frontend
  // sends it as body_image on every /generate-look call so Gemini matches
  // the client's real height, build, and posture.
  const [bodyImage, setBodyImage] = useState(null);
  const [bodyImagePreview, setBodyImagePreview] = useState(null);
  // Feb 2026 — Background-preload the 3 fit previews for the current
  // client. Placed HERE (below bodyImage's declaration) so the closure
  // has both `clientImage` and `bodyImage` in scope — moving it any
  // earlier hit a TDZ error at Dashboard mount.
  //
  // Oct 2026 (user request — "don't automatically render previews
  // unless the button is clicked for fits") — the previous background
  // preload fired 3 parallel `/api/fit-preview` calls every time the
  // tailor switched clients, burning Gemini (and object-storage
  // budget) on previews that often went un-viewed. Removed.
  //
  // The Change Fit popover now opens with ONLY the cached white-
  // mannequin references (fetched inside FitPicker from the public
  // `/api/fit-preview/mannequin/{fit}` cache) visible. The tailor
  // taps "Render previews" to opt into the client-specific renders.
  // `fitPreviewCache` is kept in state so a manual render inside the
  // popover still persists across reopens of the same client.
  const handleBodyImage = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = (ev) => {
      const b64 = ev.target.result.split(',')[1];
      setBodyImage(b64);
      setBodyImagePreview(ev.target.result);
      if (selectedClientId && /^[a-f0-9]{24}$/i.test(selectedClientId)) {
        axios.post(`${BACKEND_URL}/api/clients/${selectedClientId}/body-photo-b64`, { image: b64 }, { withCredentials: true })
          .then(() => toast.success('Full-body photo saved'))
          .catch(() => { /* non-fatal */ });
      }
    };
    r.readAsDataURL(file);
  }, [selectedClientId]);

  // Load any saved body photo on client switch
  React.useEffect(() => {
    if (!selectedClientId || !/^[a-f0-9]{24}$/i.test(selectedClientId)) {
      setBodyImage(null); setBodyImagePreview(null); return;
    }
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/clients/${selectedClientId}/body-photo-b64`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        if (r.data?.image) {
          setBodyImage(r.data.image);
          setBodyImagePreview(`data:image/jpeg;base64,${r.data.image}`);
        } else {
          setBodyImage(null); setBodyImagePreview(null);
        }
      })
      .catch(() => { setBodyImage(null); setBodyImagePreview(null); });
    return () => { cancelled = true; };
  }, [selectedClientId]);
  const [autoInspTriggered, setAutoInspTriggered] = useState(false);
  const weddingInspTriggeredRef = React.useRef(false);  // legacy flag, kept for resets only
  const [inspResults, setInspResults] = useState({});  // { insp_id: { image, look } }
  const [inspGenerating, setInspGenerating] = useState({});  // { insp_id: true }
  const [inspFailed, setInspFailed] = useState({});  // { insp_id: 'error message' }
  const FABRIC_SWATCH_MAX = 3;
  const handleFabricImage = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Hard cap: max 3 fabric swatches per client. Reject everything past the cap
    // and surface a single toast — silently dropping uploads is a worse UX than
    // explaining the limit.
    const remaining = Math.max(0, FABRIC_SWATCH_MAX - (fabricLibrary?.length || 0));
    if (remaining === 0) {
      toast.error(`Maximum ${FABRIC_SWATCH_MAX} fabric swatches per client — remove one to add another.`);
      e.target.value = '';
      return;
    }
    const accepted = files.slice(0, remaining);
    if (files.length > accepted.length) {
      toast.warning(`Only ${accepted.length} swatch${accepted.length === 1 ? '' : 'es'} added — ${FABRIC_SWATCH_MAX}-swatch limit per client.`);
    }
    accepted.forEach(file => {
      const r = new FileReader();
      r.onload = (ev) => {
        const b64 = ev.target.result.split(',')[1];
        const preview = ev.target.result;
        setFabricLibrary(prev => {
          // Defensive: also enforce the cap inside the setter in case multiple
          // FileReaders race to completion.
          if ((prev?.length || 0) >= FABRIC_SWATCH_MAX) return prev;
          return [...prev, { b64, preview }];
        });
        setFabricImage(b64);
        setFabricImagePreview(preview);
        // Persist the swatch server-side if we have a real client so it
        // survives logout / device switch / fresh logins on the demo account.
        if (selectedClientId && /^[a-f0-9]{24}$/i.test(selectedClientId)) {
          axios.post(
            `${BACKEND_URL}/api/clients/${selectedClientId}/fabric-swatches`,
            { image: b64, label: file.name?.replace(/\.[^.]+$/, '').slice(0, 40) || '' },
            { withCredentials: true }
          ).catch(() => { /* non-fatal */ });
        }
      };
      r.readAsDataURL(file);
    });
    // allow re-uploading the same file name
    e.target.value = '';
  }, [selectedClientId, fabricLibrary]);

  const canGenerate = clientImage && !isGenerating && !clientLooksLocked;

  // True if selectedClientId is a real ObjectId (24 hex chars)
  const validClientId = /^[a-f0-9]{24}$/i.test(selectedClientId || '') ? selectedClientId : '';

  const MAX_LOOKS_PER_CATEGORY = 10;

  // ──────────────────────────────────────────────────────────────────
  // ALWAYS-5 BACKFILL (user: "Ensure that the 5 essential looks ALWAYS
  // render. For sub-categories too if 5 looks are required.")
  // Gemini intermittently drops 1-2 renders in a batch; the old one-shot
  // retry silently no-op'd because a failed tile's own combo matched the
  // dedupe key. These helpers re-render the MISSING combos at a STABLE
  // slot index and loop up to N passes until the category hits its full
  // slot count. Custom Fabric is exempt (it appends on demand, no quota).
  // ──────────────────────────────────────────────────────────────────
  const MAX_BACKFILL_PASSES = 5;
  const backfillInProgressRef = React.useRef(new Set());

  // Render one specific combo into a STABLE slot (comboIdx === slotIdx) so
  // repeated passes overwrite the same slot instead of shifting. Returns true
  // on success. Non-custom only (fabric_image stays null).
  const _renderComboSlot = useCallback(async (combo, category, slotIdx, { creativeBoost = false } = {}) => {
    if (!clientImage || !combo) return false;
    const clientAtStart = existingLooksLoadedClientRef.current;
    // Clear any failed placeholder sitting in this slot first.
    setGeneratedLooks(prev => prev.filter(l => !(l.category === category && l.comboIdx === slotIdx && l.failed)));
    try {
      const resp = await withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined,
        fabric_image: null,
        garment_type: combo.garment_id, garment_name: combo.garment, category, fit,
        styles: ['flat_front'], color: combo.suit, shirt_color: combo.shirt,
        shirt_pattern: 'solid', suit_pattern: combo.pattern || 'solid',
        lapel: combo.lapel_override || 'notch', button_count: combo.buttons_override || '2',
        pocket_style: combo.pocket_override || undefined,
        shoe_type: combo.shoe, shoe_color: combo.shoeColor,
        pant_color: combo.pant ? (PANT_COLORS.find(c => c.value === combo.pant)?.name || combo.pant) : undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        fabric_note: combo.fabric_note || undefined,
        accessory: combo.accessory || undefined,
        tie_type: combo.tie_type || undefined,
        vest_style: combo.vest_style || undefined,
        vest_buttons: combo.vest_buttons || undefined,
        vest_lapel: combo.vest_lapel || undefined,
        trouser_style: combo.trouser_style || undefined,
        creative_boost: !!creativeBoost,
      }, { withCredentials: true, timeout: 90000 }), { retries: 1, baseDelayMs: 2000 });
      if (existingLooksLoadedClientRef.current !== clientAtStart) return false;
      setGeneratedLooks(prev => [...prev.filter(l => !(l.category === category && l.comboIdx === slotIdx)), {
        id: resp.data.id, image: resp.data.generated_image, label: combo.label || combo.garment,
        garment: combo.garment, garment_id: combo.garment_id, category, comboIdx: slotIdx, combo,
        tailor_notes: resp.data.tailor_notes, patternCategory: combo.patternCategory || 'solid',
      }]);
      return true;
    } catch (e) {
      if (existingLooksLoadedClientRef.current !== clientAtStart) return false;
      setGeneratedLooks(prev => [...prev.filter(l => !(l.category === category && l.comboIdx === slotIdx)), {
        id: `failed-${category}-${slotIdx}-${Date.now()}`, failed: true, label: combo.label || combo.garment,
        garment: combo.garment, garment_id: combo.garment_id, category, comboIdx: slotIdx, combo,
        patternCategory: combo.patternCategory || 'solid', errorDetail: e?.response?.data?.detail || e?.message,
      }]);
      return false;
    }
  }, [clientImage, bodyImage, fit, validClientId]);

  // Top a category up to `requiredCount` successful looks, looping with
  // backoff. Idempotent + single-flight per category.
  const backfillCategoryToFull = useCallback(async (category, allCombos, requiredCount, attempt = 1) => {
    if (!clientImage || category === 'custom_fabric' || !allCombos?.length) return;
    if (attempt === 1) {
      if (backfillInProgressRef.current.has(category)) return;
      backfillInProgressRef.current.add(category);
    }
    const done = () => {
      backfillInProgressRef.current.delete(category);
      setGeneratingCats(prev => { const n = new Set(prev); n.delete(category); return n; });
    };
    try {
      const clientAtStart = existingLooksLoadedClientRef.current;
      const norm = (v) => String(v || '').toLowerCase().trim();
      const keyOf = (o) => `${norm(o.garment_id ?? o.combo?.garment_id)}|${norm(o.suit ?? o.color ?? o.combo?.suit)}|${norm(o.pattern ?? o.suit_pattern ?? o.combo?.pattern)}`;
      const successLooks = generatedLooksRef.current.filter(l => l.category === category && !l.failed && l.image);
      const need = requiredCount - successLooks.length;
      if (need <= 0) { done(); return; }
      const successKeys = new Set(successLooks.map(keyOf));
      const missing = allCombos
        .map((c, i) => ({ c, i }))
        .filter(x => !successKeys.has(keyOf(x.c)))
        .slice(0, need);
      if (missing.length === 0) { done(); return; }
      setGeneratingCats(prev => { const n = new Set(prev); n.add(category); return n; });
      const results = await Promise.allSettled(
        missing.map(x => genLimit(() => _renderComboSlot(x.c, category, x.i, { creativeBoost: attempt > 1 }))),
      );
      if (existingLooksLoadedClientRef.current !== clientAtStart) { done(); return; }
      const nowSuccess = generatedLooksRef.current.filter(l => l.category === category && !l.failed && l.image).length;
      void results;
      if (nowSuccess >= requiredCount || attempt >= MAX_BACKFILL_PASSES) { done(); return; }
      setTimeout(() => {
        if (existingLooksLoadedClientRef.current !== clientAtStart) { done(); return; }
        backfillCategoryToFull(category, allCombos, requiredCount, attempt + 1).catch(() => { done(); });
      }, 3000 + attempt * 2500);
    } catch {
      done();
    }
  }, [clientImage, _renderComboSlot]);


  const generateComboLooks = async (combos, category, { creativeBoost = false } = {}) => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    // Custom Fabric tab follows different rules: each click = one fresh
    // render (no dedup), and there is NO cap — tailors generate as many
    // renders as they want per swatch / per silhouette. The previous
    // HARD_CAP guard at 50 was causing silent submit no-ops in
    // long-lived stores; per the user's "no cap on custom fabrics" spec,
    // every submit fires its combo straight through.
    const isCustomFabric = category === 'custom_fabric';
    let toGenerate;
    if (isCustomFabric) {
      toGenerate = combos;
    } else {
      // Skip combos that are already generated (same garment + color) to avoid regenerating the same suit.
      // Check BOTH locally-generated looks (which carry `combo`) AND restored-from-server looks (which only carry
      // top-level `color` / `garment_id` / `suit_pattern`) so re-entering a tab doesn't trigger duplicate generations.
      const existing = generatedLooks.filter(l => l.category === category);
      const norm = (v) => String(v || '').toLowerCase().trim();
      const existingKeys = new Set(existing.map(l => {
        const gId = l.garment_id || l.combo?.garment_id || '';
        const suit = l.combo?.suit || l.color || '';
        const pat = l.combo?.pattern || l.suit_pattern || '';
        return `${norm(gId)}|${norm(suit)}|${norm(pat)}`;
      }));
      const remainingSlots = Math.max(0, MAX_LOOKS_PER_CATEGORY - existing.length);
      if (remainingSlots === 0) {
        return; // silently skip — nothing to do, already full
      }
      toGenerate = combos
        .filter(c => !existingKeys.has(`${norm(c.garment_id)}|${norm(c.suit)}|${norm(c.pattern || '')}`))
        .slice(0, remainingSlots);
      if (toGenerate.length === 0) {
        return; // all variations already generated — skip silently
      }
    }
    setIsGenerating(true); setGeneratingCategory(category);
    setGeneratingCats(prev => { const next = new Set(prev); next.add(category); return next; });
    // Throttled generation — at most 3 concurrent calls to Gemini to avoid 429s.
    // Explicit 180s timeout (Gemini can legitimately take 30-60s per image under load).
    const promises = toGenerate.map((combo, i) => {
      // Capture per-combo error detail so the failure toast can surface
      // the actual server reason (especially for Custom Fabric where the
      // user wants visibility into why a render didn't land).
      let _lastErrDetail = null;
      return genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined,
        // Fabric image resolution priority (Feb 2026 · Iter 108 fix):
        //   1. combo._fabric_image_override — set by the one-off upload
        //      flow to sidestep a stale `fabricImage` state closure. When
        //      the modal fires two uploads back-to-back the second submit
        //      would previously render with the FIRST swatch because
        //      `setFabricImage` is asynchronous and `generateComboLooks`
        //      captured the pre-update value. Passing the b64 on the
        //      combo itself makes every render request self-contained.
        //   2. Top-level fabricImage state — used by the fabric-tab
        //      library-picker path, which sets state ahead of time and
        //      doesn't need per-combo overrides.
        fabric_image: (category === 'custom_fabric')
          ? (combo._fabric_image_override || fabricImage || null)
          : null,
        // fabric_id supports BOTH the custom_fabric tab (uses the global
        // `libraryFabricId` state) AND per-combo injection (e.g. random
        // fabric variants inside Business Casual scenarios), where each
        // combo carries its own `_library_fabric_id`.
        fabric_id:
          combo._library_fabric_id
          || ((category === 'custom_fabric' && libraryFabricId) ? libraryFabricId : undefined),
        garment_type: combo.garment_id, garment_name: combo.garment, category, fit,
        styles: ['flat_front'], color: combo.suit, shirt_color: combo.shirt,
        shirt_pattern: 'solid', suit_pattern: combo.pattern || 'solid',
        lapel: combo.lapel_override || 'notch', button_count: combo.buttons_override || '2',
        pocket_style: combo.pocket_override || undefined,
        shoe_type: combo.shoe, shoe_color: combo.shoeColor,
        pant_color: combo.pant ? (PANT_COLORS.find(c => c.value === combo.pant)?.name || combo.pant) : undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        fabric_note: combo.fabric_note || undefined,
        accessory: combo.accessory || undefined,
        // Iter 113 — propagate combo-level tie_type so a custom-fabric
        // combo with `noTie: true` sends 'none' and triggers the
        // backend's FINAL NECKWEAR AUTHORITY override.
        tie_type: combo.tie_type || undefined,
        // Feb 2026 — vest customisation (three-piece + tuxedo only).
        // Backend ignores these on non-vest-capable silhouettes.
        vest_style:   combo.vest_style   || undefined,
        vest_buttons: combo.vest_buttons || undefined,
        vest_lapel:   combo.vest_lapel   || undefined,
        // Feb 2026 — trouser cut (business-casual only from the
        // custom-fabric questionnaire). Backend maps this to the
        // trouser cut vocabulary (side_tabs / single_pleat /
        // double_pleat / cuffed / flat_front / satin_side_stripe).
        trouser_style: combo.trouser_style || undefined,
        creative_boost: !!creativeBoost,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        // Iter 109 v10 — custom_fabric renders ALWAYS APPEND (never replace).
        // The dedupe-by-comboIdx branch is only safe for scenario categories
        // where the same combo slot re-renders (e.g. Essentials tile #2 gets
        // re-generated). Custom fabric uploads are always brand-new looks
        // — the previous filter (comboIdx=0, category='custom_fabric')
        // silently deleted every prior upload because they ALL share the
        // same comboIdx=0. This was the "each new swatch deletes the last
        // one" bug user reported.
        const newLook = {
          id: resp.data.id, image: resp.data.generated_image, label: combo.label || combo.garment, garment: combo.garment,
          garment_id: combo.garment_id, category, comboIdx: i, combo, tailor_notes: resp.data.tailor_notes,
          patternCategory: combo.patternCategory || 'solid',
        };
        if (isCustomFabric) {
          setGeneratedLooks(prev => [...prev, newLook]);
        } else {
          setGeneratedLooks(prev => [...prev.filter(l => !(l.comboIdx === i && l.category === category)), newLook]);
        }
        return { success: true };
      }).catch(e => {
        // Stale-client guard: skip if user switched clients mid-flight.
        if (existingLooksLoadedClientRef.current !== validClientId) return { success: false, stale: true };
        _lastErrDetail = e?.response?.data?.detail || e?.message || 'Unknown error';
        logError(`Generation failed for ${combo.garment}:`, e?.response?.status, e?.response?.data);
        // Drop a placeholder "failed" look into the grid so the user sees a
        // tap-to-retry card in the exact slot where the real one would have
        // landed. This prevents scenarios from collapsing back to an empty
        // "Auto-generating..." panel after a 429 burst.
        const failedLook = {
          id: `failed-${category}-${i}-${Date.now()}`,
          failed: true,
          label: combo.label || combo.garment,
          garment: combo.garment,
          garment_id: combo.garment_id,
          category,
          comboIdx: i,
          combo,
          patternCategory: combo.patternCategory || 'solid',
          errorDetail: _lastErrDetail,
        };
        if (isCustomFabric) {
          setGeneratedLooks(prev => [...prev, failedLook]);
        } else {
          setGeneratedLooks(prev => [...prev.filter(l => !(l.comboIdx === i && l.category === category)), failedLook]);
        }
        return { success: false, errorDetail: _lastErrDetail };
      });
    });

    const results = await Promise.allSettled(promises);
    setGeneratingCats(prev => { const next = new Set(prev); next.delete(category); return next; });
    setIsGenerating(false); setGeneratingCategory(null); setGeneratingIdx(-1);
    const okCount = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
    const failCount = results.filter(r => r.status === 'fulfilled' && !r.value?.success).length;
    if (okCount > 0) toast.success(`${okCount} look${okCount > 1 ? 's' : ''} generated`);

    // Universal ALWAYS-full backfill (user: "ensure the 5 essential looks
    // ALWAYS render; for sub-categories too if 5 looks are required"). After
    // every non-custom batch, top the category up to its full slot count. The
    // backfill is idempotent + single-flight, so it no-ops when already full
    // and loops with backoff when Gemini dropped a render. The 2s delay lets
    // this batch's state commit so the backfill sees an accurate count.
    if (!isCustomFabric) {
      setTimeout(() => {
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        backfillCategoryToFull(category, combos, combos.length, 1).catch(() => {});
      }, 2000);
    }

    // Single summary toast for failures (not one per look) so a 429 burst
    // doesn't spam 5 red toasts. For Custom Fabric specifically, surface
    // the actual server error detail so the tailor can act on it instead
    // of staring at a generic "tap to retry" message.
    if (failCount > 0) {
      const firstErr = results
        .map(r => r.status === 'fulfilled' ? r.value : null)
        .find(v => v && v.success === false && v.errorDetail);
      if (category === 'custom_fabric' && firstErr?.errorDetail) {
        toast.error(`${failCount} look${failCount > 1 ? 's' : ''} failed — ${firstErr.errorDetail}`);
      } else {
        toast.error(`${failCount} look${failCount > 1 ? 's' : ''} failed — tap any card to retry or wait for auto-retry`);
      }
    }
  };

  // Custom Fabric generate — extracted into a callback so it can be invoked
  // both from the in-tab GENERATE button AND auto-fired after a Fabric
  // Library deep-link (/dashboard?fabric_id=...). When the tailor clicks
  // "Render" on a swatch, we land on the Custom Fabric tab with the
  // libraryFabricId already set and we kick off generation immediately so
  // they see results without an extra click.
  const runCustomFabricGenerate = useCallback(() => {
    if (!clientImage) { toast.error('Upload a client photo first'); return false; }
    if (!fabricImage && !libraryFabricId) { toast.error('Pick a fabric first'); return false; }
    const fabricStyles = {
      full_suits: {
        classic_suit:     { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',   suit: '#808080', shirt: 'White',      shoe: 'oxford',       shoeColor: 'Black',  label: 'Fabric · Single-Breasted' },
        double_breasted:  { garment: 'Double-Breasted Suit', garment_id: 'double_breasted',suit: '#808080', shirt: 'White',      shoe: 'derby',        shoeColor: 'Black',  label: 'Fabric · Double-Breasted' },
        three_piece:      { garment: 'Three-Piece Suit',    garment_id: 'three_piece',   suit: '#808080', shirt: 'Light Blue', shoe: 'monk_strap',   shoeColor: 'Cognac', label: 'Fabric · Three-Piece' },
        tuxedo:           { garment: 'Tuxedo / Black Tie',  garment_id: 'tuxedo',        suit: '#808080', shirt: 'White',      shoe: 'oxford',       shoeColor: 'Black',  label: 'Fabric · Tuxedo' },
      },
      business_casual: {
        single_blazer:    { garment: 'Single-Breasted Blazer', garment_id: 'single_blazer', suit: '#808080', shirt: 'White',      pant: '#36454F', shoe: 'loafer',        shoeColor: 'Brown', label: 'Fabric · SB Blazer' },
        double_blazer:    { garment: 'Double-Breasted Blazer', garment_id: 'double_blazer', suit: '#808080', shirt: 'Light Blue', pant: '#1C2841', shoe: 'derby',         shoeColor: 'Black', label: 'Fabric · DB Blazer' },
      },
      outerwear: {
        overcoat:         { garment: 'Overcoat',    garment_id: 'overcoat',    suit: '#808080', shirt: 'Cream',      pant: '#0B0B0B', shoe: 'chelsea_boot',  shoeColor: 'Black', label: 'Fabric · Overcoat' },
        car_coat:         { garment: 'Car Coat',    garment_id: 'car_coat',    suit: '#808080', shirt: 'White',      pant: '#36454F', shoe: 'derby',         shoeColor: 'Brown', label: 'Fabric · Car Coat' },
        trench_coat:      { garment: 'Trench Coat', garment_id: 'trench_coat', suit: '#808080', shirt: 'Light Blue', pant: '#1C2841', shoe: 'oxford',        shoeColor: 'Black', label: 'Fabric · Trench' },
      },
    };
    const stylesForCat = fabricStyles[fabricCategory] || {};
    const selected = stylesForCat[fabricStyle] || Object.values(stylesForCat)[0];
    if (!selected) { toast.error('Pick a style first'); return false; }
    if (isDemoStore) {
      const usedFabrics = generatedLooks.filter(l => l.category === 'custom_fabric').length;
      if (usedFabrics >= 2) {
        toast.info('Demo accounts are capped at 2 custom-fabric renders. Upgrade to unlock unlimited custom fabrics.');
        return false;
      }
    }
    generateComboLooks([selected], 'custom_fabric');
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, fabricImage, libraryFabricId, fabricCategory, fabricStyle, isDemoStore, generatedLooks]);

  // ── NEW CUSTOM FABRIC DIRECT BUTTONS (Feb 2026) ──────────────────────
  // Replaces the legacy Category+Style dropdown + single Generate button
  // with a row of dedicated silhouette buttons. Each button fires a
  // generation using the currently-selected fabric (assigned library or
  // ad-hoc upload). 5 renders per silhouette are free; everything past
  // is tracked locally for the UX badge and billed via Stripe metered
  // overage on the backend (no client-side enforcement per user spec).
  const customFabricFromButton = useCallback((categoryKey, silhouetteId, garmentName) => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (!fabricImage && !libraryFabricId) { toast.error('Pick a fabric first'); return; }
    // Demo store cap (legacy guardrail — unchanged).
    if (isDemoStore) {
      const usedFabrics = generatedLooks.filter(l => l.category === 'custom_fabric').length;
      if (usedFabrics >= 2) {
        toast.info('Demo accounts are capped at 2 custom-fabric renders. Upgrade to unlock unlimited custom fabrics.');
        return;
      }
    }
    // Sync the legacy state so any downstream code path that still reads
    // fabricCategory / fabricStyle (gallery counters, retry handler) sees
    // the correct values for THIS render.
    setFabricCategory(categoryKey);
    setFabricStyle(silhouetteId);
    // Build the combo seed — matches the legacy fabricStyles structure.
    const combo = {
      garment: garmentName,
      garment_id: silhouetteId,
      suit:  '#808080',
      shirt: 'White',
      pant:  '#36454F',
      shoe:  categoryKey === 'shirts' ? 'loafer' : 'oxford',
      shoeColor: 'Black',
      label: `Fabric · ${garmentName}`,
    };
    generateComboLooks([combo], 'custom_fabric');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, fabricImage, libraryFabricId, isDemoStore, generatedLooks]);

  // Iter 96 — extracted from the inline tile onClick so the new
  // "Preview" popout modal's Render button can fire the exact same
  // flow as a direct tile click. Both code paths use this single
  // entry point now.
  const openFabricRender = useCallback((f) => {
    if (!f) return;
    setLibraryFabricId(f.id);
    setLibraryFabricMeta(f);
    setFabricImagePreview(`${BACKEND_URL}${f.image_url}?v=2`);
    setFabricImage(null);
    setCustomFabricQTarget({
      fabric: { ...f, image_url: `${BACKEND_URL}${f.image_url}?v=2` },
    });
    setShowCustomFabricQ(true);
  }, []);

  // Handler invoked by AddCustomSwatchModal — sets the ephemeral fabric
  // image AND fires the matching silhouette in one click. UX feels as if
  // the upload itself triggers the render.
  const customFabricFromUpload = useCallback(({
    fabric_b64, fabric_preview_url, category, style, garment_name,
    shoe, shoeColour, shirtStyle, shirtColour,
    pantHex, pantName,
    noTie,
    lapel, pocketStyle, // Feb 2026 — jacket construction from upload modal
    pattern_type, primary_color_hex, color_family, fabric_name, weave,
  }) => {
    setFabricImage(fabric_b64);
    setFabricImagePreview(fabric_preview_url);
    setLibraryFabricId(null);
    // Cache the analyser's read of the swatch as pseudo library meta —
    // downstream code paths that look at libraryFabricMeta (e.g. pattern
    // proportion) will now see the same shape a real library fabric has.
    setLibraryFabricMeta({
      pattern_type: pattern_type || 'solid',
      primary_color_hex: primary_color_hex || null,
      color_family: color_family || null,
      name: fabric_name || null,
    });
    setFabricCategory(category);
    setFabricStyle(style);
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (isDemoStore) {
      const usedFabrics = generatedLooks.filter(l => l.category === 'custom_fabric').length;
      if (usedFabrics >= 2) {
        toast.info('Demo accounts are capped at 2 custom-fabric renders. Upgrade to unlock unlimited custom fabrics.');
        return;
      }
    }
    // Iter 107 — Fabric accuracy parity with the /fabrics library flow.
    // The backend's `_load_library_fabric_for_render` path auto-builds a
    // rich fabric_note with an EXACT match instruction so Gemini
    // reproduces the swatch faithfully. Build the same note client-side.
    const _cf = color_family || 'neutral';
    const _phex = primary_color_hex || '';
    const _weave = weave && weave !== 'tbd' ? weave : 'tbd';
    const _name = fabric_name ? fabric_name : 'Custom fabric';
    const uploadFabricNote =
      `Custom fabric swatch — ${_name}. Primary colour ${_cf}${_phex ? ` (${_phex})` : ''}, ` +
      `weave: ${_weave}, pattern: ${pattern_type || 'solid'}. ` +
      `Match the attached swatch reference image EXACTLY for colour, weave structure, ` +
      `micro-pattern, slubs and sheen. Do NOT smooth the texture, do NOT shift the hue, ` +
      `do NOT simplify the pattern. Preserve every thread visible in the reference.`;

    const isShirt = category === 'shirts';
    const isSportCoat = category === 'business_casual';
    const combo = {
      garment: garment_name,
      garment_id: style,
      // Suit / trouser colour fall back to the analyser's sampled hex,
      // then neutral grey. Gemini reads the swatch image directly for
      // the true fabric — this is just a scaffolding hint. Sport-coat
      // uploads dictate their own trouser colour (the user picked it
      // in the modal), everything else pairs with the swatch primary.
      suit: primary_color_hex || '#808080',
      pant: isSportCoat && pantHex ? pantHex : (primary_color_hex || '#36454F'),
      pattern: pattern_type || 'solid',
      shirt: shirtColour || 'White',
      shirt_style: shirtStyle || 'spread_collar',
      shoe: isShirt ? null : (shoe || 'oxford'),
      shoeColor: isShirt ? null : (shoeColour || 'Black'),
      // Critical parity fix — carries the "match EXACTLY" instruction
      // into the render request so the upload path renders as faithfully
      // to the swatch as a library-fabric render does.
      fabric_note: uploadFabricNote,
      // Iter 108 · Bug fix — stashing the raw fabric b64 on the combo
      // sidesteps a stale `fabricImage` state closure that caused the
      // SECOND back-to-back upload to render the FIRST swatch.
      _fabric_image_override: fabric_b64,
      // Iter 113 — propagate No-Tie opt-in to the backend so it can
      // suppress the mandatory-tie rules for full_suits / wedding /
      // custom_fabric renders.
      tie_type: noTie ? 'none' : undefined,
      // Feb 2026 — lapel + pocket construction from the upload modal
      // reach the render prompt (parity with the CustomFabricQuestionnaire
      // path). Backend ignores these for shirt categories.
      lapel_override:  lapel || undefined,
      pocket_override: pocketStyle || undefined,
      label: `${fabric_name ? fabric_name + ' · ' : 'Fabric · '}${garment_name}`,
    };
    generateComboLooks([combo], 'custom_fabric');
    setShowAddCustomSwatch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, isDemoStore, generatedLooks]);

  // ── Iter 82 — Custom Fabric Questionnaire submit handler ─────────────
  // Called when the tailor confirms category/silhouette + shoe + shirt
  // in the CustomFabricQuestionnaire modal. Fires generation with all
  // user-chosen specifics applied to the prompt.
  const customFabricFromQuestionnaire = useCallback(({
    category, silhouetteId, garmentName,
    shoe, shoeColour, shirtStyle, shirtColour,
    pantHex, pantName,  // Iter 109 v5 — sport-coat trouser dictated by CFQ modal
    pantCut,            // Feb 2026 — sport-coat trouser cut (side_tabs / single_pleat / double_pleat / cuffed / flat_front)
    noTie,              // Iter 113 — no-tie opt-in
    lapel, pocketStyle, // Iter 127 — jacket construction picks
    vestStyle, vestButtons, vestLapel, // Feb 2026 — vest customisation
  }) => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (!libraryFabricId && !fabricImage) { toast.error('No fabric selected'); return; }
    if (isDemoStore) {
      const usedFabrics = generatedLooks.filter(l => l.category === 'custom_fabric').length;
      if (usedFabrics >= 2) {
        toast.info('Demo accounts are capped at 2 custom-fabric renders. Upgrade to unlock unlimited custom fabrics.');
        return;
      }
    }
    setFabricCategory(category);
    setFabricStyle(silhouetteId);
    const isShirt    = category === 'shirts';
    const isSportCoat = category === 'business_casual';
    // Sport-coat renders use the tailor-picked trouser colour; other
    // categories fall back to the fabric's own primary hex (jacket +
    // pant match the swatch for full suits; no pants for shirts).
    const trouserHex  = isSportCoat && pantHex
      ? pantHex
      : (customFabricQTarget?.fabric?.primary_color_hex || '#36454F');
    const combo = {
      garment: garmentName,
      garment_id: silhouetteId,
      // Suit colour falls back to the fabric's primary hex when available,
      // otherwise neutral grey (Gemini reads the swatch image as the source
      // of truth anyway).
      suit: customFabricQTarget?.fabric?.primary_color_hex || '#808080',
      pant: trouserHex,
      pantName: isSportCoat ? (pantName || 'Charcoal') : undefined,
      pattern: 'solid',
      shirt: shirtColour || 'White',
      shirt_style: shirtStyle || 'spread_collar',
      shoe: isShirt ? null : (shoe || 'oxford'),
      shoeColor: isShirt ? null : (shoeColour || 'Black'),
      // Iter 113 — no-tie opt-in propagated as combo-level tie_type.
      tie_type: noTie ? 'none' : undefined,
      // Iter 127 — lapel + pocket picked in the questionnaire.
      lapel_override: lapel || undefined,
      pocket_override: pocketStyle || undefined,
      // Feb 2026 — vest customisation. Only propagated for three-piece
      // and tuxedo silhouettes (the CFQ modal only surfaces the section
      // for those two). Backend ignores these fields on other garments.
      vest_style:   vestStyle   || undefined,
      vest_buttons: vestButtons || undefined,
      vest_lapel:   vestLapel   || undefined,
      // Feb 2026 — trouser cut (any non-shirt render). Sends through
      // `generateComboLooks` as `trouser_style` so the backend prompt
      // gets the requested trouser vocabulary (side_tabs / single_pleat
      // / double_pleat / cuffed).
      // Iter 234 — previously gated on `isSportCoat` which prevented
      // suits from ever sending side_tabs. Now emits whenever a
      // non-default `pantCut` is present regardless of silhouette.
      trouser_style: (pantCut && pantCut !== 'flat_front') ? pantCut : undefined,
      label: `${customFabricQTarget?.fabric?.name || 'Fabric'} · ${garmentName}`,
    };
    generateComboLooks([combo], 'custom_fabric');
    setShowCustomFabricQ(false);
    setCustomFabricQTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, libraryFabricId, fabricImage, customFabricQTarget, isDemoStore, generatedLooks]);

  // Persist wedding setup to the selected client. Called by the
  // WeddingSetupModal once the tailor confirms groom/bride/date.
  // (Iter 78) — wedding_colours removed from the modal; the planner is
  // now the canonical palette source. Preserve any previously-saved
  // palette by merging instead of overwriting when the field is absent.
  const saveWeddingSetup = useCallback(async ({ groom_name, bride_name, wedding_date, wedding_colours }) => {
    if (!selectedClientId || !/^[a-f0-9]{24}$/i.test(selectedClientId)) {
      toast.error('Pick a saved client first — demo clients can\'t persist wedding setup');
      return;
    }
    setSavingWeddingSetup(true);
    try {
      const payload = { groom_name, bride_name, wedding_date };
      if (Array.isArray(wedding_colours)) payload.wedding_colours = wedding_colours;
      await axios.put(`${BACKEND_URL}/api/clients/${selectedClientId}`, payload, { withCredentials: true });
      setWeddingSetup(prev => ({
        ...prev,
        groom_name, bride_name, wedding_date,
        // Preserve prior palette when the modal doesn't pass one.
        wedding_colours: Array.isArray(wedding_colours) ? wedding_colours : prev.wedding_colours,
      }));
      setShowWeddingSetup(false);
      toast.success('Wedding details saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save wedding details');
    }
    setSavingWeddingSetup(false);
  }, [selectedClientId]);

  // Truthy when the tailor has filled the minimum wedding gate fields.
  const isWeddingSetupComplete = !!(weddingSetup.groom_name && weddingSetup.bride_name && weddingSetup.wedding_date);

  // ── WEDDING COLOUR INSPIRATION (Feb 2026) ────────────────────────────
  // After wedding setup is complete, the tailor can fire 5 wedding-suit
  // renders built around the couple's chosen palette. The palette can be
  // 1-3 hex codes — we distribute across 5 looks so even a single-colour
  // pick yields a varied (3 silhouettes × 2-3 ties / shirts) lookbook.
  const generateWeddingColourLooks = React.useCallback(() => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (!isWeddingSetupComplete) { toast.error('Complete wedding setup first'); return; }
    const palette = (weddingSetup.wedding_colours || []).filter(Boolean);
    if (palette.length === 0) {
      toast.error('Add at least one wedding colour in setup');
      return;
    }
    // Five wedding-suit silhouettes. Each one picks a colour from the
    // palette, cycling so a 1-colour palette yields 5 monochrome
    // variants in different silhouettes, while a 3-colour palette
    // distributes evenly (2-2-1).
    const SILHOUETTES = [
      { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  lapel: 'notch', buttons: '2', shirt: 'White' },
      { garment: 'Three-Piece Suit',     garment_id: 'three_piece',   lapel: 'peak',  buttons: '2', shirt: 'White' },
      { garment: 'Double-Breasted Suit', garment_id: 'double_breasted', lapel: 'peak', buttons: '6', shirt: 'White' },
      { garment: 'Single-Breasted Suit', garment_id: 'classic_suit',  lapel: 'peak',  buttons: '1', shirt: 'Light Pink' },
      { garment: 'Three-Piece Suit',     garment_id: 'three_piece',   lapel: 'notch', buttons: '2', shirt: 'Cream' },
    ];
    const combos = SILHOUETTES.map((sil, i) => {
      const colour = palette[i % palette.length];
      return {
        ...sil,
        suit: colour,
        pant: colour,
        shoe: 'oxford', shoeColor: 'Black',
        pattern: 'solid', patternCategory: 'solid',
        label: `Wedding · ${colour}`,
        fabric_note: `Wedding suit cut in a colour that EXACTLY matches the palette hex ${colour} — this is the couple's chosen wedding colour, treat it as the hero of the rendering. Three-piece formal construction where applicable, matching wedding waistcoat, silk tie that complements ${colour}. Match the photograph to a wedding formal context — clean light grey studio backdrop, no venue.`,
        lapel_override: sil.lapel,
        buttons_override: sil.buttons,
      };
    });
    generateComboLooks(combos, 'wedding_colour_inspo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, isWeddingSetupComplete, weddingSetup.wedding_colours]);

  // Auto-fire on Fabric Library deep-link has been REMOVED (Iter 82).
  // Clicking a fabric tile (or arriving via /dashboard?fabric_id=...) now
  // routes through the CustomFabricQuestionnaire modal so the tailor
  // explicitly picks silhouette + shoe + shirt before any render fires.
  // The previous auto-fire effect was triggering generation the moment a
  // tile was clicked, completely bypassing the new questionnaire.

  // Retry a single failed combo — called from the LookCard "Tap to retry" UI.
  // Expects the `failedLook` to carry the `combo` + `category` + `comboIdx`
  // that was stashed when the first attempt failed.
  const retryFailedCombo = useCallback(async (failedLook) => {
    if (!clientImage || !failedLook?.combo || !failedLook?.category) return;
    const { combo, category, comboIdx } = failedLook;
    // Flip the failed card back to "generating" immediately so the user sees
    // feedback without waiting for the network round-trip.
    setGeneratedLooks(prev => prev.filter(l => !(l.comboIdx === comboIdx && l.category === category)));
    setGeneratingCats(prev => { const next = new Set(prev); next.add(category); return next; });
    try {
      const resp = await withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined,
        fabric_image: (category === 'custom_fabric' && fabricImage) ? fabricImage : null,
        fabric_id: (category === 'custom_fabric' && libraryFabricId) ? libraryFabricId : undefined,
        garment_type: combo.garment_id, garment_name: combo.garment, category, fit,
        styles: ['flat_front'], color: combo.suit, shirt_color: combo.shirt,
        shirt_pattern: 'solid', suit_pattern: combo.pattern || 'solid',
        lapel: combo.lapel_override || 'notch', button_count: combo.buttons_override || '2',
        pocket_style: combo.pocket_override || undefined,
        shoe_type: combo.shoe, shoe_color: combo.shoeColor,
        pant_color: combo.pant ? (PANT_COLORS.find(c => c.value === combo.pant)?.name || combo.pant) : undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        fabric_note: combo.fabric_note || undefined,
        accessory: combo.accessory || undefined,
      }, { withCredentials: true, timeout: 90000 }), { retries: 2, baseDelayMs: 2000 });
      setGeneratedLooks(prev => [...prev.filter(l => !(l.comboIdx === comboIdx && l.category === category)), {
        id: resp.data.id, image: resp.data.generated_image, label: combo.label || combo.garment, garment: combo.garment,
        garment_id: combo.garment_id, category, comboIdx, combo, tailor_notes: resp.data.tailor_notes,
        patternCategory: combo.patternCategory || 'solid',
      }]);
      toast.success(`${combo.garment} generated`);
    } catch (e) {
      // Put the failed placeholder back so the user can retry again
      setGeneratedLooks(prev => [...prev.filter(l => !(l.comboIdx === comboIdx && l.category === category)), {
        id: `failed-${category}-${comboIdx}-${Date.now()}`,
        failed: true, label: combo.label || combo.garment, garment: combo.garment,
        garment_id: combo.garment_id, category, comboIdx, combo,
        patternCategory: combo.patternCategory || 'solid',
      }]);
      toast.error(e?.response?.data?.detail || `Retry failed for ${combo.garment}`);
    }
    setGeneratingCats(prev => { const next = new Set(prev); next.delete(category); return next; });
  }, [clientImage, fit, fabricImage, validClientId]);

  // Delete a look from the workspace (also removes from server gallery if it was saved)
  const deleteLook = useCallback(async (look, variantIdx = 0) => {
    const id = look.id;
    const variants = Array.isArray(look?.variants) ? look.variants : [];
    const variantCount = 1 + variants.length;
    // Iter 170.3 — variant-aware delete. When the card has a carousel
    // and we're deleting one slot, mutate the local state so the card
    // keeps showing the other renders. When deleting the WHOLE look
    // (single-render card, or last-variant-standing), remove the card
    // from state entirely.
    const isVariantScoped = variantCount > 1;
    if (isVariantScoped) {
      // Optimistic UI: patch the affected look in place.
      setGeneratedLooks(prev => prev.map(l => {
        if (l.id !== id) return l;
        if (variantIdx === 0) {
          // Promote variants[0] into the canonical slot; keep the rest.
          const head = variants[0];
          const remaining = variants.slice(1);
          return {
            ...l,
            image: head.image || head.image_path,
            image_thumb: head.image_thumb || head.image_path_thumb,
            label: head.label || l.label,
            variants: remaining,
          };
        }
        // Drop a historical slot at variantIdx (>= 1).
        const remaining = variants.filter((_, i) => i !== variantIdx - 1);
        return { ...l, variants: remaining };
      }));
    } else {
      // Whole-look delete (no variants) — remove from state.
      setGeneratedLooks(prev => prev.filter(l => l.id !== id));
      setInspResults(prev => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (next[k]?.id === id) delete next[k];
        }
        return next;
      });
    }
    // Hit the server. When the card is single-render we hit the
    // whole-look endpoint (soft-delete). When it's a variant scope we
    // hit the new per-variant endpoint.
    try {
      if (isVariantScoped) {
        await axios.delete(
          `${BACKEND_URL}/api/gallery/${id}/variants/${variantIdx}`,
          { withCredentials: true },
        );
        toast.success(variantIdx === 0 ? 'Latest render removed' : 'Older variant removed');
      } else {
        await axios.delete(`${BACKEND_URL}/api/gallery/${id}`, { withCredentials: true });
        toast.success('Look deleted');
      }
    } catch (e) {
      // Look may have been generated without save_to_gallery; local
      // removal is sufficient — but if the variant call itself failed,
      // surface the actual error so tailors don't think they lost data
      // silently.
      const msg = e?.response?.data?.detail;
      if (isVariantScoped && msg) toast.error(`Delete failed: ${msg}`);
      else toast.success('Look removed');
    }
  }, []);

  // Iter 116 — one-click email of a single rendered look via the
  // store's branded Resend sender. Confirms recipient in a native
  // dialog so the tailor can't misfire, then POSTs to
  // /api/email/send-looks. Actionable error surface (domain-unverified
  // etc.) bubbles up from the backend as the toast message.
  // Iter 125 — per-look "Email to client" was removed from every
  // LookCard. Client-card email delivery lives entirely on the
  // Clients page now. The former `emailLook` handler was deleted with
  // its button so we don't ship dead code.

  const handleRegenerate = async (lookData) => {
    setIsRegenerating(true);
    try {
      // ── Quick Tweak path: surgical Gemini edit on the existing image ──
      // Triggered by the CustomizeModal "Quick Tweaks" pills (lapel /
      // buttons). 3-5× faster than full regen AND keeps the face + body +
      // pose pixel-identical. Falls through to the full regen endpoint if
      // the source look doesn't have an id (hasn't been saved yet) or if
      // the edit endpoint errors.
      if (lookData.__quickEdit && lookData.id) {
        {
          // Iter 165 — removed the outer try wrapper. The inner
          // try/catch/finally around the edit-look call handles all
          // error paths and dismisses the loading toast. The payload
          // building code below is straight assignments (no I/O), so
          // there's nothing here that needs a broader catch. Kept a
          // block scope so the const declarations don't leak.
          // ── Anchor every edit to the ORIGINAL look (Iter 82.17) ────
          // Each edit-look round-trip re-encodes the image as JPEG and
          // Gemini's output is slightly softer than its input. Chaining
          // edit-N → edit-N+1 stacks that loss and the image gets
          // progressively blurry. We sidestep the whole problem by
          // ALWAYS sending the ORIGINAL look_id to the backend, even on
          // the 5th colour iteration — Gemini re-renders from the
          // crisp source every time, not from the previous edit.
          //
          // variants[] is newest-first, so the LAST entry is the
          // original render. Falls back to lookData.id when there are
          // no variants (= this IS the original).
          const rootLookId = (Array.isArray(lookData.variants) && lookData.variants.length > 0)
            ? (lookData.variants[lookData.variants.length - 1].id || lookData.id)
            : lookData.id;
          const editPayload = { look_id: rootLookId };
          if (lookData.__pantOnly) {
            // STRICT pant-only edit — strip every other field so the
            // backend's is_pant_only_edit check passes and the strict
            // jacket-lock prompt + jacket-crop second reference fires.
            // Without this, the spread of `look` in CustomizeModal's
            // instantRegenerate poisons the payload with stale state
            // (suit color, shirt, shoe, lapel, etc.) and the backend
            // treats it as a multi-attribute edit.
            if (lookData.customPant) editPayload.pant_color = lookData.customPant;
          } else if (lookData.__buttonsOnly) {
            // STRICT buttons-only edit — same payload-stripping
            // mechanic as the pant-only path. Only `button_color`
            // crosses to the backend so its is_buttons_only_edit
            // check fires and the strict jacket-fabric-lock prompt
            // (with the jacket-crop second reference) activates.
            if (lookData.customButtonColor) editPayload.button_color = lookData.customButtonColor;
          } else if (lookData.__tieOnly) {
            // STRICT tie-only edit (Iter 88) — single colour swap on
            // the necktie / bow tie. All other dials stripped so the
            // backend's is_tie_only_edit check fires and the strict
            // jacket+pants+shoes+face lock prompt activates.
            if (lookData.customTieColor) editPayload.tie_color = lookData.customTieColor;
          } else if (lookData.__noTieOnly) {
            // STRICT no-tie-only edit (Iter 114) — removes the tie /
            // bow tie from the render and redraws the natural open-
            // collar shirt underneath. Backend detects this via
            // `edits == {tie_type: 'none'}` and activates the
            // dedicated NO-TIE EDIT lock which keeps jacket, shirt,
            // pants, shoes and face pixel-identical.
            editPayload.tie_type = 'none';
          } else if (lookData.__silhouetteOnly) {
            // STRICT silhouette-only edit (Iter 115) — restructures
            // the jacket (SB↔DB / open↔closed / add-remove vest) in
            // one pass. Multiple silhouette fields may be set at
            // once — the backend detects `edits ⊆ {jacket_style,
            // jacket_state, vest}` and activates the silhouette_only
            // lock which stacks the selected directives while keeping
            // fabric, colour, tie, pants, shoes, face, pose locked.
            if (lookData.jacketStyleSilh) editPayload.jacket_style = lookData.jacketStyleSilh;
            if (lookData.jacketStateSilh) editPayload.jacket_state = lookData.jacketStateSilh;
            if (lookData.vestSilh)        editPayload.vest         = lookData.vestSilh;
            if (lookData.lapelSilh)       editPayload.lapel_style  = lookData.lapelSilh;
            if (lookData.tieFormSilh)     editPayload.tie_type     = lookData.tieFormSilh;
            if (lookData.vestStyleSilh)   editPayload.vest_style   = lookData.vestStyleSilh;
            if (lookData.vestButtonsSilh) editPayload.vest_buttons = lookData.vestButtonsSilh;
            if (lookData.vestLapelSilh)   editPayload.vest_lapel   = lookData.vestLapelSilh;
          } else if (lookData.__coatLengthOnly) {
            // Sep 2026 (user request — mid-calf overcoat restructure) —
            // STRICT coat-length-only edit for overcoat cards. Only
            // `coat_length` crosses to the backend so the dedicated
            // `is_coat_length_only_edit` lock engages and Gemini
            // redraws ONLY the hem (face, pose, fabric, buttons,
            // collar, cuffs, backdrop, base outfit — all identical).
            if (lookData.customCoatLength) editPayload.coat_length = lookData.customCoatLength;
          } else if (lookData.__collarOnly) {
            // Feb 2026 — STRICT collar-only edit for dress-shirt cards.
            // Only `collar_style` crosses to the backend; every other
            // field is stripped so Gemini treats this as a single-
            // attribute edit and keeps shirt fabric / cuffs / pants
            // pixel-identical.
            if (lookData.customCollar) editPayload.collar_style = lookData.customCollar;
          } else if (lookData.__cuffOnly) {
            // Feb 2026 — STRICT cuff-only edit for dress-shirt cards.
            if (lookData.customCuff) editPayload.cuff_style = lookData.customCuff;
          } else if (lookData.__trouserCutOnly) {
            // Feb 2026 — STRICT trouser-cut-only edit. Only
            // `trouser_style` crosses to the backend; every other
            // field is stripped so Gemini treats this as a
            // single-attribute structural edit and the
            // structural_only_lock keeps jacket, shirt, tie, shoes,
            // face, pose and background pixel-identical.
            //
            // Iter 154 — the "Restructure · Trousers & Shoes" panel
            // now also emits `shoe_type` and `shoe_color`. These
            // ride on the same __trouserCutOnly bundle because they
            // share the structural-only lock (and belts + shoes are
            // colour-linked in the same edit call).
            if (lookData.customTrouserStyle) editPayload.trouser_style = lookData.customTrouserStyle;
            if (lookData.customShoeType)  editPayload.shoe_type  = lookData.customShoeType;
            if (lookData.customShoeColor) editPayload.shoe_color = lookData.customShoeColor;
          } else if (lookData.__pantWithRestructure) {
            // Iter 156 — pant colour bundled with pending trouser
            // cut / belt / shoe restructure attributes in ONE edit
            // so the tailor doesn't have to fire two separate
            // calls. Backend routes this through the structural_
            // only_lock (pant_color is now in _structural_keys)
            // which emits a CHANGE TROUSER COLOUR directive above
            // the cut / shoe / belt directives.
            if (lookData.customPant)         editPayload.pant_color    = lookData.customPant;
            if (lookData.customTrouserStyle) editPayload.trouser_style = lookData.customTrouserStyle;
            if (lookData.customShoeType)     editPayload.shoe_type     = lookData.customShoeType;
            if (lookData.customShoeColor)    editPayload.shoe_color    = lookData.customShoeColor;
          } else {
            if (lookData.customLapel) editPayload.lapel = lookData.customLapel;
            if (typeof lookData.customSatinLapel === 'boolean') editPayload.satin_lapel = lookData.customSatinLapel;
            if (lookData.customPocket) editPayload.pocket_style = lookData.customPocket;
            if (lookData.customButtonCount) editPayload.button_count = lookData.customButtonCount;
            if (lookData.customButtonStyle) editPayload.button_style = lookData.customButtonStyle;
            if (lookData.customColor) editPayload.color = lookData.customColor;
            if (lookData.customShirt) editPayload.shirt_color = lookData.customShirt;
            if (lookData.customShirtPattern && lookData.customShirtPattern !== 'solid') editPayload.shirt_pattern = lookData.customShirtPattern;
            if (lookData.customPattern && lookData.customPattern !== 'solid') editPayload.suit_pattern = lookData.customPattern;
            if (lookData.customPant) editPayload.pant_color = lookData.customPant;
            if (lookData.customShoe) editPayload.shoe_type = lookData.customShoe;
            if (lookData.customShoeColor) editPayload.shoe_color = lookData.customShoeColor;
            if (lookData.customTie) editPayload.tie_type = lookData.customTie;
            if (lookData.customTieColor) editPayload.tie_color = lookData.customTieColor;
            if (lookData.customTrouserStyle && lookData.customTrouserStyle !== 'flat_front') editPayload.trouser_style = lookData.customTrouserStyle;
          }
          // Iter 161 — NO-OP GUARD (cache-hit UX fix). If every value in
          // the outgoing editPayload matches what's already persisted on
          // the look's `applied_edits`, skip the fetch entirely. Prior
          // behaviour fell through to the backend which then cache-hit
          // and returned the same image with `cached: True` — the tailor
          // saw a toast fire but no visual change and read it as "the
          // edit is broken". Instead we short-circuit here with a clear
          // "already applied" message so the tailor knows nothing was
          // wrong; they just re-clicked something they already had.
          //
          // trouser_style comparison is case-insensitive on the whole
          // composite (both round-trips normalise casing).
          const alreadyApplied = lookData.applied_edits && typeof lookData.applied_edits === 'object'
            ? lookData.applied_edits
            : null;
          if (alreadyApplied) {
            const payloadKeys = Object.keys(editPayload).filter(k => k !== 'look_id');
            const wouldChange = payloadKeys.some(k => {
              const nextVal = String(editPayload[k] ?? '').trim().toLowerCase();
              const prevVal = String(alreadyApplied[k] ?? '').trim().toLowerCase();
              return nextVal !== prevVal;
            });
            if (payloadKeys.length > 0 && !wouldChange) {
              toast('Already applied — nothing changed', {
                icon: 'i', duration: 2200, id: `noop-${Date.now()}`,
              });
              return;
            }
          }
          // Immediate tactile feedback toast — fires the instant the user
          // clicks a pill, even though the real Gemini call takes 3-5s.
          // Dismissed the moment the real response arrives so it doesn't
          // linger past the result.
          //
          // Iter 165 — WRAP the edit call in try/finally so the loading
          // toast is guaranteed to be dismissed on EVERY exit path
          // (success, error, timeout, or fallback to generate-look).
          // Prior behaviour: the inner catch(editErr) at the bottom of
          // this try-block only logged and fell through to the full
          // regen path without dismissing the toast — the "Applying
          // edit…" pill lingered on the top-right of the screen for
          // the full 30 s toast duration, even after the visible render
          // had already completed. User report: "there is a applying
          // edit on the top right constantly after editing".
          // Iter 205 — toast duration bumped 30 s → 180 s to match the
          // axios timeout above. Without this the pill silently vanishes
          // ~30 s in while the request is still in flight, and the
          // tailor thinks the edit was dropped.
          const toastId = toast.loading('Applying edit…', { duration: 180000 });
          let editSucceeded = false;
          try {
          const resp = await axios.post(`${BACKEND_URL}/api/edit-look`, editPayload,
            { withCredentials: true, timeout: 180000 });
          // ── Carousel: keep ALL previous renders ────────────────────
          // The Customize modal lets the consultant compare colour
          // variants side-by-side. We preserve every prior render in a
          // `variants` array — newest first — so the LookCard / Zoom
          // modal can paginate through them and the client can say
          // "yeah, the navy one." Each variant carries its own id so
          // the gallery / email-card flows can target a specific
          // render later. Capped at 8 to keep memory sane.
          const prevVariants = Array.isArray(lookData.variants) ? lookData.variants : [];
          const prevSnapshot = {
            id: lookData.id,
            // Use whichever the previous render actually displayed. Fall
            // back to the small thumb when the full-res blob is missing
            // (rehydrated looks land thumb-only first).
            image: lookData.image || lookData.image_thumb,
            label: lookData.label,
            tailor_notes: lookData.tailor_notes,
          };
          const nextVariants = [prevSnapshot, ...prevVariants].slice(0, 8);
          const updated = {
            ...lookData,
            id: resp.data.id,
            image: resp.data.generated_image,
            // Iter 83 — clear the stale thumbnail on every edit. The
            // backend updates `image_path_thumb` and returns the new
            // base64 image but does NOT inline a fresh thumb. The
            // LookCard prefers `image_thumb` over `image` for fast grid
            // rendering, so without this clear the card kept showing
            // the PRE-edit thumbnail at variantIdx=0 even after the new
            // render landed. Setting it to undefined makes LookCard fall
            // back to the full base64 we just received — guaranteed
            // post-edit pixels in the carousel.
            image_thumb: undefined,
            tailor_notes: resp.data.tailor_notes || lookData.tailor_notes,
            variants: nextVariants,
            // Iter 160 — cumulative-edit hydration. Surface the merged
            // `applied_edits` set from the response so the CustomizeModal
            // re-hydrates its restructure base state (pleats / cuffs /
            // waist / belt colour / shoe type / shoe colour) to reflect
            // the current stack of applied attributes.
            applied_edits: resp.data.applied_edits || lookData.applied_edits || {},
          };
          setGeneratedLooks(prev => prev.map(l => l.id === lookData.id ? updated : l));
          setSelectedLook(updated);
          // Iter 83 — inspiration cache parity. Inspiration cards render
          // from the `inspResults` map (keyed by inspiration spec id),
          // NOT from `generatedLooks`. Without this mirror-write any
          // edit applied to a Style / Wedding / BC / Outerwear / Shirts
          // inspiration tile reverted the moment the user closed the
          // modal because the tile read the unedited image from
          // inspResults. We locate the inspResults entry by id (each
          // entry holds the look's `id`) and replace it with the new
          // image + variants payload so re-opens stay edited.
          setInspResults(prev => {
            const matchKey = Object.keys(prev).find(k => prev[k]?.id === lookData.id);
            if (!matchKey) return prev;
            return { ...prev, [matchKey]: { ...prev[matchKey], ...updated } };
          });
          toast.dismiss(toastId);
          toast.success(resp.data.cached ? 'Applied instantly (cached)' : 'Edit applied — face preserved', { duration: 1800 });
          setIsRegenerating(false);
          editSucceeded = true;
          return;
        } catch (editErr) {
          // Iter 204 — 422 from the backend means the composite alignment
          // guard rejected all 3 Gemini attempts as torn. Do NOT fall
          // through to the full /api/generate-look path (which bypasses
          // paste-back and produces a completely different-looking render).
          // Surface a clear "please try again" toast and return.
          if (editErr?.response?.status === 422) {
            toast.dismiss(toastId);
            toast.error(
              editErr.response?.data?.detail
              || 'The AI reframed that render — please try the edit again',
              { duration: 4000 }
            );
            setIsRegenerating(false);
            return;
          }
          // Iter 205 — CLIENT-SIDE TIMEOUT (axios ECONNABORTED).
          // The iter-204 3-attempt retry can occasionally exceed the
          // 180 s axios budget (each Gemini call is 20-40 s + paste-
          // back + upload). Fallthrough to /api/generate-look here
          // would kick off ANOTHER 60-90 s call and lock the tailor
          // out for 3-4 minutes total. Better UX: dismiss the pill,
          // tell them the edit took too long, and let them retry.
          if (editErr?.code === 'ECONNABORTED' || /timeout/i.test(editErr?.message || '')) {
            toast.dismiss(toastId);
            toast.error(
              'That edit is taking longer than usual — please try again in a moment.',
              { duration: 4000 }
            );
            setIsRegenerating(false);
            return;
          }
          console.warn('[quick-edit] fell back to full regen:', editErr?.response?.data?.detail || editErr.message);
        } finally {
          // Iter 165 — GUARANTEED dismiss. If the edit-look call errored
          // or timed out (falling through to the full-regen path below),
          // or if the client aborted mid-way, we still need to kill the
          // "Applying edit…" pill from the top-right so it doesn't
          // linger for 30 s past the actual UI state. `editSucceeded`
          // gates whether we already dismissed above.
          if (!editSucceeded) {
            toast.dismiss(toastId);
          }
        }
        }
      }

      const resp = await axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, fabric_image: (lookData.category === 'custom_fabric' && fabricImage) ? fabricImage : null,
        fabric_id: (lookData.category === 'custom_fabric' && libraryFabricId) ? libraryFabricId : undefined,
        garment_type: lookData.garment_id, garment_name: lookData.garment, category: lookData.category, fit,
        styles: ['flat_front'], color: lookData.customColor, shirt_color: lookData.customShirt,
        shirt_pattern: lookData.customShirtPattern, suit_pattern: lookData.customPattern,
        lapel: lookData.customLapel, button_count: lookData.customButtonCount,
        shoe_type: lookData.customShoe, shoe_color: lookData.customShoeColor,
        pant_color: lookData.customPant,
        button_style: lookData.customButtonStyle,
        tie_type: lookData.customTie,
        tie_color: lookData.customTieColor,
        trouser_style: lookData.customTrouserStyle,
      }, { withCredentials: true, timeout: 90000 });
      const updated = { ...lookData, id: resp.data.id, image: resp.data.generated_image, tailor_notes: resp.data.tailor_notes };
      setGeneratedLooks(prev => prev.map(l => l.id === lookData.id ? updated : l));
      setSelectedLook(updated);
      toast.success('Look re-generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-generation failed'); }
    finally {
      // Iter 197 — GUARANTEED reset of the "RE-RENDERING…" state.
      // User report: "one of these edits is causing a never ending
      // editing look loop." Prior code reset `isRegenerating` in the
      // success path (line 3592, inside the quick-edit block) AND at
      // the bottom of the full-regen path, but there was no OUTERMOST
      // guard — so any exception between the quick-edit response and
      // its state-setters (e.g. a malformed `applied_edits` payload,
      // a `setInspResults` iteration error, a component-unmount race)
      // would skip the reset and leave the pill spinning forever.
      // A `finally` on the outer try makes it impossible for the
      // pill to stay stuck regardless of which specific silhouette /
      // vest / lapel / tie edit hit the bad path.
      setIsRegenerating(false);
    }
  };

  // (removed dead handler generateRandom — referenced removed constants)

  const generateInspiration = async (insp) => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    // Iter 87 — capture the current client + the image hash AT REQUEST
    // TIME so the .then handler can drop stale responses if the user
    // switches clients mid-flight. Without this guard, a 60s inspiration
    // gen for Client A that lands AFTER the tailor has switched to
    // Client B writes Client A's photo into Client B's inspResults
    // slot — the classic "bleed" the user just reported.
    const _targetClientId = validClientId || null;
    // Clear any prior failed state + mark this slot as generating so the card
    // flips to the spinner state immediately (user feedback).
    setInspFailed(prev => { const next = { ...prev }; delete next[insp.id]; return next; });
    setInspGenerating(prev => ({ ...prev, [insp.id]: true }));
    setIsGenerating(true); setGeneratingCategory(insp.category);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: insp.category, sub_category: `inspiration_${insp.category}`, fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        lapel: insp.lapel, button_count: insp.buttons,
        shoe_type: insp.shoe, shoe_color: insp.shoeColor,
        pant_color: insp.pant ? PANT_COLORS.find(c => c.value === insp.pant)?.name : undefined,
        fabric_note: insp.fabric_note || undefined,
        accessory: insp.accessory || undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
      }, { withCredentials: true, timeout: 90000 });
      // STALE-CLIENT GUARD — drop the response if the user switched
      // clients while Gemini was rendering. We compare against the
      // ref (which is mutated synchronously on every client-switch
      // useEffect) so this is robust to React 18 batching.
      if (existingLooksLoadedClientRef.current !== _targetClientId) {
        // Still clear the per-slot spinner — the user might have
        // returned to the original client, OR they might not have;
        // either way we don't want a stuck Loader.
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setIsGenerating(false); setGeneratingCategory(null);
        return;
      }
      const look = {
        id: resp.data.id, image: resp.data.generated_image, label: insp.title,
        garment: insp.garment, garment_id: insp.garment_id, category: `inspiration_${insp.category}`,
        combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
      };
      setInspResults(prev => ({ ...prev, [insp.id]: look }));
      setGeneratedLooks(prev => [...prev, look]);
      setActiveCategory(insp.category);
      toast.success(`${insp.title} generated`);
    } catch (e) {
      // Same stale-client guard on the failure path — Client B should
      // NEVER see "Client A's render failed" toasts.
      if (existingLooksLoadedClientRef.current !== _targetClientId) {
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setIsGenerating(false); setGeneratingCategory(null);
        return;
      }
      toast.error(e.response?.data?.detail || `Failed: ${insp.title}`);
      setInspFailed(prev => ({ ...prev, [insp.id]: e?.response?.data?.detail || 'Generation failed' }));
    }
    setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
    setIsGenerating(false); setGeneratingCategory(null);
  };

  // (removed dead handler generateCombo — referenced removed constants)

  const handleDownload = (look) => {
    const src = srcFor(look.image);
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = `${storeSettings.store_name}-${look.label}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Brand-this-Lookbook modal state. The modal collects a shop name + logo
  // the presenter wants stamped on THIS PDF. On confirm we append
  // `brand_name` + `brand_logo` to the export call so the backend renders the
  // PDF with those overrides instead of the logged-in user's store branding.
  const [exportModalOpen, setExportModalOpen] = useState(false);
  // Color analysis lifted up to the Dashboard so SUIT_COLORS in the customizer
  // can be star-marked when they match the client's recommended palette.
  const [colorAnalysis, setColorAnalysis] = useState(null);
  const colorMatcher = useColorMatcher(colorAnalysis);
  const [exportModalMode, setExportModalMode] = useState('main'); // 'main' | 'wedding'

  const runMainLookbookExport = async ({ brand_name, brand_logo } = {}) => {
    setExportingPdf(true);
    try {
      // Use POST so a large base64 logo doesn't blow past query-string limits.
      const r = await axios.post(`${BACKEND_URL}/api/export-lookbook`, {
        client_id: validClientId || null,
        brand_name: brand_name || null,
        brand_logo: brand_logo || null,
      }, {
        withCredentials: true, responseType: 'blob', timeout: 180000,
      });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = u;
      const prefix = (brand_name && brand_name.replace(/\s+/g, '_'))
        || (validClientId
          ? (clients.find(c => c.id === validClientId)?.name?.replace(/\s+/g, '_') || 'Client')
          : (storeSettings.store_name || 'Lookbook'));
      a.download = `${prefix}_Lookbook.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(u);
      toast.success('Lookbook exported');
      setExportModalOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error('No looks to export yet — generate some first');
      else if (status === 502 || status === 504 || err?.code === 'ECONNABORTED')
        toast.error('Export timed out. Try again in a moment or export a single client.');
      else toast.error('Export failed. Please try again.');
    }
    setExportingPdf(false);
  };

  const handleExportLookbook = () => {
    // Branding modal removed per user request — export immediately with the
    // store's saved settings (or no branding at all if none are set).
    runMainLookbookExport({
      brand_name: storeSettings.store_name || '',
      brand_logo: storeSettings.store_logo || '',
    });
  };
  // When a client is selected, load their existing looks so the studio reflects past work
  // Uses a ref tracking the client id that was last loaded — switching clients forces a re-load
  const existingLooksLoadedClientRef = React.useRef(null);
  const [clientHasExistingLooks, setClientHasExistingLooks] = useState(false);
  const [existingLooksLoaded, setExistingLooksLoaded] = useState(false);
  React.useEffect(() => {
    if (!selectedClientId) { setExistingLooksLoaded(true); return; }
    if (existingLooksLoadedClientRef.current === selectedClientId) { setExistingLooksLoaded(true); return; }
    // New client (or switched client) — wipe ALL residual state from the previous client
    // including the uploaded photo + fabric swatch so one client's reference image
    // can never bleed into another's workspace.
    const prevClientId = existingLooksLoadedClientRef.current;
    existingLooksLoadedClientRef.current = selectedClientId;
    // CRITICAL: drop every QUEUED genLimit task from the previous client.
    // Without this, the new client's auto-gens have to wait for the old
    // client's backlog (up to ~30 calls in Wedding mode) to drain through
    // the 3-slot concurrency window — making the workspace appear stuck and
    // forcing a refresh. In-flight tasks (max 3) still complete but their
    // .then handlers are guarded by `existingLooksLoadedClientRef.current
    // !== capturedClientId` so their results are discarded.
    if (prevClientId !== null && prevClientId !== selectedClientId) {
      try { genLimit.reset(); } catch { /* noop */ }
    }
    setExistingLooksLoaded(false);
    // On a true client SWITCH we wipe the looks immediately (so we don't
    // bleed one client's looks into another's workspace). On initial Dashboard
    // MOUNT (prevClientId === null) we keep whatever `_cachedLooks` populated
    // from sessionStorage so the workspace renders instantly; the /api/gallery
    // fetch below will then merge in any newer items.
    if (prevClientId !== null && prevClientId !== selectedClientId) {
      // Switching clients: WIPE FIRST to guarantee the old client's looks
      // never render alongside the new client's data even for a single
      // frame. Then hydrate from sessionStorage scoped to the new client.
      // This eliminates the cross-client photo-bleed glitch where you'd
      // briefly see Client A's wedding party in Client B's workspace.
      setGeneratedLooks([]);
      const cachedLooks = readClientLooks(selectedClientId);
      if (cachedLooks.length > 0) {
        // Defer the hydration to the next microtask so the empty-state
        // render commits first. Without this, React 18's auto-batching
        // would merge wipe + hydrate into the same render and the bleed
        // could still show on slow devices mid-transition.
        // Capture the target client id so a rapid double-switch (A→B→C
        // before microtask flushes) doesn't apply B's cached looks to C.
        const targetClientId = selectedClientId;
        Promise.resolve().then(() => {
          if (existingLooksLoadedClientRef.current === targetClientId) {
            setGeneratedLooks(cachedLooks);
          }
        });
      }
    }
    // Photo-flash mitigation: instead of clearing `inspResults` to {} (which
    // makes the workspace flash the "Generate this look" call-to-action cards
    // for ~200-400ms until /api/gallery hydrates), seed every inspiration slot
    // with a `restoring: true` marker. The renderer treats these as luxury
    // skeleton loaders (Cormorant title + spinner), and the /api/gallery
    // callback below replaces them atomically with the new client's real
    // inspirations. Slots that have no saved generation fall back to the
    // standard InspirationCard once auto-gen takes over.
    if (prevClientId && prevClientId !== selectedClientId) {
      const restoringInsp = {};
      [...STYLE_INSPIRATIONS, ...WEDDING_INSPIRATIONS, ...BC_INSPIRATIONS, ...OUTERWEAR_INSPIRATIONS, ...SHIRT_INSPIRATIONS].forEach(i => {
        restoringInsp[i.id] = { restoring: true, label: i.title, gradient: i.gradient };
      });
      setInspResults(restoringInsp);
    } else {
      setInspResults({});
    }
    setInspGenerating({});
    setInspFailed({});
    setAutoInspTriggered(false);
    essentialAutoGenRef.current = false;
    bcAutoGenRef.current = false;
    outerwearAutoGenRef.current = false;
    weddingInspTriggeredRef.current = false;
    setClientHasExistingLooks(false);
    // Hard-reset the reference photo + fabric swatch on an actual client switch
    // (not on the very first mount where prevClientId is null and we may already have
    // the correct _cachedPhoto for this client).
    if (prevClientId && prevClientId !== selectedClientId) {
      // Switching clients: aggressively wipe per-client state so nothing from
      // the previous client can paint into the new client's workspace.
      setFabricImage(null);
      setFabricImagePreview(null);
      setFabricLibrary([]);
      setLibraryFabricId(null);
      setLibraryFabricMeta(null);
      setColorAnalysis(null);
      setWeddingPartyImage(null);
      setWeddingPartyTrio(null);  // P2 — clear trio so new client doesn't see stale renders
      setDisabledSubcategories([]);
      setClientLooksLocked(false);
      // INSTANT switch: read cache synchronously and apply in ONE setState pair
      // so the user never sees a blank-photo flash. The clientCache helper checks
      // sessionStorage first (latest), then falls back to localStorage (cross-tab
      // + survives reloads). Per-client scoping is enforced inside the helper —
      // it WILL NOT return another client's photo even if `selectedClientId` was
      // null or stale at call time.
      const cached = readClientPhoto(selectedClientId);
      if (cached?.b64) {
        // Atomic swap: paint the NEW client's photo in the same render cycle as
        // we wipe stale state. Result: zero blank-photo frame.
        clientImageOwnerRef.current = selectedClientId;
        setClientImage(cached.b64);
        setClientImagePreview(cached.preview || `data:image/jpeg;base64,${cached.b64}`);
        setClientImageLoading(false);
      } else {
        // Oct 2026 — INSTANT thumbnail fallback. The /clients list
        // response now carries a 256×256 `photo_thumb_b64` on every
        // client, baked right into the state we already have in RAM.
        // Paint it immediately as the preview so the Dashboard shows
        // the client's face on the FIRST render, no roundtrip, no
        // shimmer. The full-res /photo-b64 fetch below still runs and
        // seamlessly swaps in once it lands (same aspect, same crop).
        // Only falls back to the shimmer skeleton if the client has
        // neither a cached full-res copy NOR an inline thumbnail
        // (legacy clients created before the thumb-on-upload work;
        // backfilled by POST /api/admin/backfill-thumbs).
        const clientRecord = clients.find((c) => c.id === selectedClientId);
        const thumbB64 = clientRecord?.photo_thumb_b64;
        if (thumbB64) {
          clientImageOwnerRef.current = selectedClientId;
          setClientImage(thumbB64);
          setClientImagePreview(`data:image/jpeg;base64,${thumbB64}`);
          setClientImageLoading(false);
        } else {
          // No cache → mark loading so the upload slot shows a luxury
          // skeleton with shimmer instead of a blank frame for the
          // ~150-300ms it takes the /photo-b64 fetch to resolve.
          clientImageOwnerRef.current = selectedClientId;
          setClientImage(null);
          setClientImagePreview(null);
          setClientImageLoading(true);
        }
      }
    }
    // Use thumbnail URLs instead of inline base64 — drops the response
    // payload from ~6-10MB to ~30KB on a 30-look client switch. Images
    // stream in via parallel HTTP/2 requests with 1-year browser cache.
    axios.get(`${BACKEND_URL}/api/gallery?client_id=${selectedClientId}&limit=400&inline_images=false`, { withCredentials: true })
      .then(r => {
        // Stale-guard: if the user has switched to another client while this
        // gallery request was in flight, throw the response away. Otherwise
        // we paint the previous client's looks into the new client's workspace.
        if (existingLooksLoadedClientRef.current !== selectedClientId) return;
        const items = r.data.looks || [];
        if (items.length === 0) return;
        setClientHasExistingLooks(true);
        // Map gallery items into the local look shape the Studio renders
        const mapped = items.map((it) => {
          // Iter 86 — variants persistence. The gallery now returns the
          // edit-history `variants[]` array (newest snapshot first, max
          // 8). Each entry carries an image_path / image_path_thumb so
          // we can rebuild the small-card carousel as URL refs (NOT
          // base64) — keeping the hydration payload tiny while still
          // letting the tailor page back through every prior edit.
          const rawVariants = Array.isArray(it.variants) ? it.variants : [];
          const variants = rawVariants
            .filter(v => v && (v.image_path || v.image_path_thumb))
            .map(v => ({
              id: it.id,           // variants share the parent look id
              image_path: v.image_path,
              image: galleryItemImage({ image_path: v.image_path, image_path_thumb: v.image_path_thumb, category: it.category }, { thumb: false }),
              image_thumb: galleryItemImage({ image_path: v.image_path, image_path_thumb: v.image_path_thumb, category: it.category }, { thumb: true }),
              label: v.label,
              tailor_notes: v.tailor_notes || {},
              captured_at: v.captured_at,
              edit: v.edit || {},
            }));
          return {
            id: it.id,
            image: galleryItemImage(it, { thumb: false }), // full-res for the main card
            image_thumb: galleryItemImage(it, { thumb: true }),
            label: it.garment_name || it.garment_type,
            garment: it.garment_name || it.garment_type,
            garment_id: it.garment_type,
            category: it.category || 'full_suits',
            tailor_notes: it.tailor_notes || {},
            color: it.color,
            variants,
            // Iter 160 — surface the merged applied_edits set so the
            // CustomizeModal hydrates its restructure state on modal
            // open (Chelsea boots + oxblood belt survive across page
            // reloads / client switches).
            applied_edits: it.applied_edits || {},
          };
        });
        setGeneratedLooks(prev => {
          // Build a map of the incoming real looks by id
          const incomingById = new Map(mapped.map(l => [l.id, l]));
          // Replace any existing placeholder (restoring=true) OR existing look
          // that matches an incoming id — so the API response upgrades the
          // skeleton into the real image without duplicating.
          const updated = prev.map(l => incomingById.has(l.id) ? incomingById.get(l.id) : l);
          const existingIds = new Set(updated.map(l => l.id));
          const brandNew = mapped.filter(l => !existingIds.has(l.id));
          // Drop any stale restoring=true placeholders that didn't get filled
          // (e.g. look was deleted on the server between sessions).
          const cleaned = updated.filter(l => !l.restoring);
          return [...cleaned, ...brandNew];
        });
        // Restore inspiration cards in the workspace (Style + Wedding + BC) from the saved gallery looks.
        // Match ONLY items stored with an `inspiration_*` category — so Essential 5 looks with the same
        // garment+colour never incorrectly populate an inspiration slot.
        const allInspirations = [...STYLE_INSPIRATIONS, ...WEDDING_INSPIRATIONS, ...BC_INSPIRATIONS, ...OUTERWEAR_INSPIRATIONS, ...SHIRT_INSPIRATIONS];
        const inspirationItems = items.filter(it => typeof it.category === 'string' && it.category.startsWith('inspiration_'));
        const restoredInsp = {};
        for (const insp of allInspirations) {
          const match = inspirationItems.find(it => (
            (it.garment_name === insp.garment || it.garment_type === insp.garment_id) &&
            (it.color || '').toLowerCase() === (insp.suit || '').toLowerCase()
          ));
          if (match) {
            restoredInsp[insp.id] = {
              id: match.id,
              image: galleryItemImage(match, { thumb: false }),
              image_thumb: galleryItemImage(match, { thumb: true }),
              label: insp.title,
              garment: insp.garment,
              garment_id: insp.garment_id,
              category: `inspiration_${insp.category}`,
              combo: insp,
              tailor_notes: match.tailor_notes || {},
              image_path: match.image_path,
              variants: mapped.find(l => l.id === match.id)?.variants || [],
              isInspiration: true,
            };
          }
        }
        // Atomic replace: clear `restoring:true` placeholders for slots that have
        // a real saved inspiration, and drop the placeholder for slots that
        // don't. Slots with no saved data fall back to InspirationCard once the
        // auto-gen useEffect kicks in. Using a functional set so we don't merge
        // stale entries from the previous client.
        setInspResults(prev => {
          const next = { ...prev };
          // Strip restoring markers — auto-gen will repopulate any genuinely
          // unfilled slots immediately.
          for (const k of Object.keys(next)) {
            if (next[k]?.restoring) delete next[k];
          }
          // Splice in the new client's restored inspirations
          return { ...next, ...restoredInsp };
        });
        // Restore Wedding Party group photo from gallery if one was previously generated
        const partyLook = items.find(it => it.garment_name === 'Wedding Party Group Photo');
        if (partyLook) {
          setWeddingPartyImage(galleryItemImage(partyLook, { thumb: false }));
        }
        // P2 — Restore Wedding Party render on client switch.
        // NEW (Feb 2026): backend now stores ONE composite group photo as
        // garment_type='wedding_party_trio', subject='group'. Legacy 3-doc
        // trios (subject='groom|groomsmen|bridesmaids') still hydrate via
        // the fallback below for clients that generated before the change.
        const trioItems = items.filter(it => it.garment_type === 'wedding_party_trio');
        if (trioItems.length > 0) {
          const pickLatest = (s) => trioItems
            .filter(it => it.subject === s)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
          const group = pickLatest('group');
          const groom = pickLatest('groom');
          const groomsmen = pickLatest('groomsmen');
          const bridesmaids = pickLatest('bridesmaids');
          if (group) {
            // NEW single-composite path.
            setWeddingPartyTrio({
              group_photo: { image: galleryItemImage(group, { thumb: false }), image_path: group.image_path, label: group.garment_name },
              groom: null, groomsmen: null, bridesmaids: null,
              tie_palettes: [], shoe_palettes: [],
            });
          } else if (groom || groomsmen || bridesmaids) {
            // LEGACY 3-doc trio fallback.
            const toRes = (g) => g ? { image: galleryItemImage(g, { thumb: false }), image_path: g.image_path, label: g.garment_name } : null;
            setWeddingPartyTrio({
              groom: toRes(groom),
              groomsmen: toRes(groomsmen),
              bridesmaids: toRes(bridesmaids),
              tie_palettes: [],
            });
          }
        }
      })
      .catch(() => {
        // Even on gallery failure, clean up any restoring placeholders so the
        // workspace doesn't sit stuck on luxury skeletons forever.
        setInspResults(prev => {
          const next = { ...prev };
          for (const k of Object.keys(next)) {
            if (next[k]?.restoring) delete next[k];
          }
          return next;
        });
      })
      .finally(() => {
        // Only flip the loaded flag if we still belong to this client — otherwise
        // a stale gallery response could unlock auto-gen for the new client too early.
        if (existingLooksLoadedClientRef.current === selectedClientId) {
          setExistingLooksLoaded(true);
        }
      });
    // Always fetch the saved client photo for this client — guarantees we show the
    // CORRECT photo even if sessionStorage was cleared or we're switching clients.
    axios.get(`${BACKEND_URL}/api/clients/${selectedClientId}/photo-b64`, { withCredentials: true })
      .then(r => {
        // Only apply if the user hasn't switched to yet another client while the fetch was in flight.
        if (existingLooksLoadedClientRef.current !== selectedClientId) return;
        if (r.data?.image) {
          clientImageOwnerRef.current = selectedClientId;
          setClientImage(r.data.image);
          setClientImagePreview(`data:image/jpeg;base64,${r.data.image}`);
          // Cache for instant hydration on the next client switch — capped to
          // ~5 most recent clients so sessionStorage doesn't blow up.
          try {
            sessionStorage.setItem(
              `client_photo_${selectedClientId}`,
              JSON.stringify({ b64: r.data.image, preview: `data:image/jpeg;base64,${r.data.image}` })
            );
          } catch { /* sessionStorage quota — ignore */ }
        } else {
          // Server truth: this client has NO photo on record. Wipe the state
          // and drop the stale cache so a leftover photo from a previous
          // session (or a mis-scoped read) can't stay painted on top of the
          // new client's empty slot. Fixes "photo caught, not switching".
          clientImageOwnerRef.current = selectedClientId;
          setClientImage(null);
          setClientImagePreview(null);
          try { sessionStorage.removeItem(`client_photo_${selectedClientId}`); } catch { /* noop */ }
          try { localStorage.removeItem(`client_photo_${selectedClientId}`); } catch { /* noop */ }
        }
        setClientImageLoading(false);
      })
      .catch((err) => {
        // Transient failure (backend returns 502 when storage hiccups). Leave
        // whatever we're currently showing on screen so the tailor doesn't
        // watch the photo vanish for a network blip. `null` bodies are 200
        // and land in .then, so anything reaching here is genuinely an error.
        setClientImageLoading(false);
        if (err?.response?.status && err.response.status !== 502) {
          // Non-transient error (e.g. 404 client-not-found, 401) — wipe.
          clientImageOwnerRef.current = selectedClientId;
          setClientImage(null);
          setClientImagePreview(null);
          try { sessionStorage.removeItem(`client_photo_${selectedClientId}`); } catch { /* noop */ }
          try { localStorage.removeItem(`client_photo_${selectedClientId}`); } catch { /* noop */ }
        }
      });
    // Pull per-client config (disabled categories + lock state) so tab
    // visibility updates when switching between clients.
    axios.get(`${BACKEND_URL}/api/clients/${selectedClientId}`, { withCredentials: true })
      .then(r => {
        if (existingLooksLoadedClientRef.current !== selectedClientId) return;
        if (Array.isArray(r.data?.disabled_subcategories)) {
          setDisabledSubcategories(r.data.disabled_subcategories);
        } else {
          setDisabledSubcategories([]);
        }
        setClientLooksLocked(Boolean(r.data?.looks_locked));
        // Wedding setup gateway fields — surfaced to gate the Wedding tab
        // and used by the PDF cover. Default to empty string/[] when the
        // backend hasn't seen these fields yet (legacy clients).
        setWeddingSetup({
          groom_name:      r.data?.groom_name      || '',
          bride_name:      r.data?.bride_name      || '',
          wedding_date:    r.data?.wedding_date    || '',
          wedding_colours: Array.isArray(r.data?.wedding_colours) ? r.data.wedding_colours : [],
        });
      })
      .catch(() => {});
    // Restore persisted fabric swatches for this client so the presenter
    // sees the library they built up in prior sessions.
    axios.get(`${BACKEND_URL}/api/clients/${selectedClientId}/fabric-swatches`, { withCredentials: true })
      .then(r => {
        if (existingLooksLoadedClientRef.current !== selectedClientId) return;
        const swatches = (r.data?.swatches || []).map(s => ({
          b64: s.b64,
          preview: `data:image/jpeg;base64,${s.b64}`,
          label: s.label,
        }));
        if (swatches.length > 0) {
          setFabricLibrary(swatches);
        }
      })
      .catch(() => {});
  }, [selectedClientId]);

  // Auto-generate Essential 5 ONLY when Full Suits tab is active + client photo is ready.
  // Style sub-categories (Stripes / Checks / Patterned / Cigar Night / Gala Night) are now
  // LAZY — they generate on first accordion expand (see subcatAutoGenRef handler below).
  // This change cuts first-paint load from ~36 calls → 11 calls, so Essential 5 + inspirations
  // finish in well under a minute. Browsing a subcategory is then a fast 5-call batch.
  // ── Preemptive-gen polling ─────────────────────────────────────────
  // When the New Client Intake Modal fires the 11 Gemini gens in
  // background, the looks land in /api/gallery one-by-one over ~10-18s.
  // This effect polls /api/gallery every 3s while we're in the "waiting
  // for preemptive gens" window so the user sees looks appear LIVE in
  // the workspace as they complete — instead of having to refresh.
  // Polling stops as soon as we have ≥11 looks OR after 90s (timeout).
  React.useEffect(() => {
    if (!selectedClientId) return;
    let marker;
    try {
      marker = JSON.parse(sessionStorage.getItem(`preempt_gen_${selectedClientId}`) || 'null');
    } catch { marker = null; }
    if (!marker?.ts) return;
    const age = Date.now() - marker.ts;
    if (age > 90 * 1000) return; // marker is stale, no need to poll
    const remainingMs = 90 * 1000 - age;
    let cancelled = false;
    let pollCount = 0;
    const maxPolls = Math.ceil(remainingMs / 3000);

    const tick = async () => {
      if (cancelled || pollCount >= maxPolls) return;
      pollCount += 1;
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/gallery?client_id=${selectedClientId}&limit=400&inline_images=false`,
          { withCredentials: true },
        );
        if (cancelled || existingLooksLoadedClientRef.current !== selectedClientId) return;
        const items = r.data.looks || [];
        if (items.length === 0) {
          setTimeout(tick, 3000);
          return;
        }
        // Reuse the same hydration shape as the on-mount gallery fetch
        const mapped = items.map((it) => ({
          id: it.id,
          image: galleryItemImage(it, { thumb: false }),
          image_thumb: galleryItemImage(it, { thumb: true }),
          label: it.garment_name || it.garment_type,
          garment: it.garment_name || it.garment_type,
          garment_id: it.garment_type,
          category: it.category || 'full_suits',
          tailor_notes: it.tailor_notes || {},
          color: it.color,
        }));
        setGeneratedLooks(prev => {
          const incomingById = new Map(mapped.map(l => [l.id, l]));
          const updated = prev.map(l => incomingById.has(l.id) ? incomingById.get(l.id) : l);
          const existingIds = new Set(updated.map(l => l.id));
          const brandNew = mapped.filter(l => !existingIds.has(l.id));
          const cleaned = updated.filter(l => !l.restoring);
          return [...cleaned, ...brandNew];
        });
        // Surface inspirations into inspResults as they land
        const inspirationItems = items.filter(it =>
          typeof it.category === 'string' && it.category.startsWith('inspiration_')
        );
        setInspResults(prev => {
          const next = { ...prev };
          for (const insp of STYLE_INSPIRATIONS) {
            if (next[insp.id]?.image) continue;  // already hydrated
            const match = inspirationItems.find(it => (
              (it.garment_name === insp.garment || it.garment_type === insp.garment_id) &&
              (it.color || '').toLowerCase() === (insp.suit || '').toLowerCase()
            ));
            if (match) {
              next[insp.id] = {
                id: match.id,
                image: galleryItemImage(match, { thumb: false }),
                image_thumb: galleryItemImage(match, { thumb: true }),
                label: insp.title,
                garment: insp.garment,
                garment_id: insp.garment_id,
                category: `inspiration_${insp.category}`,
                combo: insp,
                tailor_notes: match.tailor_notes || {},
                isInspiration: true,
              };
              setInspGenerating(g => ({ ...g, [insp.id]: false }));
            }
          }
          return next;
        });
        // Stop polling when we have ≥11 looks (5 Essentials + 6 Style Inspirations)
        // or after maxPolls iterations runs out.
        if (items.length >= 11) {
          try { sessionStorage.removeItem(`preempt_gen_${selectedClientId}`); } catch { /* noop */ }
          return;
        }
        setTimeout(tick, 3000);
      } catch {
        // Don't loop on errors — try once more then give up
        if (pollCount < maxPolls) setTimeout(tick, 5000);
      }
    };
    // Wait 2s before first poll — gives the first Gemini calls a head start
    const startT = setTimeout(tick, 2000);
    return () => { cancelled = true; clearTimeout(startT); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // ──────────────────────────────────────────────────────────────────────
  const essentialAutoGenRef = React.useRef(false);
  // Self-heal tick — increments after 75s so the Essentials effect
  // re-evaluates the marker even when no new looks have landed (the
  // marker may have started fresh and we said "wait", but the intake
  // gens then silently failed → without this tick the effect never
  // re-runs and the tab stays empty). 75s > the 60s "still in flight"
  // window so the re-run will see the marker as stale and regenerate.
  const [_essentialsHealTick, _setEssentialsHealTick] = React.useState(0);
  React.useEffect(() => {
    if (!selectedClientId) return;
    const t = setTimeout(() => _setEssentialsHealTick(v => v + 1), 75 * 1000);
    return () => clearTimeout(t);
  }, [selectedClientId]);
  React.useEffect(() => {
    if (clientLooksLocked) return;
    if (activeCategory !== 'full_suits' || !clientImage) return;
    if (!existingLooksLoaded) return;
    // ALWAYS-5: decide on the SUCCESSFUL essential count, not mere presence.
    // A partial intake (e.g. 3/5 landed) must still top-up to 5 instead of
    // latching "done" the moment any essential appears.
    const successCount = generatedLooksRef.current.filter(l => l.category === 'full_suits' && !l.failed && l.image).length;
    if (successCount >= SUIT_COMBOS.length) { essentialAutoGenRef.current = true; return; }
    if (generatingCats.has('full_suits') || backfillInProgressRef.current.has('full_suits')) return;
    // Respect the intake preemptive-gen marker: if NOTHING has landed yet and
    // the intake fired <60s ago, the batch is still in flight — wait.
    try {
      const marker = sessionStorage.getItem(`preempt_gen_${selectedClientId}`);
      if (marker) {
        const m = JSON.parse(marker);
        if (m?.essentials) {
          const ageMs = Date.now() - (m.ts || 0);
          if (successCount === 0 && ageMs < 60 * 1000) return; // still in flight
          if (ageMs > 60 * 1000) { try { sessionStorage.removeItem(`preempt_gen_${selectedClientId}`); } catch { /* noop */ } }
        }
      }
    } catch { /* noop */ }
    // Fire one macrotask later so Style Inspirations grab the queue first.
    const t = setTimeout(() => {
      const sc = generatedLooksRef.current.filter(l => l.category === 'full_suits' && !l.failed && l.image).length;
      if (sc >= SUIT_COMBOS.length) return;
      if (generatingCats.has('full_suits') || backfillInProgressRef.current.has('full_suits')) return;
      if (sc === 0) {
        essentialAutoGenRef.current = true;
        generateComboLooks(SUIT_COMBOS, 'full_suits').catch(() => {});
      } else {
        // Partial — top the missing essentials up to the full 5.
        backfillCategoryToFull('full_suits', SUIT_COMBOS, SUIT_COMBOS.length, 1).catch(() => {});
      }
    }, 50);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, clientImage, existingLooksLoaded, clientLooksLocked, generatedLooks.length, _essentialsHealTick]);

  // Lazy subcategory loader — fires on accordion expand. Dedupe is based on
  // ACTUAL OBSERVABLE STATE (looks already present OR a generation currently
  // in flight), not a session-bound ref. Previously a `subcatFiredRef` cached
  // every click, so if the very first attempt failed silently (closed
  // accordion mid-flight, 429 burst before any look saved, refresh during
  // generation) the ref would stay "fired" and every subsequent click was a
  // no-op — symptom: open accordion → spinner spins forever → no generation.
  const ensureSubcatGenerated = React.useCallback((subcat) => {
    if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
    if (clientLooksLocked) return;
    if (!clientImage) return;
    const cat = `full_suits_${subcat.value}`;
    // Already have looks (any kind, including failed placeholders) → skip.
    if (generatedLooksRef.current.some(l => l.category === cat)) return;
    // Currently generating → skip (avoids double-fire on rapid clicks).
    if (generatingCats.has(cat)) return;
    generateComboLooks(generateSubcategoryCombos(subcat, generatedLooksRef.current), cat).catch(() => {});
  }, [clientImage, clientLooksLocked, isDemoStore, generatingCats]);

  // Lazy-load BC scenarios — same state-based dedupe as ensureSubcatGenerated.
  //
  // CUSTOM FABRIC INJECTION (Feb 2026, user spec "5/15"):
  // For each REGULAR BC scenario (smart_friday / weekend_brunch / date_night /
  // outdoor_social) we randomly substitute ~33% of the 5 combo slots with a
  // custom-fabric variant pulled from the tailor's assigned library. House
  // signature scenarios (tom_ford / brunello_cucinelli) are NEVER touched
  // because their cinematic DNA defines the look — diluting them with random
  // fabrics breaks the brand intent.
  //
  // The substitution sends `fabric_id` so Gemini receives the actual swatch
  // as a reference image (not just a colour hint). Each slot gets a different
  // fabric to avoid clones.
  const _buildBcCombosWithFabricInjection = React.useCallback((scenario) => {
    const baseCombos = scenario.combos;
    // House signatures keep their carefully-art-directed combos as-is.
    const isHouseSignature = !!scenario.house;
    if (isHouseSignature) return baseCombos;
    if (!Array.isArray(libraryFabrics) || libraryFabrics.length === 0) return baseCombos;
    // 33% substitution rate per user "5/15" spec (1.67 of 5 ≈ 1 or 2 slots).
    const total = baseCombos.length;
    const subCount = Math.min(Math.max(1, Math.round(total / 3)), total);
    // Deterministic-ish per scenario+client so the same client sees the same
    // sprinkle every visit (no re-shuffling on tab toggle). Cheap hash.
    const seedStr = `${selectedClientId || 'demo'}_${scenario.value}`;
    let seed = 0;
    for (let i = 0; i < seedStr.length; i++) seed = ((seed * 31) + seedStr.charCodeAt(i)) >>> 0;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0xFFFFFFFF; };
    // Pick which slot indices to substitute + which fabrics to use.
    const slotIdxs = [];
    while (slotIdxs.length < subCount) {
      const idx = Math.floor(rand() * total);
      if (!slotIdxs.includes(idx)) slotIdxs.push(idx);
    }
    const fabricsCopy = [...libraryFabrics];
    const out = baseCombos.map((c, idx) => {
      if (!slotIdxs.includes(idx)) return c;
      // Pick a fabric (cycle through library deterministically so different
      // slots get different fabrics).
      const f = fabricsCopy[Math.floor(rand() * fabricsCopy.length)];
      if (!f || !f.id) return c;
      return {
        ...c,
        _library_fabric_id: f.id,
        garment: 'Sports Coat',
        garment_id: 'single_blazer',
        // Override the suit colour so the prompt builder doesn't fight
        // with the swatch (swatch wins for the jacket cloth).
        suit: f.primary_color_hex || c.suit,
        pattern: 'solid',
        label: `${f.color_family ? f.color_family.charAt(0).toUpperCase() + f.color_family.slice(1) : 'Custom'} · ${f.name || f.sku}`,
        fabric_note: `Render the sports coat in the EXACT cloth from the attached swatch reference photo — composition: ${f.composition || 'Wool & Silk'}, weave: ${f.weave || 'plain'}, primary colour: ${f.primary_color_hex || '#888888'}. Keep the rest of the outfit (shirt + ${c.pant ? 'pant' : 'chinos'} + shoes) per the original ${scenario.label} scenario flavour. Jacket worn OPEN and UNBUTTONED, patch pockets mandatory, two-button stance only.`,
      };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryFabrics, selectedClientId]);

  const ensureBcScenarioGenerated = React.useCallback((scenario) => {
    if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
    if (clientLooksLocked) return;
    if (!clientImage) return;
    const cat = `bc_${scenario.value}`;
    if (generatedLooksRef.current.some(l => l.category === cat)) return;
    if (generatingCats.has(cat)) return;
    const combos = _buildBcCombosWithFabricInjection(scenario);
    generateComboLooks(combos, cat).catch(() => {});
  }, [clientImage, clientLooksLocked, isDemoStore, generatingCats, _buildBcCombosWithFabricInjection]);

  // Lazy-load Outerwear subcategories — same state-based dedupe.
  const ensureOuterwearSubcatGenerated = React.useCallback((subcat) => {
    if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
    if (clientLooksLocked) return;
    if (!clientImage) return;
    const cat = `outerwear_${subcat.value}`;
    if (generatedLooksRef.current.some(l => l.category === cat)) return;
    if (generatingCats.has(cat)) return;
    generateComboLooks(subcat.combos, cat).catch(() => {});
  }, [clientImage, clientLooksLocked, isDemoStore, generatingCats]);

  // Lazy-load Shirt subcategories — same state-based dedupe.
  const ensureShirtSubcatGenerated = React.useCallback((subcat) => {
    // Iter 129h — "Casual Shirts" is a PICKER subcategory: it never
    // auto-generates preset combos; instead it opens a modal so the
    // tailor picks style + colours + shoes for a single custom render.
    // Feb 2026 — same behaviour for "Sweaters".
    if (subcat?.picker || subcat?.value === 'casual_shirts' || subcat?.value === 'sweaters' || subcat?.value === 'dress_shirts') {
      // Feb 2026 — Casual Shirts + Sweaters are UNLOCKED for demo
      // stores per user spec, each capped at 3 lifetime renders. When
      // the cap is hit, redirect to the upgrade toast instead of
      // opening the picker so we don't send a doomed request.
      if (isDemoStore) {
        const _demoCat = subcat?.value === 'sweaters'
          ? 'shirts_sweaters'
          : subcat?.value === 'dress_shirts'
            ? 'shirts_dress_shirts'
            : 'shirts_casual_shirts';
        const _demoLabel = subcat?.value === 'sweaters'
          ? 'sweater'
          : subcat?.value === 'dress_shirts'
            ? 'dress shirt'
            : 'casual shirt';
        const _used = generatedLooksRef.current.filter(l => l.category === _demoCat).length;
        if (_used >= 3) {
          toast.info(`Demo accounts are capped at 3 ${_demoLabel} renders. Upgrade to unlock unlimited generations.`);
          return;
        }
      }
      if (clientLooksLocked) return;
      if (subcat?.value === 'sweaters') {
        setShowSweaterQ(true);
      } else if (subcat?.value === 'dress_shirts') {
        setShowDressShirtsQ(true);
      } else {
        setShowCasualShirtsQ(true);
      }
      return;
    }
    if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
    if (clientLooksLocked) return;
    if (!clientImage) return;
    const cat = `shirts_${subcat.value}`;
    if (generatedLooksRef.current.some(l => l.category === cat)) return;
    if (generatingCats.has(cat)) return;
    generateComboLooks(subcat.combos, cat).catch(() => {});
  }, [clientImage, clientLooksLocked, isDemoStore, generatingCats]);

  // BC scenarios are now LAZY — they generate only when the user expands
  // a scenario panel (see `ensureBcScenarioGenerated` + the panel onClick).
  // We keep the inspirations panel auto-generation effect (6 cards) which
  // users actually expect to be ready when they enter the tab.
  const bcAutoGenRef = React.useRef(false);
  React.useEffect(() => {
    // Marker only — preserved for future reset hooks. No work done here.
    if (activeCategory !== 'business_casual') bcAutoGenRef.current = false;
  }, [activeCategory]);

  // Outerwear subcategories are now LAZY — they generate only when the user
  // expands a subcategory panel (see `ensureOuterwearSubcatGenerated` + the
  // panel onClick).
  const outerwearAutoGenRef = React.useRef(false);
  React.useEffect(() => {
    if (activeCategory !== 'outerwear') outerwearAutoGenRef.current = false;
  }, [activeCategory]);

  // Wedding Themes section has been removed from the Wedding tab UI.
  // Keep `generateWeddingThemes` available for any future use but don't auto-run it.

  // Auto-generate any missing STYLE inspirations (per-slot: only fills empty cards).
  // Gated on `activeCategory === 'full_suits'` so we don't burn 6 Gemini calls
  // the moment the workspace loads if the tailor immediately taps Wedding or
  // BC. Tab switch = lazy generation = near-instant subsequent tabs because
  // the active tab's calls fire in parallel via genLimit(5).
  React.useEffect(() => {
    if (clientLooksLocked) return;
    if (activeCategory !== 'full_suits') return;
    if (!clientImage || !existingLooksLoaded) return;
    // Feb 2026 — hold auto-gen until the context tiles have loaded when
    // the client has a context set. Otherwise we'd fire the legacy 6
    // Style Inspirations first, then the context-curated ones would
    // arrive late and never get generated. `contextTiles.style` populates
    // async ~150-300ms after client switch.
    if (isValidContext(clientContext) && !contextTiles.style) return;
    const styleArr = contextTiles.style || STYLE_INSPIRATIONS;
    const missing = styleArr.filter(insp => !inspResults[insp.id]?.image && !inspGenerating[insp.id]);
    if (missing.length === 0) return;
    // Skip if intake modal already fired the preemptive Style Inspirations
    // gen for this client. The looks are in flight; gallery hydration will
    // surface them into inspResults below.
    //
    // BUG FIX (Iter 81 — universal) — same trust-observable-state pattern
    // as the Essentials effect above. If intake gens silently failed, the
    // marker would block dashboard regeneration forever. Now we treat
    // missing looks after 60s as a failed intake and let the regeneration
    // path below take over.
    try {
      const marker = sessionStorage.getItem(`preempt_gen_${selectedClientId}`);
      if (marker) {
        const m = JSON.parse(marker);
        if (m?.style_inspirations) {
          const ageMs = Date.now() - (m.ts || 0);
          // Count how many style inspirations have actually landed.
          const landedCount = styleArr.filter(insp => inspResults[insp.id]?.image).length;
          if (landedCount >= styleArr.length) {
            // All landed — intake succeeded.
            return;
          }
          if (ageMs < 60 * 1000) {
            // Likely still in flight — show skeletons, don't refire.
            setInspGenerating(prev => {
              const next = { ...prev };
              missing.forEach(insp => { next[insp.id] = true; });
              return next;
            });
            return;
          }
          // >60s old and looks haven't landed → intake failed, fall through
          // and regenerate. Clear marker so we don't loop.
          try { sessionStorage.removeItem(`preempt_gen_${selectedClientId}`); } catch { /* noop */ }
        }
      }
    } catch { /* noop */ }
    setInspGenerating(prev => {
      const next = { ...prev };
      missing.forEach(insp => { next[insp.id] = true; });
      return next;
    });
    missing.forEach(insp => {
      genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: insp.category, sub_category: `inspiration_${insp.category}`, fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        lapel: insp.lapel, button_count: insp.buttons,
        shoe_type: insp.shoe, shoe_color: insp.shoeColor,
        pant_color: insp.pant ? PANT_COLORS.find(c => c.value === insp.pant)?.name : undefined,
        fabric_note: insp.fabric_note || undefined,
        accessory: insp.accessory || undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        creative_boost: true,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: insp.title,
          garment: insp.garment, garment_id: insp.garment_id, category: `inspiration_${insp.category}`,
          combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
        };
        setInspResults(prev => ({ ...prev, [insp.id]: look }));
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch((e) => {
        // Stale-client guard: if the user switched clients while this
        // generation was in flight, swallow the failure silently — we
        // don't want OLD client's "Generation failed" status leaking into
        // NEW client's inspiration tiles.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        logError(`Inspiration failed (${insp.title}):`, e?.response?.status, e?.response?.data);
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setInspFailed(prev => ({ ...prev, [insp.id]: 'Generation failed' }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, existingLooksLoaded, selectedClientId, activeCategory, clientContext, contextTiles]);

  // Auto-generate any missing WEDDING inspirations when Wedding tab is active.
  React.useEffect(() => {
    if (activeCategory !== 'wedding' || !clientImage || !existingLooksLoaded) return;
    if (isValidContext(clientContext) && !contextTiles.wedding) return;
    const missing = (contextTiles.wedding || WEDDING_INSPIRATIONS).filter(insp => !inspResults[insp.id]?.image && !inspGenerating[insp.id]);
    if (missing.length === 0) return;
    setInspGenerating(prev => {
      const next = { ...prev };
      missing.forEach(insp => { next[insp.id] = true; });
      return next;
    });
    missing.forEach(insp => {
      genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: insp.category, sub_category: `inspiration_wedding`, fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        lapel: insp.lapel, button_count: insp.buttons,
        shoe_type: insp.shoe, shoe_color: insp.shoeColor,
        accessory: insp.accessory || undefined,
        fabric_note: insp.fabric_note || undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        creative_boost: true,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: insp.title,
          garment: insp.garment, garment_id: insp.garment_id, category: `inspiration_wedding`,
          combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
        };
        setInspResults(prev => ({ ...prev, [insp.id]: look }));
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch((e) => {
        // Stale-client guard: if the user switched clients while this
        // generation was in flight, swallow the failure silently — we
        // don't want OLD client's "Generation failed" status leaking into
        // NEW client's inspiration tiles.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        logError(`Wedding inspiration failed (${insp.title}):`, e?.response?.status, e?.response?.data);
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setInspFailed(prev => ({ ...prev, [insp.id]: 'Generation failed' }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, clientImage, existingLooksLoaded, selectedClientId, clientContext, contextTiles]);

  // Auto-generate any missing BUSINESS-CASUAL inspirations when BC tab is active.
  React.useEffect(() => {
    if (activeCategory !== 'business_casual' || !clientImage || !existingLooksLoaded) return;
    if (isValidContext(clientContext) && !contextTiles.business_casual) return;
    const missing = (contextTiles.business_casual || BC_INSPIRATIONS).filter(insp => !inspResults[insp.id]?.image && !inspGenerating[insp.id]);
    if (missing.length === 0) return;
    setInspGenerating(prev => {
      const next = { ...prev };
      missing.forEach(insp => { next[insp.id] = true; });
      return next;
    });
    missing.forEach(insp => {
      genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: insp.category, sub_category: `inspiration_business_casual`, fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        lapel: insp.lapel, button_count: insp.buttons,
        shoe_type: insp.shoe, shoe_color: insp.shoeColor,
        pant_color: insp.pant ? (PANT_COLORS.find(c => c.value === insp.pant)?.name || insp.pant) : undefined,
        fabric_note: insp.fabric_note || undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        creative_boost: true,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: insp.title,
          garment: insp.garment, garment_id: insp.garment_id, category: `inspiration_business_casual`,
          combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
        };
        setInspResults(prev => ({ ...prev, [insp.id]: look }));
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch((e) => {
        // Stale-client guard: if the user switched clients while this
        // generation was in flight, swallow the failure silently — we
        // don't want OLD client's "Generation failed" status leaking into
        // NEW client's inspiration tiles.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        logError(`BC inspiration failed (${insp.title}):`, e?.response?.status, e?.response?.data);
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setInspFailed(prev => ({ ...prev, [insp.id]: 'Generation failed' }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, clientImage, existingLooksLoaded, selectedClientId, clientContext, contextTiles]);

  // Auto-generate any missing OUTERWEAR inspirations when Outerwear tab is active.
  React.useEffect(() => {
    if (activeCategory !== 'outerwear' || !clientImage || !existingLooksLoaded) return;
    if (isValidContext(clientContext) && !contextTiles.outerwear) return;
    const missing = (contextTiles.outerwear || OUTERWEAR_INSPIRATIONS).filter(insp => !inspResults[insp.id]?.image && !inspGenerating[insp.id]);
    if (missing.length === 0) return;
    setInspGenerating(prev => {
      const next = { ...prev };
      missing.forEach(insp => { next[insp.id] = true; });
      return next;
    });
    missing.forEach(insp => {
      genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: 'outerwear', sub_category: 'inspiration_outerwear', fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        lapel: insp.lapel, button_count: insp.buttons,
        shoe_type: insp.shoe, shoe_color: insp.shoeColor,
        fabric_note: insp.fabric_note || undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        creative_boost: true,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: insp.title,
          garment: insp.garment, garment_id: insp.garment_id, category: `inspiration_outerwear`,
          combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
        };
        setInspResults(prev => ({ ...prev, [insp.id]: look }));
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch((e) => {
        // Stale-client guard: if the user switched clients while this
        // generation was in flight, swallow the failure silently — we
        // don't want OLD client's "Generation failed" status leaking into
        // NEW client's inspiration tiles.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        logError(`Outerwear inspiration failed (${insp.title}):`, e?.response?.status, e?.response?.data);
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setInspFailed(prev => ({ ...prev, [insp.id]: 'Generation failed' }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, clientImage, existingLooksLoaded, selectedClientId, clientContext, contextTiles]);

  // Auto-generate any missing SHIRTS inspirations when the Shirts tab is active.
  // Shirts render WITHOUT a jacket — clean shirt-only portraits for each style.
  React.useEffect(() => {
    if (activeCategory !== 'shirts' || !clientImage || !existingLooksLoaded) return;
    if (isValidContext(clientContext) && !contextTiles.shirts) return;
    const missing = (contextTiles.shirts || SHIRT_INSPIRATIONS).filter(insp => !inspResults[insp.id]?.image && !inspGenerating[insp.id]);
    if (missing.length === 0) return;
    setInspGenerating(prev => {
      const next = { ...prev };
      missing.forEach(insp => { next[insp.id] = true; });
      return next;
    });
    missing.forEach(insp => {
      genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: 'shirts', sub_category: 'inspiration_shirts', fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        fabric_note: insp.fabric_note || undefined,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        creative_boost: true,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: insp.title,
          garment: insp.garment, garment_id: insp.garment_id, category: 'inspiration_shirts',
          combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
        };
        setInspResults(prev => ({ ...prev, [insp.id]: look }));
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch((e) => {
        // Stale-client guard: if the user switched clients while this
        // generation was in flight, swallow the failure silently — we
        // don't want OLD client's "Generation failed" status leaking into
        // NEW client's inspiration tiles.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        logError(`Shirts inspiration failed (${insp.title}):`, e?.response?.status, e?.response?.data);
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setInspFailed(prev => ({ ...prev, [insp.id]: 'Generation failed' }));
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, clientImage, existingLooksLoaded, selectedClientId, clientContext, contextTiles]);

  const currentInspirations = React.useMemo(() => {
    // Feb 2026 — swap tiles to the context-curated set when a client
    // has an "Occupation or upcoming event" selected. Falls back to the
    // legacy hardcoded arrays when no context is set / fetch failed.
    const styleArr    = contextTiles.style           || STYLE_INSPIRATIONS;
    const bcArr       = contextTiles.business_casual || BC_INSPIRATIONS;
    const shirtArr    = contextTiles.shirts          || SHIRT_INSPIRATIONS;
    const outerArr    = contextTiles.outerwear       || OUTERWEAR_INSPIRATIONS;
    const weddingArr  = contextTiles.wedding         || WEDDING_INSPIRATIONS;
    if (activeCategory === 'wedding') {
      // Append any extra wedding inspirations that have been generated/triggered
      const baseIds = new Set(weddingArr.map(i => i.id));
      const extras = EXTRA_WEDDING_INSPIRATIONS.filter(i => !baseIds.has(i.id) && (inspResults[i.id] || inspGenerating[i.id]));
      return [...weddingArr, ...extras];
    }
    if (activeCategory === 'business_casual') return bcArr;
    if (activeCategory === 'outerwear') return outerArr;
    if (activeCategory === 'shirts') return shirtArr;
    // Style inspirations get a deterministic shuffle per client so the same
    // 6 cards appear in a fresh order each time the consultant flips clients.
    return shuffleInspirations(styleArr, selectedClientId || 'default');
  }, [activeCategory, inspResults, inspGenerating, selectedClientId, contextTiles]);

  // De-dupe by id so the look-card grids never render two React children
  // with the same key (a transient background gallery-poll + local-render
  // overlap could briefly surface the same gallery id twice, which fired a
  // benign "two children with the same key" warning mid-stream).
  const dedupeById = (arr) => {
    const seen = new Set();
    const out = [];
    for (const l of arr) {
      if (seen.has(l.id)) continue;
      seen.add(l.id);
      out.push(l);
    }
    return out;
  };
  const getCategoryLooks = (cat) => dedupeById(generatedLooks.filter(l => l.category === cat));

  // Iter 232 (Feb 2026) — auto-clear the wardrobe RETUNING banner once
  // every tile in the currently-visible inspiration category has
  // either landed (image ready) OR errored. Placed here (above the
  // client-gate early return) so the hook order stays stable.
  React.useEffect(() => {
    if (!wardrobeRetuning) return;
    if (!currentInspirations || currentInspirations.length === 0) return;
    const allDone = currentInspirations.every(
      i => inspResults[i.id]?.image || inspFailed[i.id],
    );
    const anyGen = currentInspirations.some(i => inspGenerating[i.id]);
    if (allDone && !anyGen) {
      setWardrobeRetuning(null);
    }
  }, [wardrobeRetuning, currentInspirations, inspResults, inspFailed, inspGenerating]);

  // Wedding: "Generate New Wedding Looks" — uses the extra pool + creative_boost for distinctive renders
  const [weddingExtraGenerating, setWeddingExtraGenerating] = useState(false);
  const generateMoreWeddingInspirations = useCallback(async () => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (weddingExtraGenerating) return;
    // Only add extras we haven't already produced — dedupe by insp.id
    const existingIds = new Set(Object.keys(inspResults));
    const pool = [...EXTRA_WEDDING_INSPIRATIONS]
      .filter(i => !existingIds.has(i.id))
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);  // add up to 3 per click
    if (pool.length === 0) {
      toast.message('All extra wedding variations already generated.');
      return;
    }
    setWeddingExtraGenerating(true);
    const genState = {};
    pool.forEach(insp => { genState[insp.id] = true; });
    setInspGenerating(prev => ({ ...prev, ...genState }));

    const promises = pool.map(insp =>
      genLimit(() => withRetry(() => axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: insp.garment_id, garment_name: insp.garment,
        category: insp.category, sub_category: `inspiration_wedding`, fit, styles: ['flat_front'], color: insp.suit,
        suit_pattern: insp.pattern, shirt_color: insp.shirt, shirt_pattern: insp.shirtPattern,
        lapel: insp.lapel, button_count: insp.buttons,
        shoe_type: insp.shoe, shoe_color: insp.shoeColor,
        fabric_note: insp.fabric_note, accessory: insp.accessory,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
        creative_boost: true,
      }, { withCredentials: true, timeout: 90000 }))).then(resp => {
        // Stale-client guard: if the user switched clients mid-flight, drop
        // this response — otherwise the OLD client's look would paint into
        // the NEW client's workspace. Mirrors the gallery-fetch guard above.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: insp.title,
          garment: insp.garment, garment_id: insp.garment_id, category: `inspiration_${insp.category}`,
          combo: insp, tailor_notes: resp.data.tailor_notes, isInspiration: true,
        };
        setInspResults(prev => ({ ...prev, [insp.id]: look }));
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch((e) => {
        // Stale-client guard: if the user switched clients while this
        // generation was in flight, swallow the failure silently — we
        // don't want OLD client's "Generation failed" status leaking into
        // NEW client's inspiration tiles.
        if (existingLooksLoadedClientRef.current !== validClientId) return;
        logError(`Extra wedding inspiration failed (${insp.title}):`, e?.response?.status, e?.response?.data);
        setInspGenerating(prev => ({ ...prev, [insp.id]: false }));
      })
    );
    await Promise.allSettled(promises);
    setWeddingExtraGenerating(false);
    toast.success('New wedding looks generated');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientImage, inspResults, validClientId, fit, weddingExtraGenerating]);

  // Wedding: auto-generate all themes when tab is clicked
  const generateWeddingThemes = useCallback(async () => {    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    const genState = {};
    WEDDING_THEMES.forEach(t => { genState[t.id] = true; });
    setWeddingGenerating(genState);
    
    WEDDING_THEMES.forEach(theme => {
      const g = theme.groom;
      axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: g.garment_id, garment_name: g.garment,
        category: 'wedding', fit, styles: ['flat_front'], color: g.suit,
        suit_pattern: g.pattern, shirt_color: g.shirt,
        shoe_type: g.shoe, shoe_color: g.shoeColor,
        fabric_note: g.fabric_note, accessory: g.accessory,
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
      }, { withCredentials: true }).then(resp => {
        const look = {
          id: resp.data.id, image: resp.data.generated_image, label: theme.title,
          garment: g.garment, garment_id: g.garment_id, category: 'wedding',
          combo: g, tailor_notes: resp.data.tailor_notes, weddingTheme: theme.id,
        };
        setWeddingResults(prev => ({ ...prev, [theme.id]: look }));
        setWeddingGenerating(prev => ({ ...prev, [theme.id]: false }));
        setGeneratedLooks(prev => [...prev, look]);
      }).catch(() => {
        setWeddingGenerating(prev => ({ ...prev, [theme.id]: false }));
      });
    });
  }, [clientImage, selectedClientId, fit]);

  const weddingAutoGenRef = React.useRef(false);
  // Helper that converts a tie style slug + hex colour into a human-readable
  // description Gemini reliably renders. Every wedding prompt routes through
  // this so there's no codepath that can accidentally drop the tie.
  const describeTie = (styleSlug, hex) => {
    const labels = {
      classic_tie: 'classic silk necktie',
      skinny_tie:  'slim silk necktie',
      bowtie:      'classic silk bow tie',
      bolo_tie:    'sterling-silver-tipped bolo tie',
      cravat:      'silk ascot cravat',
      no_tie:      null,
    };
    const label = labels[styleSlug];
    if (!label) return '';
    return `${label} in ${hex}`;
  };

  // Send the curated wedding lookbook by email. Calls /api/email/wedding-lookbook
  // which pulls every wedding-category look + the wedding-party render and emails
  // a branded HTML lookbook to a single recipient.
  const sendWeddingEmail = useCallback(async () => {
    if (!selectedClientId) { toast.error('Select a client first'); return; }
    if (!weddingEmailTo || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(weddingEmailTo)) {
      toast.error('Enter a valid email address'); return;
    }
    setSendingWeddingEmail(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/email/wedding-lookbook`,
        { client_id: selectedClientId, recipient_email: weddingEmailTo },
        { withCredentials: true }
      );
      toast.success(data?.message || 'Wedding lookbook sent');
      setWeddingEmailTo('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
    } finally {
      setSendingWeddingEmail(false);
    }
  }, [selectedClientId, weddingEmailTo]);

  const generateWeddingParty = useCallback(async (theme) => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (isDemoStore && weddingPartyImage) {
      toast.info('Demo accounts can render the Wedding Party Visualizer once. Upgrade to unlock unlimited renders.');
      return;
    }
    setWeddingPartyGenerating(true);
    try {
      const groomColor = SUIT_COLORS.find(c => c.value === weddingGroomColor)?.name || weddingGroomColor;
      const groomsmenColor = SUIT_COLORS.find(c => c.value === weddingGroomsmenColor)?.name || weddingGroomsmenColor;
      const bridesmaidsColorName = weddingBridesmaidsColor;
      const groomTieDesc = describeTie(weddingGroomTieStyle, weddingGroomTieColor);
      const groomsmenTieDesc = describeTie(weddingGroomsmenTieStyle, weddingGroomsmenTieColor);
      const resp = await axios.post(`${BACKEND_URL}/api/generate-look`, {
        client_image: clientImage, body_image: bodyImage || undefined, garment_type: 'classic_suit', garment_name: 'Wedding Party Group Photo',
        category: 'wedding', fit, styles: ['flat_front'], color: weddingGroomColor,
        shirt_color: 'White', shoe_type: 'oxford', shoe_color: 'Black',
        tie_color: weddingGroomTieColor,
        fabric_note: (
          `WEDDING PARTY GROUP PHOTO with STRICT LAYOUT: `
          + `LEFT side of frame — 2-3 GROOMSMEN wearing ${weddingGroomsmenGarment} in ${groomsmenColor} (hex ${weddingGroomsmenColor}), white dress shirts, `
          + (groomsmenTieDesc ? `EACH groomsman clearly wearing a ${groomsmenTieDesc} (mandatory, visible at collar), ` : `no neckties, open collar, `)
          + `polished dress shoes. `
          + `CENTRE of frame — THE GROOM (the client from the reference photo, face identical) wearing a ${weddingGroomGarment} in ${groomColor} (hex ${weddingGroomColor}) with white dress shirt`
          + (groomTieDesc ? ` and a ${groomTieDesc} (mandatory, clearly visible at the collar, perfectly knotted and centred)` : ` (no necktie, open collar)`)
          + `. `
          + `RIGHT side of frame — 2-3 BRIDESMAIDS wearing ${weddingBridesmaidsGarment} in ${bridesmaidsColorName} (hex ${weddingBridesmaidsColor}). `
          + `All subjects full-body head-to-toe, standing in a horizontal line with slight 3/4 angle. Wide 16:9 framing, light grey studio background, editorial wedding photography style.`
        ),
        client_id: validClientId || undefined,
        save_to_gallery: !!validClientId,
      }, { withCredentials: true, timeout: 240000 });
      setWeddingPartyImage(resp.data.generated_image);
      toast.success('Wedding party photo generated');
    } catch (e) {
      logError('Wedding party generation failed:', e?.response?.status, e?.response?.data);
      toast.error(e.response?.data?.detail || (e?.code === 'ECONNABORTED' ? 'Wedding party timed out — try again' : 'Failed to generate party photo'));
    }
    setWeddingPartyGenerating(false);
  }, [clientImage, validClientId, fit, weddingGroomColor, weddingGroomsmenColor, weddingBridesmaidsColor, weddingGroomGarment, weddingGroomsmenGarment, weddingBridesmaidsGarment, weddingGroomTieStyle, weddingGroomTieColor, weddingGroomsmenTieStyle, weddingGroomsmenTieColor, isDemoStore, weddingPartyImage]);

  // P2 — TRIO generator. Backed by /api/generate-wedding-party-trio which
  // fans out 3 Gemini renders in parallel (asyncio.gather) and returns the
  // groom + groomsmen + bridesmaids plus 3 deterministic tie palettes.
  // Demo stores get the same one-time cap as the legacy single-photo flow.
  const generateWeddingPartyTrio = useCallback(async (answers) => {
    if (!clientImage) { toast.error('Upload a client photo first'); return; }
    if (isDemoStore && (weddingPartyTrio || weddingPartyImage)) {
      toast.info('Demo accounts can render the Wedding Party once. Upgrade to unlock unlimited renders.');
      return;
    }
    setWeddingPartyGenerating(true);
    try {
      const payload = {
        client_image: clientImage,
        body_image: bodyImage || undefined,
        fit,
        client_id: validClientId || undefined,
        ...answers,
      };
      const resp = await axios.post(
        `${BACKEND_URL}/api/generate-wedding-party-trio`,
        payload,
        { withCredentials: true, timeout: 240000 }
      );
      setWeddingPartyTrio(resp.data);
      // Iter 78 — persist the planner's 3 chosen palette hexes onto the
      // client as `wedding_colours` so the "Your Wedding Colour Inspiration"
      // card and the PDF cover both have a single source of truth. The
      // WeddingSetupModal no longer collects palette — the planner is now
      // the canonical source.
      const planner_palette = [
        answers?.groom_color,
        answers?.groomsmen_color,
        answers?.bridesmaids_color,
      ].filter(h => typeof h === 'string' && /^#[0-9A-Fa-f]{6}$/.test(h));
      if (validClientId && planner_palette.length > 0) {
        try {
          await axios.put(`${BACKEND_URL}/api/clients/${validClientId}`, {
            wedding_colours: planner_palette,
          }, { withCredentials: true });
          setWeddingSetup(prev => ({ ...prev, wedding_colours: planner_palette }));
        } catch (e) {
          // Non-fatal — the render itself succeeded. Just log.
          console.warn('Failed to persist planner palette', e);
        }
      }
      // Mirror the parent state used by the lookbook PDF / email flows so
      // the legacy single-photo entry-point still picks up at least the
      // composite (or groom in legacy 3-render responses).
      const composite = resp.data?.group_photo?.image;
      const legacyGroom = resp.data?.groom?.image;
      if (composite || legacyGroom) {
        setWeddingPartyImage(composite || legacyGroom);
      }
      setShowWeddingQuestionnaire(false);
      // For the new composite path: success if group_photo.image present.
      // For legacy 3-render fallback: warn on partial.
      if (resp.data?.group_photo) {
        if (resp.data.group_photo.image) {
          toast.success('Wedding party group photo generated');
        } else {
          toast.error('Wedding party render failed — try again');
        }
      } else {
        const partial = [resp.data?.groom, resp.data?.groomsmen, resp.data?.bridesmaids]
          .filter(r => r && !r.image).length;
        if (partial > 0) {
          toast.warning(`Wedding party generated — ${3 - partial} of 3 photos rendered. Tap retry to fill the missing tile.`);
        } else {
          toast.success('Wedding party trio generated');
        }
      }
    } catch (e) {
      logError('Wedding party trio failed:', e?.response?.status, e?.response?.data);
      toast.error(
        e.response?.data?.detail ||
        (e?.code === 'ECONNABORTED' ? 'Wedding party timed out — try again' : 'Failed to generate wedding party trio')
      );
    }
    setWeddingPartyGenerating(false);
  }, [clientImage, bodyImage, fit, validClientId, isDemoStore, weddingPartyTrio, weddingPartyImage]);

  const runWeddingLookbookExport = useCallback(async ({ brand_name, brand_logo } = {}) => {
    setExportingWeddingPdf(true);
    try {
      const groomName = SUIT_COLORS.find(c => c.value === weddingGroomColor)?.name || 'Black';
      const groomsmenName = SUIT_COLORS.find(c => c.value === weddingGroomsmenColor)?.name || 'Charcoal';
      const bridesmaidsNames = { '#FFB6C1': 'Blush', '#FF1493': 'Magenta', '#E6E6FA': 'Lavender', '#556B2F': 'Sage', '#006400': 'Emerald', '#4B0082': 'Plum', '#CC5500': 'Rust', '#722F37': 'Burgundy', '#008080': 'Teal', '#FF7F50': 'Coral', '#B0C4DE': 'Dusty Blue', '#F5F5DC': 'Champagne' };
      const bridesmaidsName = bridesmaidsNames[weddingBridesmaidsColor] || 'Sage';
      // POST to handle large logos that blow past URL length limits.
      const r = await axios.post(`${BACKEND_URL}/api/export-wedding-lookbook`, {
        groom_color: groomName,
        groomsmen_color: groomsmenName,
        bridesmaids_color: bridesmaidsName,
        // Send the exact HEX values the user picked so the PDF palette
        // swatches mirror the on-screen picker exactly. Backend now prefers
        // hex when present and only falls back to the name→hex lookup table.
        groom_color_hex: weddingGroomColor,
        groomsmen_color_hex: weddingGroomsmenColor,
        bridesmaids_color_hex: weddingBridesmaidsColor,
        client_id: selectedClientId || null,
        brand_name: brand_name || null,
        brand_logo: brand_logo || null,
      }, { withCredentials: true, responseType: 'blob', timeout: 180000 });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = u; a.download = `${(brand_name || 'Wedding').replace(/\s+/g, '_')}_Lookbook.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u);
      toast.success('Wedding lookbook exported');
      setExportModalOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'No wedding looks to export yet'); }
    setExportingWeddingPdf(false);
  }, [weddingGroomColor, weddingGroomsmenColor, weddingBridesmaidsColor, selectedClientId]);

  const handleExportWeddingLookbook = useCallback(() => {
    // Branding modal removed per user request — export immediately.
    runWeddingLookbookExport({
      brand_name: storeSettings.store_name || '',
      brand_logo: storeSettings.store_logo || '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSettings.store_name, storeSettings.store_logo, runWeddingLookbookExport]);

  // CLIENT GATE — must select a client before entering the studio.
  // Original dropdown picker + a small "+ Add new client" link underneath
  // that opens the 3-step intake modal. The modal is rendered as a sibling
  // (NOT nested inside the gate) so its `fixed inset-0 z-50` covers cleanly
  // on every screen size and doesn't inherit the gate's positioning context.
  if (showClientGate) {
    return (
      <>
        <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
          <Toaster position="top-right" theme="dark" />
          <div className="max-w-md w-full px-6 fade-in text-center">
            {storeSettings.store_logo ? (
              <img src={storeSettings.store_logo} alt={storeSettings.store_name} className="h-16 w-auto mx-auto mb-6" />
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 border mb-6" style={{ borderColor: storeSettings.primary_color || '#C2A878' }}>
                <Sparkles className="w-8 h-8" style={{ color: storeSettings.primary_color || '#C2A878' }} />
              </div>
            )}
            <h1 className="text-3xl font-light text-[#F5F5F5] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {storeSettings.store_name || 'TailorFlow AI'}
            </h1>
            <p className="text-sm text-[#A1A1AA] mb-8">Select a client to begin your fitting session</p>
            <div className="bg-[#121215] border border-[#27272A] p-8">
              <div className="mb-6">
                <Label className="label-luxury mb-3 block">Client</Label>
                {/* Iter 123 — search box above the picker so tailors with
                    100+ clients can jump straight to a name/email/phone
                    without scrolling through the dropdown. */}
                {clients.length > 8 && (
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search clients by name, email, phone…"
                    className="w-full mb-2 px-3 h-10 bg-[#0A0A0C] border border-[#27272A] text-[#F5F5F5] placeholder-[#52525B] focus:outline-none focus:border-[#52525B] text-sm"
                    data-testid="gate-client-search"
                    aria-label="Search clients"
                  />
                )}
                <Select value={selectedClientId} onValueChange={(val) => {
                  setSelectedClientId(val);
                  const c = clients.find(cl => cl.id === val);
                  setSelectedClientSlug(c?.slug || '');
                }}>
                  <SelectTrigger className="w-full bg-[#0A0A0C] border-[#27272A] text-[#F5F5F5] rounded-none h-12" data-testid="gate-client-select">
                    <SelectValue placeholder={clients.length === 0 ? 'No clients yet — add one below' : (filteredClients.length === 0 ? 'No matches — clear search' : 'Choose a client...')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121215] border-[#27272A] max-h-72">
                    {filteredClients.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[#52525B]" data-testid="gate-client-empty">
                        {clients.length === 0 ? 'No clients found' : 'No matches'}
                      </div>
                    ) : filteredClients.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-[#F5F5F5] hover:bg-[#1A1A1E] focus:bg-[#1A1A1E] focus:text-[#F5F5F5] tf-cv-row" data-testid={`gate-client-${c.id}`}>
                        <span className="flex flex-col items-start">
                          <span>{c.name}</span>
                          <span className="text-[10px] text-[#52525B]">
                            {c.fit_preference && <span className="uppercase tracking-wider">{c.fit_preference}</span>}
                            {c.color_season && <span className="ml-2" style={{ color: storeSettings.primary_color || '#C2A878' }}>· {c.color_season}</span>}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => {
                if (selectedClientId) {
                  localStorage.setItem('selected_client_id', selectedClientId);
                  axios.delete(`${BACKEND_URL}/api/gallery/cleanup-orphans`, { withCredentials: true }).catch(() => {});
                  setShowClientGate(false);
                }
              }} disabled={!selectedClientId}
                className="w-full rounded-none py-3 uppercase tracking-widest text-xs font-semibold"
                style={{ backgroundColor: storeSettings.button_color || storeSettings.primary_color || '#C2A878', color: '#0A0A0C' }}
                data-testid="gate-enter-btn">
                Enter Studio
              </Button>
              {/* Small "+ Add new client" link under the Enter Studio button —
                  opens the 3-step intake modal (photo + fit). Stays subtle so
                  it doesn't compete with the primary CTA. */}
              <button
                onClick={() => setShowNewClientIntake(true)}
                className="mt-4 text-xs underline inline-flex items-center gap-1 hover:opacity-80"
                style={{ color: storeSettings.primary_color || '#C2A878' }}
                data-testid="gate-add-new-client"
              >
                <Plus className="w-3 h-3" /> Add new client
              </button>
              {/* Escape hatch: lets users back out of the gate when they've
                  landed on the wrong store / wrong account. Without this,
                  a user signed into a store that has no clients (or no
                  matching client) had no way to return to /login. */}
              <div className="mt-6 pt-4 border-t border-[#27272A]">
                <button
                  onClick={async () => {
                    // Clear cached client selection so next login starts clean
                    clearSelectionMarkers();
                    await logout();
                    window.location.href = '/login';
                  }}
                  className="text-[11px] uppercase tracking-widest text-[#71717A] hover:text-[#A1A1AA] transition-colors"
                  data-testid="gate-return-to-login"
                >
                  ← Return to login
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Iter 104 — Re-add the intake modal render INSIDE the gate's
            early-return. Iter 99 part 2 moved it to a global location
            at the bottom of Dashboard, but when the gate is active the
            component returns early at this point and never reaches that
            global render — so clicking "Add new client" from the gate
            opened the modal in state but it never appeared on screen.
            We render it in BOTH places: gate-local + global (under main
            studio). Both gate the same state, so only one ever shows. */}
        {showNewClientIntake && (
          <NewClientIntakeModal
            onClose={() => setShowNewClientIntake(false)}
            onCreated={async (newClient) => {
              setShowNewClientIntake(false);
              await fetchClients();
              setSelectedClientId(newClient.id);
              setSelectedClientSlug(newClient.slug || '');
              localStorage.setItem('selected_client_id', newClient.id);
              setShowClientGate(false);
              toast.success(`${newClient.name} ready · entering studio`);
            }}
            storeSettings={storeSettings}
          />
        )}
      </>
    );
  }

  // Inspirations panel (rendered inside each relevant tab's workspace).
  // Counts are computed inline so the panel header surfaces a live
  // "{ready}/{total} ready" progress chip — turning the auto-gen wait
  // from a "blank gray cards forever" feeling into a tactile, measurable
  // countdown. The chip fades out the instant all looks land.
  const inspReadyCount = currentInspirations.filter(i => inspResults[i.id]?.image).length;
  const inspTotalCount = currentInspirations.length;
  const inspAnyGenerating = currentInspirations.some(i => inspGenerating[i.id]);
  const inspirationsPanel = (
    <div className="bg-[#121215] border border-[#27272A] p-6" data-testid="inspiration-gallery">
      {/* Iter 232 (Feb 2026) — RETUNING BANNER. Shouts the wardrobe
          context change so the tailor immediately understands why the
          gallery just collapsed to skeletons. Auto-clears when every
          tile in the active category has landed (see effect below). */}
      {wardrobeRetuning && (
        <div
          className="flex items-center gap-3 mb-4 px-4 py-3 border"
          style={{
            backgroundColor: `${storeSettings.primary_color || '#C2A878'}18`,
            borderColor: storeSettings.primary_color || '#C2A878',
          }}
          data-testid="wardrobe-retuning-banner"
        >
          <Loader2 className="w-4 h-4 spinner flex-shrink-0" style={{ color: storeSettings.primary_color || '#C2A878' }} />
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: storeSettings.primary_color || '#C2A878' }}>
              Retuning wardrobe for {wardrobeRetuning}
            </p>
            <p className="text-[10px] text-[#A1A1AA] mt-0.5">
              Freshly-curated inspirations are generating — old tiles have been cleared.
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {activeCategory === 'wedding' ? 'Wedding Inspirations' : 'Style Inspirations'}
          </h3>
        </div>
        {clientImage && inspTotalCount > 0 && inspAnyGenerating && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 border border-[#27272A] bg-[#0A0A0C]"
            data-testid="inspiration-progress-chip"
          >
            <Loader2 className="w-3 h-3 spinner" style={{ color: storeSettings.primary_color || '#C2A878' }} />
            <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">
              <span style={{ color: storeSettings.primary_color || '#C2A878' }}>{inspReadyCount}</span> / {inspTotalCount} ready
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {currentInspirations.map(insp => {
          const result = inspResults[insp.id];
          const isGen = inspGenerating[insp.id];
          if (result?.image) {
            return (
              <div key={insp.id} className="group cursor-pointer" onClick={() => setSelectedLook(result)} data-testid={`inspiration-result-${insp.id}`}>
                <div className="aspect-[3/4] bg-[#1A1A1E] border border-[#27272A] overflow-hidden relative hover:border-[hsl(var(--accent))] transition-colors">
                  <img
                    src={srcFor(result.image)}
                    alt={insp.title}
                    loading="lazy"
                    data-testid={`inspiration-image-${insp.id}`}
                    className="w-full h-full object-contain object-center"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <p className="text-xs text-[#F5F5F5] font-medium">{insp.title}</p>
                  </div>
                  {/* Hover actions — Zoom + Delete. Iter 87 — the user
                      reported they could no longer delete style
                      inspirations now that the gallery sidebar has been
                      removed. Trash button is wired to the same
                      `deleteLook` helper used by every LookCard so the
                      DB row + sessionStorage cache + carousel state
                      all stay in sync. Confirm dialog prevents
                      accidental deletes since one click on an
                      inspiration tile would otherwise nuke a
                      multi-second Gemini render. */}
                  <div className="absolute top-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); setSelectedLook(result); }}
                      className="p-1.5 bg-black/60 text-white/80 hover:text-white hover:bg-black/80"
                      title="Zoom"
                      data-testid={`inspiration-zoom-${insp.id}`}>
                      <ZoomIn className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // eslint-disable-next-line no-alert
                        if (window.confirm(`Delete this ${insp.title} render? Tap the Generate button to render a fresh one.`)) {
                          deleteLook(result);
                        }
                      }}
                      className="p-1.5 bg-black/60 text-white/80 hover:text-red-300 hover:bg-red-950/80"
                      title="Delete this inspiration"
                      data-testid={`inspiration-delete-${insp.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          // Luxury skeleton: shown briefly during client switches while gallery
          // hydration is in flight. Prevents the jarring "Generate this look"
          // CTA flash on cached clients.
          if (result?.restoring) {
            return (
              <div key={insp.id} className="relative overflow-hidden border border-[#27272A]" data-testid={`inspiration-restoring-${insp.id}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${insp.gradient} opacity-30`}></div>
                <div className="relative p-5 flex flex-col items-center justify-center min-h-[160px]">
                  <Loader2 className="w-6 h-6 text-[hsl(var(--accent))] spinner mb-2" />
                  <h4 className="text-sm font-light text-[#F5F5F5] text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{insp.title}</h4>
                  <p className="text-[10px] text-[#A1A1AA] mt-1">Restoring…</p>
                </div>
              </div>
            );
          }
          if (isGen) {
            return (
              <div key={insp.id} className="relative overflow-hidden border border-[#27272A]">
                <div className={`absolute inset-0 bg-gradient-to-br ${insp.gradient} opacity-30`}></div>
                <div className="relative p-5 flex flex-col items-center justify-center min-h-[160px]">
                  <Loader2 className="w-6 h-6 text-[hsl(var(--accent))] spinner mb-2" />
                  <h4 className="text-sm font-light text-[#F5F5F5] text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{insp.title}</h4>
                  <p className="text-[10px] text-[#A1A1AA] mt-1">Generating...</p>
                </div>
              </div>
            );
          }
          if (inspFailed[insp.id]) {
            return (
              <button
                key={insp.id}
                type="button"
                onClick={() => generateInspiration(insp)}
                disabled={!canGenerate}
                data-testid={`inspiration-retry-${insp.id}`}
                className="relative overflow-hidden border border-[#27272A] hover:border-[hsl(var(--accent))] transition-colors text-left group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${insp.gradient} opacity-20`}></div>
                <div className="relative p-5 flex flex-col items-center justify-center min-h-[160px]">
                  <RefreshCw className="w-6 h-6 text-[hsl(var(--accent))] mb-2 group-hover:rotate-45 transition-transform" />
                  <h4 className="text-sm font-light text-[#F5F5F5] text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{insp.title}</h4>
                  <p className="text-[10px] text-[#ef4444] mt-1 uppercase tracking-wider">Tap to retry</p>
                </div>
              </button>
            );
          }
          return <InspirationCard key={insp.id} inspiration={insp} onGenerate={generateInspiration} disabled={!canGenerate} />;
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0C]">
      <Header />
      {/* Feb 2026 — platform-wide maintenance banner (renders only when
          Robson toggles maintenance mode from /admin). */}
      <MaintenanceBanner />
      {/* StudioBrandingOnboarding removed per user request — branding only applies to demo sessions */}
      <Toaster position="top-right" theme="dark" />
      <ExportBrandingModal
        open={exportModalOpen}
        loading={exportingPdf || exportingWeddingPdf}
        defaultName={storeSettings.store_name || ''}
        primaryColor={storeSettings.primary_color || '#C2A878'}
        title={exportModalMode === 'wedding' ? 'Brand this Wedding Lookbook' : 'Brand this Lookbook'}
        onClose={() => setExportModalOpen(false)}
        onConfirm={(payload) => {
          if (exportModalMode === 'wedding') runWeddingLookbookExport(payload);
          else runMainLookbookExport(payload);
        }}
      />
      <main className="pt-20 sm:pt-24 pb-12 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="mb-6 sm:mb-8 fade-in">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-[#F5F5F5] tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {(() => {
              const cname = clients.find(c => c.id === selectedClientId)?.name;
              if (cname) {
                const safe = cname.replace(/_/g, ' ');
                return (
                  <>
                    <span style={{ color: storeSettings.primary_color || '#C2A878' }}>{safe}</span>
                    <span className="text-[#F5F5F5]">{"'s Virtual Fitting"}</span>
                  </>
                );
              }
              return 'Virtual Fitting Studio';
            })()}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Clean Sidebar */}
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-[#121215] border border-[#27272A] p-5">
              <Label className="label-luxury mb-3 block">Client Photo</Label>
              <div className="aspect-square border border-dashed border-[#52525B] hover:border-[hsl(var(--accent))] bg-[#0A0A0C] cursor-pointer overflow-hidden relative group transition-colors"
                onClick={() => document.getElementById('client-photo-input').click()} data-testid="client-photo-upload">
                <input type="file" id="client-photo-input" accept="image/*" className="hidden" onChange={handleClientImage} />
                {clientImagePreview ? (
                  <><img src={clientImagePreview} alt="Client" className="w-full h-full object-cover" />
                  <button onClick={(e) => {
                    e.stopPropagation();
                    clientImageOwnerRef.current = selectedClientId || 'demo';
                    setClientImage(null);
                    setClientImagePreview(null);
                    // Purge BOTH storages so the durable localStorage copy
                    // doesn't re-hydrate the just-removed photo on next mount.
                    try { sessionStorage.removeItem(`client_photo_${selectedClientId || 'demo'}`); } catch { /* noop */ }
                    try { localStorage.removeItem(`client_photo_${selectedClientId || 'demo'}`); } catch { /* noop */ }
                  }} className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3 text-white" /></button></>
                ) : clientImageLoading ? (
                  /* Luxury shimmer skeleton while the new client's photo
                     fetches — prevents the blank-frame flash between
                     client switches. */
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#121215] via-[#1A1A1E] to-[#121215] photo-shimmer relative" data-testid="client-photo-loading">
                    <Loader2 className="w-7 h-7 text-[hsl(var(--accent))] spinner" />
                    <span className="text-[10px] text-[#A1A1AA] mt-3 uppercase tracking-wider">Loading photo…</span>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center"><User className="w-10 h-10 text-[#52525B] group-hover:text-[hsl(var(--accent))] transition-colors" /><span className="text-xs text-[#52525B] mt-2">Upload face photo</span></div>
                )}
              </div>
              {/* Optional FULL-BODY photo — small inline button beneath the face
                  photo. When present, gets passed alongside the face photo on
                  every generate call so Gemini matches the client's real body
                  composition (height, build, posture). */}
              <input type="file" id="client-body-photo-input" accept="image/*" className="hidden" onChange={handleBodyImage} />
              <button
                onClick={() => document.getElementById('client-body-photo-input').click()}
                className="mt-2 w-full flex items-center justify-between px-3 py-2 border border-[#27272A] hover:border-[hsl(var(--accent))] bg-[#0A0A0C] text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-[hsl(var(--accent))] transition-colors"
                data-testid="client-body-photo-upload"
                title="Optional full-body reference photo — used to match real body composition on renders"
              >
                <span>{bodyImage ? 'Full-body photo added' : '+ Add full-body photo (optional)'}</span>
                {bodyImage ? (
                  <span onClick={(e) => { e.stopPropagation(); setBodyImage(null); setBodyImagePreview(null); if (selectedClientId && /^[a-f0-9]{24}$/i.test(selectedClientId)) { axios.delete(`${BACKEND_URL}/api/clients/${selectedClientId}/body-photo`, { withCredentials: true }).catch(() => {}); } }} className="text-red-400 hover:text-red-300 cursor-pointer"><X className="w-3 h-3" /></span>
                ) : (
                  <span className="text-[#52525B]">▸</span>
                )}
              </button>
              {bodyImagePreview && (
                <div className="mt-2 aspect-[3/4] w-24 border border-[#27272A] bg-[#0A0A0C] overflow-hidden">
                  <img src={bodyImagePreview} alt="Full body" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="bg-[#121215] border border-[#27272A] p-5">
              <Label className="label-luxury mb-3 block">Client</Label>
              {(() => {
                // Prefer the client object from our full clients list (has the
                // authoritative name + all fields); fall back to the name
                // cached in localStorage so on a cold remount (e.g. returning
                // from the Gallery page) we display the correct client name
                // INSTANTLY instead of flashing "Walk-in (Demo)" while the
                // clients list is still fetching.
                const c = clients.find(cl => cl.id === selectedClientId);
                let cachedName = '';
                try { cachedName = localStorage.getItem('selected_client_name') || ''; } catch { /* noop */ }
                const name = c?.name?.replace(/_/g, ' ') || cachedName || 'Walk-in (Demo)';
                return (
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className="text-lg font-medium tracking-tight"
                      style={{
                        color: storeSettings.primary_color || '#C2A878',
                        fontFamily: 'Cormorant Garamond, serif',
                      }}
                      data-testid="workspace-client-name"
                    >
                      {name}
                    </div>
                    <button
                      onClick={() => {
                        // Don't wipe state here — just show the gate. The
                        // useEffect at the top of Dashboard (keyed on
                        // selectedClientId) handles the client-switch
                        // bookkeeping when the user actually picks a new
                        // client from the gate dropdown. Wiping here forced
                        // a "Generate this look" CTA flash that the
                        // restoring-skeleton fix was meant to eliminate.
                        setShowClientGate(true);
                      }}
                      className="text-[10px] uppercase tracking-wider text-[#52525B] hover:text-[#F5F5F5] transition-colors"
                      data-testid="switch-client-btn"
                    >
                      Switch
                    </button>
                  </div>
                );
              })()}
              {selectedClientSlug && (
                <div className="mt-3 space-y-2">
                  <a href={`/client/${selectedClientSlug}`} target="_blank" rel="noopener noreferrer" className="text-[10px] underline block" style={{ color: storeSettings.primary_color }} data-testid="client-card-link">
                    View Client Card
                  </a>
                </div>
              )}
            </div>

            {/* Iter 228 (Feb 2026) — Colour Analysis palette lives here,
                directly under the Client card. `ColorAnalysisCard` internally
                returns null when there's no selected client, so the block
                only appears once a client is chosen. `onAnalysis` feeds the
                lifted `colorAnalysis` state that drives the star-marking on
                the SUIT_COLORS grid inside the Customize modal. */}
            {selectedClientId && (
              <ColorAnalysisCard
                clientId={selectedClientId}
                onAnalysis={setColorAnalysis}
              />
            )}

            {/* Iter 231 (Feb 2026) — Wardrobe Context now renders as the
                CURRENTLY-SELECTED TILE in the store's logo colour, with a
                small "Change" link that opens a popout of the full tile
                picker (same grid used in intake Step 2). Replaces the
                iter 228 dropdown. */}
            {selectedClientId && (() => {
              const brand = storeSettings.primary_color || '#C2A878';
              // Feb 2026 — Change Fit popup helpers. Only used when the
              // tailor opens the sidebar's "Change Fit" popover; the tile
              // row inside the popup renders 3 fit previews on the
              // client's face (opt-in via autoRender=false + a manual
              // "Render previews" button, so opening a client card does
              // not burn Gemini automatically).
              const _client = clients.find(c => c.id === selectedClientId) || {};
              const _rawFit = _client.fit_preference || 'modern';
              const uiFit = (_rawFit === 'slim' || _rawFit === 'savile_row') ? _rawFit : 'modern';
              const persistFit = async (nextFit) => {
                if (nextFit === uiFit) return;
                // Sync the workspace `fit` state immediately so any
                // /api/generate-look call fired in the same tick uses
                // the freshly-picked fit — belt-and-braces alongside
                // the useEffect that also mirrors client.fit_preference.
                setFit(nextFit);
                try {
                  await axios.put(
                    `${BACKEND_URL}/api/clients/${selectedClientId}`,
                    { fit_preference: nextFit },
                    { withCredentials: true },
                  );
                  setClients(prev => prev.map(c => c.id === selectedClientId ? { ...c, fit_preference: nextFit } : c));
                  toast.success(`Fit updated — new looks will render as ${FIT_OPTIONS.find(f => f.value === nextFit)?.label || nextFit}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to update fit');
                }
              };
              const persistContext = async (v) => {
                // Iter 232 (Feb 2026) — DUPLICATE-LOOK FIX.
                // Backend soft-deletes the old context's inspiration
                // looks inside the PATCH. Mirror that on the client so
                // the gallery collapses to skeletons INSTANTLY instead
                // of holding the old-context tiles until the new ones
                // race in. Without this, users saw the previous 6 tiles
                // AND the new 6 tiles side-by-side (12 total, half of
                // them stale) while regeneration was in flight.
                setInspResults({});
                setInspGenerating({});
                setInspFailed({});
                setGeneratedLooks(prev => prev.filter(l => !l.isInspiration));
                setClientContext(v || null);
                if (v && contextLabel(v)) {
                  setWardrobeRetuning(contextLabel(v));
                } else {
                  setWardrobeRetuning(null);
                }
                try {
                  const resp = await axios.patch(
                    `${BACKEND_URL}/api/clients/${selectedClientId}/context`,
                    { context: v || null },
                    { withCredentials: true },
                  );
                  const cleared = resp?.data?.cleared_inspirations || 0;
                  setClients(prev => prev.map(c => c.id === selectedClientId ? { ...c, context: v || null } : c));
                  if (v) {
                    toast.success(
                      cleared > 0
                        ? `Retuning wardrobe for ${contextLabel(v)} — cleared ${cleared} stale look${cleared === 1 ? '' : 's'}`
                        : `Wardrobe tuned for ${contextLabel(v)}`,
                    );
                  } else {
                    toast.success(
                      cleared > 0
                        ? `Wardrobe context cleared — removed ${cleared} stale inspiration${cleared === 1 ? '' : 's'}`
                        : 'Wardrobe context cleared',
                    );
                  }
                } catch {
                  toast.error('Could not update context');
                  setWardrobeRetuning(null);
                }
              };
              const CurrentIcon = iconForContext(clientContext);
              return (
                <div
                  className="bg-[#121215] border border-[#27272A] p-5"
                  data-testid="client-context-chip-row"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Label className="label-luxury">Wardrobe Context</Label>
                    <button
                      type="button"
                      onClick={() => setWardrobeContextPopoverOpen(true)}
                      className="text-[10px] uppercase tracking-widest hover:opacity-80 transition-opacity underline underline-offset-2"
                      style={{ color: brand }}
                      data-testid="wardrobe-context-change-link"
                    >
                      Change
                    </button>
                  </div>

                  {clientContext ? (
                    // Selected → tile filled with the store's logo colour.
                    <button
                      type="button"
                      onClick={() => setWardrobeContextPopoverOpen(true)}
                      className="w-full flex flex-col items-center justify-center text-center gap-2 border py-5 px-3 min-h-[110px] transition-transform hover:scale-[1.01]"
                      style={{
                        backgroundColor: brand,
                        borderColor: brand,
                        color: '#0A0A0C',
                      }}
                      data-testid="wardrobe-context-selected-tile"
                    >
                      <CurrentIcon
                        className="w-7 h-7"
                        style={{ color: '#0A0A0C' }}
                        strokeWidth={1.5}
                      />
                      <span className="text-[11px] uppercase tracking-widest font-semibold leading-tight">
                        {contextLabel(clientContext)}
                      </span>
                    </button>
                  ) : (
                    // Empty state → dashed placeholder that also opens the popout.
                    <button
                      type="button"
                      onClick={() => setWardrobeContextPopoverOpen(true)}
                      className="w-full flex flex-col items-center justify-center text-center gap-2 border border-dashed border-[#3F3F46] py-5 px-3 min-h-[110px] hover:border-[hsl(var(--accent))] transition-colors"
                      data-testid="wardrobe-context-empty-tile"
                    >
                      <CurrentIcon
                        className="w-6 h-6"
                        style={{ color: brand }}
                        strokeWidth={1.5}
                      />
                      <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">
                        Pick an occupation or event
                      </span>
                    </button>
                  )}
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-[#52525B]">
                    Tunes inspiration tiles for the client's day-to-day
                  </p>

                  {/* Feb 2026 (user request) — Change Fit button lives
                      here, opens the fit picker in its own popup. Kept
                      OUT of the intake flow — this is the workspace path
                      for flipping an existing client's fit. */}
                  <button
                    type="button"
                    onClick={() => setFitPopoverOpen(true)}
                    className="mt-3 w-full text-[10px] uppercase tracking-widest border border-[#27272A] hover:border-[#C2A878]/60 hover:bg-[#1A1A1E] px-3 py-2 flex items-center justify-center gap-2 transition-colors"
                    style={{ color: brand }}
                    data-testid="sidebar-change-fit-btn"
                  >
                    Change Fit
                  </button>

                  {/* Popout modal — same tile grid as intake Step 2. */}
                  {wardrobeContextPopoverOpen && (
                    <div
                      className="fixed inset-0 bg-black/80 z-[130] flex items-center justify-center p-4"
                      onClick={() => setWardrobeContextPopoverOpen(false)}
                      data-testid="wardrobe-context-popover"
                    >
                      <div
                        className="bg-[#0A0A0C] border border-[#27272A] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between p-5 border-b border-[#27272A]">
                          <div>
                            <p
                              className="text-[10px] uppercase tracking-[0.25em] mb-1"
                              style={{ color: brand }}
                            >
                              Wardrobe Context
                            </p>
                            <h2
                              className="text-2xl font-light text-[#F5F5F5]"
                              style={{ fontFamily: 'Cormorant Garamond, serif' }}
                            >
                              Retune this client
                            </h2>
                          </div>
                          <button
                            onClick={() => setWardrobeContextPopoverOpen(false)}
                            className="text-[#71717A] hover:text-[#F5F5F5]"
                            data-testid="wardrobe-context-popover-close"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="p-6 space-y-6">
                          <ContextTilePicker
                            value={clientContext}
                            onChange={async (v) => {
                              await persistContext(v);
                              setWardrobeContextPopoverOpen(false);
                            }}
                            accent={brand}
                            activeColor={brand}
                            testidPrefix="sidebar-context-tile"
                          />
                          {clientContext && (
                            <button
                              type="button"
                              onClick={async () => {
                                await persistContext(null);
                                setWardrobeContextPopoverOpen(false);
                              }}
                              className="w-full text-[10px] uppercase tracking-widest text-[#71717A] hover:text-[#F5F5F5] py-2 border border-[#27272A]"
                              data-testid="wardrobe-context-clear"
                            >
                              Clear wardrobe context
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Feb 2026 — Change Fit popup. Same modal pattern as
                      the wardrobe-context popover, houses the 3-tile
                      FitPicker with previews rendered on the client's
                      face (opt-in via the "Render previews" button
                      inside FitPicker). Tapping a tile persists via
                      PATCH /api/clients/{id} and closes the popup. */}
                  {fitPopoverOpen && (
                    <div
                      className="fixed inset-0 bg-black/80 z-[130] flex items-center justify-center p-4"
                      onClick={() => setFitPopoverOpen(false)}
                      data-testid="fit-popover"
                    >
                      <div
                        className="bg-[#0A0A0C] border border-[#27272A] max-w-xl w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between p-5 border-b border-[#27272A]">
                          <div>
                            <p className="text-xs uppercase tracking-widest" style={{ color: brand }}>
                              Change Fit
                            </p>
                            <p className="text-[10px] uppercase tracking-widest text-[#52525B] mt-1">
                              Classic · Savile Row · Slim on a studio mannequin
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFitPopoverOpen(false)}
                            className="text-[#A1A1AA] hover:text-[#F5F5F5] text-2xl leading-none"
                            data-testid="fit-popover-close"
                          >
                            ×
                          </button>
                        </div>
                        <div className="p-5">
                          <FitPicker
                            options={FIT_OPTIONS}
                            value={uiFit}
                            onChange={async (nextFit) => {
                              await persistFit(nextFit);
                              setFitPopoverOpen(false);
                            }}
                            clientImage={clientImage}
                            bodyImage={bodyImage}
                            accent={brand}
                            testidPrefix="fit-popover"
                            autoRender={false}
                            preloadedRenders={fitPreviewCache[selectedClientId] || null}
                          />
                          <p className="mt-4 text-[10px] uppercase tracking-widest text-[#52525B]">
                            Changing the fit doesn't re-render existing looks — only affects new generations
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6">
            <Tabs value={activeCategory} onValueChange={(val) => {
              setActiveCategory(val);
            }}>
              <TabsList className="w-full bg-[#121215] border border-[#27272A] p-1 mb-6 grid grid-cols-6 rounded-none h-auto">
                {!disabledSubcategories.includes('full_suits') && <TabsTrigger value="full_suits" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[#0A0A0C] text-[#A1A1AA] rounded-none py-3 flex items-center gap-2" data-testid="tab-full_suits"><Shirt className="w-4 h-4" /><span className="hidden sm:inline text-xs uppercase tracking-wider">Full Suits</span></TabsTrigger>}
                {!disabledSubcategories.includes('business_casual') && <TabsTrigger value="business_casual" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[#0A0A0C] text-[#A1A1AA] rounded-none py-3 flex items-center gap-2" data-testid="tab-business_casual"><Coffee className="w-4 h-4" /><span className="hidden sm:inline text-xs uppercase tracking-wider">Business Casual</span></TabsTrigger>}
                {!disabledSubcategories.includes('shirts') && <TabsTrigger value="shirts" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[#0A0A0C] text-[#A1A1AA] rounded-none py-3 flex items-center gap-2" data-testid="tab-shirts"><Tag className="w-4 h-4" /><span className="hidden sm:inline text-xs uppercase tracking-wider">Shirts</span></TabsTrigger>}
                {!disabledSubcategories.includes('outerwear') && <TabsTrigger value="outerwear" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[#0A0A0C] text-[#A1A1AA] rounded-none py-3 flex items-center gap-2" data-testid="tab-outerwear"><Layers className="w-4 h-4" /><span className="hidden sm:inline text-xs uppercase tracking-wider">Outerwear</span></TabsTrigger>}
                {!disabledSubcategories.includes('wedding') && <TabsTrigger value="wedding" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[#0A0A0C] text-[#A1A1AA] rounded-none py-3 flex items-center gap-2" data-testid="tab-wedding"><Heart className="w-4 h-4" /><span className="hidden sm:inline text-xs uppercase tracking-wider">Wedding</span></TabsTrigger>}
                {!disabledSubcategories.includes('custom_fabric') && <TabsTrigger value="custom_fabric" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[#0A0A0C] text-[#A1A1AA] rounded-none py-3 flex items-center gap-2" data-testid="tab-custom_fabric"><Palette className="w-4 h-4" /><span className="hidden sm:inline text-xs uppercase tracking-wider">Custom Fabric</span></TabsTrigger>}
              </TabsList>

              {/* FULL SUITS */}
              <TabsContent value="full_suits" className="mt-0 space-y-4">
                {inspirationsPanel}
                {/* Essential 5 */}
                <div className="bg-[#121215] border border-[#27272A] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div><h3 className="text-xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Essentials</h3></div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {getCategoryLooks('full_suits').map((look, idx) => (
                      <LookCard key={look.id} look={look} priority={idx < 5} onClick={setSelectedLook} isGenerating={false} onZoom={setSelectedLook} onDelete={deleteLook} onRetry={retryFailedCombo} />
                    ))}
                    {isCatGenerating('full_suits') && Array.from({ length: Math.max(0, 5 - generatedLooks.filter(l => l.category === 'full_suits').length) }).map((_, i) => (
                      <LookCard key={`gen-${i}`} look={{ label: 'Generating...' }} onClick={noopClick} isGenerating={true} onZoom={null} />
                    ))}
                  </div>
                  {!isGenerating && getCategoryLooks('full_suits').length === 0 && (
                    <div className="text-center py-12"><Shirt className="w-10 h-10 text-[#52525B] mx-auto mb-3" /><p className="text-sm text-[#A1A1AA]">{clientImage ? 'Looks will auto-generate shortly...' : 'Upload a client photo to auto-generate the Essentials'}</p></div>
                  )}
                  {/* "Generate New Looks" button removed per product decision (Feb 2026).
                      The Essential 5 grid is now a fixed curated set; broader exploration
                      happens via the style subcategory accordions below. */}
                  {getCategoryLooks('full_suits').length >= 10 && (
                    <p className="text-[10px] text-[#52525B] uppercase tracking-wider text-center mt-4 pt-4 border-t border-[#27272A]">
                      Collection full — 10 suits per category maximum
                    </p>
                  )}
                </div>
                {/* Style subcategory generators (LAZY — content generates on first expand) */}
                {(() => {
                  // Gender-aware subcategory swap (Feb 2026) — when the
                  // selected client is female, surface the women's
                  // tailored pant-suit / skirt-suit / sheath / Le Smoking
                  // subcategories instead of the men's house collections.
                  // Falls back to the men's pool when gender is missing
                  // or 'male'.
                  const _cg = (clients.find(c => c.id === selectedClientId)?.gender || '').toLowerCase();
                  const _src = _cg === 'female' ? WOMENS_STYLE_SUBCATEGORIES : STYLE_SUBCATEGORIES;
                  return _src;
                })().filter(s => {
                  // Feb 2026 — Template Looks is a special read-only tab:
                  //   • bypasses the store's enabled_subcategories whitelist
                  //     (so tenants that never opted-in still see it), and
                  //   • only appears once at least one template has been
                  //     applied to THIS client (never a bare empty tab).
                  if (s.is_template) {
                    return generatedLooks.some(l => l.category === `full_suits_${s.value}`);
                  }
                  const wl = storeSettings?.enabled_subcategories?.full_suits;
                  return !Array.isArray(wl) || wl.length === 0 || wl.includes(s.value);
                }).map(subcat => {
                  const cat = `full_suits_${subcat.value}`;
                  const subcatLooks = dedupeById(generatedLooks.filter(l => l.category === cat));
                  const isGenSubcat = isCatGenerating(cat);
                  // React-tracked open state — replaces the old DOM classList
                  // toggle. Looks-present OR currently-generating implies open
                  // (so a refresh / tab switch doesn't hide in-flight panels).
                  const isOpen = isSubcatOpen(cat) || subcatLooks.length > 0 || isGenSubcat;
                  return (
                    <div key={subcat.value} data-scenario={subcat.value} className="bg-[#121215] border border-[#27272A]">
                      <div className={`flex items-center justify-between px-4 sm:px-5 py-4 ${isDemoStore ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:bg-[#1A1A1E]'} transition-colors`} onClick={() => {
                        if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
                        toggleSubcat(cat);
                        // Lazy-load: only fire generations on first expand.
                        // Template Looks is a read-only tab — never auto-generate.
                        if (!subcat.is_template) ensureSubcatGenerated(subcat);
                      }}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="inline-block w-1.5 h-1.5 bg-[hsl(var(--accent))] shrink-0" />
                          <span className="text-sm font-medium text-[#F5F5F5] truncate">{subcat.label}</span>
                          <span className="hidden md:inline text-[10px] text-[#52525B] truncate">{subcat.desc}</span>
                          {subcatLooks.length > 0 && <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] shrink-0">{subcatLooks.length}</span>}
                          {isDemoStore && <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#C2A878]/40 text-[#C2A878] shrink-0">Premium</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isGenSubcat && <Loader2 className="w-3 h-3 spinner text-[hsl(var(--accent))]" />}
                          {isDemoStore
                            ? <Lock className="w-4 h-4 text-[#C2A878]" data-testid={`full-suits-subcat-${subcat.value}-lock`} />
                            : <svg className={`w-4 h-4 text-[#52525B] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          }
                        </div>
                      </div>
                      <div data-panel="true" className={isOpen ? 'px-4 pb-4' : 'hidden px-4 pb-4'}>
                        {subcatLooks.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {subcatLooks.map((look, idx) => <LookCard key={look.id} look={look} priority={idx < 5} onClick={setSelectedLook} isGenerating={false} onZoom={handleZoomLook} onDelete={deleteLook} onRetry={retryFailedCombo} />)}
                          </div>
                        ) : subcat.is_template ? (
                          <p className="text-xs text-[#52525B] text-center py-6">
                            No suits seeded from templates yet. Apply a template from /admin/templates.
                          </p>
                        ) : clientImage ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {[1,2,3,4,5].map(i => <LookCard key={i} look={{ label: 'Generating...' }} onClick={noopClick} isGenerating={true} />)}
                          </div>
                        ) : (
                          <p className="text-xs text-[#52525B] text-center py-6">{`Upload a client photo to auto-generate 5 ${subcat.label.toLowerCase()} suits`}</p>
                        )}
                        {!subcat.is_template && subcatLooks.length >= 5 && subcatLooks.length < 10 && !isGenSubcat && (
                          <div className="mt-4 pt-4 border-t border-[#27272A] flex justify-center">
                            <Button
                              onClick={() => generateComboLooks(generateSubcategoryCombos(subcat, generatedLooksRef.current), `full_suits_${subcat.value}`, { creativeBoost: true })}
                              disabled={!canGenerate}
                              size="sm"
                              className="rounded-none px-5 py-2 uppercase tracking-widest text-[10px] font-semibold"
                              style={btnStyle}
                              data-testid={`generate-new-${subcat.value}-btn`}
                            >
                              <Sparkles className="w-3 h-3 mr-2" /> Generate New Looks
                            </Button>
                          </div>
                        )}
                        {!subcat.is_template && subcatLooks.length >= 8 && (
                          <p className="text-[10px] text-[#52525B] uppercase tracking-wider text-center mt-3 pt-3 border-t border-[#27272A]">
                            Collection full — 8 {subcat.label.toLowerCase()} max
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              {/* BUSINESS CASUAL — 4 scenario dropdowns */}
              <TabsContent value="business_casual" className="mt-0 space-y-4">
                {inspirationsPanel}
                <div className="bg-[#121215] border border-[#27272A] p-6">
                  <h3 className="text-xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Business Casual</h3>
                  <p className="text-xs text-[#A1A1AA] mt-1">Choose a scenario — fun sports coats and blazers with chinos or jeans</p>
                </div>
                {(() => {
                  // Gender-aware BC scenario swap (Feb 2026) — female
                  // clients see the Wrap Dress + Midi-Skirt-Blouse
                  // women's scenarios instead of the men's Smart Friday
                  // / Weekend Brunch / Date Night pool.
                  const _cg = (clients.find(c => c.id === selectedClientId)?.gender || '').toLowerCase();
                  const _src = _cg === 'female' ? WOMENS_BC_SCENARIOS : BC_SCENARIOS;
                  return _src;
                })().filter(s => {
                  // Feb 2026 — Template Looks: bypass store whitelist and
                  // hide until a template has actually seeded this client.
                  if (s.is_template) {
                    return generatedLooks.some(l => l.category === `bc_${s.value}`);
                  }
                  const wl = storeSettings?.enabled_subcategories?.business_casual;
                  return !Array.isArray(wl) || wl.length === 0 || wl.includes(s.value);
                }).map(scenario => {
                  const cat = `bc_${scenario.value}`;
                  const scenarioLooks = dedupeById(generatedLooks.filter(l => l.category === cat));
                  const isGenScenario = isCatGenerating(cat);
                  const isOpen = isSubcatOpen(cat) || scenarioLooks.length > 0 || isGenScenario;
                  return (
                    <div key={scenario.value} data-scenario={scenario.value} className="bg-[#121215] border border-[#27272A]">
                      <div className={`flex items-center justify-between px-4 sm:px-5 py-4 ${isDemoStore ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:bg-[#1A1A1E]'} transition-colors`} data-testid={`bc-scenario-${scenario.value}-header`} onClick={() => {
                        if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
                        toggleSubcat(cat);
                        // Template Looks is read-only — never auto-generate.
                        if (!scenario.is_template) ensureBcScenarioGenerated(scenario);
                      }}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="inline-block w-1.5 h-1.5 bg-[hsl(var(--accent))] shrink-0" />
                          <span className="text-sm font-medium text-[#F5F5F5] truncate">{scenario.label}</span>
                          <span className="hidden md:inline text-[10px] text-[#52525B] truncate">{scenario.desc}</span>
                          {scenarioLooks.length > 0 && <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] shrink-0">{scenarioLooks.length}</span>}
                          {isDemoStore && <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#C2A878]/40 text-[#C2A878] shrink-0">Premium</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isGenScenario && <Loader2 className="w-3 h-3 spinner text-[hsl(var(--accent))]" />}
                          {isDemoStore
                            ? <Lock className="w-4 h-4 text-[#C2A878]" data-testid={`bc-scenario-${scenario.value}-lock`} />
                            : <svg className={`w-4 h-4 text-[#52525B] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          }
                        </div>
                      </div>
                      <div data-panel="true" className={isOpen ? 'px-4 pb-4' : 'hidden px-4 pb-4'}>
                        {scenarioLooks.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {scenarioLooks.map((look, idx) => <LookCard key={look.id} look={look} priority={idx < 5} onClick={setSelectedLook} isGenerating={false} onZoom={handleZoomLook} onDelete={deleteLook} onRetry={retryFailedCombo} />)}
                          </div>
                        ) : scenario.is_template ? (
                          <p className="text-xs text-[#52525B] text-center py-6">
                            No business casual looks seeded from templates yet. Apply a template from /admin/templates.
                          </p>
                        ) : clientImage ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {[1,2,3,4,5].map(i => <LookCard key={i} look={{ label: 'Generating...' }} onClick={noopClick} isGenerating={true} />)}
                          </div>
                        ) : (
                          <p className="text-xs text-[#52525B] text-center py-6">{`Upload a client photo to auto-generate 5 ${scenario.label.toLowerCase()} looks`}</p>
                        )}
                        {!scenario.is_template && scenarioLooks.length >= 5 && scenarioLooks.length < 10 && !isGenScenario && (
                          <div className="mt-4 pt-4 border-t border-[#27272A] flex justify-center">
                            <Button
                              onClick={() => {
                                // Recolour the scenario combos with random jacket/pant shuffles for new variations
                                const variants = scenario.combos.map((c, idx) => {
                                  const newSuit = SUIT_COLORS_POOL[(idx + Math.floor(Math.random() * SUIT_COLORS_POOL.length)) % SUIT_COLORS_POOL.length];
                                  return { ...c, suit: newSuit.value, label: `${newSuit.name} ${c.garment}` };
                                }).sort(() => Math.random() - 0.5);
                                generateComboLooks(variants, `bc_${scenario.value}`, { creativeBoost: true });
                              }}
                              disabled={!canGenerate}
                              size="sm"
                              className="rounded-none px-5 py-2 uppercase tracking-widest text-[10px] font-semibold"
                              style={btnStyle}
                              data-testid={`generate-new-bc-${scenario.value}-btn`}
                            >
                              <Sparkles className="w-3 h-3 mr-2" /> Generate New Looks
                            </Button>
                          </div>
                        )}
                        {scenarioLooks.length >= 10 && (
                          <p className="text-[10px] text-[#52525B] uppercase tracking-wider text-center mt-3 pt-3 border-t border-[#27272A]">
                            Collection full — 10 {scenario.label.toLowerCase()} looks max
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              {/* SHIRTS — inspiration cards + accordion subcategories (essential dress, long-sleeve polos, untucked patterns, golf) */}
              <TabsContent value="shirts" className="mt-0 space-y-4">
                {inspirationsPanel}
                <div className="bg-[#121215] border border-[#27272A] p-6">
                  <h3 className="text-xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Shirts</h3>
                  <p className="text-xs text-[#A1A1AA] mt-1">Essential dress shirts · stripe & pattern shirts · casual shirts (polos & tees) — all TUCKED IN</p>
                </div>
                {SHIRT_SUBCATEGORIES.filter(s => {
                  // Feb 2026 — Template Looks: bypass store whitelist and
                  // hide until a template has actually seeded this client.
                  if (s.is_template) {
                    return generatedLooks.some(l => l.category === `shirts_${s.value}`);
                  }
                  const wl = storeSettings?.enabled_subcategories?.shirts;
                  return !Array.isArray(wl) || wl.length === 0 || wl.includes(s.value);
                }).map(subcat => {
                  const subcatLooks = dedupeById(generatedLooks.filter(l => l.category === `shirts_${subcat.value}`));
                  const isGenSubcat = isCatGenerating(`shirts_${subcat.value}`);
                  const isOpen = subcatLooks.length > 0 || isGenSubcat;
                  // Feb 2026 — Casual Shirts + Sweaters + Dress Shirt
                  // Builder are UNLOCKED for demo stores (each capped
                  // at 3 lifetime renders). Every other shirt subcat
                  // stays locked behind Premium.
                  const _demoUnlocked = subcat.value === 'casual_shirts' || subcat.value === 'sweaters' || subcat.value === 'dress_shirts';
                  const _headerLocked = isDemoStore && !_demoUnlocked;
                  return (
                    <div key={subcat.value} data-shirt-subcat={subcat.value} className="bg-[#121215] border border-[#27272A]">
                      <div className={`flex items-center justify-between px-4 sm:px-5 py-4 ${_headerLocked ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:bg-[#1A1A1E]'} transition-colors`} data-testid={`shirts-subcat-${subcat.value}-header`} onClick={(e) => {
                        if (_headerLocked) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
                        const content = e.currentTarget.nextElementSibling;
                        const wasHidden = content?.classList.contains('hidden');
                        if (content) content.classList.toggle('hidden');
                        e.currentTarget.querySelector('[data-chevron]')?.classList.toggle('rotate-180');
                        // Template Looks is read-only — never auto-generate.
                        if (wasHidden && !subcat.is_template) ensureShirtSubcatGenerated(subcat);
                      }}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="inline-block w-1.5 h-1.5 bg-[hsl(var(--accent))] shrink-0" />
                          <span className="text-sm font-medium text-[#F5F5F5] truncate">{subcat.label}</span>
                          <span className="hidden md:inline text-[10px] text-[#52525B] truncate">{subcat.desc}</span>
                          {subcatLooks.length > 0 && <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] shrink-0">{subcatLooks.length}</span>}
                          {_headerLocked && <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#C2A878]/40 text-[#C2A878] shrink-0">Premium</span>}
                          {isDemoStore && _demoUnlocked && (
                            <span
                              className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] shrink-0"
                              title="Demo accounts can render 3 of these looks per client — upgrade to unlock unlimited"
                              data-testid={`shirts-subcat-${subcat.value}-demo-quota`}
                            >
                              Demo · {Math.max(0, 3 - subcatLooks.length)} left
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isGenSubcat && <Loader2 className="w-3 h-3 spinner text-[hsl(var(--accent))]" />}
                          {_headerLocked
                            ? <Lock className="w-4 h-4 text-[#C2A878]" data-testid={`shirts-subcat-${subcat.value}-lock`} />
                            : <svg data-chevron="true" className={`w-4 h-4 text-[#52525B] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          }
                        </div>
                      </div>
                      <div data-panel="true" className={subcatLooks.length > 0 || isGenSubcat ? 'px-4 pb-4' : 'hidden px-4 pb-4'}>
                        {(subcatLooks.length > 0 || isGenSubcat) ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {/* Existing renders stay put — new renders
                                never wipe them out. */}
                            {subcatLooks.map((look, idx) => <LookCard key={look.id} look={look} priority={idx < 5} onClick={setSelectedLook} isGenerating={false} onZoom={handleZoomLook} onDelete={deleteLook} onRetry={retryFailedCombo} />)}
                            {/* Feb 2026 — loading card "takes its spot"
                                right at the tail of the grid so the
                                tailor sees exactly where the fresh
                                render will land. Picker subcats fire
                                one combo at a time (1 loading card);
                                auto-gen subcats fill loading spots for
                                every remaining combo in the pack. */}
                            {isGenSubcat && (() => {
                              const _target = subcat.combos?.length || 5;
                              const _pending = subcat.picker
                                ? 1
                                : Math.max(0, _target - subcatLooks.length);
                              return Array.from({ length: _pending }).map((_, i) => (
                                <LookCard
                                  key={`gen-${subcat.value}-${i}`}
                                  look={{ label: 'Generating...' }}
                                  onClick={noopClick}
                                  isGenerating={true}
                                />
                              ));
                            })()}
                          </div>
                        ) : subcat.is_template ? (
                          <p className="text-xs text-[#52525B] text-center py-6">
                            No shirts seeded from templates yet. Apply a template from /admin/templates.
                          </p>
                        ) : subcat.picker ? (
                          <div className="text-center py-6 space-y-3">
                            <p className="text-xs text-[#A1A1AA]">Pick a style, colours and shoes — we&apos;ll render one editorial look.</p>
                            <button
                              type="button"
                              onClick={() => {
                                if (subcat.value === 'sweaters') setShowSweaterQ(true);
                                else if (subcat.value === 'dress_shirts') setShowDressShirtsQ(true);
                                else setShowCasualShirtsQ(true);
                              }}
                              disabled={!clientImage}
                              title={!clientImage ? 'Upload a client photo first' : undefined}
                              className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))]/20 disabled:opacity-40"
                              data-testid={`shirts-subcat-${subcat.value}-open-picker`}
                            >
                              Open Style Picker
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-[#52525B] text-center py-6">{clientImage ? 'Auto-generating...' : `Upload a client photo to auto-generate ${subcat.combos?.length || 5} ${subcat.label.toLowerCase()}`}</p>
                        )}
                        {/* Iter 129i — dedicated "+ New Casual Shirt Look" CTA
                            for picker subcats so the tailor always has an
                            obvious way to add another render even after
                            looks already exist. Feb 2026 — the button is
                            HIDDEN while a render is in flight (the tail
                            loading card in the grid above does the
                            "queued" signalling), so the tailor can't
                            accidentally stack a second render on top.
                            No cap on how many total renders can be
                            queued sequentially — casual_shirts and
                            sweaters accept unlimited looks. */}
                        {subcat.picker && subcatLooks.length > 0 && !isGenSubcat && (
                          <div className="mt-4 pt-4 border-t border-[#27272A] flex justify-center">
                            <button
                              type="button"
                              onClick={() => {
                                if (subcat.value === 'sweaters') setShowSweaterQ(true);
                                else if (subcat.value === 'dress_shirts') setShowDressShirtsQ(true);
                                else setShowCasualShirtsQ(true);
                              }}
                              disabled={!clientImage || isGenerating}
                              title={!clientImage ? 'Upload a client photo first' : undefined}
                              className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))]/20 disabled:opacity-40"
                              data-testid={`shirts-subcat-${subcat.value}-add-look`}
                            >
                              <span className="text-base leading-none">+</span>
                              {subcat.value === 'sweaters' ? 'Generate Another Sweater Look' : 'Generate Another Casual Shirt Look'}
                            </button>
                          </div>
                        )}
                        {subcatLooks.length >= 8 && (
                          <p className="text-[10px] text-[#52525B] uppercase tracking-wider text-center mt-3 pt-3 border-t border-[#27272A]">
                            Collection full — 10 {subcat.label.toLowerCase()} max
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>


              {/* OUTERWEAR — one accordion per coat type, same layout as Full Suits style subcategories */}
              <TabsContent value="outerwear" className="mt-0 space-y-4">
                {inspirationsPanel}
                <div className="bg-[#121215] border border-[#27272A] p-6">
                  <h3 className="text-xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Outerwear</h3>
                  <p className="text-xs text-[#A1A1AA] mt-1">Tailored coats — overcoats, car coats, trench coats, mandarin coats and peacoats</p>
                </div>
                {OUTERWEAR_SUBCATEGORIES.filter(s => {
                  const wl = storeSettings?.enabled_subcategories?.outerwear;
                  return !Array.isArray(wl) || wl.length === 0 || wl.includes(s.value);
                }).map(subcat => {
                  const subcatLooks = dedupeById(generatedLooks.filter(l => l.category === `outerwear_${subcat.value}`));
                  const isGenSubcat = isCatGenerating(`outerwear_${subcat.value}`);
                  const isOpen = subcatLooks.length > 0 || isGenSubcat;
                  return (
                    <div key={subcat.value} data-outerwear={subcat.value} className="bg-[#121215] border border-[#27272A]">
                      <div className={`flex items-center justify-between px-4 sm:px-5 py-4 ${isDemoStore ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:bg-[#1A1A1E]'} transition-colors`} data-testid={`outerwear-subcat-${subcat.value}-header`} onClick={(e) => {
                        if (isDemoStore) { toast.info('Subcategory generation is a premium feature — upgrade to unlock.'); return; }
                        const content = e.currentTarget.nextElementSibling;
                        const wasHidden = content?.classList.contains('hidden');
                        if (content) content.classList.toggle('hidden');
                        e.currentTarget.querySelector('[data-chevron]')?.classList.toggle('rotate-180');
                        // Lazy-load on first expand.
                        if (wasHidden) ensureOuterwearSubcatGenerated(subcat);
                      }}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="inline-block w-1.5 h-1.5 bg-[hsl(var(--accent))] shrink-0" />
                          <span className="text-sm font-medium text-[#F5F5F5] truncate">{subcat.label}</span>
                          <span className="hidden md:inline text-[10px] text-[#52525B] truncate">{subcat.desc}</span>
                          {subcatLooks.length > 0 && <span className="text-[10px] px-2 py-0.5 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] shrink-0">{subcatLooks.length}</span>}
                          {isDemoStore && <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#C2A878]/40 text-[#C2A878] shrink-0">Premium</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isGenSubcat && <Loader2 className="w-3 h-3 spinner text-[hsl(var(--accent))]" />}
                          {isDemoStore
                            ? <Lock className="w-4 h-4 text-[#C2A878]" data-testid={`outerwear-subcat-${subcat.value}-lock`} />
                            : <svg data-chevron="true" className={`w-4 h-4 text-[#52525B] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          }
                        </div>
                      </div>
                      <div data-panel="true" className={subcatLooks.length > 0 || isGenSubcat ? 'px-4 pb-4' : 'hidden px-4 pb-4'}>
                        {subcatLooks.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {subcatLooks.map((look, idx) => <LookCard key={look.id} look={look} priority={idx < 5} onClick={setSelectedLook} isGenerating={false} onZoom={handleZoomLook} onDelete={deleteLook} onRetry={retryFailedCombo} />)}
                          </div>
                        ) : isGenSubcat ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {[1,2,3,4,5].map(i => <LookCard key={i} look={{ label: 'Generating...' }} onClick={noopClick} isGenerating={true} />)}
                          </div>
                        ) : (
                          <p className="text-xs text-[#52525B] text-center py-6">{clientImage ? 'Auto-generating...' : `Upload a client photo to auto-generate 5 ${subcat.label.toLowerCase()}`}</p>
                        )}
                        {subcatLooks.length >= 5 && subcatLooks.length < 10 && !isGenSubcat && (
                          <div className="mt-4 pt-4 border-t border-[#27272A] flex justify-center">
                            <Button
                              onClick={() => {
                                const variants = subcat.combos.map((c, idx) => {
                                  const newSuit = SUIT_COLORS_POOL[(idx + Math.floor(Math.random() * SUIT_COLORS_POOL.length)) % SUIT_COLORS_POOL.length];
                                  return { ...c, suit: newSuit.value, label: `${newSuit.name} ${c.garment}` };
                                }).sort(() => Math.random() - 0.5);
                                generateComboLooks(variants, `outerwear_${subcat.value}`, { creativeBoost: true });
                              }}
                              disabled={!canGenerate}
                              size="sm"
                              className="rounded-none px-5 py-2 uppercase tracking-widest text-[10px] font-semibold"
                              style={btnStyle}
                              data-testid={`generate-new-outerwear-${subcat.value}-btn`}
                            >
                              <Sparkles className="w-3 h-3 mr-2" /> Generate New Looks
                            </Button>
                          </div>
                        )}
                        {subcatLooks.length >= 8 && (
                          <p className="text-[10px] text-[#52525B] uppercase tracking-wider text-center mt-3 pt-3 border-t border-[#27272A]">
                            Collection full — 8 {subcat.label.toLowerCase()} max
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              {/* WEDDING */}
              <TabsContent value="wedding" className="mt-0 space-y-4">
                {!isWeddingSetupComplete ? (
                  /* Wedding gateway (Feb 2026) — block access until couple
                     identity is captured. Prevents accidental wedding
                     renders without the names + date that drive the PDF
                     cover, seasonal emails and palette inspiration. */
                  <div className="relative bg-gradient-to-br from-[#121215] to-[#0A0A0C] border border-[hsl(var(--accent))]/30 p-8 text-center overflow-hidden" data-testid="wedding-setup-cta">
                    <div
                      className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-15"
                      style={{ backgroundColor: 'hsl(var(--accent))' }}
                    />
                    <div className="relative max-w-xl mx-auto">
                      <div
                        className="inline-flex items-center justify-center w-14 h-14 border mb-4 mx-auto"
                        style={{ borderColor: 'hsl(var(--accent))', backgroundColor: 'hsl(var(--accent) / 0.08)' }}
                      >
                        <Heart className="w-6 h-6" style={{ color: 'hsl(var(--accent))' }} />
                      </div>
                      <h3 className="text-3xl font-light text-[#F5F5F5] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        Set up the Wedding
                      </h3>
                      <p className="text-sm text-[#A1A1AA] mb-6 leading-relaxed">
                        Capture the couple and the date before generating wedding looks — these details flow through to every render, the seasonal emails, and the final lookbook PDF cover.
                      </p>
                      <Button
                        onClick={() => setShowWeddingSetup(true)}
                        className="rounded-none uppercase tracking-wider text-xs px-8 py-5"
                        style={btnStyle}
                        data-testid="open-wedding-setup"
                      >
                        <Sparkles className="w-4 h-4 mr-2" /> Begin Wedding Setup
                      </Button>
                      <p className="text-[10px] text-[#52525B] mt-4 uppercase tracking-wider">
                        Required · Groom · Bride · Wedding Date
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Quick-glance couple summary + edit affordance once
                        wedding setup is complete. Sits above the standard
                        inspirations panel so the tailor always sees the
                        couple they're working with. */}
                    <div className="bg-[#121215] border border-[#27272A] px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="wedding-setup-summary">
                      <div className="flex items-center gap-3 min-w-0">
                        <Heart className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(var(--accent))' }} />
                        <div className="min-w-0">
                          <p className="text-sm text-[#F5F5F5] truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                            {weddingSetup.groom_name} <span className="opacity-50">&amp;</span> {weddingSetup.bride_name}
                          </p>
                          <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mt-0.5">
                            {(() => {
                              try {
                                const d = new Date(weddingSetup.wedding_date);
                                return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
                              } catch { return weddingSetup.wedding_date; }
                            })()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(weddingSetup.wedding_colours || []).map(hex => (
                          <div key={hex} className="w-5 h-5 border border-[#27272A]" style={{ backgroundColor: hex }} title={hex} />
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowWeddingSetup(true)}
                          className="text-[#A1A1AA] hover:text-[#F5F5F5] hover:bg-[#1A1A1E] text-xs h-8 rounded-none uppercase tracking-wider"
                          data-testid="edit-wedding-setup"
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                    {inspirationsPanel}
                    {/* Wedding Colour Inspiration — 5 looks built from the
                        palette captured in WeddingSetupModal. Sits below
                        the general wedding inspirations panel. */}
                    {(weddingSetup.wedding_colours || []).length > 0 && (() => {
                      const colourLooks = dedupeById(generatedLooks.filter(l => l.category === 'wedding_colour_inspo'));
                      const isGenColour = isCatGenerating('wedding_colour_inspo');
                      return (
                        <div className="bg-[#121215] border border-[#27272A]" data-testid="wedding-colour-inspo">
                          <div className="flex items-center justify-between px-4 sm:px-5 py-4 flex-wrap gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <h3 className="text-base font-medium text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                                  Your Wedding Colour Inspiration
                                </h3>
                                <div className="flex items-center gap-1.5">
                                  {(weddingSetup.wedding_colours || []).map(hex => (
                                    <div key={hex} className="w-4 h-4 border border-[#27272A]" style={{ backgroundColor: hex }} title={hex} />
                                  ))}
                                </div>
                              </div>
                              <p className="text-[10px] text-[#52525B] mt-1 uppercase tracking-wider">
                                5 wedding-suit silhouettes rendered in the couple&apos;s chosen palette
                              </p>
                            </div>
                            <Button
                              onClick={generateWeddingColourLooks}
                              disabled={!clientImage || isGenColour || colourLooks.length >= 5}
                              size="sm"
                              className="rounded-none uppercase tracking-wider text-xs h-9"
                              style={btnStyle}
                              data-testid="generate-wedding-colour-inspo"
                            >
                              {isGenColour ? <Loader2 className="w-3.5 h-3.5 spinner mr-2" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
                              {colourLooks.length >= 5 ? 'Done' : (isGenColour ? 'Rendering…' : 'Generate 5 Looks')}
                            </Button>
                          </div>
                          <div className="px-4 pb-4">
                            {colourLooks.length > 0 ? (
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                {colourLooks.map(look => (
                                  <LookCard key={look.id} look={look} onClick={setSelectedLook} isGenerating={false} onZoom={handleZoomLook} onDelete={deleteLook} onRetry={retryFailedCombo} />
                                ))}
                              </div>
                            ) : isGenColour ? (
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                {[1,2,3,4,5].map(i => <LookCard key={i} look={{ label: 'Generating...' }} onClick={noopClick} isGenerating={true} />)}
                              </div>
                            ) : (
                              <p className="text-xs text-[#52525B] text-center py-6">Click <strong>Generate 5 Looks</strong> to render wedding suits in your selected palette.</p>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                {/* Wedding Planner — clean CTA card. The old "Wedding Party
                    Colours & Attire" panel was redundant because the new
                    questionnaire modal (WeddingPartyQuestionnaire) collects
                    every colour / garment / tie choice in a guided flow.
                    State variables (weddingGroomColor etc.) are preserved
                    upstream because the lookbook PDF export still consumes
                    them as defaults. */}
                <div className="relative bg-gradient-to-br from-[#121215] to-[#0A0A0C] border border-[#27272A] p-6 overflow-hidden" data-testid="wedding-planner-cta">
                  {/* Soft accent halo backdrop */}
                  <div
                    className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20"
                    style={{ backgroundColor: 'hsl(var(--accent))' }}
                  />
                  <div className="relative flex flex-col md:flex-row md:items-center gap-5">
                    <div
                      className="flex-shrink-0 w-14 h-14 flex items-center justify-center border"
                      style={{ borderColor: 'hsl(var(--accent))', backgroundColor: 'hsl(var(--accent) / 0.08)' }}
                    >
                      <Users className="w-7 h-7" style={{ color: 'hsl(var(--accent))' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-light text-[#F5F5F5] leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        Plan the Wedding Party
                      </h3>
                      <p className="text-xs text-[#A1A1AA] mt-1.5 max-w-xl">
                        A guided questionnaire captures every detail — groom, groomsmen, bridesmaids, colour palettes, neckwear, footwear — then renders the full trio together. One tap, one beautiful preview.
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowWeddingQuestionnaire(true)}
                      disabled={!clientImage || weddingPartyGenerating || (isDemoStore && (!!weddingPartyTrio || !!weddingPartyImage))}
                      className="rounded-none uppercase tracking-wider text-xs px-6 py-3"
                      style={btnStyle}
                      data-testid="plan-wedding-party-btn"
                    >
                      {weddingPartyGenerating
                        ? <Loader2 className="w-4 h-4 spinner mr-2" />
                        : (isDemoStore && (weddingPartyTrio || weddingPartyImage))
                          ? <Lock className="w-4 h-4 mr-2" />
                          : <Sparkles className="w-4 h-4 mr-2" />}
                      {isDemoStore && (weddingPartyTrio || weddingPartyImage) ? 'Demo limit · Upgrade for more' : (weddingPartyTrio ? 'Re-plan Wedding Party' : 'Plan Wedding Party')}
                    </Button>
                  </div>
                  {/* Subtle bottom accent bar — matches LookCard styling */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[2px]"
                    style={{
                      backgroundImage:
                        'linear-gradient(90deg, transparent 0%, hsl(var(--accent) / 0.2) 12%, hsl(var(--accent)) 50%, hsl(var(--accent) / 0.2) 88%, transparent 100%)',
                      boxShadow: '0 0 8px hsl(var(--accent) / 0.4)',
                    }}
                  />
                </div>

                {/* ───────────────────────────────────────────────────────
                    Wedding Party TRIO preview (P2) — 3-card grid like the
                    demo page (16:10 each), one card per subject:
                      • Groom (with face)
                      • Groomsmen (faceless mannequins)
                      • Bridesmaids (faceless mannequins)
                    Plus 3 backend-suggested tie palettes below the grid.
                    If no trio yet but legacy single image exists (from old
                    /api/generate-look wedding flow OR gallery hydration),
                    fall back to the single-image view.
                   ─────────────────────────────────────────────────────── */}
                {(weddingPartyTrio || weddingPartyGenerating || weddingPartyImage) && (
                  <div className="bg-[#121215] border border-[#27272A] p-6" data-testid="wedding-party-preview">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Wedding Party</h3>
                      <span className="text-[10px] text-[#52525B] uppercase tracking-wider">
                        {weddingPartyTrio ? 'Groom · Groomsmen · Bridesmaids' : 'Groomsmen · Groom · Bridesmaids'}
                      </span>
                    </div>
                    {weddingPartyGenerating ? (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {['Groom', 'Groomsmen', 'Bridesmaids'].map(label => (
                          <div key={label} className="aspect-[16/10] bg-[#0A0A0C] border border-[#27272A] flex items-center justify-center">
                            <div className="text-center">
                              <Loader2 className="w-6 h-6 text-[hsl(var(--accent))] spinner mx-auto mb-2" />
                              <p className="text-[11px] text-[#A1A1AA] uppercase tracking-wider">{label}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : weddingPartyTrio ? (
                      <>
                        {/* NEW (Feb 2026): single composite group photo. The
                            backend now renders ONE wide image with the groom
                            (with real face) in the centre + groomsmen LEFT +
                            bridesmaids RIGHT as faceless white mannequins.
                            Matches the live-demo aesthetic the user wanted.
                            Legacy 3-tile trio response is still supported as
                            a fallback for older gallery hydrations. */}
                        {weddingPartyTrio.group_photo ? (
                          <figure className="border border-[#1C1C1F] bg-[#0E0E11] overflow-hidden" data-testid="wedding-trio-group">
                            <div className="aspect-[16/9] bg-[#1A1A1E] overflow-hidden relative group">
                              {weddingPartyTrio.group_photo.image ? (
                                <>
                                  <img
                                    src={srcFor(weddingPartyTrio.group_photo.image)}
                                    alt="Wedding Party"
                                    className="w-full h-full object-cover cursor-zoom-in"
                                    decoding="async"
                                    onClick={() => setZoomImage(weddingPartyTrio.group_photo.image)}
                                  />
                                  <button
                                    onClick={() => setZoomImage(weddingPartyTrio.group_photo.image)}
                                    className="absolute top-2 right-2 p-1.5 bg-black/60 text-white/80 hover:text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Zoom"
                                  >
                                    <ZoomIn className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                                  <p className="text-[11px] text-[#ef4444] uppercase tracking-wider">Wedding Party · Failed</p>
                                  <p className="text-[10px] text-[#71717A] mt-1">{weddingPartyTrio.group_photo?.error?.slice(0, 80) || 'Try regenerating'}</p>
                                </div>
                              )}
                            </div>
                            <figcaption className="p-3">
                              <p className="text-sm text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>Wedding Party — Group Render</p>
                              <p className="text-[10px] text-[#71717A] mt-0.5">Groom (with face) · Groomsmen (mannequins) · Bridesmaids (mannequins)</p>
                            </figcaption>
                          </figure>
                        ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {[
                            { key: 'groom',       data: weddingPartyTrio.groom,       label: 'Groom',       sublabel: 'With face — your client' },
                            { key: 'groomsmen',   data: weddingPartyTrio.groomsmen,   label: 'Groomsmen',   sublabel: 'Faceless mannequins' },
                            { key: 'bridesmaids', data: weddingPartyTrio.bridesmaids, label: 'Bridesmaids', sublabel: 'Faceless mannequins' },
                          ].map(({ key, data, label, sublabel }) => (
                            <figure key={key} className="border border-[#1C1C1F] bg-[#0E0E11] overflow-hidden" data-testid={`wedding-trio-${key}`}>
                              <div className="aspect-[16/10] bg-[#1A1A1E] overflow-hidden relative group">
                                {data?.image ? (
                                  <>
                                    <img
                                      src={srcFor(data.image)}
                                      alt={label}
                                      className="w-full h-full object-cover cursor-zoom-in"
                                      decoding="async"
                                      onClick={() => setZoomImage(data.image)}
                                    />
                                    <button
                                      onClick={() => setZoomImage(data.image)}
                                      className="absolute top-2 right-2 p-1.5 bg-black/60 text-white/80 hover:text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Zoom"
                                    >
                                      <ZoomIn className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                                    <p className="text-[11px] text-[#ef4444] uppercase tracking-wider">{label} · Failed</p>
                                    <p className="text-[10px] text-[#71717A] mt-1">{data?.error?.slice(0, 60) || 'Try regenerating'}</p>
                                  </div>
                                )}
                              </div>
                              <figcaption className="p-3">
                                <p className="text-sm text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>{label}</p>
                                <p className="text-[10px] text-[#71717A] mt-0.5">{sublabel}</p>
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                        )}

                        {/* Suggested tie + shoe palettes — research-based pairings */}
                        {(Array.isArray(weddingPartyTrio.tie_palettes) && weddingPartyTrio.tie_palettes.length > 0) ||
                         (Array.isArray(weddingPartyTrio.shoe_palettes) && weddingPartyTrio.shoe_palettes.length > 0) ? (
                          <div className="mt-5 pt-4 border-t border-[#27272A]" data-testid="wedding-palettes">
                            {Array.isArray(weddingPartyTrio.tie_palettes) && weddingPartyTrio.tie_palettes.length > 0 && (
                              <div data-testid="wedding-tie-palettes">
                                <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EAB308' }}>
                                  Suggested tie palettes
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                                  {weddingPartyTrio.tie_palettes.map((p, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-[#0A0A0C] border border-[#27272A] p-3" data-testid={`tie-palette-${idx}`}>
                                      <div className="flex gap-1 flex-shrink-0">
                                        <span className="block w-7 h-7 border border-[#27272A]" style={{ backgroundColor: p.hex }} />
                                        {p.accent_hex && (
                                          <span className="block w-3 h-7 border border-[#27272A]" style={{ backgroundColor: p.accent_hex }} />
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-[10px] uppercase tracking-wider text-[#71717A] leading-none mb-0.5">Tie option {idx + 1}</p>
                                        <p className="text-xs text-[#F5F5F5] leading-snug truncate">{p.label || p.name}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {Array.isArray(weddingPartyTrio.shoe_palettes) && weddingPartyTrio.shoe_palettes.length > 0 && (
                              <div data-testid="wedding-shoe-palettes">
                                <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EAB308' }}>
                                  Suggested shoe palettes
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  {weddingPartyTrio.shoe_palettes.map((p, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-[#0A0A0C] border border-[#27272A] p-3" data-testid={`shoe-palette-${idx}`}>
                                      <div className="flex gap-1 flex-shrink-0">
                                        <span className="block w-7 h-7 border border-[#27272A]" style={{ backgroundColor: p.hex }} />
                                        {p.accent_hex && (
                                          <span className="block w-3 h-7 border border-[#27272A]" style={{ backgroundColor: p.accent_hex }} />
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-[10px] uppercase tracking-wider text-[#71717A] leading-none mb-0.5">Shoe option {idx + 1}</p>
                                        <p className="text-xs text-[#F5F5F5] leading-snug truncate">{p.label || p.name}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </>
                    ) : weddingPartyImage && (
                      // Legacy single-image fallback (old looks pre-P2)
                      <div className="aspect-[21/9] bg-[#0A0A0C] border border-[#27272A] overflow-hidden relative group cursor-zoom-in" onClick={() => setZoomImage(weddingPartyImage)}>
                        <img
                          src={srcFor(weddingPartyImage)}
                          alt="Wedding Party"
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        <button onClick={(e) => { e.stopPropagation(); setZoomImage(weddingPartyImage); }}
                          className="absolute top-3 right-3 p-2 bg-black/60 text-white/70 hover:text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity">
                          <ZoomIn className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Export Wedding Lookbook to PDF — branded with the store's
                    primary color, packed with the Wedding Party Visualizer
                    render + every wedding look + Groom's Inspirations +
                    Groomsmen Collection. Beautiful one-tap download the
                    consultant hands to the bride/groom at the end of the
                    consult. */}
                <div className="bg-[#121215] border border-[#27272A] p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Wedding Lookbook PDF</h3>
                      <p className="text-xs text-[#A1A1AA] mt-1">Beautifully branded PDF — every wedding look, the Wedding Party Visualizer, Groom&rsquo;s Inspirations, Groomsmen Collection. Hand it to the bride or groom on the spot.</p>
                    </div>
                    <Button
                      onClick={async () => {
                        if (!selectedClientId) { toast.error('Select a client first'); return; }
                        setSendingWeddingEmail(true); // reuse the busy state
                        try {
                          // Pull the chosen palette colors so the PDF picks them up
                          const groomHex = weddingGroomColor || '#0B0B0B';
                          const groomsmenHex = weddingGroomsmenColor || '#36454F';
                          const bridesmaidsHex = weddingBridesmaidsColor || '#556B2F';
                          const params = new URLSearchParams({
                            client_id: selectedClientId,
                            groom_color_hex: groomHex,
                            groomsmen_color_hex: groomsmenHex,
                            bridesmaids_color_hex: bridesmaidsHex,
                            include_inspiration: 'true',
                            include_groomsmen: 'true',
                            brand_name: storeSettings.store_name || 'TailorFlow',
                            brand_logo: storeSettings.store_logo || '',
                          });
                          const resp = await axios.get(
                            `${BACKEND_URL}/api/export-wedding-lookbook?${params.toString()}`,
                            { withCredentials: true, responseType: 'blob', timeout: 180000 },
                          );
                          const blob = new Blob([resp.data], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          const safe = (storeSettings.store_name || 'TailorFlow').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
                          link.download = `wedding-lookbook-${safe}-${Date.now()}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Wedding Lookbook downloaded');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Could not generate the PDF');
                        } finally {
                          setSendingWeddingEmail(false);
                        }
                      }}
                      disabled={sendingWeddingEmail || !selectedClientId}
                      className="rounded-none px-6 py-3 uppercase tracking-wider text-xs whitespace-nowrap"
                      style={btnStyle}
                      data-testid="download-wedding-pdf-btn"
                    >
                      {sendingWeddingEmail ? <Loader2 className="w-4 h-4 spinner mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                      Download PDF
                    </Button>
                  </div>
                </div>
                  </>
                )}
              </TabsContent>

              {/* CUSTOM FABRIC */}
              <TabsContent value="custom_fabric" className="mt-0">
                <div className="bg-[#121215] border border-[#27272A] p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                    <div>
                      <h3 className="text-xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Custom Fabric</h3>
                      <p className="text-xs text-[#A1A1AA] mt-1">Pick a fabric from your library and render any silhouette — unlimited renders</p>
                    </div>
                  {/* The standalone generate button is rendered further down below the style picker. */}
                  </div>

                  {/* Iter 129c — explicit banner when a Render can't fire because the
                      client photo hasn't been uploaded yet. Previously the fabric
                      preview's "Render with this fabric" button silently disabled,
                      the questionnaire modal's Render Look fired a toast that could
                      be missed, and the Add Custom Swatch modal did the same — the
                      tailor saw "Render doesn't work" with no visible reason. This
                      banner surfaces the requirement inline so the state is obvious. */}
                  {!clientImage && (
                    <div
                      className="mb-5 px-4 py-3 border border-[#4F2F1F] bg-[#2A1A0F] flex items-start gap-3"
                      data-testid="custom-fabric-no-client-photo-warning"
                    >
                      <div className="text-[#D4A87A] text-sm mt-0.5">⚠</div>
                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-widest text-[#D4A87A] font-semibold">
                          Upload a client photo to render
                        </p>
                        <p className="text-[11px] text-[#A1A1AA] mt-1 leading-relaxed">
                          Custom Fabric renders need the client&apos;s face reference. Add a photo in the <em>Client Photo</em> panel on the left, then any Render button will unlock.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── ASSIGNED FABRIC LIBRARY (per-store, grouped by collection × colour) ──
                      Master admin assigns swatches to this store via the
                      /fabrics page. Two-level grouping (Iter 83):
                        L1 = Collection (e.g. "Tom Ford", "House Fabrics")
                        L2 = Colour family within that collection (Blues,
                             Greys, Browns, …) — opens as a nested
                             collapsible inside each collection
                      Fabrics with no category fall back to a single
                      "Uncategorised · {Colour}" group at the bottom so
                      nothing is orphaned during the legacy → category
                      migration.                                          */}
                  {libraryFabrics.length > 0 && (() => {
                    const COLOUR_ORDER = ['blue', 'grey', 'brown', 'green', 'black', 'white', 'red', 'orange', 'yellow', 'purple', 'neutral'];
                    const COLOUR_LABELS = { blue: 'Blues', grey: 'Greys', brown: 'Browns', green: 'Greens', black: 'Blacks', white: 'Whites', red: 'Reds', orange: 'Oranges', yellow: 'Yellows', purple: 'Purples', neutral: 'Neutrals' };
                    // L1 group → category (or __color_x bucket for legacy
                    // uncategorised fabrics). L2 group → colour family.
                    const grouped = fabricLibraryFiltered.reduce((acc, f) => {
                      const cat = (f.category || '').trim();
                      const colourKey = (f.color_family || 'neutral').toLowerCase();
                      if (cat) {
                        acc[cat] = acc[cat] || {};
                        (acc[cat][colourKey] = acc[cat][colourKey] || []).push(f);
                      } else {
                        const legacyKey = `__color_${colourKey}`;
                        acc[legacyKey] = acc[legacyKey] || {};
                        (acc[legacyKey][colourKey] = acc[legacyKey][colourKey] || []).push(f);
                      }
                      return acc;
                    }, {});
                    const explicitKeys = Object.keys(grouped).filter(k => !k.startsWith('__color_')).sort();
                    const colourKeys = Object.keys(grouped).filter(k => k.startsWith('__color_')).sort();
                    const orderedKeys = [...explicitKeys, ...colourKeys];

                    const sortedColourKeys = (subMap) => {
                      const present = Object.keys(subMap);
                      const inOrder = COLOUR_ORDER.filter(k => present.includes(k));
                      const extras = present.filter(k => !COLOUR_ORDER.includes(k)).sort();
                      return [...inOrder, ...extras];
                    };

                    const renderSwatch = (f, idx = 0) => {
                      const isActive = libraryFabricId === f.id;
                      return (
                        <div
                          key={f.id}
                          className="group swatch-pop"
                          style={{ animationDelay: `${Math.min(idx, 16) * 28}ms` }}
                          data-testid={`dashboard-fabric-${f.sku}`}
                          title={`${f.sku} — ${f.name}${f.category ? ` · ${f.category}` : ''}`}
                        >
                          <div className={`aspect-square rounded-lg overflow-hidden bg-[#0A0A0C] relative border-2 transition duration-300 ${isActive ? 'border-[#C2A878] shadow-[0_0_0_3px_rgba(194,168,120,0.22)]' : 'border-[#27272A] group-hover:border-[#C2A878]/60 group-hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.7)]'}`}>
                            <button
                              type="button"
                              onClick={() => openFabricRender(f)}
                              className="block w-full h-full overflow-hidden"
                              aria-label={`Render with ${f.name}`}
                              data-testid={`dashboard-fabric-tile-${f.sku}`}
                            >
                              <img src={`${BACKEND_URL}${f.image_url}?v=2`} alt={f.name} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" loading="lazy" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setPreviewFabric(f); }}
                              className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/85 text-white/95 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-200 shadow-lg"
                              aria-label={`Preview ${f.name}`}
                              data-testid={`dashboard-fabric-preview-${f.sku}`}
                            >
                              <Maximize2 className="w-3.5 h-3.5" />
                            </button>
                            {isActive && (
                              <div className="absolute bottom-0 left-0 right-0 bg-[#C2A878] text-[#0A0A0C] text-[8px] uppercase tracking-wider text-center py-0.5 font-semibold pointer-events-none">Active</div>
                            )}
                          </div>
                          <div className="mt-1.5 px-0.5">
                            <p className={`text-[11px] leading-tight truncate transition-colors ${isActive ? 'text-[#C2A878] font-semibold' : 'text-[#E4E4E7] group-hover:text-[#F5F5F5]'}`}>{f.name}</p>
                            <p className="text-[9px] uppercase tracking-[0.12em] text-[#52525B] mt-0.5">{f.sku}</p>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <div className="mb-6" data-testid="dashboard-fabric-library">
                        {/* Iter 126 — Suits / Shirts / Liners segmentation */}
                        <div className="flex gap-1 mb-3 border border-[#27272A] w-fit" data-testid="fabric-garment-tabs">
                          {[
                            { id: 'suits',  label: 'Suits' },
                            { id: 'shirts', label: 'Shirts' },
                            { id: 'liners', label: 'Liners' },
                          ].map(g => (
                            <button
                              key={g.id}
                              type="button"
                              onClick={() => setLibGarmentCat(g.id)}
                              className={`px-4 h-8 text-[10px] uppercase tracking-widest transition-colors ${libGarmentCat === g.id ? 'bg-[#C2A878] text-[#0A0A0C] font-semibold' : 'text-[#A1A1AA] hover:text-[#F5F5F5]'}`}
                              data-testid={`fabric-garment-tab-${g.id}`}
                            >
                              {g.label}
                              <span className="ml-1 opacity-60">
                                ({libraryFabrics.filter(f => (f.garment_category || 'suits') === g.id).length})
                              </span>
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                          <Label className="label-luxury">
                            Your Assigned Fabrics
                            <span className="opacity-50 ml-1">
                              ({fabricLibraryFiltered.length}{fabricSearchQ ? ` of ${libraryFabrics.length}` : ''})
                            </span>
                          </Label>
                          <div className="flex items-center gap-2 flex-1 max-w-xs">
                            {/* Iter 123 — search input. Debounced 120ms.
                                Filters across sku / name / composition /
                                colour family / category so tailors can
                                jump straight to "cha" (charcoal) or "H1520"
                                (SKU) in a huge library. */}
                            <input
                              type="text"
                              value={fabricSearch}
                              onChange={(e) => setFabricSearch(e.target.value)}
                              placeholder="Search fabrics…"
                              className="w-full h-8 px-3 text-xs bg-[#0A0A0C] border border-[#27272A] text-[#F5F5F5] placeholder-[#52525B] focus:outline-none focus:border-[#52525B]"
                              data-testid="fabric-library-search"
                              aria-label="Search fabrics"
                            />
                            {fabricSearch && (
                              <button
                                type="button"
                                onClick={() => setFabricSearch('')}
                                className="text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-[#F5F5F5]"
                                data-testid="fabric-library-search-clear"
                              >
                                Clear
                              </button>
                            )}
                            {libraryLoading && <Loader2 className="w-3 h-3 spinner text-[#52525B]" />}
                          </div>
                        </div>
                        {/* Quick COLOUR filter chips — jump straight to a
                            colour family across all collections. Clicking a
                            chip filters the cards + flyout; click again or "All"
                            to clear. */}
                        {(() => {
                          const COLOUR_DOT = { blue: '#3B5BA5', grey: '#8A8D91', brown: '#7A5230', green: '#4A6B3A', black: '#1A1A1A', white: '#E8E8E8', red: '#9B2D2D', orange: '#C2742B', yellow: '#C9A227', purple: '#6B4A8A', neutral: '#B7A98F' };
                          const garmentFabrics = libraryFabrics.filter(f => (f.garment_category || 'suits') === libGarmentCat);
                          const colourCount = {};
                          garmentFabrics.forEach(f => { const c = (f.color_family || 'neutral').toLowerCase(); colourCount[c] = (colourCount[c] || 0) + 1; });
                          const availableColours = [
                            ...COLOUR_ORDER.filter(c => colourCount[c]),
                            ...Object.keys(colourCount).filter(c => !COLOUR_ORDER.includes(c)).sort(),
                          ];
                          if (availableColours.length <= 1) return null;
                          return (
                            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="fabric-colour-filters">
                              <button
                                type="button"
                                onClick={() => setLibColourFilter(null)}
                                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[10px] uppercase tracking-widest transition duration-200 border ${!libColourFilter ? 'bg-[#C2A878] text-[#0A0A0C] border-[#C2A878] font-semibold' : 'bg-[#121214] text-[#A1A1AA] border-[#27272A] hover:text-[#F5F5F5] hover:border-[#C2A878]/50'}`}
                                data-testid="fabric-colour-filter-all"
                              >
                                All
                                <span className={!libColourFilter ? 'text-[#0A0A0C]/55' : 'text-[#52525B]'}>{garmentFabrics.length}</span>
                              </button>
                              {availableColours.map(c => {
                                const active = libColourFilter === c;
                                return (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() => setLibColourFilter(active ? null : c)}
                                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[10px] uppercase tracking-widest transition duration-200 border ${active ? 'bg-[#C2A878] text-[#0A0A0C] border-[#C2A878] font-semibold shadow-[0_4px_14px_-4px_rgba(194,168,120,0.6)]' : 'bg-[#121214] text-[#D4D4D8] border-[#27272A] hover:text-[#F5F5F5] hover:border-[#C2A878]/50'}`}
                                    data-testid={`fabric-colour-filter-${c}`}
                                  >
                                    <span className="w-3 h-3 rounded-full ring-1 ring-black/30 flex-shrink-0" style={{ backgroundColor: COLOUR_DOT[c] || '#888' }} />
                                    {COLOUR_LABELS[c] || c}
                                    <span className={active ? 'text-[#0A0A0C]/55' : 'text-[#52525B]'}>{colourCount[c]}</span>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* Collection TILE CARDS — each shows a mini mosaic of
                            its swatches with the name + count overlaid. Click a
                            card to "pop out" all its swatches in a flyout panel.
                            Active card gets the gold ring. */}
                        <div className="flex flex-wrap gap-3" data-testid="fabric-collection-buttons">
                          {orderedKeys.map(key => {
                            const subMap = grouped[key];
                            const isColourFallback = key.startsWith('__color_');
                            const cf = isColourFallback ? key.replace('__color_', '') : null;
                            const groupLabel = isColourFallback
                              ? (`Uncategorised · ${COLOUR_LABELS[cf] || cf}`)
                              : key;
                            const groupTestKey = isColourFallback ? cf : key.toLowerCase().replace(/\s+/g, '-');
                            const allSwatches = Object.values(subMap).flat();
                            const total = allSwatches.length;
                            const mosaic = allSwatches.slice(0, 4);
                            const isOpen = openFabricCollection === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setOpenFabricCollection(isOpen ? null : key)}
                                className={`group relative w-[160px] aspect-[4/3] rounded-xl overflow-hidden text-left transition duration-300 focus:outline-none ${isOpen ? 'ring-2 ring-[#C2A878] shadow-[0_10px_30px_-8px_rgba(194,168,120,0.45)] scale-[1.02]' : 'ring-1 ring-[#27272A] hover:ring-[#C2A878]/50 hover:scale-[1.02] hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.8)]'}`}
                                data-testid={`fabric-collection-btn-${groupTestKey}`}
                              >
                                {/* Mosaic backdrop */}
                                {mosaic.length === 1 ? (
                                  <img src={`${BACKEND_URL}${mosaic[0].image_url}?v=2`} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" loading="lazy" />
                                ) : (
                                  <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                      mosaic[i]
                                        ? <img key={i} src={`${BACKEND_URL}${mosaic[i].image_url}?v=2`} alt="" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                                        : <div key={i} className="w-full h-full bg-[#121214]" />
                                    ))}
                                  </div>
                                )}
                                {/* Legibility gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/5" />
                                {/* Gold active wash */}
                                {isOpen && <div className="absolute inset-0 bg-[#C2A878]/10" />}
                                {/* Count chip */}
                                <span className={`absolute top-2 right-2 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full text-[10px] font-semibold tabular-nums ${isOpen ? 'bg-[#C2A878] text-[#0A0A0C]' : 'bg-black/60 backdrop-blur-sm text-[#F5F5F5]'}`}>{total}</span>
                                {/* Label + indicator */}
                                <div className="absolute inset-x-0 bottom-0 p-2.5 flex items-end justify-between gap-1.5">
                                  <span className={`text-[11px] uppercase tracking-[0.1em] leading-tight font-medium ${isOpen ? 'text-[#C2A878]' : 'text-[#F5F5F5]'}`}>{groupLabel}</span>
                                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#C2A878]' : 'text-[#D4D4D8]'}`} />
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {/* Pop-out panel for the open collection */}
                        {openFabricCollection && grouped[openFabricCollection] && (() => {
                          const subMap = grouped[openFabricCollection];
                          const isColourFallback = openFabricCollection.startsWith('__color_');
                          const cf = isColourFallback ? openFabricCollection.replace('__color_', '') : null;
                          const groupLabel = isColourFallback
                            ? (`Uncategorised · ${COLOUR_LABELS[cf] || cf}`)
                            : openFabricCollection;
                          const colourBuckets = sortedColourKeys(subMap);
                          const showColourLabels = !isColourFallback && colourBuckets.length > 1;
                          const grandTotal = Object.values(subMap).reduce((s, arr) => s + arr.length, 0);
                          let swatchIdx = 0;
                          return (
                            <div className="fabric-flyout mt-4 rounded-xl border border-[#C2A878]/35 bg-[#0A0A0C] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)] overflow-hidden" data-testid="fabric-collection-popout">
                              <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A] bg-gradient-to-r from-[#1A1A1E] to-[#121214]">
                                <div className="flex items-center gap-2.5">
                                  <span className="w-1 h-4 rounded-full bg-[#C2A878]" />
                                  <span className="text-xs uppercase tracking-[0.14em] text-[#F5F5F5] font-semibold">{groupLabel}</span>
                                  <span className="text-[10px] text-[#52525B] tracking-widest">{grandTotal} {grandTotal === 1 ? 'fabric' : 'fabrics'}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setOpenFabricCollection(null)}
                                  className="w-7 h-7 flex items-center justify-center rounded-full text-[#A1A1AA] hover:text-[#F5F5F5] hover:bg-white/5 transition-colors"
                                  aria-label="Close fabric collection"
                                  data-testid="fabric-collection-popout-close"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="p-4 max-h-[440px] overflow-y-auto space-y-5">
                                {colourBuckets.map(c => (
                                  <div key={c}>
                                    {showColourLabels && (
                                      <div className="flex items-center gap-2 mb-2.5">
                                        <span className="inline-block w-3 h-3 rounded-full border border-black/40 shadow-sm" style={{ backgroundColor: subMap[c][0]?.primary_color_hex || '#888' }} />
                                        <span className="text-[10px] uppercase tracking-[0.14em] text-[#D4D4D8] font-medium">{COLOUR_LABELS[c] || c}</span>
                                        <span className="text-[10px] text-[#52525B]">({subMap[c].length})</span>
                                        <span className="flex-1 h-px bg-[#1F1F23]" />
                                      </div>
                                    )}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                      {subMap[c].map(f => renderSwatch(f, swatchIdx++))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        <p className="text-[10px] text-[#52525B] mt-2">
                          Click any swatch to open the render questionnaire (silhouette · shoe · shirt).
                        </p>
                      </div>
                    );
                  })()}
                    {/* Iter 82 — direct silhouette dropdown buttons removed.
                        Clicking an assigned fabric tile (above) now opens
                        the CustomFabricQuestionnaire which captures the
                        silhouette + shoe + shirt details before generation. */}

                  {/* Add Custom Swatch — pretty modal flow (replaces the
                      old in-tab Fabric Swatches grid + Category/Style
                      dropdowns + Generate button). */}
                  <div className="mb-6">
                    <Button
                      onClick={() => setShowAddCustomSwatch(true)}
                      disabled={!clientImage || isGenerating}
                      size="sm"
                      variant="outline"
                      className="rounded-none uppercase tracking-wider text-xs h-10 border-[#3F3F46] bg-transparent text-[#F5F5F5] hover:bg-[#1A1A1E] hover:text-[#F5F5F5]"
                      data-testid="open-add-custom-swatch"
                    >
                      <Palette className="w-3.5 h-3.5 mr-2" />
                      + Add Custom Swatch
                    </Button>
                    <p className="text-[10px] text-[#52525B] mt-1.5">
                      Use for a one-off swatch that isn&apos;t in your assigned library yet.
                    </p>
                  </div>
                  {(() => {
                    const cfLooks = getCategoryLooks('custom_fabric');
                    const cfGenerating = generatingCats?.has('custom_fabric') || generatingCategory === 'custom_fabric';
                    if (cfLooks.length === 0 && !cfGenerating) {
                      return (
                        <div className="text-center py-12"><Palette className="w-10 h-10 text-[#52525B] mx-auto mb-3" /><p className="text-sm text-[#A1A1AA]">Pick a fabric above and choose a silhouette to render.</p></div>
                      );
                    }
                    // Render existing looks PLUS a skeleton row when a new
                    // render is in flight, so the tailor sees the shimmer
                    // animation appended to the grid (not buried under a
                    // collapsed "either-or" branch like the legacy code).
                    const skeletonCount = cfGenerating ? (cfLooks.length === 0 ? 5 : 1) : 0;
                    return (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                        {cfLooks.map(look => (
                          <LookCard key={look.id} look={look} onClick={setSelectedLook} isGenerating={false} onZoom={setSelectedLook} onDelete={deleteLook} onRetry={retryFailedCombo} />
                        ))}
                        {Array.from({ length: skeletonCount }).map((_, i) => (
                          <LookCard key={`cf-gen-${i}`} look={{ label: 'Rendering custom fabric' }} onClick={noopClick} isGenerating={true} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

            </Tabs>
          </div>
        </div>
      </main>
      <CustomizeModal look={selectedLook} open={!!selectedLook} onClose={() => setSelectedLook(null)} onRegenerate={handleRegenerate} isRegenerating={isRegenerating} colorMatcher={colorMatcher} isDemoStore={isDemoStore} storeSettings={storeSettings} isMasterAdmin={isMasterAdmin} onDesign={(look) => {
        // Iter 125 — launch the SuitDesigner with the look as fabric
        // context. If the look was rendered from an assigned custom
        // fabric we already have its id/name/composition; otherwise
        // synthesize a lightweight fabric-like object from the look
        // itself so the designer preview still tiles the correct texture
        // in its SVG.
        const asFabric = look?.fabric_id
          ? {
              id: look.fabric_id,
              name: look.fabric_name || look.label || 'Selected fabric',
              sku: look.fabric_sku || '—',
              composition: look.fabric_composition || 'Wool & Silk',
              texture_notes: look.fabric_texture_notes || '',
              color_family: look.fabric_color_family || 'neutral',
              primary_color_hex: look.color || '#3A3A3E',
              // Serve the FABRIC swatch (not the rendered look) as the
              // designer's tile texture when we have a real fabric row.
              image_url: `/api/fabrics/${look.fabric_id}/image`,
            }
          : {
              // Fallback: use the look image itself as the tile. Not a real
              // fabric row, but gives the designer preview something to
              // render + the prompt-builder inherits color context from the
              // look on the backend side.
              id: null,
              name: look?.label || 'This look',
              sku: '—',
              composition: 'Custom render',
              texture_notes: '',
              color_family: 'neutral',
              primary_color_hex: look?.color || '#3A3A3E',
              image_url: null,
            };
        setSelectedLook(null);
        // Small tick so the CustomizeModal fully closes before the full-
        // screen designer opens (avoids Radix Dialog focus-trap fights).
        const imagePathMatch = typeof look?.image === 'string' && look.image.match(/\/api\/images\/([^?]+)/);
        setTimeout(() => setDesignerFabric({ ...asFabric, sourceLookId: look?.id || null,
          sourceLookImage: look?.image, sourceLookPath: look?.linerSourcePath || (imagePathMatch ? imagePathMatch[1] : null) }), 60);
      }} />
      {/* P2 — Wedding party trio questionnaire */}
      <WeddingPartyQuestionnaire
        open={showWeddingQuestionnaire}
        onClose={() => setShowWeddingQuestionnaire(false)}
        onSubmit={generateWeddingPartyTrio}
        generating={weddingPartyGenerating}
      />
      {/* Feb 2026 — Wedding setup gateway (groom / bride / date) */}
      <WeddingSetupModal
        open={showWeddingSetup}
        onClose={() => setShowWeddingSetup(false)}
        onSubmit={saveWeddingSetup}
        initialGroomName={weddingSetup.groom_name}
        initialBrideName={weddingSetup.bride_name}
        initialWeddingDate={weddingSetup.wedding_date}
        saving={savingWeddingSetup}
      />
      {/* Feb 2026 — One-off custom swatch upload + render in one flow */}
      <AddCustomSwatchModal
        open={showAddCustomSwatch}
        onClose={() => setShowAddCustomSwatch(false)}
        onSubmit={customFabricFromUpload}
        isGenerating={isGenerating}
        currentUser={user}
        hasClientImage={!!clientImage}
      />
      {/* Iter 82 — Custom Fabric Questionnaire (silhouette + shoe + shirt) */}
      <CustomFabricQuestionnaire
        open={showCustomFabricQ}
        onClose={() => { setShowCustomFabricQ(false); setCustomFabricQTarget(null); }}
        onSubmit={customFabricFromQuestionnaire}
        fabric={customFabricQTarget?.fabric}
        generating={isGenerating}
        hasClientImage={!!clientImage}
      />
      {/* Iter 129h — Casual Shirts picker. Opens from the shirts tab's
          "Casual Shirts" subcategory header. On submit, drops the
          returned combo straight into the standard generate-look flow
          under category `shirts_casual_shirts`. */}
      <CasualShirtsQuestionnaire
        open={showCasualShirtsQ}
        onClose={() => setShowCasualShirtsQ(false)}
        generating={isGenerating}
        hasClientImage={!!clientImage}
        onSubmit={({ combo, category }) => {
          if (!clientImage) { toast.error('Upload a client photo first'); return; }
          setShowCasualShirtsQ(false);
          generateComboLooks([combo], category || 'shirts_casual_shirts').catch(() => {});
        }}
      />
      {/* Feb 2026 — Sweater picker. Opens from the shirts tab's
          "Sweaters" subcategory header (directly below Casual Shirts).
          Fires a single render under `shirts_sweaters` which the
          backend routes to its dedicated `sweaters` knitwear prompt. */}
      <SweaterQuestionnaire
        open={showSweaterQ}
        onClose={() => setShowSweaterQ(false)}
        generating={isGenerating}
        hasClientImage={!!clientImage}
        onSubmit={({ combo, category }) => {
          if (!clientImage) { toast.error('Upload a client photo first'); return; }
          setShowSweaterQ(false);
          generateComboLooks([combo], category || 'shirts_sweaters').catch(() => {});
        }}
      />
      {/* Feb 2026 iter 170 — Dress Shirt Builder picker (collar × weave
          × colour × pattern + trouser + shoes). Opens from the shirts
          tab's "Dress Shirt Builder" subcategory header. Fires ONE
          render under `shirts_dress_shirts` which the backend routes to
          its existing `shirts` base_category prompt. */}
      <DressShirtsQuestionnaire
        open={showDressShirtsQ}
        onClose={() => setShowDressShirtsQ(false)}
        generating={isGenerating}
        hasClientImage={!!clientImage}
        onSubmit={({ combo, category }) => {
          if (!clientImage) { toast.error('Upload a client photo first'); return; }
          setShowDressShirtsQ(false);
          generateComboLooks([combo], category || 'shirts_dress_shirts').catch(() => {});
        }}
      />
      {/* Zoom Modal */}
      {zoomImage && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center cursor-zoom-out p-4" onClick={() => setZoomImage(null)}>
          <button className="absolute top-6 right-6 text-white/70 hover:text-white p-2 z-10" onClick={() => setZoomImage(null)}><X className="w-6 h-6" /></button>
          <img src={`data:image/jpeg;base64,${zoomImage}`} alt="Zoom" className="max-w-[95vw] max-h-[95vh] object-contain" style={{ imageRendering: 'auto' }} />
        </div>
      )}
      {/* Iter 99 follow-up — GLOBAL NewClientIntakeModal render. Lives at
          the bottom of the Dashboard return so opening it from
          ?addClient=1 (or the gate's "Add new client" link) works whether
          the gate is showing or the main studio is showing. */}
      {showNewClientIntake && (
        <NewClientIntakeModal
          onClose={() => setShowNewClientIntake(false)}
          onCreated={async (newClient) => {
            setShowNewClientIntake(false);
            await fetchClients();
            setSelectedClientId(newClient.id);
            setSelectedClientSlug(newClient.slug || '');
            localStorage.setItem('selected_client_id', newClient.id);
            setShowClientGate(false);
            toast.success(`${newClient.name} ready · entering studio`);
          }}
          storeSettings={storeSettings}
        />
      )}
      {/* Iter 124 — Suit Designer. Instant-swap SVG preview canvas; only
          the Finalize step fires the two-shot Gemini editorial. */}
      {designerFabric && (
        <SuitDesignerModal
          open={!!designerFabric}
          onClose={() => setDesignerFabric(null)}
          fabric={designerFabric}
          clientId={selectedClientId}
          clientImage={clientImage}
          sourceLookId={designerFabric?.sourceLookId || null}
          sourceLookImage={designerFabric?.sourceLookImage}
          sourceLookPath={designerFabric?.sourceLookPath}
          // Feb 2026 — full designer PUBLICLY RELEASED (user's explicit
          // go). Was `fullDesigner={isMasterAdmin}` while in beta; now
          // every logged-in tailor gets the full option set (buttons,
          // lapels, vents, view switching, liner) on the designed suit.
          fullDesigner={true}
          onRendered={(looks, sourceLookId) => {
            if (!Array.isArray(looks) || looks.length === 0) return;
            if (sourceLookId) {
              // Iter 128 — the liner pair replaces the 2nd photo of the
              // source look's carousel (mirrors the backend merge).
              const fresh = looks.map(l => ({
                id: sourceLookId,
                image: l.generated_image,
                image_path: l.image_path,
                image_path_thumb: l.image_path_thumb,
                label: l.label,
                tailor_notes: {},
                edit: { designed_liner: true },
              }));
              const src = generatedLooks.find(lk => lk.id === sourceLookId)
                || Object.values(inspResults).find(lk => lk.id === sourceLookId);
              if (src) {
                const original = Array.isArray(src.variants) ? src.variants : [];
                let rest = original.filter(v => !(v?.edit || {}).designed_liner);
                // Iter 129g — auto-jump the card + zoom modal to the
                // "client holding jacket open" render (variantIdx=1)
                // as soon as the fresh pair lands. `_focusVariantIdx`
                // is watched by LookCard via `_focusToken`.
                const merged = {
                  ...src,
                  variants: [...fresh, ...rest].slice(0, 8),
                  _focusVariantIdx: 1,
                  _focusToken: Date.now(),
                };
                setGeneratedLooks(prev => prev.map(lk => (lk.id === sourceLookId ? merged : lk)));
                setInspResults(prev => Object.fromEntries(Object.entries(prev).map(([key, lk]) => [
                  key, lk?.id === sourceLookId ? { ...lk, variants: merged.variants } : lk,
                ])));
                setSelectedLook(merged);
              }
              return;
            }
            const mapped = looks.map(l => ({
              id: l.id,
              image: l.generated_image,
              image_path: l.image_path,
              image_path_thumb: l.image_path_thumb,
              label: l.label,
              garment: l.label,
              garment_id: l.garment_type,
              category: l.category || 'custom_fabric',
              tailor_notes: 'Client-designed editorial pair',
            }));
            setGeneratedLooks(prev => [...prev, ...mapped]);
            // Land the user where the pair now lives + open the two-shot
            // carousel immediately so they see the designed suit at once.
            setActiveCategory('custom_fabric');
            const primary = mapped[0];
            setSelectedLook({
              ...primary,
              variants: mapped.slice(1).map(m => ({ image: m.image, label: m.label })),
            });
          }}
        />
      )}

      {/* Iter 96 — Fabric preview popout. Opens when the tailor clicks the
          small Maximize2 icon in the top-right of a fabric tile. Shows a
          much larger swatch preview (so the pattern / weave / colour
          variation is actually legible) plus a single "Render with this
          fabric" CTA underneath that fires the existing CustomFabric
          Questionnaire flow via openFabricRender(). */}
      {previewFabric && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-6"
          onClick={() => setPreviewFabric(null)}
          data-testid="fabric-preview-modal"
        >
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white p-2 z-10"
            onClick={() => setPreviewFabric(null)}
            data-testid="fabric-preview-close"
            aria-label="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            className="flex flex-col items-center gap-5 max-w-[600px] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full max-w-[480px] aspect-square border border-[#27272A] bg-[#0A0A0C] overflow-hidden">
              <img
                src={`${BACKEND_URL}${previewFabric.image_url}?v=2`}
                alt={previewFabric.name}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                data-testid="fabric-preview-image"
              />
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA]" data-testid="fabric-preview-sku">
                {previewFabric.sku}
              </div>
              <div className="text-sm text-[#F5F5F5] mt-0.5 font-medium" data-testid="fabric-preview-name">
                {previewFabric.name}
              </div>
              {previewFabric.category && (
                <div className="text-[10px] uppercase tracking-widest text-[#52525B] mt-1">
                  {previewFabric.category}
                </div>
              )}
            </div>
            <Button
              onClick={() => {
                const f = previewFabric;
                setPreviewFabric(null);
                openFabricRender(f);
              }}
              disabled={!clientImage || isGenerating}
              title={!clientImage ? 'Upload a client photo first — the render needs a face reference' : (isGenerating ? 'Another render is in progress…' : undefined)}
              className="w-full max-w-[320px] h-11 rounded-none uppercase tracking-widest text-xs font-semibold"
              style={{ backgroundColor: '#C2A878', color: '#0A0A0C' }}
              data-testid="fabric-preview-render-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-2" />
              Render with this fabric
            </Button>
            {!clientImage && (
              <p
                className="text-[10px] text-[#D4A87A] uppercase tracking-widest text-center max-w-[320px]"
                data-testid="fabric-preview-no-client-hint"
              >
                ⚠ Upload a client photo to enable this render
              </p>
            )}
            {/* Iter 124 — "Design" CTA. Feb 2026: DISABLED per user
                — the Suit Designer studio is not ready for use from
                the Custom Fabric zoom view yet. Greyed out with a
                "Coming Soon" label so tailors know it's on the roadmap
                but don't hit an unfinished flow. Match the style of
                the Customize Liner "Coming Soon" chip in CustomizeModal.jsx. */}
            <Button
              disabled
              aria-disabled="true"
              variant="outline"
              className="w-full max-w-[320px] h-10 rounded-none uppercase tracking-widest text-xs font-semibold border-neutral-700 text-neutral-500 bg-neutral-900/30 cursor-not-allowed opacity-60 hover:bg-transparent"
              data-testid="fabric-preview-design-btn-disabled"
              title="Suit Designer for custom fabrics — coming soon"
            >
              <Palette className="w-3.5 h-3.5 mr-2 opacity-70" />
              Design · Coming Soon
            </Button>
            {!clientImage && (
              <p className="text-[10px] text-[#52525B] uppercase tracking-wider" data-testid="fabric-preview-no-client">
                Select a client first to enable rendering
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
